// Policies module — extracted from deskte-full.jsx (refactor #6).
// Includes: PolicyForm (create/edit), PolicyCard (display), PaymentInfoBlock
// (autopay summary), PoliciesView (the "All Policies" page), PolicyTypeManager
// (custom-type editor inside a modal).
//
// PolicyForm and PolicyCard are also used from inside ClientDetail (still in
// deskte-full.jsx). They get re-exported through the deskte-full.jsx imports
// until ClientDetail itself is extracted (refactor #7).

import { useState, useMemo, useEffect, useRef, useCallback, useDeferredValue } from 'react';
import {
  Plus, Search, Eye, Edit2, Trash2, ChevronDown, Save, AlertTriangle, Bell, X,
  CreditCard, Check, Mail, Phone, MessageSquare, Upload, CheckSquare,
  StickyNote, Send, Loader2, Image as ImageIcon, Copy, Pin, FileText,
  ArrowRightLeft, RefreshCw, Users, Tag,
} from 'lucide-react';
import { Modal, Input, Select, Textarea, Avatar, Combobox, confirmDialog, ClientSearchSelect, NoteFormatButtons, RichNote , RichNoteBox, ImageLightbox} from './ui.jsx';
import { seatLabelFor, attendOptions } from './AttendedBy.jsx';
import { opcionesDeSuplemento, suplementosUsados, normalizarTipoDeSuplemento } from '../tiposDeSuplemento.js';
// SOA y HRA de Medicare (Lissette 2026-09-13): mismo patron que el tipo de
// suplemento — sugerencias mas lo que la agencia ya usa.
import {
  ESTATUS_DE_SOA, ESTATUS_DE_HRA, FIRMA_DEL_SOA, opcionesConLasUsadas, valoresUsados, normalizarFirmaDeSoa,
  ELEGIBILIDAD_DE_MEDICAID, NIVELES_DE_LIS, lisUsadosVigentes, normalizarElegibilidadDeMedicaid,
} from '../camposDeMedicare.js';
import { contarEtiquetas, pasaElFiltroDeEtiqueta, textoSinEtiqueta } from '../filtroDeEtiquetas.js';
// Como se lee el estado y el agente de un cliente vive en clientFilters.js: es
// lo mismo que preguntan los filtros de Correo Masivo, y con una copia por
// pantalla el dia que aparezca otra forma de guardarlo unas filtrarian bien y
// otras no (Lissette 2026-09-11).
import { estadoDeCliente, agenteDeCliente } from '../clientFilters.js';
import { toast } from './Toast.jsx';
import { MoverPolizaModal } from './MoverPolizaModal.jsx';
import { CambiarEstatusModal } from './CambiarEstatusModal.jsx';
import { CambioMasivoModal } from './CambioMasivoModal.jsx';
import { QuitarEtiquetaModal, sinEtiqueta } from './QuitarEtiquetaModal.jsx';
import { GestorMotivosDeCancelacion } from './GestorMotivosDeCancelacion.jsx';
import { llevaTresPrimas, primaElegida, sufijoDePeriodo, nombreDePeriodo, PERIODOS_DE_PRIMA } from '../primaDePoliza.js';
import { enviarSmsAlCliente } from '../enviarSms.js';
import {
  SIN_DEFINIR, FORMAS_DE_PAGO, METODOS_DE_PAGO,
  pasaFiltroDeForma, pasaFiltroDeMetodo, pasaFiltroDeNota, pasaFiltroDeColumna,
  nombreDeForma, nombreDeMetodo,
} from '../filtrosDePagosDelMes.js';
import {
  claveDeColumnaPropia, comparadorDePagos, flechaDeOrden, siguienteOrden,
} from '../ordenDePagosDelMes.js';
import { pegarCuerposDePagos } from '../pegarCuerposDePagos.js';
import { elegirPlantilla, etapasDisponibles, nombreDeEtapa, mesesDeAtraso, ETIQUETAS_DE_RECORDATORIO, rellenarRecordatorio, plantillaDeFabrica } from '../plantillaDeRecordatorio.js';
import { ACEPTA_NOTA, archivoAdmitido, motivoRechazo, nombreAdjunto, tipoAdjunto } from '../adjuntosNota.js';
import { AiDraftButton } from './AiDraftButton.jsx';
import { MOTIVOS_DE_CANCELACION, etiquetaDeMotivo, partirMotivo, unirMotivo, limpiarMotivo, mostrarMotivo, todosLosMotivos, setMotivosDeAgencia, getMotivosDeAgencia } from '../motivosDeCancelacion.js';
import { aseguradosDeLaPoliza } from '../aseguradosDeLaPoliza.js';
import { vehiculosDe, conCantidad, camposConVehiculos, tieneDatos, nombreDeVehiculo, MAX_VEHICULOS } from '../vehiculosDePoliza.js';
import { espejoHaciaLaPoliza, laFichaTieneMedicare } from '../medicareDelCliente.js';
import { fmt, daysUntil, fmtMoney, fmtMoneyZero, fmtMonth, MONTHS_LIST, getCollaboratorsPref, collaboratorLabel, tagColorFor, tagColorKey, tagChipStyle, TAG_FLUOR_COLORS, importDayIso, amountInRange, parseMoney, coincideConSeleccion, componerMemberIds, formatClientName } from '../utils.js';
import { prefsGet, prefsSet } from '../uiPrefsStore.js';
import {
  allLeadSources, getAgencyLeadSources, setAgencyLeadSources, esOrigenConocido, idParaOrigenNuevo,
  // OJO: estos dos se USAN aqui dentro (la tarjeta de poliza y el importador) Y
  // ADEMAS se reexportan mas abajo. Tienen que estar en ESTE import: un
  // `export { x } from '...'` publica el nombre hacia fuera pero NO lo declara
  // dentro del archivo. Ver la nota junto a la reexportacion.
  LEAD_TYPE_PRESETS, leadTypeLabel,
} from '../leadSources.js';
import {
  POLICY_TYPES, CUSTOM_ICON_CHOICES, getPolicyType,
  addCustomPolicyType, removeCustomPolicyType, getCustomPolicyTypes,
  hasCurrentSpouse, esPolizaDeSalud, tiposPropiosDeLaAgencia,
} from '../policyTypes.js';
import {
  getAllPolicyStatuses, getPolicyStatusMeta, getCustomPolicyStatuses, setCustomPolicyStatuses, esEstatusCancelado,
  esNombreDeBaja, setPaymentStatusCatalog,
} from '../policyStatuses.js';
import { esPolizaCancelada } from '../polizaEstado.js';
import {
  getPolicyCustomFieldDefs, addPolicyCustomFieldDefs,
  getAllPolicyCustomFieldDefs, removePolicyCustomFieldDef, renamePolicyCustomFieldDef,
} from '../policyCustomFields.js';
// Los mismos mapas con los que el importador de CLIENTES traduce el texto
// suelto de una hoja ("Casado", "Married", "EAD"…) al vocabulario del
// formulario. Se importan en vez de copiarse: dos vocabularios del mismo campo
// se separan con el tiempo y el síntoma es una casilla que sale vacía.
import { STATUS_MAP, LANG_MAP, MARITAL_MAP, GENDER_MAP, IMMIGRATION_MAP, normalizeEnum } from '../api.js';
import { atiendeDesdeElArchivo } from '../atiende.js';
import {
  camposDelConyuge, camposDeDependientes, campoParaClaveDeApizeal,
  MAX_DEPENDIENTES_AL_IMPORTAR,
} from '../camposDeHogarAlImportar.js';
import { isApizealWorkbook, parseApizeal, etiquetaDeClaveApizeal } from '../apizealImport.js';
import {
  updatePolicy as apiUpdatePolicy, deletePolicy as apiDeletePolicy, listUsers,
  bulkDeletePolicies as apiBulkDeletePolicies,
  quitarCancelacionProgramada as apiQuitarCancelacionProgramada,
  createPolicy as apiCreatePolicy, importPolicies as apiImportPolicies,
  getPaymentTracker, markPolicyPayment, uploadPaymentNoteImage,
  listPolicyPaymentNotes, addPolicyPaymentNote, deletePolicyPaymentNote, updatePolicyPaymentNote, sendPaymentReminder,
  mirrorAllPaymentNotes,
  importPaymentsPreview, importPaymentsCommit, aiMapPaymentColumns, undoPaymentImport,
  listReminderTemplates, saveReminderTemplate, deleteReminderTemplate,
  updateAgencySettings as apiUpdateAgencySettings,
  getAgencySettings as apiGetAgencySettings,
  listClientPaymentMethods, savePaymentCustomData,
  listPolicyNotes, addPolicyNote, deletePolicyNote, updatePolicyNote,
  togglePolicyNotePin, togglePaymentNotePin,
} from '../api.js';
import CustomColumnsManager from './CustomColumnsManager.jsx';
import CeldaPropia from './CeldaPropia.jsx';
import { MARCA_SI, normalizeColumns, opcionesDeColumna, tipoDeColumna } from '../customColumns.js';
// Los filtros de esta lista se quedan puestos por persona (ver stickyFilter.js).
import { useStickyFilter, useStickyFilterList } from '../stickyFilter.js';
// El año en que EMPEZÓ la póliza (su fecha efectiva): el filtro de año y la
// esquina de la tarjeta salen de aquí, no cada uno del suyo (Lissette
// 2026-09-13, preguntado por varias agencias).
import { anioDePoliza, aniosDePolizas, ordenarPorAnio, pasaElFiltroDeAnio, polizasSinAnio } from '../anioDePoliza.js';
import { ordenarPorCliente } from '../ordenPorCliente.js';
import { FiltroMultiple } from './FiltroMultiple.jsx';
import { pasaElFiltroDeMarketplace, opcionesDeMarketplace, SIN_MARKETPLACE } from '../filtroDeMarketplace.js';
import { CajaConScroll, useAltoHastaAbajo, verMenuEntero } from './CajaConScroll.jsx';
// Cada agente ordena a su gusto los campos de la ficha de la poliza (2026-10-02).
import { CamposOrdenables, OrdenDeFichaContext, guardarOrdenes, restablecerOrdenes } from './CamposOrdenables.jsx';
import { guardarColumnasDeAjuste } from '../columnasDeTabla.js';

// Popular US insurance carriers organized by policy type. The PolicyForm
// carrier autocomplete filters this map by the currently-selected type,
// so picking "Auto" surfaces Progressive/GEICO/State Farm (not Aetna or
// Humana). Carriers the agency has already used are merged on top and
// deduped against whichever list applies.
//
// Keys match the frontend policyType ids in policyTypes.js. Custom or
// unknown types fall back to the union of every list (FALLBACK_CARRIERS).
// Una cifra de dinero escrita a mano ("2500", "$2,500") como "$2,500.00"; si no
// es una cifra ("varía"), se enseña tal cual en vez de inventar un $0.
const montoLegible = (v) => { const n = parseMoney(v); return n === null ? String(v) : fmtMoneyZero(n); };

export const CARRIERS_BY_TYPE = {
  // Health & ACA
  aca:      ['Aetna', 'Ambetter', 'Anthem', 'Blue Cross Blue Shield', 'Centene', 'Cigna', 'Florida Blue', 'Humana', 'Kaiser Permanente', 'Molina Healthcare', 'Oscar', 'UnitedHealthcare', 'WellCare'],
  ma:       ['Aetna', 'Anthem', 'Cigna', 'Devoted Health', 'Florida Blue', 'Humana', 'Kaiser Permanente', 'UnitedHealthcare', 'WellCare'],
  medsup:   ['AARP / UnitedHealthcare', 'Aetna', 'Cigna', 'Humana', 'Mutual of Omaha', 'Physicians Mutual', 'State Farm', 'UnitedHealthcare'],
  partd:    ['Aetna', 'Cigna', 'Humana', 'SilverScript', 'UnitedHealthcare', 'WellCare'],
  dental:   ['Aetna', 'Ameritas', 'Cigna', 'Delta Dental', 'Guardian', 'Humana', 'MetLife', 'Renaissance', 'UnitedHealthcare'],
  vision:   ['Anthem', 'Davis Vision', 'EyeMed', 'Humana', 'UnitedHealthcare', 'VSP'],
  accident: ['Aflac', 'Colonial Life', 'MetLife', 'Mutual of Omaha', 'UnitedHealthcare'],
  suppl:    ['Aflac', 'Colonial Life', 'Mutual of Omaha', 'Washington National'],
  referred: ['Aetna', 'Anthem', 'Blue Cross Blue Shield', 'Cigna', 'Humana', 'UnitedHealthcare'],
  // Life
  life:     ['AAA Life', 'AIG', 'Banner Life', 'John Hancock', 'Lincoln Financial', 'MassMutual', 'MetLife', 'Mutual of Omaha', 'New York Life', 'Northwestern Mutual', 'Pacific Life', 'Prudential', 'State Farm', 'Transamerica'],
  final:    ['AARP / New York Life', 'AIG', 'Foresters', 'Gerber Life', 'Globe Life', 'Mutual of Omaha', 'Royal Neighbors', 'Transamerica'],
  // P&C — Personal lines
  auto:     ['Allstate', 'American Family', 'Erie Insurance', 'Farmers', 'GEICO', 'Liberty Mutual', 'Nationwide', 'Progressive', 'Safeco', 'State Farm', 'The Hartford', 'Travelers', 'USAA'],
  home:     ['Allstate', 'American Family', 'Citizens', 'Erie Insurance', 'Farmers', 'Liberty Mutual', 'Nationwide', 'Safeco', 'State Farm', 'The Hartford', 'Travelers', 'USAA'],
  renters:  ['Allstate', 'Erie Insurance', 'Farmers', 'GEICO', 'Lemonade', 'Liberty Mutual', 'Nationwide', 'Progressive', 'State Farm', 'Travelers'],
  umbrella: ['Allstate', 'Chubb', 'Farmers', 'GEICO', 'Liberty Mutual', 'Nationwide', 'Progressive', 'State Farm', 'Travelers', 'USAA'],
  // P&C — Commercial
  commercial: ['Allstate Business', 'Berkshire Hathaway', 'Chubb', 'CNA', 'Hiscox', 'Liberty Mutual', 'Nationwide', 'Next Insurance', 'Progressive Commercial', 'The Hartford', 'Travelers'],
  // Specialty
  pet:      ['ASPCA', 'Embrace', 'Figo', 'Healthy Paws', 'Lemonade', 'Nationwide', 'Petplan', 'Pets Best', 'Trupanion'],
  travel:   ['AIG Travel Guard', 'Allianz', 'AXA Assistance', 'Berkshire Hathaway Travel', 'IMG', 'Travelex', 'World Nomads'],
};

// Union of every typed list — used when the selected policy type isn't
// in the map (e.g. a user-created custom type from PolicyTypeManager).
const FALLBACK_CARRIERS = [
  ...new Set(Object.values(CARRIERS_BY_TYPE).flat()),
];

// `carrierSuggestions` is the list of carrier names already used in this
// agency's policy book. Feeds the <Combobox> so the carrier field behaves
// like an autocomplete dropdown that shows the FULL list on focus (no need to
// clear the typed value first): the agent picks from the menu for the common
// case, but can still type any value the agency hasn't used before. Pass []
// (or omit) to use only the POPULAR_CARRIERS fallback.
//
// The dropdown ALWAYS has options — the popular-carriers list seeds it on
// day one so a brand-new agency isn't staring at an empty dropdown.
// Agency-specific carriers are merged on top and deduped.
// Lead-source presets for the policy card. Esta lista es AHORA LA UNICA forma
// de poner un origen: Lissette, 2026-08-28, "quita donde dice origen del lead
// la opcion de colocar otro y colocar un nombre, solo deja lo que ya estan
// predeterminados". Antes habia un campo libre y los valores escritos se
// reofrecian como fichas reusables; las polizas que ya los tienen los conservan
// y se pueden quitar con su ✕, pero no se crean nuevos.
// El catalogo de origenes del lead vive en crm-app/leadSources.js: lo leen tres
// pantallas y asi se puede probar de verdad (aqui dentro arrastraba React y las
// pruebas tenian que leer el texto fuente en vez de los valores).
//
// Se REEXPORTA porque Comisiones importa `leadTypeLabel` desde este archivo.
//
// SE REEXPORTAN LOS NOMBRES YA IMPORTADOS ARRIBA, no `export … from`. La forma
// `export { x } from '../leadSources.js'` publica el nombre hacia fuera pero NO
// LO DECLARA DENTRO DE ESTE ARCHIVO: es un puente, no un import. Escrito asi,
// `leadTypeLabel(...)` en la tarjeta de poliza y `LEAD_TYPE_PRESETS` en el
// importador reventaban en cuanto se abria una poliza — y `vite build` compila
// sin una queja, porque es JavaScript valido. Roto en produccion el 2026-08-29
// (E-148, hermano del E-144). Si vuelves a tocar esta linea: lo que se usa aqui
// dentro va en el import de arriba, siempre.
export { LEAD_TYPE_PRESETS, leadTypeLabel };

// ── Etiquetas de PÓLIZA ──────────────────────────────────────────────────────
// Lissette 2026-08-15: "creame un tag personalizado en todas la tarjeta de
// poliza ... ojo este tag personalizado no tiene nada que ver con los tag
// personalizado de cliente, llamalo diferente".
//
// Son otra cosa que las etiquetas del cliente y por eso tienen su propio
// catalogo, sus propios colores y su propia columna. Una etiqueta de cliente
// describe a la PERSONA ("VIP", "no contestar"); esta describe a la POLIZA
// ("pendiente de firma", "pago atrasado", "renovar en octubre"), se borra con
// ella y no ensucia los filtros ni las exportaciones de clientes.
//
// Vale para CUALQUIER tipo de poliza, incluidos los que cree la agencia: la
// etiqueta cuelga de la poliza, no del tipo, asi que un tipo nuevo la tiene
// desde el primer dia sin tocar nada.
//
// El catalogo se GUARDA (no se deriva de las polizas que la lleven puesta): si
// se dedujera, quitar la etiqueta de la ultima poliza la borraria del CRM y
// habria que volver a escribirla — el fallo que ya se corrigio en las de
// cliente (E de 2026-08-10).
export function PolicyTagsField({
  tags = [], onChange, lang,
  library = [], onLibraryChange, colors = {}, onColorsChange,
}) {
  const es = lang === 'es';
  const [draft, setDraft] = useState('');
  const [palette, setPalette] = useState(null); // nombre cuyo color se esta eligiendo
  const list = Array.isArray(tags) ? tags : [];
  const has = (name) => list.some((t) => tagColorKey(t) === tagColorKey(name));

  const add = (name) => {
    const v = String(name || '').trim().slice(0, 40);
    if (!v || has(v) || list.length >= 30) { setDraft(''); return; }
    onChange([...list, v]);
    setDraft('');
    // Se recuerda en el catalogo de la agencia para poder reusarla sin volver a
    // escribirla. Solo el nombre: el color se elige aparte.
    if (typeof onLibraryChange === 'function') {
      onLibraryChange((prev) => {
        const cur = Array.isArray(prev) ? prev : [];
        return cur.some((t) => tagColorKey(t) === tagColorKey(v)) ? cur : [...cur, v];
      });
    }
  };
  const remove = (name) => onChange(list.filter((t) => tagColorKey(t) !== tagColorKey(name)));

  // Quitar del catalogo NO se la quita a las polizas que ya la llevan: solo deja
  // de ofrecerse para reusar. Se confirma porque es un cambio de toda la agencia
  // hecho desde la pantalla de una poliza.
  const forget = (name) => {
    if (typeof onLibraryChange !== 'function') return;
    const ok = window.confirm(es
      ? `¿Quitar "${name}" de la lista de etiquetas de póliza?\n\nDejará de aparecer para reusar. Las pólizas que ya la tengan la conservan.`
      : `Remove "${name}" from the policy tag list?\n\nIt will no longer show up to reuse. Policies that already carry it keep it.`);
    if (!ok) return;
    onLibraryChange((prev) => (Array.isArray(prev) ? prev : [])
      .filter((t) => tagColorKey(t) !== tagColorKey(name)));
  };

  const setColor = (name, color) => {
    if (typeof onColorsChange !== 'function') return;
    const key = tagColorKey(name);
    onColorsChange((prev) => {
      const next = { ...(prev && typeof prev === 'object' ? prev : {}) };
      if (color) next[key] = color; else delete next[key];
      return next;
    });
    setPalette(null);
  };

  const reusable = (Array.isArray(library) ? library : [])
    .map((t) => String(t || '').trim())
    .filter((t) => t && !has(t))
    .sort((a, b) => a.localeCompare(b));

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: list.length ? '.5rem' : 0 }}>
        {list.map((tag) => {
          const st = tagChipStyle(tagColorFor(tag, colors));
          return (
            <span key={tag} style={{ display: 'inline-flex', alignItems: 'center', background: st.bg, border: `1px solid ${st.border}`, borderRadius: 100 }}>
              {/* El 🎨 va A LA VISTA. Antes había que adivinar que pulsando el
                  NOMBRE de la etiqueta salía la paleta, y una función que hay
                  que adivinar no existe para quien la usa (Lissette
                  2026-08-15: "quiero poder escoger colores para los tag"). */}
              <button type="button" onClick={() => setPalette(palette === tag ? null : tag)}
                title={es ? `Color de "${tag}"` : `Colour for "${tag}"`}
                style={{ fontSize: '.62rem', background: 'none', color: st.fg, padding: '.16rem .1rem .16rem .45rem', border: 'none', cursor: 'pointer', fontFamily: 'inherit', opacity: palette === tag ? 1 : .75, lineHeight: 1 }}>🎨</button>
              <button type="button" onClick={() => setPalette(palette === tag ? null : tag)}
                title={es ? `Color de "${tag}"` : `Colour for "${tag}"`}
                style={{ fontSize: '.66rem', background: 'none', color: st.fg, padding: '.16rem .3rem .16rem .2rem', border: 'none', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                {tag}
              </button>
              <button type="button" onClick={() => remove(tag)} title={es ? 'Quitar de esta póliza' : 'Remove from this policy'}
                style={{ background: 'none', border: 'none', color: st.fg, cursor: 'pointer', padding: '.16rem .5rem .16rem .2rem', fontSize: '.66rem', lineHeight: 1, opacity: .6, fontFamily: 'inherit' }}>✕</button>
            </span>
          );
        })}
      </div>

      {list.length > 0 && !palette && (
        <div style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>
          {es ? 'Pulsa 🎨 en una etiqueta para darle color. El color es el mismo para toda la agencia.' : 'Tap 🎨 on a tag to colour it. The colour is shared across the agency.'}
        </div>
      )}
      {palette && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', marginBottom: '.5rem' }}>
          <span style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', fontWeight: 700 }}>{es ? `Color de "${palette}"` : `Colour for "${palette}"`}</span>
          {TAG_FLUOR_COLORS.map((c) => (
            <button key={c} type="button" onClick={() => setColor(palette, c)} title={c}
              style={{ width: 18, height: 18, borderRadius: '50%', background: c, border: '1px solid rgba(255,255,255,.25)', cursor: 'pointer', padding: 0 }} />
          ))}
          <button type="button" onClick={() => setColor(palette, '')}
            style={{ fontSize: '.62rem', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 6, padding: '.15rem .45rem', cursor: 'pointer', fontFamily: 'inherit' }}>
            {es ? 'sin color' : 'no colour'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 5 }}>
        <input value={draft} maxLength={40} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(draft); } }}
          placeholder={es ? 'Escribe una etiqueta y pulsa Enter…' : 'Type a tag and press Enter…'}
          className="dkt-inp" style={{ flex: 1, fontSize: '.75rem', padding: '.4rem .6rem' }} />
        <button type="button" onClick={() => add(draft)} disabled={!draft.trim()}
          style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 8, padding: '.35rem .8rem', fontSize: '.72rem', fontWeight: 700, cursor: draft.trim() ? 'pointer' : 'default', opacity: draft.trim() ? 1 : .5 }}>
          {es ? 'Agregar' : 'Add'}
        </button>
      </div>

      {reusable.length > 0 && (
        <>
          <div style={{ fontSize: '.6rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em', margin: '.55rem 0 .3rem' }}>
            {es ? 'Reusar etiqueta (clic para agregar · ✕ la quita de la lista)' : 'Reuse a tag (click to add · ✕ removes it from the list)'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, maxHeight: 110, overflowY: 'auto' }}>
            {reusable.map((tag) => {
              const st = tagChipStyle(tagColorFor(tag, colors));
              return (
                <span key={tag} style={{ display: 'inline-flex', alignItems: 'center', background: st.bg, border: `1px solid ${st.border}`, borderRadius: 100 }}>
                  <button type="button" onClick={() => add(tag)} title={es ? `Agregar "${tag}"` : `Add "${tag}"`}
                    style={{ fontSize: '.62rem', background: 'none', color: st.fg, padding: '.14rem .2rem .14rem .55rem', border: 'none', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }}>
                    <span style={{ fontSize: '.62rem', lineHeight: 1 }}>＋</span>{tag}
                  </button>
                  {typeof onLibraryChange === 'function' && (
                    <button type="button" onClick={() => forget(tag)} title={es ? `Quitar "${tag}" de la lista` : `Remove "${tag}" from the list`}
                      style={{ background: 'none', border: 'none', color: st.fg, cursor: 'pointer', padding: '.14rem .45rem .14rem .2rem', fontSize: '.62rem', lineHeight: 1, opacity: .55, fontFamily: 'inherit' }}>✕</button>
                  )}
                </span>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Motivos de cancelación de uso diario, para no escribir el mismo a mano cada
// vez (Lissette 2026-08-17, con la lista que ya usa la agencia). Se guardan
// como TEXTO en el mismo campo `cancelReason` de siempre: los motivos escritos
// a mano antes de esta lista siguen viéndose igual, y quien necesite uno que no
// esté aquí elige "Otro" y lo escribe.
//
// LO QUE SE GUARDA va en inglés a propósito: es el vocabulario con el que la
// agencia ya habla con las aseguradoras, y traducirlo partiría en dos el mismo
// motivo según quién lo cancelara. Pero eso vale para el VALOR, no para lo que
// se lee: desde 2026-09-01 la pantalla enseña la etiqueta en el idioma de quien
// mira (`motivosDeCancelacion.js`), y debajo se sigue guardando el inglés de
// siempre. Las pólizas ya canceladas no cambian ni una letra.
// Marketplaces de salud: el federal HealthCare.gov y los 21 estatales (SBM).
// Lissette 2026-08-18, con la tabla oficial: la lista de la foto mas
// HealthCare.gov, que no venia en ella por ser el federal.
//
// Se guardan por su NOMBRE, no por un id: es lo que la agencia escribe en el
// tramite y lo que aparece en los correos de la aseguradora, y asi una poliza
// vieja con el nombre escrito a mano sigue coincidiendo. Un valor que no este
// en la lista no se pierde: el desplegable lo anade como opcion suya.
// Los que añade la AGENCIA, además de los 22 de fábrica. Se cargan de los
// ajustes al abrir el módulo y se guardan ahí mismo, así que lo que una agente
// escriba una vez lo encuentra la siguiente en la lista (Lissette 2026-08-19:
// "poder colocar un personalizado y que se quede allí para las próximas").
let _marketplacesAgencia = [];
export function setAgencyMarketplaces(list) {
  _marketplacesAgencia = (Array.isArray(list) ? list : [])
    .map((x) => String(x || '').trim()).filter(Boolean);
}
export function getAgencyMarketplaces() { return _marketplacesAgencia.slice(); }
// La lista completa, sin repetidos y sin distinguir mayúsculas: si alguien
// escribe "healthcare.gov" no se crea un gemelo del de fábrica.
export function allMarketplaces() {
  const vistos = new Set(MARKETPLACES.map((m) => m.toLowerCase()));
  return [...MARKETPLACES, ..._marketplacesAgencia.filter((m) => {
    const k = m.toLowerCase();
    if (vistos.has(k)) return false;
    vistos.add(k);
    return true;
  })];
}

const MARKETPLACES = [
  'HealthCare.gov',
  'Covered California',
  'Connect for Health Colorado',
  'Access Health CT',
  'DC Health Link',
  'Georgia Access',
  'Your Health Idaho',
  'Get Covered Illinois',
  'kynect health coverage',
  'CoverME.gov',
  'Maryland Health Connection',
  'Massachusetts Health Connector',
  'MNsure',
  'Nevada Health Link',
  'Get Covered New Jersey',
  'beWellnm',
  'NY State of Health',
  'Pennie',
  'HealthSource RI',
  'Vermont Health Connect',
  "Virginia's Insurance Marketplace",
  'Washington Healthplanfinder',
];



export function PolicyForm({ policy, onSave, onClose, t, lang, dependents, client, carrierSuggestions = [], supplementSuggestions = [], soaStatusSuggestions = [], hraStatusSuggestions = [], medicaidSuggestions = [], lisSuggestions = [], productTypeSuggestions = [], users = [], currentUser, knownAgents = [], knownAnalysts = [], knownProcessors = [] }) {
  const tp = t.policies;
  const empty = {
    type:"aca", carrier:"", plan:"", policyNumber:"", memberId:"", memberIdsByPerson:{}, applicationNumber:"",
    monthlyPremium:"", familySize:"1", membersActive:"1",
    // Nueva póliza entra como PENDIENTE (Lissette 2026-07-11): pasa a Activa
    // cuando se marca "Kit" en Prospecto y Referido. En edición, `...policy`
    // sobrescribe este default, así que solo aplica a pólizas nuevas.
    effectiveDate:"", renewalDate:"", status:"pending", cancelReason:"",
    deductible:"", agentName:"", producerCode:"", coverageType:"",
    liabilityCoverage:"", coverageAmount:"", beneficiary:"",
    // Life extras — mirror the carrier PolicySummary export columns (Lissette
    // 2026-07-02): owner, term, issue/premium-due dates, billing, agent/agency #.
    policyOwnerName:"", termDuration:"", issueDate:"", premiumDueDate:"",
    billablePremium:"", billingFrequency:"", billingMethod:"",
    agentNumber:"", agencyNumber:"",
    // Medicare extras (Lissette 2026-08-09) — ver POLICY_DETAIL_KEYS en api.js.
    medicareNumber:"", partADate:"", partBDate:"", companyId:"", planYear:"",
    initialPremium:"", currentPremium:"", enrollmentCode:"",
    // SOA y HRA (Lissette 2026-09-13) — ver crm-app/camposDeMedicare.js.
    soaStatus:"", soaSigned:"", hraStatus:"",
    medicaidEligibility:"", soaSignedDate:"", lisLevel:"",
    // Ficha de Medicare Advantage (Lissette 2026-10-01).
    productType:"", givebackAmount:"", partBPremium:"", contractPbpSegment:"", medicaidNumber:"", subAgent:"",
    // Vida, segunda tanda (Lissette 2026-08-09) — ciclo de la solicitud.
    approvedDate:"", deliveryPapersDate:"", deliveryDate:"", placingDate:"",
    beneficiaryContingent:"", conversionAge:"", riskClass:"",
    commissionPaid:"", commissionSplit:"", ownerAddress:"", verificationForm:"",
    // ACA y P&C (Lissette 2026-08-09) — ver POLICY_DETAIL_KEYS en api.js.
    enrollmentPeriod:"",
    premiumAnnual:"", premiumSemiAnnual:"", generalAgent:"", premiumDefault:"",
    // Dental / Visión / Mascotas / Gastos Finales (Lissette 2026-08-09).
    supplementType:"",
    annualMaximum:"", waitingPeriod:"", networkType:"", coinsurance:"", missingToothClause:"",
    examCopay:"", frameAllowance:"", contactsAllowance:"",
    petName:"", petSpecies:"", petBreed:"", petAge:"", microchipNumber:"", reimbursementPercent:"",
    funeralHomeAssignment:"",
    propertyAddress:"", propertyType:"", yearBuilt:"", squareFootage:"",
    insuredValue:"", dwellingCoverage:"",
    vehicleYear:"", vehicleMake:"", vehicleModel:"", vin:"",
    licensePlate:"", driversOnPolicy:"",
    businessName:"", businessType:"", businessAddress:"",
    numEmployees:"", annualRevenue:"",
    policyInsured:[], policyInsuredExtra:[],
    paymentType:"", paymentMethod:"", cardLast4:"", cardExpMonth:"", cardExpYear:"", cardBrand:"", bankLast4:"", payerName:"", billingDay:"",
    leadType:"", agentLabel:"", processedBy:"", processingDate:"", analystName:"", commissionPercent:"", commissionAmount:"", saleDate:"", customFields:[],
  };
  // Agents = owners/agents on the team; the account's MAIN agent defaults the
  // "Agente" field on new policies. Seats are included for "Procesado por".
  const teamAgents = (users||[]).filter(u=>["agency_owner","agency_admin","agent","solo_agent"].includes(u.role));
  const mainAgentName = (teamAgents[0]?.name) || currentUser?.name || "";
  // Agente combobox options: registered agents (NO seats — teamAgents already
  // excludes assistants) + any custom agent names already used on other policies
  // (knownAgents) + the agent assigned to THIS client, so the policy defaults to
  // "el agente como tal" and reuses hand-typed names (Lissette 2026-06-17). E-015.
  // Collaborators predefined in the agent's own profile (Mi Perfil) + the team's
  // principal agents (users carry an `npn`). Both are offered in the Agente toggle,
  // and picking one auto-fills its NPN into Producer Code (Lissette 2026-06-18).
  const profileCollaborators = getCollaboratorsPref();
  const collaboratorNames = profileCollaborators.map(collaboratorLabel).filter(Boolean);
  const npnByAgentName = (() => {
    const m = {};
    (users||[]).forEach(u => { if (u?.name && u?.npn) m[String(u.name).trim()] = String(u.npn); });
    profileCollaborators.forEach(co => { const n = collaboratorLabel(co); if (n && co.npn) m[n] = String(co.npn); });
    return m;
  })();
  const agentOptions = [...new Set([...teamAgents.map(u=>u.name), ...knownAgents, ...collaboratorNames, client?.assignedAgent].filter(Boolean))].sort();
  // Procesado options: every team member (agent + seats) + any custom name
  // typed on a previous policy (knownProcessors) so typed names are remembered.
  const processedOptions = [...new Set([...(users||[]).map(u=>u.name), ...knownProcessors].filter(Boolean))].sort();
  // Analista combobox options: names used on prior policies — type once, reuse.
  const analystOptions = [...new Set([...knownAnalysts, ...(users||[]).map(u=>u.name)].filter(Boolean))].sort();
  // No one is pre-marked. The agent explicitly picks who is insured on THIS
  // policy (primary, spouse, each dependent) — nothing is assumed. A saved
  // selection is loaded as-is; a new or never-edited policy starts empty so
  // the count reflects exactly what the agent checks.
  const hasSpouse = hasCurrentSpouse(client);
  const [form,setForm] = useState(
    policy
      ? {...empty, ...policy,
          policyInsured: Array.isArray(policy.policyInsured) ? policy.policyInsured : [],
          policyInsuredExtra: policy.policyInsuredExtra || [],
          processingDate: policy.processingDate || "",
          customFields: Array.isArray(policy.customFields) ? policy.customFields : []}
      : {...empty, policyInsured: [], agentLabel: (client?.assignedAgent || mainAgentName)}
  );

  // El origen escrito a mano que YA tiene esta poliza, si lo tiene. Se sigue
  // enseñando aunque no se puedan crear nuevos (Lissette 2026-08-28):
  // esconderlo no lo borraria de la base, y la agente creeria que la poliza no
  // tiene origen. La ✕ que lleva al lado es la unica forma de quitarlo.
  //
  // VA AQUI ABAJO Y NO ARRIBA: lee `form`, y declarado antes que el useState
  // que lo crea reventaba al abrir el formulario — la pantalla no llegaba ni a
  // pintarse. Ver E-144.
  const origenEscritoAMano = form.leadType && !esOrigenConocido(form.leadType)
    ? form.leadType : "";

  // Las personas aseguradas en ESTA poliza, MENOS el titular: son las que
  // pueden tener un member id propio ademas del suyo (Lissette 2026-08-28).
  // Las claves son las mismas de policyInsured, que es lo que permite guardar
  // cada numero atado a la persona y no como una lista suelta.
  //
  // VA DEBAJO DEL useState QUE CREA `form`, no encima: leerlo antes de que
  // exista revienta la pantalla entera al abrirla, y no da error al compilar.
  // Ver E-144.
  const otrosAsegurados = (() => {
    const marcadas = Array.isArray(form.policyInsured) ? form.policyInsured : [];
    if (marcadas.length < 2) return [];
    const out = [];
    if (client && hasSpouse && marcadas.includes("spouse")) {
      const n = `${client.spouse.firstName||""} ${client.spouse.lastName||""}`.trim();
      out.push({ key:"spouse", name:n||(lang==="es"?"Cónyuge":"Spouse"), rel:lang==="es"?"Cónyuge":"Spouse" });
    }
    (dependents||[]).forEach((d,i)=>{
      const key = d.id || ("dep"+i);
      if (!marcadas.includes(key)) return;
      const n = `${d.firstName||""} ${d.lastName||""}`.trim();
      out.push({ key, name:n||(lang==="es"?`Dependiente ${i+1}`:`Dependent ${i+1}`), rel:d.relation||"" });
    });
    return out;
  })();
  // Pick the popular-carriers list that matches the current policy type;
  // fall back to the union of every list for custom/unknown types.
  // Agency-specific carriers always merge on top regardless of type.
  // MUST be declared after `form` — accessing form.type before useState
  // returns is a ReferenceError (temporal dead zone) and crashes the
  // modal to a blank screen.
  const mergedCarriers = useMemo(() => {
    const typed = CARRIERS_BY_TYPE[form.type] || FALLBACK_CARRIERS;
    return [...new Set([...carrierSuggestions, ...typed])].sort();
  }, [carrierSuggestions, form.type]);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  // Cancelling a policy requires a written reason (Lissette 2026-06-26): the
  // textarea appears only when status is "cancelled" and Save is blocked until
  // it's filled. The reason rides along in type_details (cancelReason) and is
  // shown on the cancelled policy card.
  const [cancelErr,setCancelErr]=useState("");
  // El motivo se guarda en UN campo de texto, pero se edita en dos: la lista y
  // una nota libre. Cualquiera de los dos basta para cancelar, y se pueden usar
  // los dos a la vez (Lissette 2026-08-17: "si escoge uno del drop down y no
  // pone motivo, dejar cancelar; y si pone motivo y no drop down, también").
  // Al abrir una póliza ya cancelada se parte lo guardado para poder seguir
  // editándolo: si empieza por un motivo de la lista, ese es el de la lista y
  // lo que venga tras el guion es la nota; si no, todo es nota.
  const cancelParts = partirMotivo(form.cancelReason);
  const setCancelParts = (next) => {
    const preset = next.preset !== undefined ? next.preset : cancelParts.motivo;
    const note = next.note !== undefined ? next.note : cancelParts.nota;
    // SIN recortar. Antes había un `note.trim()` AQUÍ, y se ejecutaba en cada
    // tecla: al escribir "hola " el espacio del final desaparecía en el acto y
    // la siguiente letra caía pegada — "holamundo" (Lissette 2026-09-01: "no
    // acepta espacio, todo lo escribe pegado"). Los extremos se limpian al
    // GUARDAR, que es cuando estorban.
    set("cancelReason", unirMotivo(preset, note));
    if (cancelErr) setCancelErr("");
  };
  // Saved payment methods for THIS client (masked list — holder name + last-4,
  // no PIN needed). Lets the agent auto-fill the policy's Payment Info from a
  // method already on file (foto 4) instead of re-typing the account holder +
  // last-4. Loaded once per client.
  const [savedPayMethods, setSavedPayMethods] = useState([]);
  const [selectedSavedId, setSelectedSavedId] = useState(""); // which saved method the dropdown shows (#13)
  // Create a custom status right from the policy card/form (Lissette 2026-07-01):
  // "+ Estatus" next to the status selector opens the manager; statusVer re-reads
  // the catalog so a just-created status shows in the dropdown immediately.
  const [statusMgr,setStatusMgr]=useState(false);
  // Repinta el desplegable de marketplaces cuando la agencia añade uno.
  const [mktVer,setMktVer]=useState(0);
  // Repinta el desplegable de motivos cuando la agencia anade uno.
  const [motivoVer,setMotivoVer]=useState(0);
  const [gestorMotivos,setGestorMotivos]=useState(false); // editar/quitar los motivos propios (2026-10-22)
  const [statusVer,setStatusVer]=useState(0);
  // Maps a saved method's `type` to the policy form's paymentMethod options.
  const PM_TYPE_MAP = { credit_card:"credit_card", debit_card:"debit_card", bank_account:"bank_account" };
  // Fill the policy Payment Info fields from a saved method (holder + last-4,
  // plus card brand/exp). Never blanks a field the agent already typed.
  const applySavedMethod = (m) => {
    if (!m) return;
    const mapped = PM_TYPE_MAP[m.type];
    setForm(f => ({
      ...f,
      paymentType: f.paymentType || "autopay",
      ...(mapped ? { paymentMethod: mapped } : {}),
      payerName: m.cardholderName || f.payerName || "",
      ...(m.type === "bank_account"
        ? { bankLast4: m.acctLast4 || f.bankLast4 || "" }
        : { cardLast4: m.cardLast4 || f.cardLast4 || "", cardBrand: m.cardBrand || f.cardBrand || "", cardExpMonth: m.expiryMonth || f.cardExpMonth || "", cardExpYear: m.expiryYear || f.cardExpYear || "" }),
    }));
  };
  useEffect(() => {
    let cancelled = false;
    const apiMode = typeof window !== "undefined" && !!window.__deskteAuth;
    if (!apiMode || !client?.id) { setSavedPayMethods([]); return; }
    listClientPaymentMethods(client.id)
      .then(list => { if (!cancelled) setSavedPayMethods(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setSavedPayMethods([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);
  // Pre-select the saved method matching the policy's current payment so the
  // dropdown shows the CHOSEN account, not "— Seleccionar —" (Lissette #13:
  // "tras elegir cuenta sigue diciendo selecciona cuenta").
  useEffect(() => {
    if (selectedSavedId || !savedPayMethods.length) return;
    const l4 = form.bankLast4 || form.cardLast4;
    if (!l4) return;
    const m = savedPayMethods.find(x => (x.acctLast4 || x.cardLast4) === l4);
    if (m) setSelectedSavedId(m.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedPayMethods, form.bankLast4, form.cardLast4]);
  // Agency-shared custom fields: as soon as the type is known (and whenever it
  // changes), surface that type's defined field NAMES as empty rows ready to
  // fill. A field defined on any health policy thus appears on every health
  // policy; defined on a life policy → every life policy; and so on. Only ADDS
  // rows that aren't already present (by label) — never wipes a typed value,
  // and rows the agent removed manually for this policy stay removed until the
  // modal is reopened (the catalog re-seeds on open, which is intended: the
  // field belongs to all policies of the type).
  useEffect(() => {
    const defs = getPolicyCustomFieldDefs(form.type);
    if (!defs.length) return;
    setForm((f) => {
      const existing = Array.isArray(f.customFields) ? f.customFields : [];
      const present = new Set(
        existing.map((cf) => String(cf?.label || '').trim().toLowerCase()).filter(Boolean)
      );
      const toAdd = defs
        .filter((label) => !present.has(label.toLowerCase()))
        .map((label) => ({ label, value: '' }));
      if (!toAdd.length) return f;
      return { ...f, customFields: [...existing, ...toAdd] };
    });
  }, [form.type]);
  // Save: before handing the policy up, promote any field NAMES typed on this
  // policy into the agency-shared catalog for its type, so the same fields show
  // up on every other policy of that type. Persisting the catalog is best-effort
  // (it must never block saving the policy itself) and mirrors how the custom
  // policy-status manager writes straight to agency settings.
  // UN SOLO GUARDADO A LA VEZ (Lissette 2026-10-20: en la agencia de Josef
  // "se crean 4 veces"). Con el servidor lento, el boton seguia activo y cada
  // clic creaba otra poliza. Ahora el boton queda en "Guardando…" hasta que
  // termina, y la poliza nueva lleva una clave para que el servidor tampoco
  // cree dos con el mismo clic (ver claveDeCreacion en policies.routes.ts).
  const [guardandoPoliza, setGuardandoPoliza] = useState(false);
  const guardandoRef = useRef(false);
  const claveDeCreacion = useRef(
    (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `k${Date.now()}${Math.random().toString(36).slice(2)}`,
  );
  const handleSave = async () => {
    if (guardandoRef.current) return;
    guardandoRef.current = true;
    setGuardandoPoliza(true);
    try { await guardarPoliza(); } finally { guardandoRef.current = false; setGuardandoPoliza(false); }
  };
  const guardarPoliza = async () => {
    // Block cancellation without the DATE. The API rejects it too; catching it
    // here means the agent gets told in the form instead of via a save failure.
    // Checked before the reason so the error matches the field order (the date
    // input renders first in the cancellation cluster).
    if (esEstatusCancelado(form.status) && !String(form.cancellationDate || '').trim()) {
      setCancelErr(lang === 'es'
        ? 'Pon la fecha de cancelación para poder guardar.'
        : 'Enter the cancellation date before saving.');
      return;
    }
    // ...and without a reason — the agent must say WHY (Lissette). Vale
    // CUALQUIERA de los dos caminos: los dos escriben en `cancelReason`, así
    // que basta con que el campo no quede vacío.
    if (esEstatusCancelado(form.status) && !String(form.cancelReason || '').trim()) {
      setCancelErr(lang === 'es'
        ? 'Elige un motivo de la lista o escríbelo — con uno de los dos basta.'
        : 'Pick a reason from the list or type one — either is enough.');
      return;
    }

    const labels = (form.customFields || [])
      .map((cf) => String(cf?.label || '').trim())
      .filter(Boolean);
    const nextMap = addPolicyCustomFieldDefs(form.type, labels);
    if (nextMap) {
      try { await apiUpdateAgencySettings({ policyCustomFieldDefs: nextMap }); }
      catch { /* non-fatal: the field still saves on the policy itself */ }
    }
    // Los espacios de los extremos se recortan AQUÍ, al guardar, y no mientras
    // se teclea: al guardar estorban, al escribir son la palabra siguiente.
    // Se manda una copia — mutar `form`, que es estado de React, deja la
    // pantalla y el estado contando cosas distintas.
    // El codigo de Medicaid se endereza tambien AQUI: el `onBlur` del campo
    // cubre el camino normal, pero guardar sin salir del campo no pasaria por
    // el, y un `qmb+` guardado tal cual parte en dos la cuenta de "cuantos QMB
    // tengo".
    await onSave({
      ...form,
      ...(form.id ? {} : { claveDeCreacion: claveDeCreacion.current }),
      // Cancelada = no se renueva: la fecha de renovacion se anula (el servidor
      // hace lo mismo; aqui es para que la pantalla no la siga ensenando).
      ...(esEstatusCancelado(form.status) ? { renewalDate: '' } : {}),
      cancelReason: limpiarMotivo(form.cancelReason),
      medicaidEligibility: normalizarElegibilidadDeMedicaid(form.medicaidEligibility),
    });
  };
  const isPC = POLICY_TYPES.find(p=>p.id===form.type)?.pc;
  const healthTypes = POLICY_TYPES.filter(p=>!p.pc);
  const pcTypes = POLICY_TYPES.filter(p=>p.pc);
  const customHealthTypes = healthTypes.filter(p=>p.custom);
  const customPCTypes = pcTypes.filter(p=>p.custom);
  const isHome = ["home","renters"].includes(form.type);
  const isAuto = form.type==="auto";
  const isComm = form.type==="commercial";
  // Life / Final Expense carry a "face amount" (death benefit) + beneficiary.
  const isLife = ["life","final"].includes(form.type);
  // Medicare (Advantage / Suplemento / Parte D) trae el MBI y las fechas de
  // Parte A y B — datos que no existen en ningún otro ramo.
  const isMedicare = ["ma","medsup","partd"].includes(form.type);
  // ACA / Marketplace: ID del hogar, prima neta, período de inscripción (OE/SEP)
  // y documentos pendientes.
  const isAca = form.type==="aca";
  // El # de Aplicación, el Subsidio y el Costo Total del Plan son SOLO de Salud
  // (Lissette 2026-08-28). La regla vive en policyTypes.js: antes eran dos
  // listas negativas escritas a mano, una aquí y otra en la tarjeta, que ya se
  // habían separado entre sí. Familia, Miembros Activos y la Prima Mensual se
  // quedan en todos los ramos, como siempre.
  const esSalud = esPolizaDeSalud(form.type);
  // Ramos con "términos del plan" propios en los checklists de solicitud.
  const isDental = form.type==="dental";
  const isVision = form.type==="vision";
  const isPet = form.type==="pet";
  const isFinal = form.type==="final";
  // Short Term Medical: cobertura temporal por meses. Sus datos propios son el
  // termino, el maximo de bolsillo, el beneficio maximo del periodo, si es
  // renovable y si excluye preexistentes — lo ultimo es LA caracteristica que
  // distingue este ramo y que el agente tiene que poder consultar despues.
  const isShortTerm = form.type==="shortterm";
  // Suplementario: "el ramo" no dice QUE suplemento es. Ver tiposDeSuplemento.js.
  const isSuppl = form.type==="suppl";
  // "Familia" + "Miembros Activos" are derived, never hand-typed: Family =
  // household size; Active = how many people are actually selected as insured
  // on THIS policy. Keeps these boxes in agreement with the card + chips
  // instead of the old manual fields that were frozen at 1.
  const householdSize = 1 + (hasSpouse?1:0) + ((dependents&&dependents.length)||0);
  const insuredCount = ((form.policyInsured&&form.policyInsured.length)||0)
    + ((form.policyInsuredExtra&&form.policyInsuredExtra.filter(Boolean).length)||0);
  const SectionLabel = ({children}) => (
    <div style={{fontSize:".62rem",fontWeight:800,color:"var(--c-cyan,#00d4ff)",textTransform:"uppercase",
      letterSpacing:".1em",marginTop:".25rem",marginBottom:"-.25rem",paddingBottom:".25rem",
      borderBottom:"1px solid var(--c-border,#1a3550)"}}>
      {children}
    </div>
  );
  {/* Enter dentro del formulario guardaba por AQUI, saltandose `handleSave`:
      sin recortar el motivo de cancelacion, sin registrar los campos propios
      nuevos y sin enderezar el codigo de Medicaid. Es el mismo boton. */}
  return <><form onSubmit={e=>{e.preventDefault();handleSave();}} style={{display:"flex",flexDirection:"column",gap:".875rem"}}>
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{tp.type}</label>
      <div className="mb-1">
        <div className="text-[10px] font-bold text-blue-500 uppercase tracking-wider mb-1.5 px-0.5">Health . Medicare . Life</div>
        <div className="grid grid-cols-4 gap-1.5">
          {healthTypes.map(pt=><button key={pt.id} type="button" onClick={()=>set("type",pt.id)}
            className={`flex flex-col items-center gap-1 p-2 rounded-xl border transition-all text-center ${form.type===pt.id?"border-blue-400 bg-blue-600/10":"border-gray-200 bg-gray-50 hover:border-blue-300"}`}>
            <span className="text-lg">{pt.icon}</span>
            <span className="text-[9px] text-gray-700 leading-tight">{lang==="es"?pt.labelEs:pt.label}</span>
          </button>)}
        </div>
      </div>
      <div className="mt-2">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="text-[10px] font-bold text-cyan-600 uppercase tracking-wider px-0.5">Property &amp; Casualty (P&amp;C)</div>
          <span className="text-[9px] bg-cyan-500/10 text-cyan-600 px-2 py-0.5 rounded-full font-semibold">New</span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {pcTypes.map(pt=><button key={pt.id} type="button" onClick={()=>set("type",pt.id)}
            className={`flex flex-col items-center gap-1 p-2 rounded-xl border transition-all text-center ${form.type===pt.id?"border-cyan-400 bg-cyan-600/10":"border-cyan-100 bg-cyan-50/50 hover:border-cyan-300"}`}>
            <span className="text-lg">{pt.icon}</span>
            <span className="text-[9px] text-gray-700 leading-tight">{lang==="es"?pt.labelEs:pt.label}</span>
          </button>)}
        </div>
      </div>
      {(customHealthTypes.length>0 || customPCTypes.length>0) && <div className="mt-2">
        <div className="text-[10px] font-bold text-purple-500 uppercase tracking-wider mb-1.5 px-0.5">{lang==="es"?"Personalizados":"Custom"}</div>
        <div className="grid grid-cols-5 gap-1.5">
          {[...customHealthTypes, ...customPCTypes].map(pt=><button key={pt.id} type="button" onClick={()=>set("type",pt.id)}
            className={`flex flex-col items-center gap-1 p-2 rounded-xl border transition-all text-center ${form.type===pt.id?"border-purple-400 bg-purple-600/10":"border-purple-100 bg-purple-50/50 hover:border-purple-300"}`}>
            <span className="text-lg">{pt.icon}</span>
            <span className="text-[9px] text-gray-700 leading-tight">{lang==="es"?pt.labelEs:pt.label}</span>
          </button>)}
        </div>
      </div>}
    </div>
    {isPC && <div className="flex items-center gap-2 bg-cyan-50 border border-cyan-200 rounded-xl px-3 py-2">
      <span className="text-sm">{POLICY_TYPES.find(p=>p.id===form.type)?.icon}</span>
      <span className="text-xs font-semibold text-cyan-700">P&amp;C - {lang==="es"?POLICY_TYPES.find(p=>p.id===form.type)?.labelEs:POLICY_TYPES.find(p=>p.id===form.type)?.label}</span>
    </div>}
    <SectionLabel>{lang==="es"?"📋 Información General":"📋 General Info"}</SectionLabel>
    <div className="grid grid-cols-2 gap-3">
      <Combobox
        label={tp.carrier}
        value={form.carrier}
        onChange={v=>set("carrier",v)}
        required
        placeholder={isPC?"State Farm / GEICO / Allstate":"Aetna / Humana"}
        options={mergedCarriers}
        lang={lang}
      />
      <Input label={tp.plan} value={form.plan} onChange={e=>set("plan",e.target.value)} placeholder={isPC?"HO-3 / Full Coverage / BOP":"Plan name"}/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={tp.policyNumber} value={form.policyNumber} onChange={e=>set("policyNumber",e.target.value)} placeholder=""/>
      {isPC
        ? <Input label={lang==="es"?"Tipo de Cobertura":"Coverage Type"} value={form.coverageType||""} onChange={e=>set("coverageType",e.target.value)} placeholder={isAuto?"Liability / Comp / Collision":isHome?"HO-3 / HO-4 / HO-6":"BOP / GL / WC"}/>
        : <Input label={tp.memberId} value={form.memberId} onChange={e=>set("memberId",e.target.value)} placeholder=""/>
      }
    </div>
    {/* El # de Aplicación es del trámite del Marketplace; una póliza de mascota
        no pasa por ahí (Lissette 2026-08-10: "mascota quita ese"). */}
    {!isPC && !isPet && <div className={`grid gap-3 ${isAca?"grid-cols-3":"grid-cols-2"}`}>
      {esSalud && <Input label={lang==="es"?"Número de Aplicación":"Application Number"} value={form.applicationNumber||""} onChange={e=>set("applicationNumber",e.target.value)} placeholder={lang==="es"?"# de aplicación (Marketplace)":"Application # (Marketplace)"}/>}
      {/* Período de inscripción — subido aquí desde su propia sección
          (Lissette 2026-08-10: "mueve el otro arriba"). Va junto al # de
          aplicación porque son el mismo momento del trámite, y de paso llena
          el hueco que quedaba a la derecha. Solo en ACA: OE y SEP son períodos
          del Marketplace y no significan nada en los demás ramos. */}
      {isAca
        ? <Input label={lang==="es"?"Período de inscripción":"Enrollment Period"} value={form.enrollmentPeriod||""} onChange={e=>set("enrollmentPeriod",e.target.value)} placeholder="OE / SEP"/>
        : <div/>}
      {/* Nombre del Marketplace — al lado del período de inscripción, que es el
          mismo momento del trámite (Lissette 2026-08-18). Un valor que ya
          estuviera escrito y no esté en la lista se añade como opción propia:
          elegir del desplegable no puede borrar lo que ya había. */}
      {isAca && (
        <Select label={lang==="es"?"Nombre del Marketplace":"Marketplace Name"} value={form.marketplaceName||""}
          onChange={async (e)=>{
            const v = e.target.value;
            if (v !== "__nuevo__") { set("marketplaceName", v); return; }
            // "Agregar otro" — se pregunta el nombre y se guarda EN LA AGENCIA,
            // no solo en esta póliza: la próxima ya lo encuentra en la lista.
            const nombre = (window.prompt(lang==="es"
              ? "Nombre del Marketplace (se guardará para las próximas pólizas):"
              : "Marketplace name (it will be saved for future policies):") || "").trim();
            if (!nombre) return;                      // canceló: no se toca nada
            set("marketplaceName", nombre);
            if (allMarketplaces().some((m)=>m.toLowerCase()===nombre.toLowerCase())) return; // ya estaba
            const siguiente = [...getAgencyMarketplaces(), nombre];
            setAgencyMarketplaces(siguiente);         // visible YA, sin esperar al servidor
            setMktVer((n)=>n+1);
            try {
              await apiUpdateAgencySettings({ marketplaces: siguiente });
            } catch (err) {
              // Si no se pudo guardar hay que DECIRLO: quedarse callada haría
              // creer que está en la lista y desaparecería al recargar.
              toast.error((lang==="es"
                ? "No se pudo guardar el marketplace para las próximas pólizas: "
                : "Could not save the marketplace for future policies: ") + (err.message||""));
            }
          }}>
          <option value="">{lang==="es"?"— Seleccionar —":"— Select —"}</option>
          {(()=>{ void mktVer; return allMarketplaces().map((m)=><option key={m} value={m}>{m}</option>); })()}
          {form.marketplaceName && !allMarketplaces().some((m)=>m.toLowerCase()===String(form.marketplaceName).toLowerCase()) && <option value={form.marketplaceName}>{form.marketplaceName}</option>}
          <option value="__nuevo__">{lang==="es"?"➕ Agregar otro…":"➕ Add another…"}</option>
        </Select>
      )}
    </div>}
    <SectionLabel>{lang==="es"?"💰 Prima y Cobertura":"💰 Premium & Coverage"}</SectionLabel>
    <div className={`grid gap-3 ${isPet && !isPC ? "grid-cols-1" : "grid-cols-3"}`}>
      <Input label={lang==="es"?"Prima Mensual ($)":"Monthly Premium ($)"} type="number" step="0.01" min="0" value={form.monthlyPremium} onChange={e=>set("monthlyPremium",e.target.value)} placeholder="0.00"/>
      {isPC && <Input label={lang==="es"?"Deducible ($)":"Deductible ($)"} value={form.deductible||""} onChange={e=>set("deductible",e.target.value)} placeholder="1,000"/>}
      {isPC && <Input label={lang==="es"?"Cobertura Responsabilidad":"Liability Coverage ($)"} value={form.liabilityCoverage||""} onChange={e=>set("liabilityCoverage",e.target.value)} placeholder="100,000"/>}
      {!isPC && !isPet && <Input label={tp.familySize} type="number" value={householdSize} readOnly title={lang==="es"?"Automático: tamaño del hogar (titular + cónyuge + dependientes)":"Automatic: household size (primary + spouse + dependents)"}/>}
      {!isPC && !isPet && <Input label={tp.membersActive} type="number" value={insuredCount} readOnly title={lang==="es"?"Automático: personas marcadas como aseguradas en esta póliza":"Automatic: people marked insured on this policy"}/>}
    </div>
    {/* Mensual, semestral y anual (Lissette 2026-10-22): en todos los ramos
        menos Medicare, Salud y Vida. P&C ya tenía la anual y la semestral en
        su sección; aquí se añaden para los demás, y para todos se elige cuál
        se enseña en la vista previa y cuenta para la comisión. */}
    {llevaTresPrimas(form.type) && <div className="grid grid-cols-3 gap-3">
      {!isPC && <Input label={lang==="es"?"Prima semestral ($)":"Semi-annual Premium ($)"} type="number" step="0.01" min="0" value={form.premiumSemiAnnual||""} onChange={e=>set("premiumSemiAnnual",e.target.value)} placeholder="0.00"/>}
      {!isPC && <Input label={lang==="es"?"Prima anual ($)":"Annual Premium ($)"} type="number" step="0.01" min="0" value={form.premiumAnnual||""} onChange={e=>set("premiumAnnual",e.target.value)} placeholder="0.00"/>}
      <Select label={lang==="es"?"Prima a mostrar en la vista previa":"Premium shown in the preview"} value={form.premiumDefault||""} onChange={e=>set("premiumDefault",e.target.value)}>
        <option value="">{lang==="es"?"La que esté llena":"Whichever is filled"}</option>
        {PERIODOS_DE_PRIMA.map(pp=><option key={pp} value={pp}>{nombreDePeriodo(pp, lang)}</option>)}
      </Select>
    </div>}
    {esSalud && <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Subsidio ($) — opcional":"Subsidy ($) — optional"} type="number" step="0.01" min="0" value={form.subsidy||""} onChange={e=>set("subsidy",e.target.value)} placeholder="0.00"/>
      <Input label={lang==="es"?"Costo Total del Plan ($) — opcional":"Plan Total Cost ($) — optional"} type="number" step="0.01" min="0" value={form.planTotalCost||""} onChange={e=>set("planTotalCost",e.target.value)} placeholder="0.00"/>
    </div>}
    {/* Los costos del plan de salud (Lissette 2026-10-08): lo que el cliente
        pregunta cuando va al médico. El deducible es la MISMA columna
        `deductible` de los demás ramos y el máximo de bolsillo la misma clave
        `outOfPocketMax` de Short Term; solo el PCP y la emergencia son nuevos.
        Esos dos son texto libre a propósito: un plan los da en dólares ("$30")
        y otro en porcentaje ("20% después del deducible"). Se llenan a mano o
        llegan solos al crear la póliza desde una cotización. */}
    {esSalud && <>
    <SectionLabel>{lang==="es"?"🩺 Costos del plan":"🩺 Plan Costs"}</SectionLabel>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Deducible ($)":"Deductible ($)"} value={form.deductible||""} onChange={e=>set("deductible",e.target.value)} placeholder="2,500"/>
      <Input label={lang==="es"?"Máximo de bolsillo (Out of Pocket) ($)":"Out-of-pocket Max ($)"} value={form.outOfPocketMax||""} onChange={e=>set("outOfPocketMax",e.target.value)} placeholder="9,200"/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Doctor primario (PCP)":"Primary Care (PCP)"} value={form.pcpCopay||""} onChange={e=>set("pcpCopay",e.target.value)} placeholder={lang==="es"?"$30 / 20%":"$30 / 20%"}/>
      <Input label={lang==="es"?"Sala de emergencia":"Emergency Room"} value={form.erCopay||""} onChange={e=>set("erCopay",e.target.value)} placeholder={lang==="es"?"$500 / 40% después del deducible":"$500 / 40% after deductible"}/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Especialista":"Specialist"} value={form.specialistCopay||""} onChange={e=>set("specialistCopay",e.target.value)} placeholder={lang==="es"?"$60 / 30%":"$60 / 30%"}/>
      <Input label={lang==="es"?"Medicina genérica":"Generic Drugs"} value={form.genericDrugCopay||""} onChange={e=>set("genericDrugCopay",e.target.value)} placeholder="$15"/>
    </div>
    </>}
    {isPC && <>
    <SectionLabel>{lang==="es"?"🏘️ Prima P&C y agente general":"🏘️ P&C Premium & General Agent"}</SectionLabel>
    {/* P&C (Lissette 2026-08-09): aquí la prima casi nunca es mensual — se
        cotiza anual o semestral. La mensual de arriba se queda como está; estas
        dos se AÑADEN para poder comparar sin hacer cuentas a mano. */}
    <div className="grid grid-cols-3 gap-3">
      <Input label={lang==="es"?"Prima anual ($)":"Annual Premium ($)"} type="number" step="0.01" min="0" value={form.premiumAnnual||""} onChange={e=>set("premiumAnnual",e.target.value)} placeholder="0.00"/>
      <Input label={lang==="es"?"Prima semestral ($)":"Semi-annual Premium ($)"} type="number" step="0.01" min="0" value={form.premiumSemiAnnual||""} onChange={e=>set("premiumSemiAnnual",e.target.value)} placeholder="0.00"/>
      <Input label={lang==="es"?"Agente general (MGA)":"General Agent (MGA)"} value={form.generalAgent||""} onChange={e=>set("generalAgent",e.target.value)} placeholder=""/>
    </div>
    </>}
    {/* Dental / Visión / Mascotas / Gastos Finales (Lissette 2026-08-09).
        Salen de la sección "Plan Terms to Review with Client" de los checklists
        de solicitud: son las condiciones que se repasan con el cliente al
        vender y que nadie recuerda cuando llama a reclamar. El deducible NO se
        repite aquí — ya existe arriba y es el mismo dato. */}
    {isSuppl && <>
    <SectionLabel>{lang==="es"?"➕ Tipo de suplemento":"➕ Supplement type"}</SectionLabel>
    <div className="grid grid-cols-2 gap-3">
      {/* Desplegable que TAMBIEN deja escribir (el mismo Combobox de la
          aseguradora). La lista son sugerencias mas lo que la agencia ya usa:
          cerrarla obligaria a tenerla completa el primer dia, y lo que llega
          de un importe es el texto que puso otra persona. */}
      <Combobox
        label={lang==="es"?"¿Qué suplemento es?":"Which supplement is it?"}
        value={form.supplementType||""}
        onChange={v=>set("supplementType",v)}
        options={opcionesDeSuplemento(supplementSuggestions, form.supplementType)}
        placeholder={lang==="es"?"Accidente, Cáncer, Hospitalización…":"Accident, Cancer, Hospital…"}
        lang={lang}
      />
    </div>
    </>}
    {isDental && <>
    <SectionLabel>{lang==="es"?"🦷 Términos del plan dental":"🦷 Dental Plan Terms"}</SectionLabel>
    {/* El deducible va aquí y no en "Prima y Cobertura": arriba solo se muestra
        en P&C, así que dental se quedaba sin él pese a que su checklist lo pide
        junto al máximo anual (Lissette 2026-08-10). Es la MISMA columna
        `deductible` de siempre, no un campo nuevo. */}
    <div className="grid grid-cols-3 gap-3">
      <Input label={lang==="es"?"Máximo anual ($)":"Annual Maximum ($)"} value={form.annualMaximum||""} onChange={e=>set("annualMaximum",e.target.value)} placeholder="1,500"/>
      <Input label={lang==="es"?"Deducible ($)":"Deductible ($)"} value={form.deductible||""} onChange={e=>set("deductible",e.target.value)} placeholder="1,000"/>
      <Input label={lang==="es"?"Tipo de red":"Network Type"} value={form.networkType||""} onChange={e=>set("networkType",e.target.value)} placeholder="DPPO / DHMO / indemnización"/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Períodos de espera":"Waiting Periods"} value={form.waitingPeriod||""} onChange={e=>set("waitingPeriod",e.target.value)} placeholder={lang==="es"?"Básicos 6 meses / mayores 12":"Basic 6 mo / major 12"}/>
      <Input label={lang==="es"?"Coaseguro (prev./básico/mayor)":"Coinsurance (prev./basic/major)"} value={form.coinsurance||""} onChange={e=>set("coinsurance",e.target.value)} placeholder="100 / 80 / 50"/>
    </div>
    <Input label={lang==="es"?"Cláusula de diente ausente / preexistencias":"Missing Tooth Clause / Pre-existing"} value={form.missingToothClause||""} onChange={e=>set("missingToothClause",e.target.value)} placeholder={lang==="es"?"Sí / No — y qué limita":"Yes / No — and what it limits"}/>
    </>}
    {isShortTerm && <>
    <SectionLabel>{lang==="es"?"⏳ Términos del plan temporal":"⏳ Short Term Plan Terms"}</SectionLabel>
    <div className="grid grid-cols-3 gap-3">
      <Input label={lang==="es"?"Duración del término":"Term Duration"} value={form.termDuration||""} onChange={e=>set("termDuration",e.target.value)} placeholder={lang==="es"?"3 meses":"3 months"}/>
      <Input label={lang==="es"?"Deducible ($)":"Deductible ($)"} value={form.deductible||""} onChange={e=>set("deductible",e.target.value)} placeholder="2,500"/>
      <Input label={lang==="es"?"Coaseguro":"Coinsurance"} value={form.coinsurance||""} onChange={e=>set("coinsurance",e.target.value)} placeholder="80 / 20"/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Máximo de bolsillo ($)":"Out-of-pocket Max ($)"} value={form.outOfPocketMax||""} onChange={e=>set("outOfPocketMax",e.target.value)} placeholder="7,000"/>
      <Input label={lang==="es"?"Beneficio máximo del término ($)":"Max Benefit per Term ($)"} value={form.benefitMax||""} onChange={e=>set("benefitMax",e.target.value)} placeholder="1,000,000"/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="dkt-input-lbl">{lang==="es"?"¿Renovable al vencer?":"Renewable at term end?"}</label>
        <select className="dkt-inp" value={form.renewable||""} onChange={e=>set("renewable",e.target.value)}>
          <option value="">{lang==="es"?"— sin definir —":"— not set —"}</option>
          <option value="yes">{lang==="es"?"Sí":"Yes"}</option>
          <option value="no">{lang==="es"?"No":"No"}</option>
        </select>
      </div>
      <div>
        {/* La razon por la que este ramo es barato, y la que mas reclamos
            genera cuando nadie la anoto. Se guarda para poder demostrar que se
            informo, no como adorno. */}
        <label className="dkt-input-lbl">{lang==="es"?"¿Excluye preexistentes?":"Pre-existing excluded?"}</label>
        <select className="dkt-inp" value={form.preExistingExcluded||""} onChange={e=>set("preExistingExcluded",e.target.value)}>
          <option value="">{lang==="es"?"— sin definir —":"— not set —"}</option>
          <option value="yes">{lang==="es"?"Sí (lo habitual)":"Yes (typical)"}</option>
          <option value="no">{lang==="es"?"No":"No"}</option>
        </select>
      </div>
    </div>
    </>}
    {isVision && <>
    <SectionLabel>{lang==="es"?"👁️ Términos del plan de visión":"👁️ Vision Plan Terms"}</SectionLabel>
    <div className="grid grid-cols-3 gap-3">
      <Input label={lang==="es"?"Examen: frecuencia y copago":"Exam Frequency & Copay"} value={form.examCopay||""} onChange={e=>set("examCopay",e.target.value)} placeholder={lang==="es"?"1 al año / $10":"1/yr / $10"}/>
      <Input label={lang==="es"?"Asignación armazón ($)":"Frame Allowance ($)"} value={form.frameAllowance||""} onChange={e=>set("frameAllowance",e.target.value)} placeholder="150"/>
      <Input label={lang==="es"?"Asignación lentes de contacto ($)":"Contacts Allowance ($)"} value={form.contactsAllowance||""} onChange={e=>set("contactsAllowance",e.target.value)} placeholder="150"/>
    </div>
    <Input label={lang==="es"?"Tipo de red / reembolso fuera de red":"Network Type / Out-of-network"} value={form.networkType||""} onChange={e=>set("networkType",e.target.value)} placeholder=""/>
    </>}
    {isPet && <>
    <SectionLabel>{lang==="es"?"🐶 Datos de la mascota y del plan":"🐶 Pet & Plan Details"}</SectionLabel>
    <div className="grid grid-cols-3 gap-3">
      <Input label={lang==="es"?"Nombre de la mascota":"Pet Name"} value={form.petName||""} onChange={e=>set("petName",e.target.value)} placeholder=""/>
      <Input label={lang==="es"?"Especie":"Species"} value={form.petSpecies||""} onChange={e=>set("petSpecies",e.target.value)} placeholder={lang==="es"?"Perro / Gato":"Dog / Cat"}/>
      <Input label={lang==="es"?"Raza":"Breed"} value={form.petBreed||""} onChange={e=>set("petBreed",e.target.value)} placeholder=""/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Edad":"Age"} value={form.petAge||""} onChange={e=>set("petAge",e.target.value)} placeholder=""/>
      <Input label={lang==="es"?"N° de microchip":"Microchip Number"} value={form.microchipNumber||""} onChange={e=>set("microchipNumber",e.target.value)} placeholder=""/>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <Input label={lang==="es"?"Límite anual ($)":"Annual Limit ($)"} value={form.annualMaximum||""} onChange={e=>set("annualMaximum",e.target.value)} placeholder="10,000"/>
      <Input label={lang==="es"?"Deducible ($)":"Deductible ($)"} value={form.deductible||""} onChange={e=>set("deductible",e.target.value)} placeholder="1,000"/>
      <Input label={lang==="es"?"Reembolso (%)":"Reimbursement (%)"} value={form.reimbursementPercent||""} onChange={e=>set("reimbursementPercent",e.target.value)} placeholder="80"/>
    </div>
    <Input label={lang==="es"?"Períodos de espera":"Waiting Periods"} value={form.waitingPeriod||""} onChange={e=>set("waitingPeriod",e.target.value)} placeholder={lang==="es"?"Accidente / enfermedad / ortopédico":"Accident / illness / orthopedic"}/>
    </>}
    {isFinal && <>
    <SectionLabel>{lang==="es"?"🌹 Gastos finales":"🌹 Final Expense"}</SectionLabel>
    {/* La clase (nivel / graduada / emisión garantizada) usa el mismo campo
        "Clase de riesgo final" que ya aparece arriba en el bloque de Vida —
        Gastos Finales es vida, así que no se duplica. */}
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Espera de beneficio graduado":"Graded Benefit Waiting Period"} value={form.waitingPeriod||""} onChange={e=>set("waitingPeriod",e.target.value)} placeholder={lang==="es"?"2 años / ninguna":"2 years / none"}/>
      <Input label={lang==="es"?"Asignación a funeraria":"Funeral Home Assignment"} value={form.funeralHomeAssignment||""} onChange={e=>set("funeralHomeAssignment",e.target.value)} placeholder={lang==="es"?"Nombre de la funeraria":"Funeral home name"}/>
    </div>
    </>}
    {isLife && <>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"💵 Face Amount / Monto de Cobertura ($)":"💵 Face Amount ($)"} value={form.coverageAmount||""} onChange={e=>set("coverageAmount",e.target.value)} placeholder="100,000"/>
      <Input label={lang==="es"?"Beneficiario":"Beneficiary"} value={form.beneficiary||""} onChange={e=>set("beneficiary",e.target.value)} placeholder={lang==="es"?"Nombre del beneficiario":"Beneficiary name"}/>
    </div>
    {/* Life extras — the columns the agency fills on a carrier PolicySummary
        (Lissette 2026-07-02). All optional, stored in the type_details blob. */}
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Dueño de la póliza (Owner)":"Owner Name"} value={form.policyOwnerName||""} onChange={e=>set("policyOwnerName",e.target.value)} placeholder={lang==="es"?"Si es distinto al asegurado":"If different from insured"}/>
      <Input label={lang==="es"?"Duración del término":"Term Duration"} value={form.termDuration||""} onChange={e=>set("termDuration",e.target.value)} placeholder={lang==="es"?"10 / 20 / 30 años":"10 / 20 / 30 years"}/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Fecha de emisión":"Date of Issue"} type="date" value={form.issueDate||""} onChange={e=>set("issueDate",e.target.value)}/>
      <Input label={lang==="es"?"Fecha de pago de prima":"Premium Due Date"} type="date" value={form.premiumDueDate||""} onChange={e=>set("premiumDueDate",e.target.value)}/>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <Input label={lang==="es"?"Prima facturable ($)":"Billable Premium ($)"} type="number" step="0.01" min="0" value={form.billablePremium||""} onChange={e=>set("billablePremium",e.target.value)} placeholder="0.00"/>
      <Input label={lang==="es"?"Frecuencia de pago":"Billing Frequency"} value={form.billingFrequency||""} onChange={e=>set("billingFrequency",e.target.value)} placeholder={lang==="es"?"Mensual / Anual":"Monthly / Annual"}/>
      <Input label={lang==="es"?"Método de facturación":"Billing Method"} value={form.billingMethod||""} onChange={e=>set("billingMethod",e.target.value)} placeholder="Direct Bill / EFT"/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Número de agente":"Agent Number"} value={form.agentNumber||""} onChange={e=>set("agentNumber",e.target.value)} placeholder=""/>
      <Input label={lang==="es"?"Número de agencia":"Agency Number"} value={form.agencyNumber||""} onChange={e=>set("agencyNumber",e.target.value)} placeholder=""/>
    </div>
    {/* Vida, segunda tanda (Lissette 2026-08-09). Se agrupan por para-qué-sirven
        en vez de en el orden del Excel: primero el seguimiento del caso (que es
        lo que la agente mira a diario), después beneficiario contingente y
        datos del dueño, y de último la comisión. */}
    <SectionLabel>{lang==="es"?"📌 Seguimiento del caso":"📌 Case Tracking"}</SectionLabel>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Fecha de aprobación":"Approved Date"} type="date" value={form.approvedDate||""} onChange={e=>set("approvedDate",e.target.value)}/>
      <Input label={lang==="es"?"Papeles de entrega enviados":"Delivery Papers Submitted"} type="date" value={form.deliveryPapersDate||""} onChange={e=>set("deliveryPapersDate",e.target.value)}/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Fecha de entrega":"Delivery Date"} type="date" value={form.deliveryDate||""} onChange={e=>set("deliveryDate",e.target.value)}/>
      <Input label={lang==="es"?"Fecha de colocación (pagada)":"Placing Date (to be paid)"} type="date" value={form.placingDate||""} onChange={e=>set("placingDate",e.target.value)}/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Beneficiario contingente":"Contingent Beneficiary"} value={form.beneficiaryContingent||""} onChange={e=>set("beneficiaryContingent",e.target.value)} placeholder={lang==="es"?"Nombre, relación, %":"Name, relationship, %"}/>
      <Input label={lang==="es"?"Clase de riesgo final":"Final Risk Class"} value={form.riskClass||""} onChange={e=>set("riskClass",e.target.value)} placeholder={lang==="es"?"Preferred / Standard / Tabla":"Preferred / Standard / Table"}/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Edad de conversión":"Conversion Age"} value={form.conversionAge||""} onChange={e=>set("conversionAge",e.target.value)} placeholder="65 / 70"/>
      <Input label={lang==="es"?"Formulario de verificación":"Verification Form"} value={form.verificationForm||""} onChange={e=>set("verificationForm",e.target.value)} placeholder={lang==="es"?"Recibido / Pendiente":"Received / Pending"}/>
    </div>
    <Input label={lang==="es"?"Dirección del dueño de la póliza":"Owner Address"} value={form.ownerAddress||""} onChange={e=>set("ownerAddress",e.target.value)} placeholder={lang==="es"?"Solo si es distinta a la del asegurado":"Only if different from the insured's"}/>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Comisión pagada":"Commission Paid"} value={form.commissionPaid||""} onChange={e=>set("commissionPaid",e.target.value)} placeholder={lang==="es"?"Sí / No / fecha":"Yes / No / date"}/>
      <Input label={lang==="es"?"Split (reparto)":"Split"} value={form.commissionSplit||""} onChange={e=>set("commissionSplit",e.target.value)} placeholder="50/50"/>
    </div>
    </>}
    {isMedicare && <>
    <SectionLabel>{lang==="es"?"⭐ Datos de Medicare":"⭐ Medicare Details"}</SectionLabel>
    {/* EL ESPEJO desde la ficha del cliente (Lissette 2026-09-19: "que a su vez
        se haga espejo en las fichas de medicare esos datos").

        Va en UN SENTIDO: la ficha rellena la poliza, la poliza NO reescribe la
        ficha. Al contrario, abrir una poliza vieja con el MBI mal escrito le
        pisaria a la ficha el bueno y nadie sabria cual era el correcto.

        Y solo rellena lo que esta VACIO: lo que una agente escribio en esta
        poliza concreta no se le toca nunca. Se ofrece con un boton en vez de
        hacerlo solo — un formulario que se rellena por su cuenta al abrirlo
        queda "modificado" sin que nadie escribiera nada, y entonces salir sin
        guardar pregunta si quieres descartar cambios que no hiciste. */}
    {(() => {
      if (!client || !laFichaTieneMedicare(client.customValues)) return null;
      const pendiente = espejoHaciaLaPoliza(client.customValues, form);
      if (!Object.keys(pendiente).length) return null;
      const cuantos = Object.keys(pendiente).length;
      return (
        <div className="dkt-phase" style={{marginBottom:".5rem",display:"flex",alignItems:"center",gap:".6rem",flexWrap:"wrap"}}>
          <span>⭐ {lang==="es"
            ? `La ficha del cliente tiene ${cuantos===1?"un dato":`${cuantos} datos`} de Medicare que aqui esta${cuantos===1?"":"n"} vacio${cuantos===1?"":"s"}.`
            : `The client's file has ${cuantos===1?"a Medicare value":`${cuantos} Medicare values`} that ${cuantos===1?"is":"are"} blank here.`}</span>
          <button type="button" className="dkt-btn-s" onClick={()=>setForm(f=>({...f,...pendiente}))}>
            {lang==="es"?"Traerlos de la ficha":"Copy from the file"}
          </button>
        </div>
      );
    })()}
    {/* Columnas del Excel de la agente + el checklist de inscripción de
        Medicare (Lissette 2026-08-09). Todas opcionales, viven en type_details.
        El MBI es dato sensible pero NO es el Social: va en claro como el resto
        del número de póliza, igual que lo trae la tarjeta roja/blanca/azul. */}
    <div className="grid grid-cols-3 gap-3">
      <Input label={lang==="es"?"N° de Medicare (MBI)":"Medicare Number (MBI)"} value={form.medicareNumber||""} onChange={e=>set("medicareNumber",e.target.value)} placeholder="1EG4-TE5-MK73"/>
      <Input label={lang==="es"?"Fecha Parte A":"Part A Date"} type="date" value={form.partADate||""} onChange={e=>set("partADate",e.target.value)}/>
      <Input label={lang==="es"?"Fecha Parte B":"Part B Date"} type="date" value={form.partBDate||""} onChange={e=>set("partBDate",e.target.value)}/>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <Input label={lang==="es"?"ID de la compañía":"Company ID"} value={form.companyId||""} onChange={e=>set("companyId",e.target.value)} placeholder={lang==="es"?"ID que asigna la aseguradora":"Carrier-assigned ID"}/>
      <Input label={lang==="es"?"Plan del año":"Plan Year"} value={form.planYear||""} onChange={e=>set("planYear",e.target.value)} placeholder="2026"/>
      <Input label={lang==="es"?"Código de inscripción":"Enrollment Code"} value={form.enrollmentCode||""} onChange={e=>set("enrollmentCode",e.target.value)} placeholder=""/>
    </div>
    {/* SOA Y HRA (Lissette 2026-09-13). Los dos son TRAMITES con estado, no
        datos del plan, asi que van juntos y con su propio titulo: quien los
        persigue los mira en bloque ("cuales me faltan"), no de uno en uno.
        Los dos desplegables ADEMAS dejan escribir y se van llenando con lo que
        la agencia ya usa; el de la firma no, porque ahi ella dicto las dos
        opciones y un si/no con texto libre no se puede contar. Ver
        crm-app/camposDeMedicare.js. */}
    <SectionLabel>{lang==="es"?"📝 SOA y HRA":"📝 SOA & HRA"}</SectionLabel>
    <div className="grid grid-cols-3 gap-3">
      <Combobox
        label={lang==="es"?"Estatus del SOA":"SOA status"}
        value={form.soaStatus||""}
        onChange={v=>set("soaStatus",v)}
        options={opcionesConLasUsadas(ESTATUS_DE_SOA, soaStatusSuggestions, form.soaStatus)}
        placeholder={lang==="es"?"Pendiente, Enviado…":"Pending, Sent…"}
        lang={lang}
      />
      <div>
        <label className="dkt-input-lbl">{lang==="es"?"SOA firmado":"SOA signed"}</label>
        <select className="dkt-inp" style={{width:"100%"}} value={form.soaSigned||""}
          onChange={e=>set("soaSigned",e.target.value)}>
          <option value="">{lang==="es"?"— Sin marcar —":"— Not set —"}</option>
          {FIRMA_DEL_SOA.map((o)=>(
            <option key={o} value={o}>{lang==="es"?o:(o==="Firmado"?"Signed":"Not signed")}</option>
          ))}
          {/* Lo que llego de un importe y no es ninguna de las dos se sigue
              viendo: esconderlo lo borraria al guardar sin avisar. */}
          {form.soaSigned && !FIRMA_DEL_SOA.includes(form.soaSigned) && (
            <option value={form.soaSigned}>{form.soaSigned}</option>
          )}
        </select>
      </div>
      <Combobox
        label={lang==="es"?"Estatus del HRA":"HRA status"}
        value={form.hraStatus||""}
        onChange={v=>set("hraStatus",v)}
        options={opcionesConLasUsadas(ESTATUS_DE_HRA, hraStatusSuggestions, form.hraStatus)}
        placeholder={lang==="es"?"Pendiente, Completado…":"Pending, Completed…"}
        lang={lang}
      />
    </div>
    {/* MEDICAID, FECHA DE FIRMA DEL SOA Y LIS (Lissette 2026-09-14). La
        categoria de Medicaid decide quien paga la prima y los copagos del
        cliente, y el LIS cuanto le subsidian las medicinas: son los dos datos
        que deciden en que plan se le puede inscribir. La fecha va al lado de la
        firma porque es la misma pregunta ("cuando firmo") y CMS la pide.
        Los codigos se enderezan al escribirlos (qmb+ -> QMB PLUS). */}
    <div className="grid grid-cols-3 gap-3">
      <Combobox
        label={lang==="es"?"Elegibilidad de Medicaid":"Medicaid eligibility"}
        value={form.medicaidEligibility||""}
        onChange={v=>set("medicaidEligibility",v)}
        onBlur={()=>set("medicaidEligibility",normalizarElegibilidadDeMedicaid(form.medicaidEligibility))}
        options={opcionesConLasUsadas(ELEGIBILIDAD_DE_MEDICAID, medicaidSuggestions, form.medicaidEligibility)}
        placeholder="QMB, SLMB PLUS, FBDE…"
        lang={lang}
      />
      <Input label={lang==="es"?"Fecha de firma del SOA":"SOA signature date"} type="date" value={form.soaSignedDate||""} onChange={e=>set("soaSignedDate",e.target.value)}/>
      <Combobox
        label="LIS (Extra Help)"
        value={form.lisLevel||""}
        onChange={v=>set("lisLevel",v)}
        options={opcionesConLasUsadas(NIVELES_DE_LIS, lisUsadosVigentes(lisSuggestions), form.lisLevel)}
        placeholder="LIS LEVEL 3, NO LIS…"
        lang={lang}
      />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Prima inicial ($)":"Initial Premium ($)"} type="number" step="0.01" min="0" value={form.initialPremium||""} onChange={e=>set("initialPremium",e.target.value)} placeholder="0.00"/>
      <Input label={lang==="es"?"Prima actual ($)":"Current Premium ($)"} type="number" step="0.01" min="0" value={form.currentPremium||""} onChange={e=>set("currentPremium",e.target.value)} placeholder="0.00"/>
    </div>
    {/* Los datos nuevos de la ficha de Medicare Advantage (Lissette
        2026-10-01). Solo en MA: Suplemento y Parte D se quedan como estaban.
        Tipo de producto se escribe libre y se va llenando con lo ya usado. */}
    {form.type==="ma" && <>
    <div className="grid grid-cols-3 gap-3">
      <Combobox
        label={lang==="es"?"Tipo de producto":"Product type"}
        value={form.productType||""}
        onChange={v=>set("productType",v)}
        options={opcionesConLasUsadas([], productTypeSuggestions, form.productType)}
        placeholder="HMO, PPO, D-SNP…"
        lang={lang}
      />
      <Input label={lang==="es"?"Monto Giveback ($)":"Giveback amount ($)"} type="number" step="0.01" min="0" value={form.givebackAmount||""} onChange={e=>set("givebackAmount",e.target.value)} placeholder="0.00"/>
      <Input label={lang==="es"?"Prima Parte B ($)":"Part B premium ($)"} type="number" step="0.01" min="0" value={form.partBPremium||""} onChange={e=>set("partBPremium",e.target.value)} placeholder="0.00"/>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <Input label="Contract / PBP / Segment" value={form.contractPbpSegment||""} onChange={e=>set("contractPbpSegment",e.target.value)} placeholder="H1234-001-000"/>
      <Input label={lang==="es"?"N.º de Medicaid":"Medicaid number"} value={form.medicaidNumber||""} onChange={e=>set("medicaidNumber",e.target.value)}/>
      <Input label={lang==="es"?"Subagente":"Sub-agent"} value={form.subAgent||""} onChange={e=>set("subAgent",e.target.value)}/>
    </div>
    </>}
    </>}
    <SectionLabel>{lang==="es"?"💳 Información de Pago":"💳 Payment Info"}</SectionLabel>
    <div style={{display:"flex",gap:".5rem",flexWrap:"wrap"}}>
      {[
        {id:"autopay", emoji:"🔄", es:"Autopay", en:"Autopay", desc_es:"Pago automático", desc_en:"Auto-charged"},
        {id:"client", emoji:"👤", es:"Cliente paga", en:"Client pays", desc_es:"Pago manual", desc_en:"Pays manually"},
        {id:"force", emoji:"⚡", es:"Forzar", en:"Force", desc_es:"Pago forzado", desc_en:"Forced payment"},
        // Prima $0 (ticket 2026-10-12): una forma de pago más. Sigue saliendo
        // en Pagos del Mes como las demás, con su propia etiqueta.
        {id:"zero", emoji:"🆓", es:"Prima $0", en:"$0 Premium", desc_es:"Pago de prima $0", desc_en:"$0 premium payment"},
      ].map(opt => {
        const on = form.paymentType === opt.id;
        return (
          <button key={opt.id} type="button" onClick={()=>set("paymentType", on ? "" : opt.id)}
            style={{flex:"1 1 200px",display:"flex",alignItems:"center",gap:".6rem",padding:".7rem .9rem",borderRadius:10,cursor:"pointer",fontFamily:"inherit",
              border:`1px solid ${on?"rgba(0,212,255,.5)":"var(--c-border,#1a3550)"}`,
              background:on?"rgba(0,212,255,.08)":"var(--c-surface2,#112538)",
              transition:"all .15s"}}>
            <span style={{fontSize:"1.3rem"}}>{opt.emoji}</span>
            <div style={{textAlign:"left"}}>
              <div style={{fontSize:".82rem",fontWeight:700,color:on?"var(--c-cyan,#00d4ff)":"var(--c-white,#eef6ff)"}}>{lang==="es"?opt.es:opt.en}</div>
              <div style={{fontSize:".65rem",color:"var(--c-text3,#45688a)"}}>{lang==="es"?opt.desc_es:opt.desc_en}</div>
            </div>
          </button>
        );
      })}
    </div>
    {form.paymentType === "autopay" && <>
      {savedPayMethods.length>0 && (
        <Select label={lang==="es"?"Usar método de pago guardado del cliente":"Use client's saved payment method"} value={selectedSavedId}
          onChange={e=>{ const id=e.target.value; setSelectedSavedId(id); const m=savedPayMethods.find(x=>x.id===id); if(m) applySavedMethod(m); }}>
          <option value="">{lang==="es"?"— Seleccionar para auto-llenar —":"— Select to auto-fill —"}</option>
          {savedPayMethods.map(m=>{
            const typeLbl = {credit_card:lang==="es"?"Tarjeta de Crédito":"Credit Card",debit_card:lang==="es"?"Tarjeta de Débito":"Debit Card",bank_account:lang==="es"?"Cuenta Bancaria":"Bank Account",cash:lang==="es"?"Efectivo":"Cash",check:lang==="es"?"Cheque":"Check",other:lang==="es"?"Otro":"Other"}[m.type]||m.type;
            const last4 = m.acctLast4||m.cardLast4;
            return <option key={m.id} value={m.id}>{(m.label||typeLbl)}{last4?` ····${last4}`:""}{m.cardholderName?` — ${m.cardholderName}`:""}</option>;
          })}
        </Select>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Select label={lang==="es"?"Método":"Method"} value={form.paymentMethod||""}
          onChange={e=>{
            const v=e.target.value;
            // Selecting a method type auto-fills holder + last-4 from a matching
            // saved method when the fields are still empty (don't overwrite).
            const match=savedPayMethods.find(m=>PM_TYPE_MAP[m.type]===v);
            if(match && !form.payerName && !form.bankLast4 && !form.cardLast4){ applySavedMethod(match); }
            else { set("paymentMethod",v); }
          }}>
          <option value="">--</option>
          <option value="credit_card">{lang==="es"?"Tarjeta de Crédito":"Credit Card"}</option>
          <option value="debit_card">{lang==="es"?"Tarjeta de Débito":"Debit Card"}</option>
          <option value="bank_account">{lang==="es"?"Cuenta Bancaria (ACH)":"Bank Account (ACH)"}</option>
        </Select>
        <Input label={lang==="es"?"Día de cobro":"Billing Day"} type="number" min="1" max="31" value={form.billingDay||""} onChange={e=>set("billingDay",e.target.value)} placeholder="15"/>
      </div>
      {(form.paymentMethod === "credit_card" || form.paymentMethod === "debit_card") && <>
        <div className="grid grid-cols-4 gap-3">
          <Select label={lang==="es"?"Marca":"Brand"} value={form.cardBrand||""} onChange={e=>set("cardBrand",e.target.value)}>
            <option value="">--</option>
            <option value="visa">Visa</option>
            <option value="mastercard">Mastercard</option>
            <option value="amex">Amex</option>
            <option value="discover">Discover</option>
            <option value="other">{lang==="es"?"Otra":"Other"}</option>
          </Select>
          <Input label={lang==="es"?"Últimos 4":"Last 4"} maxLength="4" value={form.cardLast4||""} onChange={e=>set("cardLast4",e.target.value.replace(/\D/g,"").slice(0,4))} placeholder="4242"/>
          <Select label={lang==="es"?"Mes Venc.":"Exp Month"} value={form.cardExpMonth||""} onChange={e=>set("cardExpMonth",e.target.value)}>
            <option value="">--</option>
            {Array.from({length:12},(_,i)=><option key={i+1} value={String(i+1).padStart(2,"0")}>{String(i+1).padStart(2,"0")}</option>)}
          </Select>
          <Select label={lang==="es"?"Año Venc.":"Exp Year"} value={form.cardExpYear||""} onChange={e=>set("cardExpYear",e.target.value)}>
            <option value="">--</option>
            {Array.from({length:15},(_,i)=>{const y=new Date().getFullYear()+i;return <option key={y} value={y}>{y}</option>;})}
          </Select>
        </div>
        {form.cardExpMonth && form.cardExpYear && (() => {
          const exp = new Date(parseInt(form.cardExpYear), parseInt(form.cardExpMonth), 0);
          const daysLeft = Math.ceil((exp - new Date()) / 86400000);
          if (daysLeft < 0) return <div style={{background:"rgba(239,68,68,.1)",border:"1px solid rgba(239,68,68,.3)",borderRadius:9,padding:".55rem .8rem",display:"flex",alignItems:"center",gap:".5rem"}}><AlertTriangle size={14} color="#ef4444"/><span style={{fontSize:".75rem",fontWeight:700,color:"#ef4444"}}>⚠️ {lang==="es"?`Tarjeta vencida hace ${Math.abs(daysLeft)} días — contactar al cliente urgente`:`Card expired ${Math.abs(daysLeft)} days ago — contact client urgently`}</span></div>;
          if (daysLeft <= 30) return <div style={{background:"rgba(255,183,0,.1)",border:"1px solid rgba(255,183,0,.3)",borderRadius:9,padding:".55rem .8rem",display:"flex",alignItems:"center",gap:".5rem"}}><AlertTriangle size={14} color="#ffb700"/><span style={{fontSize:".75rem",fontWeight:700,color:"#ffb700"}}>⚠️ {lang==="es"?`Tarjeta vence en ${daysLeft} días — solicita nueva tarjeta al cliente`:`Card expires in ${daysLeft} days — request new card from client`}</span></div>;
          if (daysLeft <= 60) return <div style={{background:"rgba(249,115,22,.08)",border:"1px solid rgba(249,115,22,.25)",borderRadius:9,padding:".5rem .8rem",display:"flex",alignItems:"center",gap:".5rem"}}><Bell size={13} color="#f97316"/><span style={{fontSize:".7rem",color:"#f97316"}}>{lang==="es"?`Tarjeta vence en ${daysLeft} días`:`Card expires in ${daysLeft} days`}</span></div>;
          return null;
        })()}
      </>}
      {form.paymentMethod === "bank_account" && (
        <div className="grid grid-cols-2 gap-3">
          <Input label={lang==="es"?"Titular de Cuenta":"Account Holder"} value={form.payerName||""} onChange={e=>set("payerName",e.target.value)} placeholder=""/>
          <Input label={lang==="es"?"Últimos 4 de Cuenta":"Account Last 4"} maxLength="4" value={form.bankLast4||""} onChange={e=>set("bankLast4",e.target.value.replace(/\D/g,"").slice(0,4))} placeholder="1234"/>
        </div>
      )}
    </>}
    {form.paymentType === "client" && (
      <div style={{background:"rgba(0,212,255,.05)",border:"1px solid rgba(0,212,255,.2)",borderRadius:9,padding:".55rem .8rem",fontSize:".72rem",color:"var(--c-text2,#7aa3c0)"}}>
        ℹ️ {lang==="es"?"El cliente paga directamente a la aseguradora cada mes.":"Client pays the carrier directly each month."}
      </div>
    )}
    {form.paymentType === "force" && (
      <div style={{background:"rgba(168,85,247,.06)",border:"1px solid rgba(168,85,247,.25)",borderRadius:9,padding:".55rem .8rem",fontSize:".72rem",color:"var(--c-text2,#7aa3c0)"}}>
        ⚡ {lang==="es"?"Pago forzado — se cobra manualmente cada mes.":"Forced payment — charged manually each month."}
      </div>
    )}
    {isHome && <>
      <SectionLabel>🏠 {lang==="es"?"Datos de la Propiedad":"Property Details"}</SectionLabel>
      <Input label={lang==="es"?"Dirección de la Propiedad":"Property Address"} value={form.propertyAddress||""} onChange={e=>set("propertyAddress",e.target.value)} placeholder=""/>
      <div className="grid grid-cols-3 gap-3">
        <Select label={lang==="es"?"Tipo de Propiedad":"Property Type"} value={form.propertyType||""} onChange={e=>set("propertyType",e.target.value)}>
          <option value="">--</option>
          <option value="single_family">{lang==="es"?"Casa Unifamiliar":"Single Family"}</option>
          <option value="condo">Condo</option>
          <option value="townhouse">Townhouse</option>
          <option value="rental">{lang==="es"?"Rental/Renter":"Rental/Renter"}</option>
          <option value="multi_family">{lang==="es"?"Multi-familiar":"Multi-Family"}</option>
        </Select>
        <Input label={lang==="es"?"Año de Construcción":"Year Built"} value={form.yearBuilt||""} onChange={e=>set("yearBuilt",e.target.value)} placeholder=""/>
        <Input label={lang==="es"?"Pies Cuadrados":"Sq. Footage"} value={form.squareFootage||""} onChange={e=>set("squareFootage",e.target.value)} placeholder="1,800"/>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label={lang==="es"?"Valor Asegurado / Dwelling ($)":"Insured Value / Dwelling ($)"} value={form.insuredValue||""} onChange={e=>set("insuredValue",e.target.value)} placeholder=""/>
        <Input label={lang==="es"?"Cobertura de Contenido ($)":"Personal Property ($)"} value={form.dwellingCoverage||""} onChange={e=>set("dwellingCoverage",e.target.value)} placeholder="50,000"/>
      </div>
    </>}
    {isAuto && <>
      <SectionLabel>🚗 {lang==="es"?"Datos del Vehículo":"Vehicle Details"}</SectionLabel>
      {/* Cuántos carros lleva la póliza, y los datos de cada uno (Lissette
          2026-10-12). El primero se sigue guardando también en los campos de
          siempre — ver crm-app/vehiculosDePoliza.js. */}
      {(()=>{
        const autos = vehiculosDe(form);
        const guardar = (lista)=>setForm(f=>({...f, ...camposConVehiculos(lista)}));
        const cambiarCantidad = async (n)=>{
          const { lista, quitados } = conCantidad(autos, n);
          if (quitados > 0 && !(await confirmDialog({ message: lang==="es"
            ? `Se quitarán ${quitados} carro(s) que ya tienen datos. ¿Seguir?`
            : `${quitados} vehicle(s) with data will be removed. Continue?`, danger: true }))) return;
          guardar(lista);
        };
        const cambiar = (i, k, v)=>guardar(autos.map((a,j)=>j===i?{...a,[k]:v}:a));
        return <>
          <div style={{maxWidth:220}}>
            <Select label={lang==="es"?"¿Cuántos carros?":"How many vehicles?"} value={String(autos.length)} onChange={e=>cambiarCantidad(e.target.value)}>
              {Array.from({length:MAX_VEHICULOS},(_,i)=><option key={i+1} value={String(i+1)}>{i+1}</option>)}
            </Select>
          </div>
          {autos.map((a,i)=>(
            <div key={i} style={autos.length>1?{border:"1px solid var(--c-border,#1a3550)",borderRadius:10,padding:".6rem .7rem .2rem",display:"flex",flexDirection:"column",gap:".5rem"}:{display:"contents"}}>
              {autos.length>1 && <div style={{fontSize:".72rem",fontWeight:800,color:"var(--c-cyan,#00d4ff)",textTransform:"uppercase",letterSpacing:".05em"}}>🚗 {lang==="es"?`Carro ${i+1}`:`Vehicle ${i+1}`}</div>}
              <div className="grid grid-cols-3 gap-3">
                <Input label={lang==="es"?"Año":"Year"} value={a.year} onChange={e=>cambiar(i,"year",e.target.value)} placeholder=""/>
                <Input label={lang==="es"?"Marca":"Make"} value={a.make} onChange={e=>cambiar(i,"make",e.target.value)} placeholder=""/>
                <Input label={lang==="es"?"Modelo":"Model"} value={a.model} onChange={e=>cambiar(i,"model",e.target.value)} placeholder=""/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="VIN" value={a.vin} onChange={e=>cambiar(i,"vin",e.target.value)} placeholder=""/>
                <Input label={lang==="es"?"Placa":"License Plate"} value={a.plate} onChange={e=>cambiar(i,"plate",e.target.value)} placeholder=""/>
              </div>
            </div>
          ))}
        </>;
      })()}
      <Input label={lang==="es"?"Conductores en la Póliza":"Drivers on Policy"} value={form.driversOnPolicy||""} onChange={e=>set("driversOnPolicy",e.target.value)} placeholder={lang==="es"?"Ej: Maria Rodriguez, Jose Perez":"e.g. Maria Rodriguez, Jose Perez"}/>
    </>}
    {isComm && <>
      <SectionLabel>🏢 {lang==="es"?"Datos del Negocio":"Business Details"}</SectionLabel>
      <div className="grid grid-cols-2 gap-3">
        <Input label={lang==="es"?"Nombre del Negocio":"Business Name"} value={form.businessName||""} onChange={e=>set("businessName",e.target.value)} placeholder=""/>
        <Select label={lang==="es"?"Tipo de Negocio":"Business Type"} value={form.businessType||""} onChange={e=>set("businessType",e.target.value)}>
          <option value="">--</option>
          <option value="retail">{lang==="es"?"Retail / Tienda":"Retail / Store"}</option>
          <option value="restaurant">{lang==="es"?"Restaurante":"Restaurant"}</option>
          <option value="contractor">Contractor</option>
          <option value="professional">{lang==="es"?"Servicios Profesionales":"Professional Services"}</option>
          <option value="trucking">Trucking</option>
          <option value="other">{lang==="es"?"Otro":"Other"}</option>
        </Select>
      </div>
      <Input label={lang==="es"?"Dirección del Negocio":"Business Address"} value={form.businessAddress||""} onChange={e=>set("businessAddress",e.target.value)} placeholder=""/>
      <div className="grid grid-cols-2 gap-3">
        <Input label={lang==="es"?"Número de Empleados":"# Employees"} type="number" min="0" value={form.numEmployees||""} onChange={e=>set("numEmployees",e.target.value)} placeholder="5"/>
        <Input label={lang==="es"?"Ingresos Anuales ($)":"Annual Revenue ($)"} value={form.annualRevenue||""} onChange={e=>set("annualRevenue",e.target.value)} placeholder=""/>
      </div>
    </>}
    {/* Insured names section - for ALL policy types */}
    <SectionLabel>👥 {lang==="es"?"Personas Aseguradas":"Insured Persons"}</SectionLabel>
    <div style={{display:"flex",flexDirection:"column",gap:".4rem"}}>
      {/* Titular + cónyuge as toggleable chips (only when client is known). */}
      {client&&(
        <div style={{display:"flex",flexWrap:"wrap",gap:".35rem",marginBottom:".35rem"}}>
          {[
            {key:"primary", name:`${client.firstName||""} ${client.lastName||""}`.trim(), rel:lang==="es"?"Titular":"Primary"},
            /* El chip del conyuge sale si esta casado O si ya viene marcado
               como asegurado: sin lo segundo, una poliza importada con el
               conyuge dentro no tenia donde enseñarlo — ni para quitarlo. */
            ...((hasSpouse || (client.spouse && client.spouse.firstName && (form.policyInsured||[]).includes("spouse")))
              ?[{key:"spouse", name:`${client.spouse.firstName||""} ${client.spouse.lastName||""}`.trim(), rel:lang==="es"?"Cónyuge":"Spouse"}]:[]),
          ].map(m=>{
            const selected=(form.policyInsured||[]).includes(m.key);
            return(
              <button key={m.key} type="button"
                onClick={()=>{
                  const cur=form.policyInsured||[];
                  set("policyInsured",selected?cur.filter(x=>x!==m.key):[...cur,m.key]);
                }}
                style={{padding:".28rem .65rem",borderRadius:100,border:"1px solid",fontSize:".7rem",fontWeight:600,cursor:"pointer",transition:"all .15s",fontFamily:"inherit",
                  background:selected?"rgba(0,212,255,.12)":"transparent",
                  borderColor:selected?"rgba(0,212,255,.35)":"var(--c-border,#1a3550)",
                  color:selected?"var(--c-cyan,#00d4ff)":"var(--c-text,#c8dff0)"}}>
                {selected?"✓ ":""}{m.name} <span style={{opacity:.7,fontWeight:400}}>· {m.rel}</span>
              </button>
            );
          })}
        </div>
      )}
      {/* Show dependents as toggleable chips if available */}
      {dependents&&dependents.length>0&&(
        <div style={{display:"flex",flexWrap:"wrap",gap:".35rem",marginBottom:".35rem"}}>
          {dependents.map((dep,di)=>{
            const key=dep.id||("dep"+di);
            const selected=(form.policyInsured||[]).includes(key);
            return(
              <button key={key} type="button"
                onClick={()=>{
                  const cur=form.policyInsured||[];
                  set("policyInsured",selected?cur.filter(x=>x!==key):[...cur,key]);
                }}
                style={{padding:".28rem .65rem",borderRadius:100,border:"1px solid",fontSize:".7rem",fontWeight:600,cursor:"pointer",transition:"all .15s",fontFamily:"inherit",
                  background:selected?"rgba(0,212,255,.12)":"transparent",
                  borderColor:selected?"rgba(0,212,255,.35)":"var(--c-border,#1a3550)",
                  color:selected?"var(--c-cyan,#00d4ff)":"var(--c-text,#c8dff0)"}}>
                {selected?"✓ ":""}{dep.firstName} {dep.lastName}
              </button>
            );
          })}
        </div>
      )}
      {/* Free-text names — for extra people not in dependents */}
      {(form.policyInsuredExtra||[]).map((name,ni)=>(
        <div key={ni} style={{display:"flex",alignItems:"center",gap:".4rem"}}>
          <input className="dkt-inp" style={{flex:1}} value={name}
            onChange={e=>{const arr=[...(form.policyInsuredExtra||[])];arr[ni]=e.target.value;set("policyInsuredExtra",arr);}}
            placeholder={lang==="es"?"Nombre completo":"Full name"}/>
          <button type="button" onClick={()=>{const arr=[...(form.policyInsuredExtra||[])];arr.splice(ni,1);set("policyInsuredExtra",arr);}}
            style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-red,#ff3d6b)",fontSize:"1rem",padding:"0 .25rem",fontFamily:"inherit"}}>✕</button>
        </div>
      ))}
      <button type="button"
        onClick={()=>set("policyInsuredExtra",[...(form.policyInsuredExtra||[]),""])}
        style={{alignSelf:"flex-start",display:"flex",alignItems:"center",gap:".3rem",fontSize:".72rem",color:"var(--c-text3,#45688a)",background:"none",border:"1px dashed var(--c-border,#1a3550)",borderRadius:7,padding:".28rem .65rem",cursor:"pointer",fontFamily:"inherit",transition:"color .15s"}}
        onMouseEnter={e=>e.currentTarget.style.color="var(--c-cyan,#00d4ff)"}
        onMouseLeave={e=>e.currentTarget.style.color="var(--c-text3,#45688a)"}>
        + {lang==="es"?"Agregar otra persona":"Add another person"}
      </button>
    </div>
    {/* UN MEMBER ID POR PERSONA (Lissette 2026-08-28): en varios planes de
        salud la aseguradora emite una tarjeta por persona y cada una trae SU
        numero. El del TITULAR sigue siendo el de arriba —no se duplica aqui
        para que no haya dos casillas editando el mismo dato— y este bloque
        añade el de los demas.

        Solo sale cuando hay MAS DE UNA persona marcada como asegurada: en una
        poliza individual seria una seccion vacia pidiendo algo que no existe. */}
    {otrosAsegurados.length > 0 && <>
      <SectionLabel>🪪 {lang==="es"?"Member ID por persona":"Member ID per person"}</SectionLabel>
      <div style={{display:"flex",flexDirection:"column",gap:".4rem"}}>
        <div style={{fontSize:".7rem",color:"var(--c-text3,#45688a)",lineHeight:1.45}}>
          {lang==="es"
            ? "El del titular es el de arriba. Aquí van los de los demás, solo si la aseguradora les dio uno distinto."
            : "The primary's is the one above. These are for the others, only if the carrier issued them a different one."}
        </div>
        {otrosAsegurados.map(per=>(
          <div key={per.key} style={{display:"flex",alignItems:"center",gap:".5rem"}}>
            <span style={{flex:"0 0 40%",fontSize:".76rem",color:"var(--c-text2,#7aa3c0)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
              title={per.name}>
              {per.name}{per.rel?<span style={{color:"var(--c-text3,#45688a)"}}> · {per.rel}</span>:null}
            </span>
            <input className="dkt-inp" style={{flex:1}}
              value={(form.memberIdsByPerson||{})[per.key]||""}
              onChange={e=>{
                const mapa={...(form.memberIdsByPerson||{})};
                const v=e.target.value;
                // Vaciar la casilla QUITA la entrada en vez de guardar "". Son
                // lo mismo, y la cadena vacia haria que la tarjeta pintara una
                // fila con un hueco en vez de no pintarla.
                if(v.trim()) mapa[per.key]=v; else delete mapa[per.key];
                set("memberIdsByPerson",mapa);
              }}
              placeholder={lang==="es"?"Member ID de esta persona":"This person's Member ID"}/>
          </div>
        ))}
      </div>
    </>}
    <SectionLabel>{lang==="es"?"🎯 Origen del Lead":"🎯 Lead Source"}</SectionLabel>
    <div style={{display:"flex",flexWrap:"wrap",gap:".4rem",alignItems:"center"}}>
      {allLeadSources().map(p=>({id:p.id,label:`${p.icon} ${lang==="es"?p.es:p.en}`})).map(opt=>{
        const on=form.leadType===opt.id;
        return <button key={opt.id} type="button" onClick={()=>set("leadType", on?"":opt.id)}
          style={{padding:".3rem .7rem",borderRadius:100,border:"1px solid",fontSize:".72rem",fontWeight:600,cursor:"pointer",fontFamily:"inherit",transition:"all .15s",
            background:on?"rgba(0,212,255,.12)":"transparent",
            borderColor:on?"rgba(0,212,255,.4)":"var(--c-border,#1a3550)",
            color:on?"var(--c-cyan,#00d4ff)":"var(--c-text2,#7aa3c0)"}}>
          {on?"✓ ":""}{opt.label}
        </button>;
      })}
      {/* El origen escrito a mano deja de poder ELEGIRSE (Lissette 2026-08-28:
          "quita la opcion de colocar otro y colocar un nombre, solo deja lo que
          ya estan predeterminados"). Pero el que esta guardado en ESTA poliza
          se sigue viendo: esconderlo no lo borraria de la base y la agente
          creeria que la poliza no tiene origen. Su ✕ lo quita — "dejale una x
          por si lo quieren eliminar ellos".
          Ya NO se ofrecen los valores escritos en OTRAS polizas: eso era la
          paleta que permitia seguir usandolos. */}
      {/* CREAR UN ORIGEN PROPIO DE LA AGENCIA (Lissette 2026-08-28: "la opcion de
          crear etiquetas personalizadas").

          NO es el texto libre que se quito ayer: aquello escribia en ESTA
          poliza y hacia nacer gemelos ("Referido"/"referido"). Esto guarda EN
          LOS AJUSTES DE LA AGENCIA y desde ese momento sale como ficha para
          todo el mundo — el mismo patron que los marketplaces y los estatus. */}
      <button type="button" onClick={async ()=>{
          const nombre = (window.prompt(lang==="es"
            ? "Nombre del origen (se guardara para toda la agencia):"
            : "Source name (it will be saved for the whole agency):") || "").trim();
          if (!nombre) return;                       // cancelo: no se toca nada
          // El emoji es opcional. Sin el, la ficha sale con uno neutro en vez de
          // quedarse sin icono al lado de las que si lo tienen.
          const icono = (window.prompt(lang==="es"
            ? "Emoji para esta ficha (opcional):"
            : "Emoji for this chip (optional):") || "").trim() || "🏷️";
          const yaEsta = allLeadSources().find((x)=>
            (x.es||"").toLowerCase()===nombre.toLowerCase() || (x.en||"").toLowerCase()===nombre.toLowerCase());
          if (yaEsta) { set("leadType", yaEsta.id); return; }   // ya existia: se elige y ya
          const id = idParaOrigenNuevo(nombre, allLeadSources().map((x)=>x.id));
          const siguiente = [...getAgencyLeadSources(), { id, label:nombre, labelEs:nombre, icon:icono }];
          setAgencyLeadSources(siguiente);           // visible YA, sin esperar al servidor
          set("leadType", id);
          try {
            await apiUpdateAgencySettings({ leadSources: siguiente });
          } catch (err) {
            // Si no se pudo guardar hay que DECIRLO: callarse dejaria a la
            // agente creyendo que el origen quedo para las proximas polizas.
            toast.error(lang==="es"
              ? "El origen se usa en esta poliza, pero NO se pudo guardar para las proximas: "+(err?.message||err)
              : "The source applies to this policy, but could NOT be saved for future ones: "+(err?.message||err));
          }
        }}
        style={{padding:".3rem .7rem",borderRadius:100,border:"1px dashed var(--c-cyan,#00d4ff)",fontSize:".72rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit",background:"transparent",color:"var(--c-cyan,#00d4ff)"}}>
        + {lang==="es"?"Origen":"Source"}
      </button>
      {origenEscritoAMano && (
        <span title={lang==="es"?"Origen escrito a mano. Ya no se pueden crear nuevos; pulsa la ✕ para quitarlo.":"Free-text source. New ones can no longer be created; press ✕ to remove it."}
          style={{display:"inline-flex",alignItems:"center",gap:5,padding:".3rem .55rem .3rem .7rem",borderRadius:100,border:"1px dashed var(--c-border,#1a3550)",fontSize:".72rem",fontWeight:600,fontFamily:"inherit",background:"rgba(148,163,184,.1)",color:"var(--c-text2,#7aa3c0)"}}>
          ✓ {origenEscritoAMano}
          <button type="button" onClick={()=>set("leadType","")}
            title={lang==="es"?"Quitar este origen":"Remove this source"}
            style={{background:"none",border:"none",color:"var(--c-text3,#45688a)",cursor:"pointer",padding:0,fontSize:".72rem",lineHeight:1,fontFamily:"inherit"}}>✕</button>
        </span>
      )}
    </div>
    <SectionLabel>{lang==="es"?"📅 Fechas, Agente y Proceso":"📅 Dates, Agent & Processing"}</SectionLabel>
    {/* Orden pedido por Lissette 2026-08-09: "el analista al lado de fecha de
        venta y NPN al lado de agente en poliza". Cada fila empareja campos que
        se llenan juntos, en vez de dejar huecos sueltos:
          Fecha de Venta + Analista        (quien vendio y cuando)
          Agente en poliza + Cod. Productor (el NPN se autocompleta del agente)
          Procesado por + Fecha de procesamiento (quien proceso y cuando) */}
    <div className="grid grid-cols-2 gap-3">
      <Input label={lang==="es"?"Fecha de Venta":"Sale Date"} type="date" value={form.saleDate||""} onChange={e=>set("saleDate",e.target.value)}/>
      {/* Analista: combobox; el nombre escrito queda reusable en otras pólizas. */}
      <div>
        <Combobox label={lang==="es"?"Analista":"Analyst"} value={form.analystName||""} onChange={v=>set("analystName",v)}
          options={analystOptions} placeholder={lang==="es"?"Nombre del analista":"Analyst name"} lang={lang}/>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Input label={tp.effectiveDate} type="date" value={form.effectiveDate} onChange={e=>set("effectiveDate",e.target.value)}/>
      <Input label={tp.renewalDate} type="date" value={form.renewalDate} onChange={e=>set("renewalDate",e.target.value)}/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {/* Agente: por defecto el principal, personalizable; los customs quedan reusables (combobox muestra toda la lista). */}
      <div>
        <Combobox label={lang==="es"?"Agente en póliza":"Agent on policy"} value={form.agentLabel||""}
          onChange={v=>{ set("agentLabel",v); const npn=npnByAgentName[(v||"").trim()]; if(npn) set("producerCode",npn); }}
          options={agentOptions} placeholder={mainAgentName||(lang==="es"?"Nombre del agente":"Agent name")} lang={lang}/>
      </div>
      {/* El NPN va aqui a proposito: al elegir agente arriba, el onChange lo
          autocompleta desde npnByAgentName, asi que verlos lado a lado hace
          visible de donde salio el numero. */}
      <Input label={lang==="es"?"Código Productor":"Producer Code"} value={form.producerCode||""} onChange={e=>set("producerCode",e.target.value)} placeholder=""/>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {/* Procesado por: equipo + nombres ya usados (combobox muestra toda la lista; el typed se recuerda). */}
      <div>
        <Combobox label={lang==="es"?"Procesado por":"Processed by"} value={form.processedBy||""} onChange={v=>set("processedBy",v)}
          options={processedOptions} placeholder={lang==="es"?"Nombre":"Name"} lang={lang}/>
      </div>
      {/* La otra mitad de "Procesado por": cuando la compania la proceso.
          type="date" -> el valor viaja como YYYY-MM-DD, que es exactamente lo
          que espera la columna DATE del backend. */}
      <div>
        <label className="dkt-input-lbl">{lang==="es"?"Fecha de procesamiento":"Processing date"}</label>
        <input type="date" className="dkt-inp" value={form.processingDate||""}
          onChange={e=>set("processingDate",e.target.value)}/>
      </div>
    </div>
    {/* % Comisión y Comisión se editan ahora en Cotizaciones → Comisiones
        (Lissette 2026-06-17). Los valores siguen en el form state (cargados de
        la póliza) y se preservan en type_details al guardar; solo se quitó la UI. */}
    <div>
      {(()=>{ void statusVer; return (
      <div style={{display:"flex",alignItems:"flex-end",gap:8}}>
        <div style={{flex:1,minWidth:0}}>
          <Select label={tp.status} value={form.status} onChange={e=>set("status",e.target.value)}>
            {getAllPolicyStatuses().map((s)=>(
              <option key={s.id} value={s.id}>{(lang==="es"?(s.labelEs||s.label):s.label)}</option>
            ))}
            {/* Un estatus que la póliza ya lleva pero que hoy no se ofrece —borrado,
                o movido al catálogo de Pagos del Mes— sigue seleccionable y se lee
                con su nombre: getPolicyStatusMeta cae al catálogo de pagos. */}
            {form.status && !getAllPolicyStatuses().some((s)=>s.id===form.status) && (
              <option value={form.status}>{getPolicyStatusMeta(form.status, lang).label}</option>
            )}
          </Select>
        </div>
        <button type="button" onClick={()=>setStatusMgr(true)} className="dkt-btn ghost xs"
          style={{whiteSpace:"nowrap",marginBottom:2}}
          title={lang==="es"?"Crear un estatus personalizado":"Create a custom status"}>+ {lang==="es"?"Estatus":"Status"}</button>
      </div>
      ); })()}
    </div>
    {/* Cancelación — FECHA + MOTIVO juntos, obligatorios al marcar "Cancelada".
        Van aquí, justo debajo del selector de estatus, para que aparezcan donde
        el agente está mirando cuando elige "Cancelada". Antes el campo de fecha
        se renderizaba mucho más arriba (junto a las Fechas), así que el agente
        veía el error "falta la fecha de cancelación" sin ver el campo para
        escribirla (reporte de soporte, 2026-07-23). La fecha además la exige la
        API — perderla rompe los clawbacks de comisión y la reconciliación con la
        aseguradora (Lissette 2026-07-22). */}
    {esEstatusCancelado(form.status) && <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <Input label={(lang==="es"?"Fecha de cancelación":"Cancellation date") + " *"} type="date"
          value={form.cancellationDate||""} onChange={e=>{ set("cancellationDate",e.target.value); if(cancelErr) setCancelErr(""); }}
          style={!String(form.cancellationDate||"").trim() ? { borderColor: "var(--c-red,#ff3d6b)" } : undefined}/>
        <div style={{ alignSelf:"end", fontSize:".72rem", color: String(form.cancellationDate||"").trim() ? "var(--c-text3,#45688a)" : "var(--c-red,#ff3d6b)", paddingBottom:".5rem" }}>
          {String(form.cancellationDate||"").trim()
            ? (lang==="es"?"Queda registrada para reportes y comisiones. La fecha de renovación se anula al guardar.":"Recorded for reports and commissions. The renewal date is cleared on save.")
            : (lang==="es"?"Obligatoria para poder cancelar la póliza.":"Required to cancel the policy.")}
        </div>
      </div>
      {/* Los DOS caminos valen, y valen sueltos: elegir de la lista y no
          escribir nada, o escribir y no elegir. Un motivo guardado antes de que
          existiera la lista no coincide con ninguna opción y aparece entero en
          la nota, intacto. */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label style={{fontSize:".68rem",color:"var(--c-text3,#45688a)",display:"block",marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>
            {lang==="es"?"Motivo de cancelación":"Cancellation reason"}
          </label>
          <select className="dkt-inp" style={{width:"100%"}}
            value={cancelParts.motivo}
            onChange={async e=>{
              const v = e.target.value;
              if (v === "__gestionar__") { setGestorMotivos(true); return; }
              if (v !== "__nuevo__") { setCancelParts({ preset: v }); return; }
              // "Agregar otro" — se guarda EN LA AGENCIA, no solo en esta
              // poliza: la proxima agente ya lo encuentra en la lista
              // (Lissette 2026-09-19). Mismo mecanismo que los Marketplaces.
              const nombre = (window.prompt(lang==="es"
                ? "Motivo de cancelación (se guardará en la lista para las próximas):"
                : "Cancellation reason (it will be saved to the list for future ones):") || "").trim();
              if (!nombre) return;                      // cancelo: no se toca nada
              setCancelParts({ preset: nombre });
              if (todosLosMotivos().some((m)=>m.toLowerCase()===nombre.toLowerCase())) return; // ya estaba
              const siguiente = [...getMotivosDeAgencia(), nombre];
              setMotivosDeAgencia(siguiente);           // visible YA, sin esperar al servidor
              setMotivoVer((n)=>n+1);
              try {
                await apiUpdateAgencySettings({ cancelReasons: siguiente });
              } catch (err) {
                // Si no se pudo guardar hay que DECIRLO: callarse haria creer
                // que esta en la lista, y desapareceria al recargar.
                toast.error((lang==="es"
                  ? "No se pudo guardar el motivo para las próximas pólizas: "
                  : "Could not save the reason for future policies: ") + (err.message||""));
              }
            }}>
            <option value="">{lang==="es"?"— Elegir de la lista —":"— Choose from the list —"}</option>
            {(()=>{ void motivoVer; return todosLosMotivos().map(r=><option key={r} value={r}>{etiquetaDeMotivo(r, lang)}</option>); })()}
            {/* Un motivo escrito a mano ANTES de que existiera la lista, o por
                otra agencia: se ofrece como opcion suya para que al reabrir la
                poliza el desplegable no salga vacio y se le borre al guardar. */}
            {cancelParts.motivo && !todosLosMotivos().some((m)=>m.toLowerCase()===cancelParts.motivo.toLowerCase()) && <option value={cancelParts.motivo}>{cancelParts.motivo}</option>}
            <option value="__nuevo__">{lang==="es"?"➕ Agregar otro…":"➕ Add another…"}</option>
            {getMotivosDeAgencia().length>0&&<option value="__gestionar__">{lang==="es"?"⚙️ Editar o quitar personalizados…":"⚙️ Edit or remove custom ones…"}</option>}
          </select>
          {gestorMotivos&&<GestorMotivosDeCancelacion lang={lang} onClose={()=>setGestorMotivos(false)} onChange={()=>setMotivoVer((n)=>n+1)}/>}
        </div>
        <div style={{alignSelf:"end",fontSize:".72rem",color:"var(--c-text3,#45688a)",paddingBottom:".5rem",lineHeight:1.35}}>
          {lang==="es"?"Con la lista basta. Si prefieres, escríbelo abajo — o las dos cosas."
            :"The list alone is enough. Or type it below — or both."}
        </div>
      </div>
      <Textarea
        label={lang==="es"?"…o escríbelo tú":"…or type it yourself"}
        value={cancelParts.nota}
        onChange={e=>setCancelParts({ note: e.target.value })}
        placeholder={lang==="es"?"Ej. el cliente consiguió otro plan, falta de pago, se mudó de estado…":"E.g. client found another plan, non-payment, moved states…"}/>
      {cancelErr && <div style={{color:"#ef4444",fontSize:".72rem",fontWeight:700,marginTop:4}}>{cancelErr}</div>}
    </div>}
    {/* Notas de la póliza. El campo existía en la base, el importador lo mapeaba
        ("📝 Notas") y el servidor lo guardaba — pero NINGUNA pantalla de pólizas
        lo mostraba: solo se veía en Hojas y en el portal del cliente. Así que
        mapear notas al importar parecía no funcionar (Lissette 2026-08-16:
        "quiero poder mapear nota de pólizas"): se guardaba y al abrir la póliza
        no estaba en ninguna parte. Es la nota de la PÓLIZA (una sola, la que
        viene de la importación), distinta del hilo de mensajes de la ficha. */}
    <Textarea
      label={lang==="es"?"📝 Notas de la póliza":"📝 Policy notes"}
      value={form.notes||""}
      onChange={e=>set("notes",e.target.value)}
      placeholder={lang==="es"?"Lo que venga en la columna de notas al importar aparece aquí.":"Whatever comes in the notes column on import shows up here."}/>
    <SectionLabel>{lang==="es"?"🧩 Campos personalizados":"🧩 Custom fields"}</SectionLabel>
    <div className="flex flex-col gap-2">
      <div className="text-xs text-gray-500 leading-snug">
        {lang==="es"
          ? "Agrega cualquier dato que pida la compañía (ej. Nº de grupo, código de plan…). El nombre del campo se comparte con TODAS las pólizas del mismo tipo; el valor lo llenas en cada cliente. Aparece en la tarjeta cuando tiene valor."
          : "Add any field a carrier needs (e.g. Group #, plan code…). The field NAME is shared across EVERY policy of the same type; you fill the value per client. It shows on the card once it has a value."}
      </div>
      {/* Las filas marcadas hidden son campos del catalogo que el agente quito
          de ESTA poliza. Se conservan en el array (para que el re-sembrado no
          las vuelva a anadir) pero no se dibujan. Se mapea sin filtrar para no
          desplazar el indice `i`, del que dependen las ediciones de abajo. */}
      {(form.customFields||[]).map((cf,i)=> (cf && cf.hidden) ? null : (
        <div key={i} className="flex gap-2 items-center">
          {/* maxLength mirrors customFields[].label.max(60) in policies.routes.ts.
              Without it the field name could be typed/pasted past the API limit and
              the save failed with a message naming a field the agent often couldn't
              even see (Maricarmen/Pomares 2026-08-07). Stop it at the keyboard. */}
          <input value={cf.label||""} maxLength={60} onChange={e=>{const a=[...(form.customFields||[])];a[i]={...a[i],label:e.target.value};set("customFields",a);}}
            placeholder={lang==="es"?"Nombre del campo":"Field name"} className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm"/>
          <input value={cf.value||""} onChange={e=>{const a=[...(form.customFields||[])];a[i]={...a[i],value:e.target.value};set("customFields",a);}}
            placeholder={lang==="es"?"Valor":"Value"} className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm"/>
          {/* Quitar un campo de ESTA poliza.
              Dos arreglos en uno (Lissette 2026-08-07):
              1. Antes no preguntaba nada, mientras que el gestor de campos por
                 tipo si confirmaba. Ahora confirma cuando hay algo que perder;
                 una fila recien anadida y vacia se quita sin ruido.
              2. La agencia de Maricarmen reportaba que el campo "vuelve a
                 salir" al reabrir. Es cierto: el nombre vive en el catalogo
                 compartido del tipo de poliza y el efecto de arriba lo vuelve a
                 sembrar. Por eso, si el campo viene del catalogo, no se borra la
                 fila: se marca hidden. El re-sembrado la ve como presente y ya
                 no la duplica, y el render la omite — queda quitada de verdad
                 en esta poliza sin tocar a las demas. */}
          <button type="button" onClick={async ()=>{
              const cur=(form.customFields||[])[i]||{};
              const label=String(cur.label||"").trim();
              const hasContent=label!=="" || String(cur.value||"").trim()!=="";
              const fromCatalog=label!=="" && getPolicyCustomFieldDefs(form.type)
                .some(d=>String(d).trim().toLowerCase()===label.toLowerCase());
              if(hasContent){
                const ok=await confirmDialog({
                  title:lang==="es"?"Quitar campo":"Remove field",
                  message:lang==="es"
                    ? `¿Quitar "${label||"sin nombre"}" de esta póliza?${fromCatalog?" Seguirá disponible en las demás pólizas de este tipo; para borrarlo en todas, usa el gestor de campos personalizados.":""}`
                    : `Remove "${label||"unnamed"}" from this policy?${fromCatalog?" It stays available on other policies of this type; to delete it everywhere use the custom-fields manager.":""}`,
                  danger:true, confirmLabel:lang==="es"?"Quitar":"Remove",
                });
                if(!ok) return;
              }
              set("customFields", fromCatalog
                ? (form.customFields||[]).map((cf,j)=>j===i?{...cf,value:"",hidden:true}:cf)
                : (form.customFields||[]).filter((_,j)=>j!==i));
            }}
            className="text-gray-400 hover:text-red-500 px-1" title={lang==="es"?"Quitar":"Remove"}><X size={16}/></button>
        </div>
      ))}
      <button type="button" onClick={()=>set("customFields",[...(form.customFields||[]),{label:"",value:""}])}
        className="self-start text-blue-600 hover:text-blue-500 text-sm font-semibold flex items-center gap-1 mt-1">
        <Plus size={14}/>{lang==="es"?"Agregar campo":"Add field"}
      </button>
    </div>
    <div className="flex gap-3 pt-1">
      <button type="button" onClick={handleSave} disabled={guardandoPoliza} className="flex-1 bg-blue-600 hover:bg-blue-500 text-gray-900 font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2" style={guardandoPoliza?{opacity:.6,cursor:"wait"}:undefined}><Save size={16}/>{guardandoPoliza?(lang==="es"?"Guardando…":"Saving…"):tp.save}</button>
      <button type="button" onClick={onClose} className="px-5 bg-gray-100 text-gray-700 font-semibold py-2.5 rounded-xl">{tp.cancel}</button>
    </div>
  </form>
  {statusMgr && <PolicyStatusManager lang={lang} onClose={()=>setStatusMgr(false)} onChanged={()=>setStatusVer((v)=>v+1)}/>}
  </>;
}

// Manager for agency-defined custom policy statuses (reusable list). Adds /
// removes { id, label, color } and persists to AgencySetting.policyStatuses so
// every seat sees the same options. Built-ins (Activo/Pendiente/Período de
// gracia/Cancelada) are shown read-only for reference.
function PolicyStatusManager({ lang, onClose, onChanged }) {
  const es = lang === 'es';
  const [list, setList] = useState(getCustomPolicyStatuses());
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('#0ea5e9');
  const [saving, setSaving] = useState(false);

  const persist = async (next) => {
    setSaving(true);
    try {
      await apiUpdateAgencySettings({ policyStatuses: next });
      setCustomPolicyStatuses(next);
      setList(getCustomPolicyStatuses());
      if (onChanged) onChanged();
    } catch (e) { toast.error(e.message || (es ? 'No se pudo guardar' : 'Could not save')); }
    finally { setSaving(false); }
  };
  const add = () => {
    const l = label.trim(); if (!l) return;
    persist([...(list || []), { id: 'cs_' + Date.now().toString(36), label: l, labelEs: l, color }]);
    setLabel(''); setColor('#0ea5e9');
  };
  const remove = async (s) => {
    const ok = await confirmDialog({
      title: es ? 'Quitar estatus' : 'Remove status',
      message: es ? `¿Quitar el estatus "${s.label}"? Las pólizas que lo usen quedarán sin ese estatus.` : `Remove the "${s.label}" status? Policies using it will lose it.`,
      danger: true, confirmLabel: es ? 'Quitar' : 'Remove',
    });
    if (!ok) return;
    persist((list || []).filter((x) => x.id !== s.id));
  };

  const builtins = getAllPolicyStatuses().filter((s) => s.builtin);
  const inp = { width: '100%', padding: '.5rem .6rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text,#eef6ff)', fontSize: '.82rem', boxSizing: 'border-box' };

  return <Modal title={es ? 'Estatus de pólizas' : 'Policy statuses'} onClose={onClose}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 340 }}>
      <div>
        <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>{es ? 'Predeterminados' : 'Built-in'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {builtins.map((s) => (
            <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '.25rem .6rem', borderRadius: 100, background: `${s.color}1a`, border: `1px solid ${s.color}55`, color: s.color, fontSize: '.74rem', fontWeight: 700 }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: s.color }} />{es ? s.labelEs : s.label}
            </span>
          ))}
        </div>
      </div>
      <div>
        <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>{es ? `Personalizados (${list.length})` : `Custom (${list.length})`}</div>
        {list.length === 0 && <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', padding: '.3rem 0' }}>{es ? 'Aún no tienes estatus personalizados.' : 'No custom statuses yet.'}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((s) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.4rem .6rem', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8 }}>
              <span style={{ width: 12, height: 12, borderRadius: 99, background: s.color || '#64748b', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: '.82rem', fontWeight: 600, color: 'var(--c-white,#eef6ff)' }}>{s.label}</span>
              <button onClick={() => remove(s)} disabled={saving} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: '.74rem' }}>{es ? 'Quitar' : 'Remove'}</button>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: 10 }}>
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} title={es ? 'Color' : 'Color'}
          style={{ width: 38, height: 34, padding: 0, border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, background: 'transparent', cursor: 'pointer', flexShrink: 0 }} />
        <input value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          placeholder={es ? 'Nombre del estatus (ej. En revisión)' : 'Status name (e.g. Under review)'} style={inp} />
        <button onClick={add} disabled={saving || !label.trim()} style={{ ...btnStyle(saving || !label.trim(), '#00d4ff'), padding: '.45rem .9rem', whiteSpace: 'nowrap' }}>
          <Plus size={14} />{es ? 'Agregar' : 'Add'}
        </button>
      </div>
      {/* Un estatus propio que se llama "Cancelada" cuenta como BAJA en toda la
          aplicación, y eso hay que decirlo ANTES de crearlo: es la diferencia
          entre una etiqueta de adorno y una póliza que deja de contar en las
          comisiones. Es un aviso, no un bloqueo — crear su propia "Cancelada"
          es legítimo, lo que no lo era es que no hiciera nada. */}
      {esNombreDeBaja(label) && (
        <div style={{ fontSize: '.74rem', color: '#f59e0b', background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.35)', borderRadius: 8, padding: '.45rem .6rem', lineHeight: 1.4 }}>
          {es
            ? `Ojo: "${label.trim()}" cuenta como póliza de baja — no saldrá en Activas y dejará de sumar meses en comisiones.`
            : `Heads up: "${label.trim()}" counts as a cancelled policy — it won't show under Active and stops adding months in commissions.`}
        </div>
      )}
    </div>
  </Modal>;
}

// Estatus del PAGO DEL MES (modulo Pagos del Mes). Es un catalogo APARTE del de
// pólizas: "Prima $0" o "Pago procesado" contestan "como pago este mes", no
// "como va el tramite de la poliza". Hasta hoy el boton de Pagos del Mes abria
// el administrador de estatus de POLIZA, asi que todo lo que se creaba desde
// aqui terminaba en el desplegable de Editar Poliza y NO en el de pago -- justo
// al reves de lo que hacia falta (Lissette 2026-08-18, con dos fotos: los tacho
// en Editar Poliza y los pidio en la columna ESTADO de Pagos del Mes).
//
// "Traer de estatus de poliza" mueve uno del catalogo de polizas a este,
// conservando su id: lo que ya estuviera marcado -- en pagos o en pólizas -- se
// sigue leyendo con su nombre y su color (getPolicyStatusMeta cae al catalogo de
// pagos). Mover NO borra nada: solo cambia en que desplegable se ofrece.
function PaymentStatusManager({ lang, statuses, onSaved, onClose, usage }) {
  const es = lang === 'es';
  const [list, setList] = useState(Array.isArray(statuses) ? statuses : []);
  const [policyCustoms, setPolicyCustoms] = useState(getCustomPolicyStatuses());
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('#0ea5e9');
  const [saving, setSaving] = useState(false);
  const use = usage || { pol: {}, pay: {} };

  // Un solo guardado para los dos catalogos: mover es quitar de uno y poner en
  // el otro, y partirlo en dos llamadas podria dejarlo en ningun sitio.
  const persist = async (next, nextPolicy) => {
    setSaving(true);
    try {
      const patch = { paymentStatuses: next };
      if (nextPolicy) patch.policyStatuses = nextPolicy;
      await apiUpdateAgencySettings(patch);
      setPaymentStatusCatalog(next);
      setList(next);
      if (nextPolicy) { setCustomPolicyStatuses(nextPolicy); setPolicyCustoms(getCustomPolicyStatuses()); }
      if (onSaved) onSaved(next);
      return true;
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo guardar' : 'Could not save'));
      return false;
    } finally { setSaving(false); }
  };

  const add = () => {
    const l = label.trim(); if (!l) return;
    persist([...(list || []), { id: 'ps_' + Date.now().toString(36), label: l, labelEs: l, color }]);
    setLabel(''); setColor('#0ea5e9');
  };

  const remove = async (st) => {
    const usados = use.pay[st.id] || 0;
    const ok = await confirmDialog({
      title: es ? 'Quitar estatus de pago' : 'Remove payment status',
      message: es
        ? `Quitar "${st.label}" del desplegable de Pagos del Mes.${usados ? ` Hay ${usados} pago(s) marcados asi: seguiran mostrandose, pero no podras volver a elegirlo.` : ''}`
        : `Remove "${st.label}" from the Monthly Payments dropdown.${usados ? ` ${usados} payment(s) use it: they keep showing it, but you won't be able to pick it again.` : ''}`,
      danger: true, confirmLabel: es ? 'Quitar' : 'Remove',
    });
    if (!ok) return;
    persist((list || []).filter((x) => x.id !== st.id));
  };

  const mover = async (st) => {
    const enPolizas = use.pol[st.id] || 0;
    const ok = await confirmDialog({
      title: es ? 'Mover a Pagos del Mes' : 'Move to Monthly Payments',
      message: es
        ? `"${st.label}" dejara de aparecer como estatus de POLIZA y pasara a ofrecerse al marcar el pago del mes.`
          + (enPolizas ? ` Ojo: ${enPolizas} poliza(s) lo tienen puesto ahora mismo; lo seguiran mostrando, pero no podras volver a elegirlo en la poliza.` : '')
        : `"${st.label}" will stop being a POLICY status and will be offered when marking the month's payment.`
          + (enPolizas ? ` Note: ${enPolizas} policy(ies) currently use it; they keep showing it, but you won't be able to pick it on a policy again.` : ''),
      confirmLabel: es ? 'Mover' : 'Move',
    });
    if (!ok) return;
    const yaEsta = (list || []).some((x) => x.id === st.id);
    const next = yaEsta ? list : [...(list || []), { id: st.id, label: st.label || st.labelEs, labelEs: st.labelEs || st.label, color: st.color || '#64748b' }];
    await persist(next, policyCustoms.filter((x) => x.id !== st.id));
  };

  const builtins = [
    { id: 'unpaid', label: es ? 'No pago' : 'Unpaid', color: '#ef4444' },
    { id: 'paid', label: es ? 'Pago' : 'Paid', color: '#10b981' },
    { id: 'grace', label: getPolicyStatusMeta('grace', lang).label, color: getPolicyStatusMeta('grace', lang).color },
  ];
  const candidatos = policyCustoms.filter((x) => !(list || []).some((y) => y.id === x.id));
  const inp = { width: '100%', padding: '.5rem .6rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text,#eef6ff)', fontSize: '.82rem', boxSizing: 'border-box' };
  const rot = { fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 };

  return <Modal title={es ? 'Estatus del pago del mes' : 'Monthly payment statuses'} onClose={onClose}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 360, maxWidth: 520 }}>
      <div style={{ fontSize: '.76rem', color: 'var(--c-text2,#7da0c4)', lineHeight: 1.45 }}>
        {es
          ? 'Estos son los estatus que ves al marcar el pago de cada mes. No tienen nada que ver con el estatus de la poliza en su tarjeta.'
          : "These are the statuses you see when marking each month's payment. They are separate from the policy status on its card."}
      </div>
      <div>
        <div style={rot}>{es ? 'Predeterminados' : 'Built-in'}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {builtins.map((st) => (
            <span key={st.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '.25rem .6rem', borderRadius: 100, background: `${st.color}1a`, border: `1px solid ${st.color}55`, color: st.color, fontSize: '.74rem', fontWeight: 700 }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: st.color }} />{st.label}
            </span>
          ))}
        </div>
      </div>
      <div>
        <div style={rot}>{es ? `Tuyos (${list.length})` : `Yours (${list.length})`}</div>
        {list.length === 0 && <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', padding: '.3rem 0' }}>{es ? 'Todavia no tienes estatus propios de pago.' : 'No payment statuses of your own yet.'}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((st) => (
            <div key={st.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.4rem .6rem', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8 }}>
              <span style={{ width: 12, height: 12, borderRadius: 99, background: st.color || '#64748b', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: '.82rem', fontWeight: 600, color: 'var(--c-white,#eef6ff)' }}>{es ? (st.labelEs || st.label) : (st.label || st.labelEs)}</span>
              {use.pay[st.id] ? <span style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)' }}>{es ? `${use.pay[st.id]} pago(s)` : `${use.pay[st.id]} payment(s)`}</span> : null}
              <button onClick={() => remove(st)} disabled={saving} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: '.74rem' }}>{es ? 'Quitar' : 'Remove'}</button>
            </div>
          ))}
        </div>
      </div>
      {candidatos.length > 0 && (
        <div style={{ borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: 10 }}>
          <div style={rot}>{es ? 'Los tienes en estatus de poliza' : 'Currently policy statuses'}</div>
          <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)', marginBottom: 8, lineHeight: 1.4 }}>
            {es
              ? 'Si alguno de estos habla del PAGO y no de la poliza, muevelo aqui: dejara de salir en Editar Poliza y pasara a salir al marcar el pago.'
              : 'If any of these is about the PAYMENT rather than the policy, move it here: it leaves the Edit Policy dropdown and shows up when marking the payment.'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {candidatos.map((st) => (
              <div key={st.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.4rem .6rem', background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 8 }}>
                <span style={{ width: 12, height: 12, borderRadius: 99, background: st.color || '#64748b', flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '.82rem', fontWeight: 600, color: 'var(--c-white,#eef6ff)' }}>{es ? (st.labelEs || st.label) : (st.label || st.labelEs)}</span>
                {use.pay[st.id] ? <span style={{ fontSize: '.66rem', color: '#10b981', fontWeight: 700 }}>{es ? 'usado en pagos' : 'used in payments'}</span> : null}
                <button onClick={() => mover(st)} disabled={saving} style={{ ...btnStyle(saving, '#00d4ff'), padding: '.3rem .7rem', fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                  {es ? 'Mover aqui' : 'Move here'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: 10 }}>
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} title={es ? 'Color' : 'Color'}
          style={{ width: 38, height: 34, padding: 0, border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, background: 'transparent', cursor: 'pointer', flexShrink: 0 }} />
        <input value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          placeholder={es ? 'Nombre del estatus (ej. Prima $0)' : 'Status name (e.g. Zero premium)'} style={inp} />
        <button onClick={add} disabled={saving || !label.trim()} style={{ ...btnStyle(saving || !label.trim(), '#00d4ff'), padding: '.45rem .9rem', whiteSpace: 'nowrap' }}>
          <Plus size={14} />{es ? 'Agregar' : 'Add'}
        </button>
      </div>
    </div>
  </Modal>;
}

export function PaymentInfoBlock({ policy, lang }) {
  const es = lang === "es";
  const isAutopay = policy.paymentType === "autopay";
  const isForce = policy.paymentType === "force";
  const isCard = policy.paymentMethod === "credit_card" || policy.paymentMethod === "debit_card";
  const isBank = policy.paymentMethod === "bank_account";
  // Calculate card expiration status
  let cardStatus = null;
  if (isCard && policy.cardExpMonth && policy.cardExpYear) {
    const exp = new Date(parseInt(policy.cardExpYear), parseInt(policy.cardExpMonth), 0);
    const daysLeft = Math.ceil((exp - new Date()) / 86400000);
    if (daysLeft < 0) cardStatus = { color: "#ef4444", bg: "rgba(239,68,68,.12)", border: "rgba(239,68,68,.35)", icon: "🔴", text: es ? `Vencida hace ${Math.abs(daysLeft)} días` : `Expired ${Math.abs(daysLeft)} days ago` };
    else if (daysLeft <= 30) cardStatus = { color: "#ffb700", bg: "rgba(255,183,0,.12)", border: "rgba(255,183,0,.35)", icon: "⚠️", text: es ? `Vence en ${daysLeft} días — pedir nueva tarjeta` : `Expires in ${daysLeft} days — request new card` };
    else if (daysLeft <= 60) cardStatus = { color: "#f97316", bg: "rgba(249,115,22,.08)", border: "rgba(249,115,22,.25)", icon: "🔔", text: es ? `Vence en ${daysLeft} días` : `Expires in ${daysLeft} days` };
  }
  const methodLabel = {
    credit_card: es ? "Tarjeta de Crédito" : "Credit Card",
    debit_card: es ? "Tarjeta de Débito" : "Debit Card",
    bank_account: es ? "Cuenta Bancaria (ACH)" : "Bank Account (ACH)"
  }[policy.paymentMethod] || "—";
  const brandIcon = { visa: "💳", mastercard: "💳", amex: "💳", discover: "💳", other: "💳" }[policy.cardBrand] || "💳";
  return (
    <div style={{marginTop:".75rem",paddingTop:".75rem",borderTop:"1px solid var(--c-border,#1a3550)"}}>
      <div style={{fontSize:".62rem",fontWeight:800,color:"var(--c-cyan,#00d4ff)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:".55rem"}}>
        💳 {es ? "Información de Pago" : "Payment Info"}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem"}}>
        <div style={{background:isAutopay?"rgba(0,255,136,.06)":isForce?"rgba(168,85,247,.06)":"rgba(255,183,0,.06)",border:`1px solid ${isAutopay?"rgba(0,255,136,.25)":isForce?"rgba(168,85,247,.25)":"rgba(255,183,0,.25)"}`,borderRadius:8,padding:".45rem .6rem"}}>
          <div style={{fontSize:".58rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",marginBottom:2}}>{es?"Tipo de Pago":"Payment Type"}</div>
          <div style={{fontSize:".78rem",fontWeight:700,color:isAutopay?"var(--c-green,#00ff88)":isForce?"#a855f7":"var(--c-amber,#ffb700)"}}>
            {isAutopay ? (es ? "🔄 Autopay" : "🔄 Autopay") : isForce ? (es ? "⚡ Forzar" : "⚡ Force") : policy.paymentType === "zero" ? (es ? "🆓 Prima $0" : "🆓 $0 Premium") : (es ? "👤 Cliente paga" : "👤 Client pays")}
          </div>
        </div>
        {isAutopay && policy.paymentMethod && (
          <div style={{background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border,#1a3550)",borderRadius:8,padding:".45rem .6rem"}}>
            <div style={{fontSize:".58rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",marginBottom:2}}>{es?"Método":"Method"}</div>
            <div style={{fontSize:".76rem",fontWeight:700,color:"var(--c-text,#c8dff0)"}}>{methodLabel}</div>
          </div>
        )}
        {isAutopay && isCard && policy.cardLast4 && (
          <div style={{background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border,#1a3550)",borderRadius:8,padding:".45rem .6rem"}}>
            <div style={{fontSize:".58rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",marginBottom:2}}>{es?"Tarjeta":"Card"}</div>
            <div style={{fontSize:".76rem",fontWeight:700,color:"var(--c-text,#c8dff0)",fontFamily:"var(--font-mono)"}}>
              {brandIcon} {policy.cardBrand ? policy.cardBrand.toUpperCase() : ""} •••• {policy.cardLast4}
            </div>
          </div>
        )}
        {isAutopay && isCard && policy.cardExpMonth && policy.cardExpYear && (
          <div style={{background:cardStatus?cardStatus.bg:"var(--c-surface,#0d1c2e)",border:`1px solid ${cardStatus?cardStatus.border:"var(--c-border,#1a3550)"}`,borderRadius:8,padding:".45rem .6rem"}}>
            <div style={{fontSize:".58rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",marginBottom:2}}>{es?"Vencimiento":"Expiration"}</div>
            <div style={{fontSize:".76rem",fontWeight:700,color:cardStatus?cardStatus.color:"var(--c-text,#c8dff0)",fontFamily:"var(--font-mono)"}}>
              {policy.cardExpMonth}/{String(policy.cardExpYear).slice(-2)}
            </div>
          </div>
        )}
        {isAutopay && isBank && policy.bankLast4 && (
          <div style={{background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border,#1a3550)",borderRadius:8,padding:".45rem .6rem"}}>
            <div style={{fontSize:".58rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",marginBottom:2}}>{es?"Cuenta":"Account"}</div>
            <div style={{fontSize:".76rem",fontWeight:700,color:"var(--c-text,#c8dff0)",fontFamily:"var(--font-mono)"}}>🏦 •••• {policy.bankLast4}</div>
          </div>
        )}
        {isAutopay && policy.billingDay && (
          <div style={{background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border,#1a3550)",borderRadius:8,padding:".45rem .6rem"}}>
            <div style={{fontSize:".58rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",marginBottom:2}}>{es?"Día de Cobro":"Billing Day"}</div>
            <div style={{fontSize:".76rem",fontWeight:700,color:"var(--c-text,#c8dff0)",fontFamily:"var(--font-mono)"}}>{es?`Día ${policy.billingDay}`:`Day ${policy.billingDay}`}</div>
          </div>
        )}
      </div>
      {cardStatus && (
        <div style={{marginTop:".55rem",padding:".5rem .7rem",borderRadius:8,background:cardStatus.bg,border:`1px solid ${cardStatus.border}`,display:"flex",alignItems:"center",gap:".5rem"}}>
          <span style={{fontSize:".9rem"}}>{cardStatus.icon}</span>
          <span style={{fontSize:".72rem",fontWeight:700,color:cardStatus.color}}>{cardStatus.text}</span>
        </div>
      )}
    </div>
  );
}

// #3 (Lissette 2026-07-08): independent note thread on each policy card. Text +
// pasted/attached images. Separate from the monthly-payment notes.

// ── Adjuntos de las notas de la póliza: fotos, PDF y audio ──────────────────
//
// Las reglas de qué se admite viven en ../adjuntosNota.js, un módulo plano que
// las pruebas comparan contra la lista del servidor: estaban escritas dos
// veces y se separaron, que es exactamente lo que dejó fuera los audios de
// Jennifer Acevedo (.amr, .webm y compañía).
//
// GRABAR desde Deskte se descartó a propósito. Florida exige el consentimiento
// de TODAS las partes para grabar una conversación (Fla. Stat. 934.03). Un
// botón de grabar dentro del CRM invita a grabar llamadas de clientes sin
// pedir permiso, y eso es una exposición legal más grande que la de HIPAA.
// Subir un archivo que ya existe no tiene ese problema: la grabación la hizo
// otro, en otro sitio, bajo sus reglas.

function PolicyNotesBubble({ policyId, currentUserId, lang }) {
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState([]);
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [pending, setPending] = useState([]); // {blobUrl, preview}
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editPending, setEditPending] = useState([]); // {blobUrl, preview} while editing
  const fileRef = useRef(null);
  const editFileRef = useRef(null);
  const composerTaRef = useRef(null);
  const editTaRef = useRef(null);

  // Lazy count so the badge shows how many notes exist without opening.
  useEffect(() => {
    let cancelled = false;
    listPolicyNotes(policyId).then((d) => { if (!cancelled) setCount(d.length); }).catch(() => {});
    return () => { cancelled = true; };
  }, [policyId]);

  const load = async () => {
    setLoading(true);
    try { const d = await listPolicyNotes(policyId); setThread(d); setCount(d.length); }
    catch (e) { toast.error(e.message || (es ? 'No se pudieron cargar las notas' : 'Failed to load notes')); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const uploadFiles = async (files, setter = setPending) => {
    for (const file of files) {
      // Antes se descartaba EN SILENCIO todo lo que no fuera imagen: el
      // servidor ya aceptaba PDF desde julio, pero esta linea lo tiraba antes
      // de intentarlo, y el agente veia como no pasaba nada. Ahora se admite lo
      // mismo que admite el servidor y, si algo no vale, se dice.
      if (!archivoAdmitido(file)) {
        // Dice QUE formato es y por que no vale. El mensaje anterior decia "se
        // admiten... y audio" mientras rechazaba un audio, y quien lo leia
        // concluia que los audios no se aceptaban (Jennifer Acevedo).
        toast.error(motivoRechazo(file, es));
        continue;
      }
      try {
        const named = file.name ? file : new File([file], 'pegado.png', { type: file.type || 'image/png' });
        const blobUrl = await uploadPaymentNoteImage(named);
        setter((p) => [...p, { blobUrl, preview: URL.createObjectURL(file), nombre: named.name }]);
      } catch (e) { toast.error(e.message || (es ? 'No se pudo subir el archivo' : 'Upload failed')); }
    }
  };
  const onPaste = (e) => {
    const imgs = [...(e.clipboardData?.items || [])].filter((i) => i.type.startsWith('image/')).map((i) => i.getAsFile()).filter(Boolean);
    if (imgs.length) { e.preventDefault(); uploadFiles(imgs); }
  };
  const onEditPaste = (e) => {
    const imgs = [...(e.clipboardData?.items || [])].filter((i) => i.type.startsWith('image/')).map((i) => i.getAsFile()).filter(Boolean);
    if (imgs.length) { e.preventDefault(); uploadFiles(imgs, setEditPending); }
  };
  // Seed the edit form with the note's existing photos (signed URLs are fine —
  // the backend strips the SAS query before storing). New photos append.
  const startEdit = (m) => { setEditingId(m.id); setEditText(m.text || ''); setEditPending((m.imageUrls || []).map((u) => ({ blobUrl: u, preview: u }))); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); setEditPending([]); };

  const send = async () => {
    const t2 = text.trim();
    if (!t2 && pending.length === 0) return;
    setBusy(true);
    try {
      await addPolicyNote({ policyId, text: t2, imageUrls: pending.map((p) => p.blobUrl) });
      setText(''); setPending([]);
      await load();
    } catch (e) { toast.error(e.message || (es ? 'No se pudo guardar' : 'Failed to save')); }
    finally { setBusy(false); }
  };

  const removeNote = async (id) => {
    const ok = await confirmDialog({ title: es ? '¿Borrar esta nota?' : 'Delete this note?', danger: true, confirmLabel: es ? 'Borrar' : 'Delete' });
    if (!ok) return;
    try { await deletePolicyNote(id); await load(); }
    catch (e) { toast.error(e.message || (es ? 'No se pudo borrar' : 'Failed to delete')); }
  };
  // Pin/unpin — any agent, not just the author (organizing action). Optimistic.
  const togglePin = async (m) => {
    const next = !m.isPinned;
    setThread((prev) => prev.map((x) => x.id === m.id ? { ...x, isPinned: next } : x));
    try { await togglePolicyNotePin(m.id, next); await load(); }
    catch (e) { setThread((prev) => prev.map((x) => x.id === m.id ? { ...x, isPinned: m.isPinned } : x)); toast.error(e.message || (es ? 'No se pudo fijar' : 'Failed to pin')); }
  };
  const saveEdit = async (id) => {
    const t2 = editText.trim();
    if (!t2 && editPending.length === 0) { toast.error(es ? 'La nota debe tener texto o una foto.' : 'The note must have text or a photo.'); return; }
    try { await updatePolicyNote(id, t2, editPending.map((p) => p.blobUrl)); cancelEdit(); await load(); }
    catch (e) { toast.error(e.message || (es ? 'No se pudo editar' : 'Failed to edit')); }
  };

  const fmtWhen = (iso) => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };

  // Pinned first, then newest → oldest (oldest ends up at the bottom). Same
  // ordering as the client Notes tab so every note surface reads the same way.
  const orderedThread = [...thread].sort((a, b) =>
    ((b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0)) ||
    String(b.createdAt || '').localeCompare(String(a.createdAt || '')),
  );

  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={es ? 'Notas de la póliza' : 'Policy notes'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '.2rem .5rem', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit',
          background: count ? 'var(--c-cyan-dim,rgba(0,212,255,.12))' : 'transparent',
          border: `1px solid ${count ? 'rgba(0,212,255,.35)' : 'var(--c-border,#1a3550)'}`,
          color: count ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)', fontSize: '.68rem', fontWeight: 700 }}>
        <StickyNote size={12} /> {es ? 'Notas' : 'Notes'}{count ? ` · ${count}` : ''}
      </button>
      {open && (
        <Modal title={`🗒️ ${es ? 'Notas de la póliza' : 'Policy notes'}`} onClose={() => setOpen(false)} wide>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem', minHeight: '55vh' }}>
            <div style={{ maxHeight: '58vh', minHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '.5rem', paddingRight: 2 }}>
              {loading && <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}><Loader2 size={14} className="dkt-spin" /> {es ? 'Cargando…' : 'Loading…'}</div>}
              {!loading && thread.length === 0 && (
                <div style={{ textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.8rem', fontStyle: 'italic', padding: '1rem 0' }}>
                  {es ? 'Sin notas todavía. Escribe la primera abajo.' : 'No notes yet. Write the first one below.'}
                </div>
              )}
              {orderedThread.map((m) => {
                const mine = m.authorUserId && m.authorUserId === currentUserId;
                return (
                  <div key={m.id} style={{ background: m.isPinned ? 'rgba(0,212,255,.07)' : 'var(--c-surface2,#112538)', border: `1px solid ${m.isPinned ? 'rgba(0,212,255,.4)' : 'var(--c-border,#1a3550)'}`, borderRadius: 10, padding: '.55rem .7rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: m.text || (m.imageUrls || []).length ? 4 : 0 }}>
                      <span style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--c-cyan,#00d4ff)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>{m.isPinned && <span title={es ? 'Fijada' : 'Pinned'}>📌</span>}{m.authorName || (es ? 'Alguien' : 'Someone')}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>{fmtWhen(m.createdAt)}</span>
                        {editingId !== m.id && (
                          <button type="button" onClick={() => togglePin(m)} title={m.isPinned ? (es ? 'Desfijar' : 'Unpin') : (es ? 'Fijar' : 'Pin')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: m.isPinned ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)', padding: 2, lineHeight: 0 }}><Pin size={12} /></button>
                        )}
                        {mine && editingId !== m.id && (
                          <>
                            <button type="button" onClick={() => startEdit(m)} title={es ? 'Editar' : 'Edit'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', padding: 2, lineHeight: 0 }}><Edit2 size={12} /></button>
                            <button type="button" onClick={() => removeNote(m.id)} title={es ? 'Borrar' : 'Delete'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', padding: 2, lineHeight: 0 }}><Trash2 size={12} /></button>
                          </>
                        )}
                      </span>
                    </div>
                    {editingId === m.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} onPaste={onEditPaste}>
                        <NoteFormatButtons taRef={editTaRef} value={editText} onChange={setEditText} lang={lang} compact />
                        <RichNoteBox boxRef={editTaRef} className="dkt-inp" value={editText} onChange={setEditText} lang={lang} rows={2} style={{ resize: 'vertical' }} placeholder={es ? 'Edita la nota… pega fotos con Ctrl/⌘+V' : 'Edit the note… paste photos with Ctrl/⌘+V'} />
                        {editPending.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {editPending.map((p, i) => (
                              <div key={i} style={{ position: 'relative' }}>
                                {tipoAdjunto(p.nombre || p.blobUrl) === 'imagen'
                                  ? <img src={p.preview} alt="" style={{ height: 56, width: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--c-border,#1a3550)' }} />
                                  : <div title={p.nombre || ''} style={{ height: 56, width: 56, borderRadius: 6, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface,#0c1c2e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                                      {tipoAdjunto(p.nombre || p.blobUrl) === 'audio' ? '\u{1F3A7}' : '\u{1F4C4}'}
                                    </div>}
                                <button type="button" onClick={() => setEditPending((prev) => prev.filter((_, j) => j !== i))} title={es ? 'Quitar' : 'Remove'}
                                  style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: 'var(--c-red,#ff3d6b)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '.7rem', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between', alignItems: 'center' }}>
                          <button type="button" onClick={() => editFileRef.current && editFileRef.current.click()} className="dkt-btn ghost xs" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <ImageIcon size={12} /> {es ? 'Foto' : 'Photo'}
                          </button>
                          <input ref={editFileRef} type="file" accept={ACEPTA_NOTA} multiple style={{ display: 'none' }} onChange={(e) => { uploadFiles(e.target.files, setEditPending); e.target.value = ''; }} />
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button type="button" className="dkt-btn ghost xs" onClick={cancelEdit}>{es ? 'Cancelar' : 'Cancel'}</button>
                            <button type="button" className="dkt-btn primary xs" onClick={() => saveEdit(m.id)}>{es ? 'Guardar' : 'Save'}</button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {m.text && <div style={{ fontSize: '.95rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.45 }}><RichNote text={m.text} /></div>}
                        {(m.imageUrls || []).length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 7 }}>
                            {m.imageUrls.map((u, i) => {
                              const tipo = tipoAdjunto(u);
                              // Un audio se escucha aqui mismo: obligar a
                              // descargarlo para oir treinta segundos es la
                              // diferencia entre que se use y que no.
                              if (tipo === 'audio') {
                                return (
                                  <audio key={i} src={u} controls preload="none" onClick={(e) => e.stopPropagation()}
                                    style={{ height: 34, maxWidth: 260 }} />
                                );
                              }
                              if (tipo === 'imagen') {
                                return (
                                  <a key={i} href={u} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                    <img src={u} alt="" style={{ maxHeight: 150, borderRadius: 8, border: '1px solid var(--c-border,#1a3550)' }} />
                                  </a>
                                );
                              }
                              // PDF y cualquier otra cosa: ficha con nombre.
                              // Pintarlo en un <img> daba una imagen rota que
                              // no decia que era ni se podia abrir.
                              return (
                                <a key={i} href={u} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '.4rem .6rem', borderRadius: 8,
                                    background: 'var(--c-surface,#0c1c2e)', border: '1px solid var(--c-border,#1a3550)',
                                    color: 'var(--c-text,#c8dff0)', textDecoration: 'none', fontSize: '.75rem', maxWidth: 240 }}>
                                  <span>{tipo === 'pdf' ? '\u{1F4C4}' : '\u{1F4CE}'}</span>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nombreAdjunto(u)}</span>
                                </a>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Composer */}
            <div style={{ borderTop: '1px solid var(--c-border,#1a3550)', paddingTop: '.6rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {pending.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {pending.map((p, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={p.preview} alt="" style={{ height: 54, borderRadius: 6, border: '1px solid var(--c-border,#1a3550)' }} />
                      <button type="button" onClick={() => setPending((arr) => arr.filter((_, j) => j !== i))}
                        style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: 'var(--c-red,#ff3d6b)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '.6rem', lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <RichNoteBox boxRef={composerTaRef} className="dkt-inp" value={text} onChange={setText} lang={lang} onPasteFiles={uploadFiles}
                placeholder={es ? 'Escribe una nota… (puedes pegar una imagen)' : 'Write a note… (you can paste an image)'} rows={2} style={{ resize: 'vertical' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button type="button" className="dkt-btn ghost xs" onClick={() => fileRef.current?.click()}><ImageIcon size={13} /> {es ? 'Imagen' : 'Image'}</button>
                  <NoteFormatButtons taRef={composerTaRef} value={text} onChange={setText} lang={lang} compact />
                </div>
                <input ref={fileRef} type="file" accept={ACEPTA_NOTA} multiple style={{ display: 'none' }} onChange={(e) => { uploadFiles([...e.target.files]); e.target.value = ''; }} />
                <button type="button" className="dkt-btn primary xs" disabled={busy || (!text.trim() && pending.length === 0)} onClick={send}>
                  {busy ? <Loader2 size={13} className="dkt-spin" /> : <Send size={13} />} {es ? 'Enviar' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export function PolicyCard({ policy, onEdit, onCopy, onDelete, onMove, onSendKit, sendingKit, t, lang, compact, dependents, client, onUpdateInsured, paymentStatus, paymentPeriod, onSetPaymentStatus, currentUserId, onUpdateTags, policyTagLibrary = [], onPolicyTagLibraryChange, policyTagColors = {}, onPolicyTagColorsChange }) {
  const [expanded, setExpanded] = useState(false);
  // Ordenar los campos de la ficha (Lissette 2026-10-02): se edita aqui, en la
  // tarjeta abierta, y al Guardar vale para todas las polizas de este tipo de
  // TODA LA AGENCIA (2026-10-03). Los borradores no tocan nada hasta Guardar.
  const [ordenando, setOrdenando] = useState(false);
  const [guardandoOrden, setGuardandoOrden] = useState(false);
  const [borradores, setBorradores] = useState({});
  const seccionesDeOrden = useRef(new Set());
  const ordenCtx = {
    editando: ordenando,
    borradores,
    secciones: seccionesDeOrden,
    setBorrador: (clave, orden) => setBorradores((b) => ({ ...b, [clave]: orden })),
  };
  const salirDeOrdenar = () => { setOrdenando(false); setBorradores({}); };
  const [showInsuredDrop, setShowInsuredDrop] = useState(null);
  const [savingInsured, setSavingInsured] = useState(false);
  const [savingPay, setSavingPay] = useState(false);
  // The "Insured on this policy" popover is z-index 9999 and was click-toggled
  // with no way to dismiss it — opening another modal or clicking elsewhere
  // left it floating on top of everything (Lissette 2026-06-23: "se queda
  // pegado, no se quita"). Close it on any outside mousedown or Escape.
  useEffect(() => {
    if (!showInsuredDrop) return;
    const onDown = (e) => { if (!e.target.closest?.('[data-insured-pop]')) setShowInsuredDrop(null); };
    const onEsc = (e) => { if (e.key === 'Escape') setShowInsuredDrop(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
  }, [showInsuredDrop]);
  const pt = getPolicyType(policy.type);
  const days = daysUntil(policy.renewalDate);
  const isPC = pt.pc;
  const isHome = ["home","renters"].includes(policy.type);
  const isAuto = policy.type==="auto";
  const isComm = policy.type==="commercial";
  const isMedicare = ["ma","medsup","partd"].includes(policy.type);
  // Medicare Advantage tiene su propia ficha (ver el bloque `isMA` abajo).
  const isMA = policy.type==="ma";
  const isAca = policy.type==="aca";
  // El # de Aplicación, el Subsidio y el Costo Total del Plan son SOLO de Salud
  // (Lissette 2026-08-28). MISMO lector que el formulario, a proposito: aqui
  // habia una copia a mano de la lista del formulario que ya se habia separado
  // de ella — la del formulario incluia "shortterm" y esta no, asi que en una
  // poliza Short Term el campo se pedia y luego no se veia.
  const esSalud = esPolizaDeSalud(policy.type);
  const isDental = policy.type==="dental";
  const isSupplCard = policy.type==="suppl";
  const isVision = policy.type==="vision";
  const isPet = policy.type==="pet";
  const isShortTerm = policy.type==="shortterm";
  const isFinal = policy.type==="final";
  const isHealth = !isPC;
  const isLife = ["life","final"].includes(policy.type);
  // Status pill — built-in or agency-defined custom — resolved through the
  // shared catalog so color + label stay consistent everywhere.
  const statusMeta = getPolicyStatusMeta(policy.status, lang);
  const sc = { bg: `${statusMeta.color}1a`, text: statusMeta.color, border: `${statusMeta.color}55` };
  // Etiquetas de PÓLIZA (ver PolicyTagsField). Se guardan al vuelo desde la
  // tarjeta, como los asegurados: `savingTags` bloquea el panel mientras el
  // servidor responde para que dos clics seguidos no se pisen.
  const policyTags = Array.isArray(policy.tags) ? policy.tags : [];
  const [tagsOpen, setTagsOpen] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const saveTags = async (next) => {
    if (typeof onUpdateTags !== "function" || savingTags) return;
    setSavingTags(true);
    try { await onUpdateTags(policy, next); } finally { setSavingTags(false); }
  };
  const accentColor = isPC ? "var(--c-cyan,#00d4ff)" : pt.color || "var(--c-cyan,#00d4ff)";
  // Single source of truth for the per-policy people math, reused by the
  // INSURED badge AND the expanded Family/Active rows so the three numbers
  // can never disagree (Lissette saw badge 0 while "members" said 1). Insured
  // is PURELY the agent's selection — nobody (titular/spouse/dep) is auto-added.
  const insuredIds = Array.isArray(policy.policyInsured) ? policy.policyInsured : [];
  // La regla vive en crm-app/aseguradosDeLaPoliza.js, no aqui. El comentario de
  // arriba decia "single source of truth" y NO lo era: era la unica copia, que
  // es distinto. La ficha impresa necesitaba lo mismo, y escribirlo alli otra
  // vez habria dejado la tarjeta diciendo "3 asegurados" y el papel listando 2,
  // sin que nada fallara (E-296).
  const insuredPeople = aseguradosDeLaPoliza(policy, client, dependents, {
    titular: lang==="es"?"Titular":"Primary",
    conyuge: lang==="es"?"Cónyuge":"Spouse",
  });
  const insuredCount = insuredPeople.length;
  // De quien es cada member id. Las claves son las de policyInsured, asi que se
  // resuelven contra la misma ficha; sin esto la tarjeta enseñaria numeros
  // sueltos que no se sabe a quien pertenecen, que es justo lo que se queria
  // evitar al guardarlos por persona.
  const nombreDeAsegurado = (key) => {
    if (key === "spouse") {
      const n = client ? `${client.spouse?.firstName||""} ${client.spouse?.lastName||""}`.trim() : "";
      return n || (lang==="es"?"Cónyuge":"Spouse");
    }
    const i = (dependents||[]).findIndex((d,di)=>(d.id||("dep"+di))===key);
    if (i >= 0) {
      const d = dependents[i];
      const n = `${d.firstName||""} ${d.lastName||""}`.trim();
      return n || (lang==="es"?`Dependiente ${i+1}`:`Dependent ${i+1}`);
    }
    // Una clave que ya no corresponde a nadie de la ficha (un dependiente que
    // se borro). Se enseña igual, sin inventarle nombre: el numero existe.
    return lang==="es"?"Otro asegurado":"Other insured";
  };
  // Household = everyone on file for the client (titular + spouse + deps),
  // independent of who's insured on THIS policy.
  const householdSize = (client ? 1 : 0) + (hasCurrentSpouse(client) ? 1 : 0) + (dependents||[]).length;
  const Row = ({icon, label, value, mono, color, wide}) => (
    <div style={{
      gridColumn: wide ? "1/-1" : "auto",
      background:"var(--c-surface2,#112538)",
      border:"1px solid var(--c-border,#1a3550)",
      borderRadius:9,
      padding:".55rem .75rem",
      display:"flex", flexDirection:"column", gap:3,
    }}>
      <span style={{fontSize:".56rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",
        letterSpacing:".08em",fontFamily:"var(--font-mono)",display:"flex",alignItems:"center",gap:4}}>
        {icon&&<span>{icon}</span>}{label}
      </span>
      <span style={{
        fontSize:".78rem",fontWeight:value?600:400,
        color:value?(color||"var(--c-text,#c8dff0)"):"var(--c-text3,#45688a)",
        fontFamily:mono?"var(--font-mono)":"var(--font-body)",
        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"
      }}>
        {value||"--"}
      </span>
    </div>
  );
  const SectionHead = ({icon,label}) => (
    <div style={{gridColumn:"1/-1",display:"flex",alignItems:"center",gap:".5rem",
      paddingBottom:".35rem",borderBottom:"1px solid var(--c-border,#1a3550)",marginTop:".25rem"}}>
      <span style={{fontSize:"1rem"}}>{icon}</span>
      <span style={{fontSize:".65rem",fontWeight:800,color:"var(--c-cyan,#00d4ff)",
        textTransform:"uppercase",letterSpacing:".1em"}}>{label}</span>
    </div>
  );
  return <div style={{
    background:"var(--c-surface2,#112538)",
    border:`1px solid ${isPC?"rgba(0,212,255,.2)":"var(--c-border,#1a3550)"}`,
    borderLeft:`3px solid ${accentColor}`,
    borderRadius:10,
    overflow:"visible",
  }}>
    <div style={{padding:".875rem 1rem"}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:".75rem",marginBottom:compact?0:".75rem"}}>
        <div style={{display:"flex",alignItems:"center",gap:".65rem",flex:1,minWidth:0}}>
          <div style={{width:36,height:36,borderRadius:9,display:"flex",alignItems:"center",
            justifyContent:"center",fontSize:"1.2rem",flexShrink:0,
            background:pt.color+"22",border:`1px solid ${pt.color}33`}}>
            {pt.icon}
          </div>
          <div style={{minWidth:0,flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:".4rem",flexWrap:"wrap"}}>
              <span style={{fontWeight:700,fontSize:".88rem",color:"var(--c-white,#eef6ff)"}}>
                {lang==="es"?pt.labelEs:pt.label}
              </span>
              {/* EL AÑO EN QUE EMPEZÓ, PEGADO AL NOMBRE DEL TIPO (Lissette
                  2026-09-13: "quiero que el año vaya al lado del nombre del
                  tipo"). Nació en la esquina derecha y se mudó aquí el mismo
                  día: en la esquina quedaba a media tarjeta del tipo, y lo que
                  se lee de un tirón es "Salud 2026", no un número suelto al
                  otro lado. NO se deja una copia en la esquina — el mismo dato
                  dos veces en la misma fila es ruido.
                  Sale del texto de la fecha efectiva, no de un `new Date`: una
                  póliza del 1 de enero se leería como del año anterior con el
                  huso de Florida. Sin fecha efectiva no se pinta nada. */}
              {anioDePoliza(policy) && (
                <span title={lang==="es"?`Año de inicio · Fecha efectiva ${fmt(policy.effectiveDate)}`:`Start year · Effective ${fmt(policy.effectiveDate)}`}
                  style={{fontSize:".7rem",padding:".1rem .45rem",borderRadius:4,fontWeight:800,
                    fontVariantNumeric:"tabular-nums",
                    background:"var(--c-surface3,#162d44)",color:"var(--c-text2,#7aa3c0)",
                    border:"1px solid var(--c-border,#1a3550)"}}>
                  {anioDePoliza(policy)}
                </span>
              )}
              {isPC&&<span style={{fontSize:".6rem",background:"var(--c-cyan-dim)",color:"var(--c-cyan,#00d4ff)",
                padding:".1rem .45rem",borderRadius:4,fontWeight:700,
                border:"1px solid rgba(0,212,255,.2)"}}>P&C</span>}
              {/* Estado de pago del mes actual — misma etiqueta/selector que en
                  Pagos del Mes, al lado del tipo (Lissette 2026-07-08). Solo se
                  muestra cuando la ficha del cliente conecta onSetPaymentStatus. */}
              {typeof onSetPaymentStatus==="function" && paymentPeriod && (()=>{
                // ESTADO DE PAGO DEL MES. NO se mezcla con el estado de la
                // PÓLIZA: son cosas distintas (Lissette 2026-08-15: "los status
                // de pólizas no lo ligues con los status de pagos del mes").
                // Antes este desplegable ofrecía además TODOS los estados
                // personalizados de póliza de la agencia, así que crear un
                // estado como "Pendiente de firma" —que habla del trámite de la
                // póliza— aparecía como si fuera una forma de haber pagado.
                //
                // El período de gracia se queda porque sí es una situación de
                // PAGO. Y los ids personalizados que alguien ya hubiera marcado
                // se siguen LEYENDO con su nombre y su color (abajo): quitarlos
                // de las opciones no puede volver ilegible lo ya guardado.
                const esL = lang==="es";
                const grace = getPolicyStatusMeta("grace", lang);
                const extra = [{id:"grace", label:grace.label, color:grace.color}];
                // Solo para LEER lo ya marcado, nunca para ofrecerlo.
                const legacyCustoms = getCustomPolicyStatuses().map(s=>({id:s.id, label: esL?(s.labelEs||s.label):s.label, color:s.color||"#64748b"}));
                const metaFor = (id) => {
                  if(id==="paid") return {label: esL?"✅ Pagó":"✅ Paid", color:"#10b981"};
                  if(id==="unpaid") return {label: esL?"❌ No pagó":"❌ Unpaid", color:"#ef4444"};
                  if(id==="pending"||!id) return {label: esL?"◻ Sin revisar":"◻ Not reviewed", color:"#7da0c4"};
                  const m = extra.find(s=>s.id===id) || legacyCustoms.find(s=>s.id===id);
                  return m?{label:`● ${m.label}`,color:m.color}:{label:id,color:"#64748b"};
                };
                const cur = paymentStatus || "pending";
                const meta = metaFor(cur);
                const reviewed = cur!=="pending";
                return (
                  <select value={cur==="pending"?"":cur} disabled={savingPay}
                    onChange={async(e)=>{ const v=e.target.value; if(!v) return; setSavingPay(true); try{ await onSetPaymentStatus(policy, v); } finally { setSavingPay(false); } }}
                    title={esL?`Estado de pago — ${paymentPeriod}`:`Payment status — ${paymentPeriod}`}
                    style={{padding:".12rem .4rem",borderRadius:6,fontSize:".62rem",fontWeight:800,appearance:"auto",cursor:savingPay?"wait":"pointer",opacity:savingPay?.6:1,
                      background: reviewed?`${meta.color}22`:"var(--c-bg2,#0a1828)",
                      border:`1px solid ${reviewed?`${meta.color}66`:"var(--c-border,#1a3550)"}`,
                      color: reviewed?meta.color:"var(--c-text2,#7da0c4)"}}>
                    {!reviewed && <option value="">{esL?"💲 Pago…":"💲 Pay…"}</option>}
                    <option value="unpaid">{esL?"❌ No pagó":"❌ Unpaid"}</option>
                    <option value="paid">{esL?"✅ Pagó":"✅ Paid"}</option>
                    {extra.map(s=><option key={s.id} value={s.id}>● {s.label}</option>)}
                  </select>
                );
              })()}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:".4rem",marginTop:2,flexWrap:"wrap"}}>
              <span style={{fontSize:".8rem",fontWeight:600,color:"var(--c-cyan,#00d4ff)"}}>{policy.carrier}</span>
              {policy.plan&&<span style={{fontSize:".75rem",color:"var(--c-text2,#7aa3c0)"}}>. {policy.plan}</span>}
            </div>
            {(()=>{
              const allInsured = insuredPeople;
              // Editable when the parent wires onUpdateInsured (the client ficha).
              // Lissette 2026-06-23: "tengo que entrar a la ficha y cambiarlo, no
              // está sincronizado" — let her toggle the insured family RIGHT on the
              // card; saving also runs the Policy→Coverage sync.
              const editable = typeof onUpdateInsured === "function" && !!client;
              // Household members keyed EXACTLY like PolicyForm (primary / spouse /
              // dep.id||"dep"+i) so toggles write the same policyInsured keys.
              // El conyuge sale si esta casado O si ya viene marcado como
              // asegurado — la MISMA regla del formulario y del contador
              // (aseguradosDeLaPoliza). Antes aqui solo salia si estaba casado:
              // una ficha con conyuge marcado pero estado civil "soltero" (o
              // vacio) contaba 1 asegurado que no aparecia en la lista, y por
              // tanto NO SE PODIA DESMARCAR (Lissette 2026-10-02, agencia de
              // Ana Maria Calle: "le tiene fijo uno que no lo pueden desmarcar").
              const hh = client ? [
                {key:"primary", name:`${client.firstName||""} ${client.lastName||""}`.trim(), rel:lang==="es"?"Titular":"Primary"},
                ...((hasCurrentSpouse(client) || (client.spouse && client.spouse.firstName && (Array.isArray(policy.policyInsured) ? policy.policyInsured : []).includes("spouse")))
                  ?[{key:"spouse", name:`${client.spouse.firstName||""} ${client.spouse.lastName||""}`.trim(), rel:lang==="es"?"Cónyuge":"Spouse"}]:[]),
                ...((dependents||[]).map((d,di)=>({key:d.id||("dep"+di), name:`${d.firstName||""} ${d.lastName||""}`.trim(), rel:d.relation||(lang==="es"?"Dependiente":"Dependent")}))),
              ].filter(m=>m.name) : [];
              const insuredKeys = Array.isArray(policy.policyInsured) ? policy.policyInsured : [];
              const extraNames = Array.isArray(policy.policyInsuredExtra) ? policy.policyInsuredExtra.filter(Boolean) : [];
              const toggleMember = async (key) => {
                if (savingInsured) return;
                const cur = Array.isArray(policy.policyInsured) ? policy.policyInsured : [];
                const next = cur.includes(key) ? cur.filter(x=>x!==key) : [...cur, key];
                setSavingInsured(true);
                try { await onUpdateInsured(policy, next); } finally { setSavingInsured(false); }
              };
              // Los nombres escritos a mano (o traidos de un importe) tambien se
              // quitan desde aqui: antes salian marcados y sin forma de
              // desmarcarlos, y solo se podian borrar abriendo la poliza.
              const quitarExtra = async (nombre) => {
                if (savingInsured) return;
                const cur = Array.isArray(policy.policyInsured) ? policy.policyInsured : [];
                const restantes = (Array.isArray(policy.policyInsuredExtra) ? policy.policyInsuredExtra : [])
                  .filter((n) => String(n || "").trim() !== nombre);
                setSavingInsured(true);
                try { await onUpdateInsured(policy, cur, restantes); } finally { setSavingInsured(false); }
              };
              return(
                <div data-insured-pop style={{position:"relative",display:"inline-block",marginTop:".4rem"}}>
                  <button type="button" onClick={()=>setShowInsuredDrop(p=>p===policy.id?null:policy.id)}
                    style={{background:"var(--c-cyan-dim)",border:"1px solid rgba(0,212,255,.35)",borderRadius:8,padding:".28rem .65rem",display:"flex",alignItems:"center",gap:6,cursor:"pointer",transition:"all .18s"}}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(0,212,255,.2)"}
                    onMouseLeave={e=>e.currentTarget.style.background="var(--c-cyan-dim)"}>
                    <span style={{fontSize:".6rem",color:"var(--c-cyan,#00d4ff)",fontFamily:"var(--font-mono)",fontWeight:700}}>🛡️ {lang==="es"?"ASEGURADOS":"INSURED"}</span>
                    <span style={{fontSize:".78rem",fontWeight:800,color:"var(--c-cyan,#00d4ff)"}}>{allInsured.length}</span>
                    <ChevronDown size={10} style={{color:"var(--c-cyan,#00d4ff)"}}/>
                  </button>
                  {showInsuredDrop===policy.id&&(
                    <div ref={verMenuEntero} style={{position:"absolute",top:"calc(100% + 6px)",left:0,zIndex:9999,background:"var(--c-bg2,#0b1622)",border:"1px solid rgba(0,212,255,.3)",borderRadius:10,padding:".6rem",minWidth:240,boxShadow:"0 12px 40px rgba(0,0,0,.8)"}}>
                      <div style={{fontSize:".58rem",color:"var(--c-text3,#45688a)",fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",fontFamily:"var(--font-mono)",marginBottom:".4rem",paddingBottom:".3rem",borderBottom:"1px solid rgba(26,53,80,.6)"}}>
                        🛡️ {editable?(lang==="es"?"Marca quién está asegurado":"Tap who's insured"):(lang==="es"?"Asegurados en esta póliza":"Insured on this policy")}
                      </div>
                      {editable
                        ? <>
                            {hh.length===0
                              ? <div style={{fontSize:".72rem",color:"var(--c-text3,#45688a)",fontStyle:"italic",padding:".25rem 0"}}>{lang==="es"?"Sin miembros del hogar":"No household members"}</div>
                              : hh.map((m)=>{ const on=insuredKeys.includes(m.key); return (
                                  <button key={m.key} type="button" disabled={savingInsured} onClick={()=>toggleMember(m.key)}
                                    style={{width:"100%",display:"flex",alignItems:"center",gap:".5rem",padding:".32rem .3rem",background:"transparent",border:"none",borderBottom:"1px solid rgba(26,53,80,.4)",cursor:savingInsured?"wait":"pointer",textAlign:"left",fontFamily:"inherit"}}>
                                    <span style={{width:18,height:18,borderRadius:5,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:".7rem",fontWeight:800,border:`1px solid ${on?"var(--c-cyan,#00d4ff)":"var(--c-border,#1a3550)"}`,background:on?"var(--c-cyan,#00d4ff)":"transparent",color:on?"#071828":"transparent"}}>{on?"✓":""}</span>
                                    <span style={{flex:1}}>
                                      <span style={{display:"block",fontSize:".75rem",fontWeight:600,color:on?"var(--c-white,#eef6ff)":"var(--c-text2,#7aa3c0)"}}>{m.name}</span>
                                      <span style={{fontSize:".6rem",color:"var(--c-text3,#45688a)"}}>{m.rel}</span>
                                    </span>
                                  </button>
                                ); })}
                            {extraNames.map((n,ni)=>(
                              <button key={"x"+ni} type="button" disabled={savingInsured} onClick={()=>quitarExtra(String(n).trim())}
                                title={lang==="es"?"Quitar de esta póliza":"Remove from this policy"}
                                style={{width:"100%",display:"flex",alignItems:"center",gap:".5rem",padding:".32rem .3rem",background:"transparent",border:"none",borderBottom:"1px solid rgba(26,53,80,.4)",cursor:savingInsured?"wait":"pointer",textAlign:"left",fontFamily:"inherit"}}>
                                <span style={{width:18,height:18,borderRadius:5,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:".7rem",fontWeight:800,border:"1px solid var(--c-cyan,#00d4ff)",background:"var(--c-cyan,#00d4ff)",color:"#071828"}}>✓</span>
                                <span style={{flex:1,fontSize:".72rem",color:"var(--c-text2,#7aa3c0)"}}>{n} <span style={{fontSize:".58rem",color:"var(--c-text3,#45688a)"}}>· {lang==="es"?"otro":"other"}</span></span>
                              </button>
                            ))}
                            <div style={{fontSize:".56rem",color:"var(--c-text3,#45688a)",marginTop:".4rem",lineHeight:1.3}}>{lang==="es"?"Se guarda al instante y sincroniza la cobertura.":"Saves instantly and syncs coverage."}</div>
                          </>
                        : (allInsured.length===0
                            ? <div style={{fontSize:".72rem",color:"var(--c-text3,#45688a)",fontStyle:"italic",padding:".25rem 0"}}>{lang==="es"?"Ninguno asignado — edita la póliza":"None assigned — edit the policy"}</div>
                            : allInsured.map((person,pi)=>(
                              <div key={pi} style={{display:"flex",alignItems:"center",gap:".5rem",padding:".3rem .25rem",borderBottom:pi<allInsured.length-1?"1px solid rgba(26,53,80,.5)":"none"}}>
                                <div style={{width:24,height:24,borderRadius:"50%",background:"var(--c-cyan-dim)",border:"1px solid rgba(0,212,255,.25)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:".55rem",fontWeight:800,color:"var(--c-cyan,#00d4ff)",flexShrink:0}}>
                                  {person.name.split(" ").map(w=>w[0]).join("").slice(0,2)}
                                </div>
                                <div>
                                  <div style={{fontSize:".75rem",fontWeight:600,color:"var(--c-white,#eef6ff)"}}>{person.name}</div>
                                  {person.relation&&<div style={{fontSize:".6rem",color:"var(--c-text3,#45688a)"}}>{person.relation}</div>}
                                </div>
                              </div>
                            )))
                      }
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:".35rem",flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
          {/* ETIQUETAS DE PÓLIZA — pegadas al estado, que es donde se miran
              (Lissette 2026-08-15: "quiero que la tag sea visible al lado de
              status, no dentro del formulario"). Se ven siempre y se editan
              aquí mismo: meterlas en el formulario obligaba a abrir la póliza
              entera para poner una palabra. */}
          {policyTags.map((tag)=>{ const st=tagChipStyle(tagColorFor(tag,policyTagColors)); return (
            <span key={tag} title={lang==="es"?"Etiqueta de póliza":"Policy tag"}
              style={{fontSize:".62rem",padding:".18rem .5rem",borderRadius:100,fontWeight:700,
                background:st.bg,color:st.fg,border:`1px solid ${st.border}`,maxWidth:150,
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tag}</span>
          );})}
          {typeof onUpdateTags==="function" && (
            <button type="button" onClick={()=>setTagsOpen(o=>!o)}
              title={lang==="es"?"Etiquetas de esta póliza":"Tags for this policy"}
              style={{background:tagsOpen?"var(--c-cyan-dim)":"var(--c-surface3,#162d44)",
                border:`1px solid ${tagsOpen?"var(--c-cyan,#00d4ff)":"var(--c-border,#1a3550)"}`,
                color:tagsOpen?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)",
                cursor:savingTags?"wait":"pointer",padding:"3px 8px",borderRadius:6,
                fontSize:".62rem",fontWeight:700,lineHeight:1.4,opacity:savingTags?.6:1}}>
              🏷️{policyTags.length?"":"＋"}
            </button>
          )}
          <span style={{fontSize:".65rem",padding:".2rem .55rem",borderRadius:100,fontWeight:700,
            background:sc.bg,color:sc.text,border:`1px solid ${sc.border}`}}>
            {statusMeta.label}
          </span>
          <button type="button" onClick={()=>setExpanded(e=>!e)}
            style={{background:expanded?"var(--c-cyan-dim)":"var(--c-surface3,#162d44)",
              border:`1px solid ${expanded?"var(--c-cyan,#00d4ff)":"var(--c-border,#1a3550)"}`,
              color:expanded?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)",
              cursor:"pointer",padding:"3px 10px",borderRadius:6,
              fontSize:".65rem",fontWeight:700,
              display:"flex",alignItems:"center",gap:4,transition:"all .2s"}}>
            <Eye size={12}/>{expanded ? (lang==="es"?"Ocultar":"Hide") : (lang==="es"?"Ver":"View")}
          </button>
          <PolicyNotesBubble policyId={policy.id} currentUserId={currentUserId} lang={lang}/>
          {onSendKit&&<button type="button" disabled={sendingKit} onClick={()=>onSendKit(policy)}
            title={lang==="es"?"Enviar kit de bienvenida":"Send welcome kit"}
            style={{background:"none",border:"none",cursor:sendingKit?"default":"pointer",color:sendingKit?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)",
              padding:4,borderRadius:6,lineHeight:0,transition:"color .2s",opacity:sendingKit?.6:1}}
            onMouseEnter={e=>{if(!sendingKit)e.currentTarget.style.color="var(--c-cyan,#00d4ff)";}}
            onMouseLeave={e=>{if(!sendingKit)e.currentTarget.style.color="var(--c-text3,#45688a)";}}><Mail size={13}/></button>}
          {onEdit&&<button type="button" onClick={()=>onEdit(policy)}
            style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text3,#45688a)",
              padding:4,borderRadius:6,lineHeight:0,transition:"color .2s"}}
            onMouseEnter={e=>e.currentTarget.style.color="var(--c-cyan,#00d4ff)"}
            onMouseLeave={e=>e.currentTarget.style.color="var(--c-text3,#45688a)"}><Edit2 size={13}/></button>}
          {/* COPIAR (Lissette 2026-10-15): abre "Agregar póliza" ya lleno con
              esta, sin # de póliza ni member IDs (crm-app/copiaDePoliza.js). */}
          {onCopy&&<button type="button" onClick={()=>onCopy(policy)}
            title={lang==="es"?"Copiar: crear una póliza nueva a partir de esta":"Copy: create a new policy from this one"}
            style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text3,#45688a)",
              padding:4,borderRadius:6,lineHeight:0,transition:"color .2s"}}
            onMouseEnter={e=>e.currentTarget.style.color="var(--c-cyan,#00d4ff)"}
            onMouseLeave={e=>e.currentTarget.style.color="var(--c-text3,#45688a)"}><Copy size={13}/></button>}
          {/* MOVER A OTRO CLIENTE (Maritza Mendoza, 2026-09-10). Va justo antes de
              la papelera a proposito: hasta ahora, una poliza en el cliente
              equivocado se arreglaba borrandola y tecleandola otra vez —y con
              ella se iban sus pagos, sus notas y sus comisiones—. Quien viene a
              borrar por ese motivo tiene que ver primero la salida que no
              pierde nada. */}
          {onMove&&<button type="button" onClick={()=>onMove(policy)}
            title={lang==="es"?"Mover a otro cliente":"Move to another client"}
            style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text3,#45688a)",
              padding:4,borderRadius:6,lineHeight:0,transition:"color .2s"}}
            onMouseEnter={e=>e.currentTarget.style.color="var(--c-cyan,#00d4ff)"}
            onMouseLeave={e=>e.currentTarget.style.color="var(--c-text3,#45688a)"}><ArrowRightLeft size={13}/></button>}
          {onDelete&&<button type="button" onClick={()=>onDelete(policy.id)}
            style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text3,#45688a)",
              padding:4,borderRadius:6,lineHeight:0,transition:"color .2s"}}
            onMouseEnter={e=>e.currentTarget.style.color="var(--c-red,#ff3d6b)"}
            onMouseLeave={e=>e.currentTarget.style.color="var(--c-text3,#45688a)"}><Trash2 size={13}/></button>}
        </div>
      </div>
      {/* Panel de etiquetas — se abre desde el 🏷️ de arriba y guarda al vuelo,
          sin abrir la póliza. */}
      {tagsOpen&&typeof onUpdateTags==="function"&&<div style={{
        marginBottom:".7rem",padding:".65rem .75rem",borderRadius:9,
        background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border,#1a3550)",
        opacity:savingTags?.6:1,pointerEvents:savingTags?"none":"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:".45rem"}}>
          <span style={{fontSize:".6rem",fontWeight:800,color:"var(--c-cyan,#00d4ff)",textTransform:"uppercase",letterSpacing:".08em"}}>
            🏷️ {lang==="es"?"Etiquetas de póliza":"Policy tags"}
          </span>
          <button type="button" onClick={()=>setTagsOpen(false)}
            style={{background:"none",border:"none",color:"var(--c-text3,#45688a)",cursor:"pointer",fontSize:".7rem",padding:0,fontFamily:"inherit"}}>✕</button>
        </div>
        <PolicyTagsField
          tags={policyTags} onChange={saveTags} lang={lang}
          library={policyTagLibrary} onLibraryChange={onPolicyTagLibraryChange}
          colors={policyTagColors} onColorsChange={onPolicyTagColorsChange}/>
      </div>}
      {/* Los cuadritos del resumen (prima, Member ID, # aplicación, renovación)
          también se ordenan (Lissette 2026-10-22: "no me deja ordenar la prima,
          el member id o la fecha de renovación"). Comparten el MISMO contexto
          que la ficha abierta, así que entran en "Ordenar campos" y se guardan
          con el mismo botón. */}
      {!compact&&<OrdenDeFichaContext.Provider value={ordenCtx}><CamposOrdenables tipo={policy.type} seccion="resumen" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:".5rem"}}>
        {/* La prima elegida en la póliza (mensual, semestral o anual), o la que esté llena (2026-10-22). */}
        {primaElegida(policy).llena&&<div campo="resumen_prima" style={{background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border,#1a3550)",borderRadius:8,padding:".45rem .6rem"}}>
          <div style={{fontSize:".58rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",marginBottom:2}}>Prima{sufijoDePeriodo(primaElegida(policy).periodo, lang)}</div>
          <div style={{fontFamily:"var(--font-display)",fontSize:".92rem",fontWeight:700,color:"var(--c-green,#00ff88)"}}>{fmtMoneyZero(primaElegida(policy).monto)}</div>
        </div>}
        {(policy.memberId||policy.policyNumber)&&<div campo="resumen_member" style={{background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border,#1a3550)",borderRadius:8,padding:".45rem .6rem"}}>
          <div style={{fontSize:".58rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",marginBottom:2}}>{policy.memberId?"Member ID":"Policy #"}</div>
          <div style={{fontSize:".72rem",fontFamily:"var(--font-mono)",color:"var(--c-text,#c8dff0)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{policy.memberId||policy.policyNumber}</div>
        </div>}
        {esSalud&&policy.applicationNumber&&<div campo="resumen_aplicacion" style={{background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border,#1a3550)",borderRadius:8,padding:".45rem .6rem"}}>
          <div style={{fontSize:".58rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",marginBottom:2}}>{lang==="es"?"# Aplicación":"Application #"}</div>
          <div style={{fontSize:".72rem",fontFamily:"var(--font-mono)",color:"var(--c-text,#c8dff0)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{policy.applicationNumber}</div>
        </div>}
        {policy.renewalDate&&<div campo="resumen_renovacion" style={{background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border,#1a3550)",borderRadius:8,padding:".45rem .6rem"}}>
          <div style={{fontSize:".58rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",marginBottom:2}}>{lang==="es"?"Renovación":"Renewal"}</div>
          <div style={{fontSize:".78rem",fontWeight:700,fontFamily:"var(--font-mono)",
            color:days!==null&&days<0?"var(--c-red,#ff3d6b)":days!==null&&days<30?"var(--c-amber,#ffb700)":"var(--c-text,#c8dff0)"}}>
            {fmt(policy.renewalDate)}
          </div>
          {/* La FECHA siempre (Lissette 2026-10-05: "por qué sale así y no la
              fecha?"). Antes, a menos de 90 días, la fecha se cambiaba por
              "87d" y no había forma de verla sin abrir la póliza. Los días
              siguen debajo, con el mismo color de aviso. */}
          {days!==null&&days<90&&<div style={{fontSize:".6rem",fontFamily:"var(--font-mono)",marginTop:1,
            color:days<0?"var(--c-red,#ff3d6b)":days<30?"var(--c-amber,#ffb700)":"var(--c-text3,#45688a)"}}>
            {days<0
              ? (lang==="es"?`vencida hace ${-days} d`:`${-days}d overdue`)
              : (lang==="es"?`en ${days} días`:`in ${days} days`)}
          </div>}
        </div>}
      </CamposOrdenables></OrdenDeFichaContext.Provider>}
      {/* Cancelación PROGRAMADA (Lissette 2026-10-20): la póliza sigue con su
          estatus hasta la fecha y el servidor la cancela ese día. Se puede quitar. */}
      {!esEstatusCancelado(policy.status)&&policy.customData?.cancelacionProgramada?.fecha&&<div style={{marginTop:compact?".6rem":".75rem",padding:".5rem .7rem",borderRadius:8,background:"rgba(245,158,11,.08)",border:"1px solid rgba(245,158,11,.35)",display:"flex",gap:8,alignItems:"flex-start"}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:".58rem",color:"#f59e0b",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",fontWeight:800,marginBottom:2}}>{lang==="es"?`⏳ Se cancela el ${fmt(policy.customData.cancelacionProgramada.fecha)}`:`⏳ Cancels on ${fmt(policy.customData.cancelacionProgramada.fecha)}`}</div>
          {policy.customData.cancelacionProgramada.motivo&&<div style={{fontSize:".78rem",color:"var(--c-text,#c8dff0)",lineHeight:1.4,whiteSpace:"pre-wrap"}}>{mostrarMotivo(policy.customData.cancelacionProgramada.motivo, lang)}</div>}
        </div>
        {window.__deskteAuth&&<button type="button" className="dkt-btn-s" style={{fontSize:".7rem",padding:"3px 8px"}}
          onClick={async(e)=>{
            e.stopPropagation();
            if(!window.confirm(lang==="es"?"¿Quitar la cancelación programada? La póliza seguirá activa.":"Remove the scheduled cancellation? The policy will stay active.")) return;
            try{ await apiQuitarCancelacionProgramada(policy.id); toast.success(lang==="es"?"Cancelación programada quitada":"Scheduled cancellation removed"); }
            catch(err){ toast.error((lang==="es"?"No se pudo quitar: ":"Could not remove: ")+(err.message||"")); }
          }}>{lang==="es"?"Quitar":"Remove"}</button>}
      </div>}
      {/* Motivo de cancelación — visible en la tarjeta de toda póliza cancelada. */}
      {esEstatusCancelado(policy.status)&&policy.cancelReason&&<div style={{marginTop:compact?".6rem":".75rem",padding:".5rem .7rem",borderRadius:8,background:"rgba(239,68,68,.08)",border:"1px solid rgba(239,68,68,.28)"}}>
        <div style={{fontSize:".58rem",color:"#ef4444",textTransform:"uppercase",letterSpacing:".07em",fontFamily:"var(--font-mono)",fontWeight:800,marginBottom:2}}>{lang==="es"?"🚫 Motivo de cancelación":"🚫 Cancellation reason"}</div>
        <div style={{fontSize:".78rem",color:"var(--c-text,#c8dff0)",lineHeight:1.4,whiteSpace:"pre-wrap"}}>{mostrarMotivo(policy.cancelReason, lang)}</div>
      </div>}
    </div>
    {expanded&&<div style={{
      borderTop:`2px solid ${accentColor}44`,
      background:"var(--c-surface,#0d1c2e)",
      padding:"1rem",
    }}>
      <OrdenDeFichaContext.Provider value={ordenCtx}>
      <div className="dkt-orden-barra">
        {!ordenando ? (
          <button type="button" onClick={()=>setOrdenando(true)} title={lang==="es"?"Mueve los campos de la ficha a tu gusto":"Move the card's fields as you like"}>
            ⠿ {lang==="es"?"Ordenar campos":"Arrange fields"}
          </button>
        ) : (<>
          <span className="dkt-orden-aviso">{lang==="es"
            ?`Arrastra cada campo a donde lo quieras (o usa las flechas ‹ ›). Al guardar, todas las pólizas de este tipo se verán así para toda la agencia.`
            :`Drag each field where you want it (or use the ‹ › arrows). When you save, every policy of this type will look like this for the whole agency.`}</span>
          <button type="button" className="dkt-orden-guardar" disabled={guardandoOrden} onClick={async()=>{
            setGuardandoOrden(true);
            try { await guardarOrdenes(borradores); salirDeOrdenar(); }
            catch (e) { toast.error((lang==="es"?"No se pudo guardar el orden: ":"Could not save the order: ")+(e?.message||"")); }
            finally { setGuardandoOrden(false); }
          }}>{guardandoOrden?(lang==="es"?"Guardando…":"Saving…"):(lang==="es"?"Guardar":"Save")}</button>
          <button type="button" onClick={salirDeOrdenar}>{lang==="es"?"Cancelar":"Cancel"}</button>
          <button type="button" disabled={guardandoOrden} onClick={async()=>{
            setGuardandoOrden(true);
            try { await restablecerOrdenes([...seccionesDeOrden.current]); salirDeOrdenar(); }
            catch (e) { toast.error((lang==="es"?"No se pudo restablecer el orden: ":"Could not reset the order: ")+(e?.message||"")); }
            finally { setGuardandoOrden(false); }
          }}>{lang==="es"?"Volver al orden original":"Reset to default"}</button>
        </>)}
      </div>
      {/* FICHA DE MEDICARE ADVANTAGE (Lissette 2026-10-01, con su dibujo):
          dos columnas, en el orden EXACTO de su dibujo, de izquierda a derecha y
          fila por fila — el grid de dos columnas las empareja solo, así que el
          orden de estas lineas ES el dibujo; no se reordenan sin preguntarle.
          Quitó prima inicial/actual, personas en familia y asegurados en esta
          póliza; juntó Estatus del SOA y SOA firmado en "SOA"; la Fecha de
          venta pasa aquí como "Fecha de cierre". Pidió conservar el ID de la
          compañía y el Analista (este sigue en Origen y Proceso). Suplemento y
          Parte D siguen con la ficha de siempre. */}
      {isMA&&<CamposOrdenables tipo={policy.type} seccion="ma" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem"}}>
        <SectionHead icon={pt.icon} label={lang==="es"?"Datos de Medicare Advantage":"Medicare Advantage Details"}/>
        <Row campo="carrier" icon="🏢" label={lang==="es"?"Compañía":"Carrier"} value={policy.carrier}/>
        <Row campo="plan_name" icon="📋" label={lang==="es"?"Nombre del plan":"Plan name"} value={policy.plan} color="var(--c-cyan,#00d4ff)"/>
        <Row campo="product_type" icon="🏷️" label={lang==="es"?"Tipo de producto":"Product type"} value={policy.productType}/>
        <Row campo="plan_year" icon="📆" label={lang==="es"?"Plan del año":"Plan year"} value={policy.planYear}/>
        <Row campo="plan_monthly_premium" icon="💵" label={lang==="es"?"Prima mensual del plan":"Plan monthly premium"} value={(policy.monthlyPremium != null && policy.monthlyPremium !== '') ? fmtMoneyZero(policy.monthlyPremium) : null} color="var(--c-green,#00ff88)"/>
        <Row campo="giveback_amount" icon="💰" label={lang==="es"?"Monto Giveback":"Giveback amount"} value={(policy.givebackAmount != null && policy.givebackAmount !== '') ? fmtMoneyZero(policy.givebackAmount) : null} color="var(--c-green,#00ff88)"/>
        <Row campo="part_b_premium" icon="💳" label={lang==="es"?"Prima Parte B":"Part B premium"} value={(policy.partBPremium != null && policy.partBPremium !== '') ? fmtMoneyZero(policy.partBPremium) : null}/>
        <Row campo="member_id" icon="🪪" label="Member ID" value={policy.memberId} mono/>
        <Row campo="medicare_number_mbi" icon="⭐" label={lang==="es"?"N.º de Medicare (MBI)":"Medicare Number (MBI)"} value={policy.medicareNumber} mono/>
        <Row campo="contract_pbp_segment" icon="🆔" label="Contract / PBP / Segment" value={policy.contractPbpSegment} mono/>
        <Row campo="part_a_date" icon="🅰️" label={lang==="es"?"Fecha Parte A":"Part A date"} value={policy.partADate ? fmt(policy.partADate) : null}/>
        <Row campo="part_b_date" icon="🅱️" label={lang==="es"?"Fecha Parte B":"Part B date"} value={policy.partBDate ? fmt(policy.partBDate) : null}/>
        <Row campo="medicaid_eligibility" icon="🟢" label={lang==="es"?"Elegibilidad Medicaid":"Medicaid eligibility"} value={policy.medicaidEligibility}/>
        <Row campo="medicaid_number" icon="🪪" label={lang==="es"?"N.º de Medicaid":"Medicaid number"} value={policy.medicaidNumber} mono/>
        <Row campo="lis_extra_help" icon="💊" label="LIS / Extra Help" value={policy.lisLevel}/>
        <Row campo="hra_status" icon="🩺" label={lang==="es"?"Estatus HRA":"HRA status"} value={policy.hraStatus}/>
        <Row campo="soa" icon="📝" label="SOA" value={[policy.soaStatus, policy.soaSigned].filter(Boolean).join(" · ") || null}/>
        <Row campo="soa_signature_date" icon="✍️" label={lang==="es"?"Fecha de firma SOA":"SOA signature date"} value={policy.soaSignedDate ? fmt(policy.soaSignedDate) : null}/>
        <Row campo="close_date" icon="📅" label={lang==="es"?"Fecha de cierre":"Close date"} value={policy.saleDate ? fmt(policy.saleDate) : null} color="var(--c-cyan,#00d4ff)"/>
        <Row campo="effective_date" icon="▶️" label={lang==="es"?"Fecha de inicio":"Effective date"} value={policy.effectiveDate ? fmt(policy.effectiveDate) : null}/>
        <Row campo="renewal_date" icon="🔄" label={lang==="es"?"Fecha de renovación":"Renewal date"} value={policy.renewalDate ? fmt(policy.renewalDate) : null} color={days!==null&&days<30?"var(--c-amber,#ffb700)":undefined}/>
        <Row campo="enrollment_code" icon="🔑" label={lang==="es"?"Código de inscripción":"Enrollment code"} value={policy.enrollmentCode} mono/>
        <Row campo="agent_on_policy" icon="👤" label={lang==="es"?"Agente en póliza":"Agent on policy"} value={policy.agentLabel || policy.agentName}/>
        <Row campo="sub_agent" icon="👤" label={lang==="es"?"Subagente":"Sub-agent"} value={policy.subAgent}/>
        <Row campo="producer_code" icon="🔢" label={lang==="es"?"Código productor":"Producer code"} value={policy.producerCode} mono/>
        <Row campo="company_id" icon="🏢" label={lang==="es"?"ID de la compañía":"Company ID"} value={policy.companyId} mono/>
        {/* Lo que no está en el dibujo pero puede tener la póliza: se enseña
            solo si tiene valor, para no perderlo de vista sin ensuciar la ficha. */}
        {Object.entries(componerMemberIds(policy))
          .filter(([k,v])=>k!=="primary"&&v)
          .map(([k,v])=>(
            <Row campo={"memberId:"+k} key={k} icon="🪪" label={`Member ID · ${nombreDeAsegurado(k)}`} value={v} mono/>
          ))}
        {policy.policyNumber && <Row campo="policy" icon="📄" label={lang==="es"?"# Póliza":"Policy #"} value={policy.policyNumber} mono/>}
        {esSalud && policy.applicationNumber && <Row campo="application" icon="📝" label={lang==="es"?"# de Aplicación":"Application #"} value={policy.applicationNumber} mono/>}
        {policy.cancellationDate && <Row campo="cancellation_date" icon="🛑" label={lang==="es"?"Fecha de cancelación":"Cancellation Date"} value={fmt(policy.cancellationDate)} color="var(--c-red,#ff3d6b)"/>}
      </CamposOrdenables>}
      {isHealth&&!isMA&&<CamposOrdenables tipo={policy.type} seccion="salud" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem"}}>
        <SectionHead icon={pt.icon} label={lang==="es"?`Datos de ${pt.labelEs}`:`${pt.label} Details`}/>
        <Row campo="plan_name" icon="📋" label={lang==="es"?"Nombre del Plan":"Plan Name"} value={policy.plan} color="var(--c-cyan,#00d4ff)"/>
        {isLife && <Row campo="face_amount" icon="💵" label={lang==="es"?"Face Amount / Cobertura":"Face Amount"} value={(policy.coverageAmount != null && policy.coverageAmount !== '') ? fmtMoneyZero(policy.coverageAmount) : null} color="var(--c-green,#00ff88)"/>}
        {isLife && <Row campo="beneficiary" icon="🎯" label={lang==="es"?"Beneficiario":"Beneficiary"} value={policy.beneficiary}/>}
        {isLife && <Row campo="owner_name" icon="👑" label={lang==="es"?"Dueño de la póliza":"Owner Name"} value={policy.policyOwnerName}/>}
        {isLife && <Row campo="term_duration" icon="⏳" label={lang==="es"?"Duración del término":"Term Duration"} value={policy.termDuration}/>}
        {isLife && <Row campo="date_of_issue" icon="🗓️" label={lang==="es"?"Fecha de emisión":"Date of Issue"} value={policy.issueDate ? fmt(policy.issueDate) : null}/>}
        {isLife && <Row campo="premium_due_date" icon="💳" label={lang==="es"?"Fecha de pago de prima":"Premium Due Date"} value={policy.premiumDueDate ? fmt(policy.premiumDueDate) : null}/>}
        {isLife && <Row campo="billable_premium" icon="🧾" label={lang==="es"?"Prima facturable":"Billable Premium"} value={(policy.billablePremium != null && policy.billablePremium !== '') ? fmtMoneyZero(policy.billablePremium) : null} color="var(--c-cyan,#00d4ff)"/>}
        {isLife && <Row campo="billing_frequency" icon="🔁" label={lang==="es"?"Frecuencia de pago":"Billing Frequency"} value={policy.billingFrequency}/>}
        {isLife && <Row campo="billing_method" icon="🏦" label={lang==="es"?"Método de facturación":"Billing Method"} value={policy.billingMethod}/>}
        {isLife && <Row campo="agent_number" icon="🧑‍💼" label={lang==="es"?"Número de agente":"Agent Number"} value={policy.agentNumber} mono/>}
        {isLife && <Row campo="agency_number" icon="🏢" label={lang==="es"?"Número de agencia":"Agency Number"} value={policy.agencyNumber} mono/>}
        {/* Vida, segunda tanda (Lissette 2026-08-09) — el ciclo de la solicitud
            en el mismo orden en que ocurre: aprobada, papeles, entregada,
            colocada. Leído de arriba abajo se ve dónde está trabado el caso. */}
        {isLife && <Row campo="approved_date" icon="✅" label={lang==="es"?"Fecha de aprobación":"Approved Date"} value={policy.approvedDate ? fmt(policy.approvedDate) : null}/>}
        {isLife && <Row campo="delivery_papers_submitted" icon="📤" label={lang==="es"?"Papeles de entrega enviados":"Delivery Papers Submitted"} value={policy.deliveryPapersDate ? fmt(policy.deliveryPapersDate) : null}/>}
        {isLife && <Row campo="delivery_date" icon="📬" label={lang==="es"?"Fecha de entrega":"Delivery Date"} value={policy.deliveryDate ? fmt(policy.deliveryDate) : null}/>}
        {isLife && <Row campo="placing_date" icon="🏁" label={lang==="es"?"Fecha de colocación (pagada)":"Placing Date"} value={policy.placingDate ? fmt(policy.placingDate) : null} color="var(--c-green,#00ff88)"/>}
        {isLife && <Row campo="contingent_beneficiary" icon="🎯" label={lang==="es"?"Beneficiario contingente":"Contingent Beneficiary"} value={policy.beneficiaryContingent}/>}
        {isLife && <Row campo="final_risk_class" icon="⚖️" label={lang==="es"?"Clase de riesgo final":"Final Risk Class"} value={policy.riskClass}/>}
        {isLife && <Row campo="conversion_age" icon="🔄" label={lang==="es"?"Edad de conversión":"Conversion Age"} value={policy.conversionAge}/>}
        {isLife && <Row campo="verification_form" icon="📄" label={lang==="es"?"Formulario de verificación":"Verification Form"} value={policy.verificationForm}/>}
        {isLife && <Row campo="owner_address" icon="🏠" label={lang==="es"?"Dirección del dueño":"Owner Address"} value={policy.ownerAddress} wide/>}
        {isLife && <Row campo="commission_paid" icon="💸" label={lang==="es"?"Comisión pagada":"Commission Paid"} value={policy.commissionPaid}/>}
        {isLife && <Row campo="split" icon="➗" label={lang==="es"?"Split (reparto)":"Split"} value={policy.commissionSplit}/>}
        {/* Medicare (Lissette 2026-08-09). Solo en MA / Suplemento / Parte D:
            un ACA o una póliza de auto no cambian en nada. Las pólizas de
            Medicare que ya existen muestran "--" hasta que se llenen, igual
            que hacen las filas de Vida — no se esconde la fila por estar
            vacía (regla de la casa) porque el hueco visible es justamente lo
            que le recuerda a la agente que falta ese dato. */}
        {/* SOA y HRA en la tarjeta (Lissette 2026-09-13): son tramites que se
            persiguen, y perseguirlos abriendo el formulario de cada poliza es
            justo lo que hace que se queden sin hacer. */}
        {/* Los cinco tramites de Medicare se pintan SIEMPRE, con su raya cuando
            estan vacios. El SOA y el HRA nacieron escondiendose (2026-09-13) y
            Lissette lo corrigio al dia siguiente: *"SI VISIBLES"*. Es lo
            correcto y es la regla 158 — a estos campos se les pregunta "¿a
            quien le falta?", y escondidos justo desaparece el que falta. */}
        {isMedicare && <Row campo="soa_status" icon="📝" label={lang==="es"?"Estatus del SOA":"SOA status"} value={policy.soaStatus}/>}
        {isMedicare && <Row campo="soa_signed" icon="✍️" label={lang==="es"?"SOA firmado":"SOA signed"} value={policy.soaSigned}/>}
        {isMedicare && <Row campo="hra_status" icon="🩺" label={lang==="es"?"Estatus del HRA":"HRA status"} value={policy.hraStatus}/>}
        {/* Estas TRES se pintan siempre, con su raya cuando estan vacias
            (*"que en las tarjetas del Medicare TODAS lleven"*, 2026-09-14).
            Escondidas no se distinguen de un dato que no existe, y la pregunta
            que se le hace a Medicaid es justo "¿a quien le falta preguntarle?".
            Es la regla 158, y es lo que ya hacen el MBI y las Partes A/B de
            aqui abajo. */}
        {isMedicare && <Row campo="medicaid_eligibility" icon="🏥" label={lang==="es"?"Elegibilidad de Medicaid":"Medicaid eligibility"} value={policy.medicaidEligibility}/>}
        {isMedicare && <Row campo="soa_signature_date" icon="🗓️" label={lang==="es"?"Fecha de firma del SOA":"SOA signature date"} value={policy.soaSignedDate ? fmt(policy.soaSignedDate) : null}/>}
        {isMedicare && <Row campo="lis_extra_help" icon="💊" label="LIS (Extra Help)" value={policy.lisLevel}/>}
        {isMedicare && <Row campo="medicare_number_mbi" icon="⭐" label={lang==="es"?"N° de Medicare (MBI)":"Medicare Number (MBI)"} value={policy.medicareNumber} mono/>}
        {isMedicare && <Row campo="part_a_date" icon="🅰️" label={lang==="es"?"Fecha Parte A":"Part A Date"} value={policy.partADate ? fmt(policy.partADate) : null}/>}
        {isMedicare && <Row campo="part_b_date" icon="🅱️" label={lang==="es"?"Fecha Parte B":"Part B Date"} value={policy.partBDate ? fmt(policy.partBDate) : null}/>}
        {isMedicare && <Row campo="company_id" icon="🏢" label={lang==="es"?"ID de la compañía":"Company ID"} value={policy.companyId} mono/>}
        {isMedicare && <Row campo="plan_year" icon="📆" label={lang==="es"?"Plan del año":"Plan Year"} value={policy.planYear}/>}
        {isMedicare && <Row campo="enrollment_code" icon="🔑" label={lang==="es"?"Código de inscripción":"Enrollment Code"} value={policy.enrollmentCode} mono/>}
        {isMedicare && <Row campo="initial_premium" icon="🥇" label={lang==="es"?"Prima inicial":"Initial Premium"} value={(policy.initialPremium != null && policy.initialPremium !== '') ? fmtMoneyZero(policy.initialPremium) : null}/>}
        {isMedicare && <Row campo="current_premium" icon="💵" label={lang==="es"?"Prima actual":"Current Premium"} value={(policy.currentPremium != null && policy.currentPremium !== '') ? fmtMoneyZero(policy.currentPremium) : null} color="var(--c-green,#00ff88)"/>}
        <Row campo="member_id" icon="🪪" label="Member ID" value={policy.memberId} mono/>
        {/* Los member id de los demas asegurados (Lissette 2026-08-28). Solo
            los que TIENEN uno propio: una fila por persona sin numero seria
            ruido. El del titular ya sale arriba, asi que se excluye. */}
        {Object.entries(componerMemberIds(policy))
          .filter(([k,v])=>k!=="primary"&&v)
          .map(([k,v])=>(
            <Row campo={"memberId:"+k} key={k} icon="🪪" label={`Member ID · ${nombreDeAsegurado(k)}`} value={v} mono/>
          ))}
        {esSalud && <Row campo="application" icon="📝" label={lang==="es"?"# de Aplicación":"Application #"} value={policy.applicationNumber} mono/>}
        {/* Período de inscripción — subido junto al # de aplicación
            (Lissette 2026-08-10). Los demás campos de la sección Marketplace se
            retiraron a petición suya; este se queda porque además alimenta el
            reporte "pólizas por período OE/SEP" (PR #1562). */}
        {isAca && <Row campo="enrollment_period" icon="🗓️" label={lang==="es"?"Período de inscripción":"Enrollment Period"} value={policy.enrollmentPeriod}/>}
        {isAca && <Row campo="marketplace" icon="🏛️" label={lang==="es"?"Marketplace":"Marketplace"} value={policy.marketplaceName}/>}
        {!isPet && <Row campo="family_size" icon="👨‍👩‍👧" label={lang==="es"?"Personas en Familia":"Family Size"} value={`${householdSize} ${lang==="es"?"personas":"members"}`}/>}
        {!isPet && <Row campo="insured_on_policy" icon="✅" label={lang==="es"?"Asegurados en esta póliza":"Insured on policy"} value={`${insuredCount} ${lang==="es"?"de":"of"} ${householdSize}`}/>}
        <Row campo="monthly_premium" icon="💰" label={lang==="es"?"Prima Mensual":"Monthly Premium"} value={(policy.monthlyPremium != null && policy.monthlyPremium !== '') ? fmtMoneyZero(policy.monthlyPremium) : null} color="var(--c-green,#00ff88)"/>
        {esSalud && <Row campo="subsidy" icon="🏷️" label={lang==="es"?"Subsidio":"Subsidy"} value={(policy.subsidy != null && policy.subsidy !== '') ? fmtMoneyZero(policy.subsidy) : null} color="var(--c-cyan,#00d4ff)"/>}
        {esSalud && <Row campo="plan_total_cost" icon="🧾" label={lang==="es"?"Costo Total del Plan":"Plan Total Cost"} value={(policy.planTotalCost != null && policy.planTotalCost !== '') ? fmtMoneyZero(policy.planTotalCost) : null}/>}
        {/* Costos del plan (Lissette 2026-10-08). Siempre visibles, con su raya
            si están vacíos: el hueco es lo que recuerda que falta (regla 158). */}
        {esSalud && <Row campo="deductible" icon="🛡️" label={lang==="es"?"Deducible":"Deductible"} value={policy.deductible ? montoLegible(policy.deductible) : null}/>}
        {esSalud && <Row campo="out_of_pocket_max" icon="🧮" label={lang==="es"?"Máximo de bolsillo":"Out-of-pocket Max"} value={policy.outOfPocketMax ? montoLegible(policy.outOfPocketMax) : null}/>}
        {esSalud && <Row campo="pcp_copay" icon="👩‍⚕️" label={lang==="es"?"Doctor primario (PCP)":"Primary Care (PCP)"} value={policy.pcpCopay}/>}
        {esSalud && <Row campo="er_copay" icon="🚑" label={lang==="es"?"Sala de emergencia":"Emergency Room"} value={policy.erCopay}/>}
        {esSalud && <Row campo="specialist_copay" icon="🩻" label={lang==="es"?"Especialista":"Specialist"} value={policy.specialistCopay}/>}
        {esSalud && <Row campo="generic_drug_copay" icon="💊" label={lang==="es"?"Medicina genérica":"Generic Drugs"} value={policy.genericDrugCopay}/>}
        <Row campo="effective_date" icon="📅" label={lang==="es"?"Fecha de Inicio":"Effective Date"} value={policy.effectiveDate ? fmt(policy.effectiveDate) : null}/>
        <Row campo="renewal_date" icon="🔁" label={lang==="es"?"Fecha Renovación":"Renewal Date"} value={policy.renewalDate ? fmt(policy.renewalDate) : null} color={days!==null&&days<30?"var(--c-amber,#ffb700)":undefined}/>
        {/* Only meaningful once the policy is actually cancelled. */}
        {policy.cancellationDate && <Row campo="cancellation_date" icon="🛑" label={lang==="es"?"Fecha de cancelación":"Cancellation Date"} value={fmt(policy.cancellationDate)} color="var(--c-red,#ff3d6b)"/>}
        <Row campo="carrier" icon="🏢" label={lang==="es"?"Compañía":"Carrier"} value={policy.carrier}/>
        <Row campo="policy" icon="📄" label={lang==="es"?"# Póliza":"Policy #"} value={policy.policyNumber} mono/>
        <Row campo="agents_on_policy" icon="👤" label={lang==="es"?"Agentes en póliza":"Agents on policy"} value={policy.agentLabel || policy.agentName}/>
        <Row campo="producer_code" icon="🔢" label={lang==="es"?"Cód. Productor":"Producer Code"} value={policy.producerCode} mono/>
      </CamposOrdenables>}
      {/* P&C (Lissette 2026-08-09). Bloque propio y NO dentro del de salud:
          allí `isHealth` es `!isPC`, así que estas filas no se verían nunca.
          Aplica a todos los ramos de P&C (hogar, auto, inquilinos, comercial,
          paraguas), por eso no va colgado del bloque de propiedad ni del de
          auto. La prima mensual de cada bloque se queda donde estaba; estas
          se añaden al lado. */}
      {/* Dental / Visión / Mascotas / Gastos Finales (Lissette 2026-08-09).
          Bloque propio: dental, visión, mascotas y gastos finales SÍ caen dentro
          de `isHealth` (`!isPC`), pero mezclar sus términos de plan con las
          filas comunes de salud dejaría la tarjeta ilegible. El deducible y la
          clase de riesgo no se repiten aquí — ya salen en sus filas de siempre. */}
      {isSupplCard && policy.supplementType && <CamposOrdenables tipo={policy.type} seccion="suplemento" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem"}}>
        <SectionHead icon="➕" label={lang==="es"?"Tipo de suplemento":"Supplement type"}/>
        <Row campo="supplement" icon="➕" label={lang==="es"?"Suplemento":"Supplement"} value={policy.supplementType}/>
      </CamposOrdenables>}
      {(isDental||isVision||isPet||isFinal)&&<CamposOrdenables tipo={policy.type} seccion="terminos" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem"}}>
        <SectionHead icon={isDental?"🦷":isVision?"👁️":isPet?"🐶":"🌹"} label={lang==="es"?"Términos del Plan":"Plan Terms"}/>
        {isDental && <Row campo="annual_maximum" icon="🏦" label={lang==="es"?"Máximo anual":"Annual Maximum"} value={policy.annualMaximum}/>}
        {isDental && <Row campo="network_type" icon="🌐" label={lang==="es"?"Tipo de red":"Network Type"} value={policy.networkType}/>}
        {isDental && <Row campo="deductible" icon="🛡️" label={lang==="es"?"Deducible":"Deductible"} value={policy.deductible ? `$${Number(String(policy.deductible).replace(/[^\d.]/g,'')).toLocaleString("en-US")}` : null}/>}
        {isDental && <Row campo="waiting_periods" icon="⏳" label={lang==="es"?"Períodos de espera":"Waiting Periods"} value={policy.waitingPeriod}/>}
        {isDental && <Row campo="coinsurance" icon="📊" label={lang==="es"?"Coaseguro (prev./básico/mayor)":"Coinsurance"} value={policy.coinsurance}/>}
        {isDental && <Row campo="missing_tooth_clause" icon="🦷" label={lang==="es"?"Cláusula de diente ausente":"Missing Tooth Clause"} value={policy.missingToothClause} wide/>}
        {isVision && <Row campo="exam_frequency_copay" icon="👁️" label={lang==="es"?"Examen: frecuencia y copago":"Exam Frequency & Copay"} value={policy.examCopay}/>}
        {isVision && <Row campo="frame_allowance" icon="🕶️" label={lang==="es"?"Asignación armazón":"Frame Allowance"} value={policy.frameAllowance}/>}
        {isVision && <Row campo="contacts_allowance" icon="🔵" label={lang==="es"?"Asignación lentes de contacto":"Contacts Allowance"} value={policy.contactsAllowance}/>}
        {isVision && <Row campo="network_out_of_network" icon="🌐" label={lang==="es"?"Tipo de red / fuera de red":"Network / Out-of-network"} value={policy.networkType}/>}
        {isPet && <Row campo="pet" icon="🐶" label={lang==="es"?"Mascota":"Pet"} value={policy.petName}/>}
        {isPet && <Row campo="species" icon="🐾" label={lang==="es"?"Especie":"Species"} value={policy.petSpecies}/>}
        {isPet && <Row campo="breed" icon="🧬" label={lang==="es"?"Raza":"Breed"} value={policy.petBreed}/>}
        {isPet && <Row campo="age" icon="🎂" label={lang==="es"?"Edad":"Age"} value={policy.petAge}/>}
        {isPet && <Row campo="microchip_number" icon="📟" label={lang==="es"?"N° de microchip":"Microchip Number"} value={policy.microchipNumber} mono/>}
        {isPet && <Row campo="annual_limit" icon="🏦" label={lang==="es"?"Límite anual":"Annual Limit"} value={policy.annualMaximum}/>}
        {isPet && <Row campo="reimbursement" icon="↩️" label={lang==="es"?"Reembolso (%)":"Reimbursement (%)"} value={policy.reimbursementPercent}/>}
        {isPet && <Row campo="deductible" icon="🛡️" label={lang==="es"?"Deducible":"Deductible"} value={policy.deductible ? `$${Number(String(policy.deductible).replace(/[^\d.]/g,'')).toLocaleString("en-US")}` : null}/>}
        {isPet && <Row campo="waiting_periods" icon="⏳" label={lang==="es"?"Períodos de espera":"Waiting Periods"} value={policy.waitingPeriod}/>}
        {/* Short Term. Guardar estos datos sin enseñarlos seria lo mismo que no
            tenerlos: son los que el agente consulta cuando el cliente llama
            preguntando que le cubre. */}
        {isShortTerm && <Row campo="term_duration" icon="⏳" label={lang==="es"?"Duración del término":"Term Duration"} value={policy.termDuration}/>}
        {isShortTerm && <Row campo="deductible" icon="🛡️" label={lang==="es"?"Deducible":"Deductible"} value={policy.deductible ? `$${policy.deductible}` : null}/>}
        {isShortTerm && <Row campo="coinsurance" icon="📊" label={lang==="es"?"Coaseguro":"Coinsurance"} value={policy.coinsurance}/>}
        {isShortTerm && <Row campo="out_of_pocket_max" icon="💸" label={lang==="es"?"Máximo de bolsillo":"Out-of-pocket Max"} value={policy.outOfPocketMax ? `$${policy.outOfPocketMax}` : null}/>}
        {isShortTerm && <Row campo="max_benefit_per_term" icon="🏦" label={lang==="es"?"Beneficio máximo del término":"Max Benefit per Term"} value={policy.benefitMax ? `$${policy.benefitMax}` : null}/>}
        {isShortTerm && <Row campo="renewable" icon="🔁" label={lang==="es"?"¿Renovable?":"Renewable?"} value={policy.renewable ? (policy.renewable==="yes" ? (lang==="es"?"Sí":"Yes") : (lang==="es"?"No":"No")) : null}/>}
        {isShortTerm && <Row campo="pre_existing_excluded" icon="⚠️" label={lang==="es"?"¿Excluye preexistentes?":"Pre-existing excluded?"} value={policy.preExistingExcluded ? (policy.preExistingExcluded==="yes" ? (lang==="es"?"Sí":"Yes") : (lang==="es"?"No":"No")) : null}/>}
        {isFinal && <Row campo="graded_benefit_wait" icon="⏳" label={lang==="es"?"Espera de beneficio graduado":"Graded Benefit Wait"} value={policy.waitingPeriod}/>}
        {isFinal && <Row campo="funeral_home_assignment" icon="🏛️" label={lang==="es"?"Asignación a funeraria":"Funeral Home Assignment"} value={policy.funeralHomeAssignment}/>}
      </CamposOrdenables>}
      {/* Semestral y anual en los ramos que no son P&C (P&C las enseña en su
          sección). Lissette 2026-10-22: mensual, semestral y anual en todos
          menos Medicare, Salud y Vida. */}
      {!isPC&&llevaTresPrimas(policy.type)&&(policy.premiumSemiAnnual||policy.premiumAnnual)&&<CamposOrdenables tipo={policy.type} seccion="primas" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem"}}>
        <SectionHead icon="💰" label={lang==="es"?"Primas":"Premiums"}/>
        <Row campo="semi_annual_premium" icon="🗓️" label={lang==="es"?"Prima semestral":"Semi-annual Premium"} value={(policy.premiumSemiAnnual != null && policy.premiumSemiAnnual !== '') ? fmtMoneyZero(policy.premiumSemiAnnual) : null}/>
        <Row campo="annual_premium" icon="📅" label={lang==="es"?"Prima anual":"Annual Premium"} value={(policy.premiumAnnual != null && policy.premiumAnnual !== '') ? fmtMoneyZero(policy.premiumAnnual) : null}/>
      </CamposOrdenables>}
      {isPC&&<CamposOrdenables tipo={policy.type} seccion="pc" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem"}}>
        <SectionHead icon="🏘️" label={lang==="es"?"Prima P&C y agente general":"P&C Premium & General Agent"}/>
        <Row campo="annual_premium" icon="📅" label={lang==="es"?"Prima anual":"Annual Premium"} value={(policy.premiumAnnual != null && policy.premiumAnnual !== '') ? fmtMoneyZero(policy.premiumAnnual) : null}/>
        <Row campo="semi_annual_premium" icon="🗓️" label={lang==="es"?"Prima semestral":"Semi-annual Premium"} value={(policy.premiumSemiAnnual != null && policy.premiumSemiAnnual !== '') ? fmtMoneyZero(policy.premiumSemiAnnual) : null}/>
        <Row campo="general_agent_mga" icon="🤝" label={lang==="es"?"Agente general (MGA)":"General Agent (MGA)"} value={policy.generalAgent} wide/>
      </CamposOrdenables>}
      {isHome&&<CamposOrdenables tipo={policy.type} seccion="casa" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem"}}>
        <SectionHead icon="🏠" label={lang==="es"?"Datos de la Propiedad":"Property Details"}/>
        <Row campo="property_address" icon="📍" label={lang==="es"?"Dirección del Inmueble":"Property Address"} value={policy.propertyAddress} wide/>
        <Row campo="property_type" icon="🏘️" label={lang==="es"?"Tipo de Propiedad":"Property Type"} value={policy.propertyType ? {single_family:lang==="es"?"Casa Unifamiliar":"Single Family",condo:"Condo",townhouse:"Townhouse",rental:"Rental/Renter",multi_family:lang==="es"?"Multi-familiar":"Multi-Family"}[policy.propertyType]||policy.propertyType : null}/>
        <Row campo="year_built" icon="🔨" label={lang==="es"?"Año de Construcción":"Year Built"} value={policy.yearBuilt}/>
        <Row campo="square_footage" icon="📐" label={lang==="es"?"Pies Cuadrados":"Square Footage"} value={policy.squareFootage ? `${policy.squareFootage} sqft` : null}/>
        <Row campo="insured_value_dwelling" icon="🏦" label={lang==="es"?"Valor Asegurado / Dwelling":"Insured Value / Dwelling"} value={policy.insuredValue ? `$${Number(String(policy.insuredValue).replace(/[^\d.]/g,'')).toLocaleString("en-US")}` : null} color="var(--c-cyan,#00d4ff)"/>
        <Row campo="personal_property" icon="📦" label={lang==="es"?"Contenido Personal":"Personal Property"} value={policy.dwellingCoverage ? `$${Number(String(policy.dwellingCoverage).replace(/[^\d.]/g,'')).toLocaleString("en-US")}` : null}/>
        <Row campo="deductible" icon="🛡️" label={lang==="es"?"Deducible":"Deductible"} value={policy.deductible ? `$${Number(String(policy.deductible).replace(/[^\d.]/g,'')).toLocaleString("en-US")}` : null}/>
        <Row campo="liability_coverage" icon="⚖️" label={lang==="es"?"Cobertura Responsabilidad":"Liability Coverage"} value={policy.liabilityCoverage ? `$${Number(String(policy.liabilityCoverage).replace(/[^\d.]/g,'')).toLocaleString("en-US")}` : null}/>
        <Row campo="monthly_premium" icon="💰" label={lang==="es"?"Prima Mensual":"Monthly Premium"} value={(policy.monthlyPremium != null && policy.monthlyPremium !== '') ? fmtMoneyZero(policy.monthlyPremium) : null} color="var(--c-green,#00ff88)"/>
        <Row campo="coverage_type" icon="📋" label={lang==="es"?"Tipo de Cobertura":"Coverage Type"} value={policy.coverageType}/>
        <Row campo="effective_date" icon="📅" label={lang==="es"?"Fecha de Inicio":"Effective Date"} value={policy.effectiveDate ? fmt(policy.effectiveDate) : null}/>
        <Row campo="renewal_date" icon="🔁" label={lang==="es"?"Fecha Renovación":"Renewal Date"} value={policy.renewalDate ? fmt(policy.renewalDate) : null} color={days!==null&&days<30?"var(--c-amber,#ffb700)":undefined}/>
        {/* Only meaningful once the policy is actually cancelled. */}
        {policy.cancellationDate && <Row campo="cancellation_date" icon="🛑" label={lang==="es"?"Fecha de cancelación":"Cancellation Date"} value={fmt(policy.cancellationDate)} color="var(--c-red,#ff3d6b)"/>}
        <Row campo="agents_on_policy" icon="👤" label={lang==="es"?"Agentes en póliza":"Agents on policy"} value={policy.agentLabel || policy.agentName}/>
        <Row campo="producer_code" icon="🔢" label={lang==="es"?"Cód. Productor":"Producer Code"} value={policy.producerCode} mono/>
      </CamposOrdenables>}
      {isAuto&&<CamposOrdenables tipo={policy.type} seccion="auto" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem"}}>
        <SectionHead icon="🚗" label={lang==="es"?"Datos del Vehículo":"Vehicle Details"}/>
        <Row campo="year" icon="📅" label={lang==="es"?"Año":"Year"} value={policy.vehicleYear}/>
        <Row campo="make" icon="🚘" label={lang==="es"?"Marca":"Make"} value={policy.vehicleMake}/>
        <Row campo="model" icon="🔖" label={lang==="es"?"Modelo":"Model"} value={policy.vehicleModel}/>
        <Row campo="vin" icon="🔑" label="VIN" value={policy.vin} mono wide/>
        <Row campo="license_plate" icon="🪪" label={lang==="es"?"Placa":"License Plate"} value={policy.licensePlate} mono/>
        <Row campo="drivers_on_policy" icon="👥" label={lang==="es"?"Conductores en la Póliza":"Drivers on Policy"} value={policy.driversOnPolicy} wide/>
        <Row campo="deductible" icon="🛡️" label={lang==="es"?"Deducible":"Deductible"} value={policy.deductible ? `$${policy.deductible}` : null}/>
        <Row campo="coverage_type" icon="📋" label={lang==="es"?"Tipo de Cobertura":"Coverage Type"} value={policy.coverageType}/>
        <Row campo="liability_coverage" icon="⚖️" label={lang==="es"?"Cobertura Responsabilidad":"Liability Coverage"} value={policy.liabilityCoverage ? `$${policy.liabilityCoverage}` : null}/>
        <Row campo="monthly_premium" icon="💰" label={lang==="es"?"Prima Mensual":"Monthly Premium"} value={(policy.monthlyPremium != null && policy.monthlyPremium !== '') ? fmtMoneyZero(policy.monthlyPremium) : null} color="var(--c-green,#00ff88)"/>
        <Row campo="effective_date" icon="📅" label={lang==="es"?"Fecha de Inicio":"Effective Date"} value={policy.effectiveDate ? fmt(policy.effectiveDate) : null}/>
        <Row campo="renewal_date" icon="🔁" label={lang==="es"?"Fecha Renovación":"Renewal Date"} value={policy.renewalDate ? fmt(policy.renewalDate) : null} color={days!==null&&days<30?"var(--c-amber,#ffb700)":undefined}/>
        {/* Only meaningful once the policy is actually cancelled. */}
        {policy.cancellationDate && <Row campo="cancellation_date" icon="🛑" label={lang==="es"?"Fecha de cancelación":"Cancellation Date"} value={fmt(policy.cancellationDate)} color="var(--c-red,#ff3d6b)"/>}
        <Row campo="agents_on_policy" icon="👤" label={lang==="es"?"Agentes en póliza":"Agents on policy"} value={policy.agentLabel || policy.agentName}/>
        <Row campo="producer_code" icon="🔢" label={lang==="es"?"Cód. Productor":"Producer Code"} value={policy.producerCode} mono/>
      </CamposOrdenables>}
      {/* Los demás carros de la póliza (el primero ya sale arriba, en los
          campos de siempre). Lissette 2026-10-12. */}
      {isAuto&&(()=>{
        const otros = vehiculosDe(policy).slice(1).filter(tieneDatos);
        if (!otros.length) return null;
        return (
          <div style={{marginTop:".6rem",display:"flex",flexDirection:"column",gap:".45rem"}}>
            <div style={{fontSize:".7rem",fontWeight:800,color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".05em"}}>
              🚗 {lang==="es"?`Otros carros en la póliza (${otros.length})`:`Other vehicles on the policy (${otros.length})`}
            </div>
            {otros.map((a,i)=>(
              <div key={i} style={{border:"1px solid var(--c-border,#1a3550)",borderRadius:9,padding:".5rem .65rem",display:"grid",gridTemplateColumns:"1fr 1fr",gap:".35rem .8rem",fontSize:".78rem"}}>
                <div style={{gridColumn:"1 / -1",fontWeight:800,color:"var(--c-white,#eef6ff)"}}>{lang==="es"?`Carro ${i+2}`:`Vehicle ${i+2}`}{nombreDeVehiculo(a)?` · ${nombreDeVehiculo(a)}`:""}</div>
                {a.vin && <div style={{gridColumn:"1 / -1"}}><span style={{color:"var(--c-text3,#45688a)"}}>VIN </span><span style={{fontFamily:"var(--font-mono)",color:"var(--c-text,#c8dff0)"}}>{a.vin}</span></div>}
                {a.plate && <div><span style={{color:"var(--c-text3,#45688a)"}}>{lang==="es"?"Placa ":"Plate "}</span><span style={{fontFamily:"var(--font-mono)",color:"var(--c-text,#c8dff0)"}}>{a.plate}</span></div>}
              </div>
            ))}
          </div>
        );
      })()}
      {isComm&&<CamposOrdenables tipo={policy.type} seccion="negocio" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem"}}>
        <SectionHead icon="🏢" label={lang==="es"?"Datos del Negocio":"Business Details"}/>
        <Row campo="business_name" icon="🏷️" label={lang==="es"?"Nombre del Negocio":"Business Name"} value={policy.businessName} color="var(--c-cyan,#00d4ff)" wide/>
        <Row campo="business_type_industry" icon="🏭" label={lang==="es"?"Tipo / Industria":"Business Type / Industry"} value={policy.businessType ? {retail:lang==="es"?"Retail / Tienda":"Retail / Store",restaurant:lang==="es"?"Restaurante":"Restaurant",contractor:"Contractor",professional:lang==="es"?"Servicios Profesionales":"Professional Services",trucking:"Trucking",other:lang==="es"?"Otro":"Other"}[policy.businessType]||policy.businessType : null}/>
        <Row campo="business_address" icon="📍" label={lang==="es"?"Dirección del Negocio":"Business Address"} value={policy.businessAddress} wide/>
        <Row campo="coverage_type_bop_gl_wc" icon="📋" label={lang==="es"?"Tipo de Cobertura":"Coverage Type (BOP/GL/WC)"} value={policy.coverageType}/>
        <Row campo="number_of_employees" icon="👥" label={lang==="es"?"Número de Empleados":"Number of Employees"} value={policy.numEmployees}/>
        <Row campo="annual_revenue" icon="💵" label={lang==="es"?"Ingresos Anuales":"Annual Revenue"} value={policy.annualRevenue ? `$${policy.annualRevenue}` : null} color="var(--c-cyan,#00d4ff)"/>
        <Row campo="deductible" icon="🛡️" label={lang==="es"?"Deducible":"Deductible"} value={policy.deductible ? `$${policy.deductible}` : null}/>
        <Row campo="liability_coverage" icon="⚖️" label={lang==="es"?"Cobertura Responsabilidad":"Liability Coverage"} value={policy.liabilityCoverage ? `$${policy.liabilityCoverage}` : null}/>
        <Row campo="monthly_premium" icon="💰" label={lang==="es"?"Prima Mensual":"Monthly Premium"} value={(policy.monthlyPremium != null && policy.monthlyPremium !== '') ? fmtMoneyZero(policy.monthlyPremium) : null} color="var(--c-green,#00ff88)"/>
        <Row campo="effective_date" icon="📅" label={lang==="es"?"Fecha de Inicio":"Effective Date"} value={policy.effectiveDate ? fmt(policy.effectiveDate) : null}/>
        <Row campo="renewal_date" icon="🔁" label={lang==="es"?"Fecha Renovación":"Renewal Date"} value={policy.renewalDate ? fmt(policy.renewalDate) : null} color={days!==null&&days<30?"var(--c-amber,#ffb700)":undefined}/>
        {/* Only meaningful once the policy is actually cancelled. */}
        {policy.cancellationDate && <Row campo="cancellation_date" icon="🛑" label={lang==="es"?"Fecha de cancelación":"Cancellation Date"} value={fmt(policy.cancellationDate)} color="var(--c-red,#ff3d6b)"/>}
        <Row campo="agents_on_policy" icon="👤" label={lang==="es"?"Agentes en póliza":"Agents on policy"} value={policy.agentLabel || policy.agentName}/>
        <Row campo="producer_code" icon="🔢" label={lang==="es"?"Cód. Productor":"Producer Code"} value={policy.producerCode} mono/>
      </CamposOrdenables>}
      {/* Shared across all policy types: lead source + who processed it. */}
      <CamposOrdenables tipo={policy.type} seccion="origen" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem",marginTop:".5rem"}}>
        <SectionHead icon="🎯" label={lang==="es"?"Origen y Proceso":"Source & Processing"}/>
        {/* En Medicare Advantage la fecha de venta sale arriba como "Fecha de cierre". */}
        {!isMA && <Row campo="sale_date" icon="🗓️" label={lang==="es"?"Fecha de Venta":"Sale Date"} value={policy.saleDate ? fmt(policy.saleDate) : null} color="var(--c-cyan,#00d4ff)"/>}
        <Row campo="lead_source" icon="🎯" label={lang==="es"?"Origen del Lead":"Lead Source"} value={leadTypeLabel(policy.leadType, lang)} color="var(--c-cyan,#00d4ff)"/>
        <Row campo="processed_by" icon="🧾" label={lang==="es"?"Procesado por":"Processed by"} value={policy.processedBy}/>
        <Row campo="processing_date" icon="📅" label={lang==="es"?"Fecha de procesamiento":"Processing date"} value={policy.processingDate ? fmt(policy.processingDate) : null}/>
        <Row campo="analyst" icon="🔎" label={lang==="es"?"Analista":"Analyst"} value={policy.analystName}/>
        {/* % Comisión / Comisión movidas a Cotizaciones → Comisiones (Lissette 2026-06-17). */}
      </CamposOrdenables>
      {/* Agent-defined custom fields — only ones with a value, so the shared
          field NAMES (which appear empty on every policy of the type) don't
          clutter the card until they're actually filled in for this client. */}
      {Array.isArray(policy.customFields) && policy.customFields.filter(f=>f && f.label && String(f.value||"").trim()).length>0 && (
        <CamposOrdenables tipo={policy.type} seccion="personalizados" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".5rem",marginTop:".5rem"}}>
          <SectionHead icon="🧩" label={lang==="es"?"Campos personalizados":"Custom Fields"}/>
          {policy.customFields.filter(f=>f && f.label && String(f.value||"").trim()).map((f,i)=>(
            <Row campo={"custom:"+f.label} key={i} icon="🔖" label={f.label} value={f.value}/>
          ))}
        </CamposOrdenables>
      )}
      {policy.paymentType && <PaymentInfoBlock policy={policy} lang={lang}/>}
      </OrdenDeFichaContext.Provider>
    </div>}
  </div>;
}

// ── Policy importer (Excel/CSV → match to existing clients) ───────────────────
// Imports a standalone policies sheet (e.g. a monday.com "HEALTH DEPARTMENT"
// export) and links each row to an existing client by name (+ email/phone/DOB
// fallback). Creates policies via the existing createPolicy endpoint. The
// monday banner rows are skipped; columns are auto-mapped and adjustable.
const POLICY_IMPORT_FIELDS = [
  { key: '',             es: '— Ignorar —',                  en: '— Ignore —' },
  { key: 'clientName',   es: '👤 Nombre del cliente (match)', en: '👤 Client name (match)' },
  // Separate name parts (Lissette 2026-07-10) — for sheets with distinct
  // First / Middle / Last columns instead of one full-name column. If the
  // full-name column isn't mapped, these are joined into the client name.
  { key: 'clientFirstName',  es: '🧑 Primer nombre',   en: '🧑 First name' },
  { key: 'clientMiddleName', es: '🧑 Segundo nombre',  en: '🧑 Middle name' },
  { key: 'clientLastName',   es: '🧑 Apellido',        en: '🧑 Last name' },
  { key: 'clientEmail',  es: '📧 Email del cliente (match)',  en: '📧 Client email (match)' },
  { key: 'clientPhone',  es: '📞 Teléfono del cliente (match)', en: '📞 Client phone (match)' },
  { key: 'clientDob',    es: '🎂 Fecha nac. cliente (match)', en: '🎂 Client DOB (match)' },
  // Client address (Lissette 2026-07-07) — saved onto the client: set on
  // newly-created clients, and fill-if-empty on matched existing ones.
  { key: 'addressStreet', es: '🏠 Dirección del cliente',    en: '🏠 Client address' },
  { key: 'addressCity',   es: '🏙️ Ciudad del cliente',       en: '🏙️ Client city' },
  { key: 'addressState',  es: '🗺️ Estado del cliente',       en: '🗺️ Client state' },
  { key: 'addressZip',    es: '📮 Zip del cliente',           en: '📮 Client ZIP' },
  { key: 'carrier',      es: '🏢 Aseguradora',               en: '🏢 Carrier' },
  { key: 'policyType',   es: '🏷️ Tipo de póliza',            en: '🏷️ Policy type' },
  { key: 'plan',         es: '📋 Plan',                      en: '📋 Plan' },
  { key: 'memberId',     es: '🪪 Member ID',                  en: '🪪 Member ID' },
  { key: 'faceAmount',   es: '💵 Face Amount (vida)',         en: '💵 Face Amount (life)' },
  { key: 'premium',      es: '💰 Prima mensual',             en: '💰 Monthly premium' },
  { key: 'effectiveDate',es: '📅 Fecha inicio',             en: '📅 Effective date' },
  { key: 'renewalDate',  es: '🔁 Fecha renovación',          en: '🔁 Renewal date' },
  // Life PolicySummary columns (Lissette 2026-07-02) — land in type_details.
  { key: 'policyOwnerName', es: '👑 Dueño de la póliza (vida)', en: '👑 Owner name (life)' },
  { key: 'termDuration',    es: '⏳ Duración del término (vida)', en: '⏳ Term duration (life)' },
  { key: 'issueDate',       es: '🗓️ Fecha de emisión (vida)',   en: '🗓️ Date of issue (life)' },
  { key: 'premiumDueDate',  es: '💳 Fecha de pago de prima (vida)', en: '💳 Premium due date (life)' },
  { key: 'billablePremium', es: '🧾 Prima facturable (vida)',   en: '🧾 Billable premium (life)' },
  { key: 'billingFrequency',es: '🔂 Frecuencia de pago (vida)', en: '🔂 Billing frequency (life)' },
  { key: 'billingMethod',   es: '🏦 Método de facturación (vida)', en: '🏦 Billing method (life)' },
  { key: 'agentNumber',     es: '🧑‍💼 Número de agente (vida)',  en: '🧑‍💼 Agent number (life)' },
  { key: 'agencyNumber',    es: '🏢 Número de agencia (vida)',  en: '🏢 Agency number (life)' },
  // Medicare (Lissette 2026-08-09) — también aterrizan en type_details.
  { key: 'medicareNumber',  es: '⭐ N° de Medicare / MBI',      en: '⭐ Medicare number / MBI' },
  { key: 'partADate',       es: '🅰️ Fecha Parte A',             en: '🅰️ Part A date' },
  { key: 'partBDate',       es: '🅱️ Fecha Parte B',             en: '🅱️ Part B date' },
  { key: 'companyId',       es: '🏢 ID de la compañía',         en: '🏢 Company ID' },
  { key: 'planYear',        es: '📆 Plan del año',              en: '📆 Plan year' },
  { key: 'enrollmentCode',  es: '🔑 Código de inscripción',     en: '🔑 Enrollment code' },
  { key: 'initialPremium',  es: '🥇 Prima inicial',             en: '🥇 Initial premium' },
  { key: 'currentPremium',  es: '💵 Prima actual',              en: '💵 Current premium' },
  // Vida, segunda tanda (Lissette 2026-08-09) — también en type_details.
  { key: 'approvedDate',       es: '✅ Fecha de aprobación (vida)',   en: '✅ Approved date (life)' },
  { key: 'deliveryPapersDate', es: '📤 Papeles de entrega (vida)',    en: '📤 Delivery papers (life)' },
  { key: 'deliveryDate',       es: '📬 Fecha de entrega (vida)',      en: '📬 Delivery date (life)' },
  { key: 'placingDate',        es: '🏁 Fecha de colocación (vida)',   en: '🏁 Placing date (life)' },
  { key: 'beneficiaryContingent', es: '🎯 Beneficiario contingente',  en: '🎯 Contingent beneficiary' },
  { key: 'riskClass',          es: '⚖️ Clase de riesgo',              en: '⚖️ Risk class' },
  { key: 'conversionAge',      es: '🔄 Edad de conversión',           en: '🔄 Conversion age' },
  { key: 'verificationForm',   es: '📄 Formulario de verificación',   en: '📄 Verification form' },
  { key: 'ownerAddress',       es: '🏠 Dirección del dueño',          en: '🏠 Owner address' },
  { key: 'commissionPaid',     es: '💸 Comisión pagada',              en: '💸 Commission paid' },
  { key: 'commissionSplit',    es: '➗ Split (reparto)',              en: '➗ Split' },
  // ACA y P&C (Lissette 2026-08-09) — también en type_details.
  { key: 'enrollmentPeriod',  es: '🗓️ Período de inscripción (OE/SEP)', en: '🗓️ Enrollment period (OE/SEP)' },
  { key: 'marketplaceName',   es: '🏛️ Nombre del Marketplace',        en: '🏛️ Marketplace name' },
  { key: 'premiumAnnual',     es: '📅 Prima anual (P&C)',           en: '📅 Annual premium (P&C)' },
  { key: 'premiumSemiAnnual', es: '🗓️ Prima semestral (P&C)',       en: '🗓️ Semi-annual premium (P&C)' },
  { key: 'generalAgent',      es: '🤝 Agente general (MGA)',        en: '🤝 General agent (MGA)' },
  { key: 'leadType',     es: '🎯 Lead Type',                 en: '🎯 Lead Type' },
  // Se llamaba solo "Agente" y no se encontraba: en la tarjeta el campo se
  // llama "Agente en póliza" (Lissette 2026-08-13). Mismo nombre en los dos
  // sitios para no tener que adivinar cuál es cuál.
  { key: 'agentLabel',   es: '🧑‍💼 Agente en póliza',         en: '🧑‍💼 Agent on policy' },
  // Mismo nombre que en la tarjeta. "Estado" a secas se confundía con el estado
  // del CLIENTE (activo/prospecto), que es otra cosa (Lissette 2026-08-13).
  { key: 'status',       es: '🚦 Policy Status',             en: '🚦 Policy Status' },
  { key: 'members',      es: '👥 Miembros/asegurados',        en: '👥 Members/insured' },
  // "Notas" a secas no dice de QUIEN son, y este importador tambien mapea
  // campos del CLIENTE (direccion, ciudad...), asi que se leia como las notas
  // del cliente (Lissette 2026-08-16: "dice notes, no nota de polizas"). Mismo
  // criterio que ya se aplico a "Status" -> "Policy Status" y a "Agente en
  // poliza": el mapeo se llama igual que el campo en la ficha.
  { key: 'notes',        es: '📝 Notas de la póliza (dentro del formulario)', en: '📝 Policy notes (inside the form)' },
  // El OTRO sitio de notas: el hilo que abre el boton "Notas" en la tarjeta.
  // Son dos cosas distintas y hay que poder elegir a cual va la columna
  // (Lissette 2026-08-16: "es muy diferente a policy notes"). Ambas admiten
  // varias columnas a la vez.
  { key: 'policyNote',   es: '📄 Notas de la tarjeta (botón Notas)', en: '📄 Card notes (Notes button)' },
  // Paridad con la tarjeta de póliza (Lissette 2026-08-13: "no veo fecha de
  // venta de la póliza, fecha de procesamiento"). Este importador ofrecía 40 de
  // los 101 campos que muestra la ficha; el resto se completaba a mano póliza
  // por póliza. Las claves coinciden con las de la tarjeta y con las del mapeo
  // Cliente + Póliza, así que un mismo Excel se comporta igual en los dos.
  { key: 'policyNumber', es: '🔢 N° de póliza (aparte del Member ID)', en: '🔢 Policy number (separate from Member ID)' },
  { key: 'saleDate',     es: '🗓️ Fecha de venta',            en: '🗓️ Sale date' },
  { key: 'processingDate', es: '⚙️ Fecha de procesamiento',  en: '⚙️ Processing date' },
  { key: 'processedBy',  es: '🧑‍💻 Procesado por',            en: '🧑‍💻 Processed by' },
  { key: 'analystName',  es: '📊 Analista',                  en: '📊 Analyst' },
  { key: 'agentName',    es: '🧑 Nombre del agente (tarjeta)', en: '🧑 Agent name (card)' },
  { key: 'producerCode', es: '🔖 Código de productor',       en: '🔖 Producer code' },
  // Etiquetas de PÓLIZA (las que salen en la tarjeta junto al estado). No son
  // las del cliente: son otra cosa y viven en la póliza.
  { key: 'policyTags',   es: '🏷️ Etiquetas de la póliza (separadas por coma)', en: '🏷️ Policy tags (comma separated)' },
  { key: 'cancellationDate', es: '🛑 Fecha de cancelación',  en: '🛑 Cancellation date' },
  { key: 'cancelReason', es: '❓ Motivo de cancelación',      en: '❓ Cancellation reason' },
  { key: 'deductible',   es: '🧮 Deducible',                 en: '🧮 Deductible' },
  { key: 'beneficiary',  es: '🎁 Beneficiario',              en: '🎁 Beneficiary' },
  { key: 'coverageType', es: '🛡️ Tipo de cobertura',         en: '🛡️ Coverage type' },
  { key: 'liabilityCoverage', es: '⚠️ Cobertura de responsabilidad', en: '⚠️ Liability coverage' },
  { key: 'familySize',   es: '👨‍👩‍👧 Tamaño familiar (póliza)', en: '👨‍👩‍👧 Family size (policy)' },
  { key: 'membersActive', es: '✅ Miembros activos',          en: '✅ Members active' },
  { key: 'applicationNumber', es: '📄 N° de aplicación',     en: '📄 Application #' },
  { key: 'subsidy',      es: '🤝 Subsidio',                  en: '🤝 Subsidy' },
  { key: 'planTotalCost', es: '💲 Costo total del plan',     en: '💲 Plan total cost' },
  { key: 'commissionPercent', es: '📈 Comisión %',           en: '📈 Commission %' },
  { key: 'commissionMonthly', es: '📆 Comisión mensual',     en: '📆 Monthly commission' },
  { key: 'commissionAmount',  es: '💰 Comisión total',       en: '💰 Commission amount' },
  // Información de pago.
  { key: 'paymentType',  es: '💳 Tipo de pago',              en: '💳 Payment type' },
  { key: 'paymentMethod', es: '💳 Método de pago',           en: '💳 Payment method' },
  { key: 'payerName',    es: '🧑 Nombre del pagador',        en: '🧑 Payer name' },
  { key: 'billingDay',   es: '📅 Día de débito',             en: '📅 Billing day' },
  { key: 'cardBrand',    es: '💳 Tarjeta — marca',           en: '💳 Card brand' },
  { key: 'cardLast4',    es: '💳 Tarjeta — últimos 4',       en: '💳 Card last 4' },
  { key: 'cardExpMonth', es: '💳 Tarjeta — mes venc.',       en: '💳 Card exp. month' },
  { key: 'cardExpYear',  es: '💳 Tarjeta — año venc.',       en: '💳 Card exp. year' },
  { key: 'bankLast4',    es: '🏦 Banco — últimos 4',         en: '🏦 Bank last 4' },
  // Auto.
  { key: 'vehicleYear',  es: '🚗 Vehículo — año',            en: '🚗 Vehicle year' },
  { key: 'vehicleMake',  es: '🚗 Vehículo — marca',          en: '🚗 Vehicle make' },
  { key: 'vehicleModel', es: '🚗 Vehículo — modelo',         en: '🚗 Vehicle model' },
  { key: 'vin',          es: '🔧 VIN',                       en: '🔧 VIN' },
  { key: 'licensePlate', es: '🔖 Placa',                     en: '🔖 License plate' },
  { key: 'driversOnPolicy', es: '🧑‍🤝‍🧑 Conductores en la póliza', en: '🧑‍🤝‍🧑 Drivers on policy' },
  // Propiedad / hogar.
  { key: 'propertyAddress', es: '🏠 Dirección de la propiedad', en: '🏠 Property address' },
  { key: 'propertyType', es: '🏠 Tipo de propiedad',         en: '🏠 Property type' },
  { key: 'yearBuilt',    es: '🏗️ Año de construcción',       en: '🏗️ Year built' },
  { key: 'squareFootage', es: '📐 Pies cuadrados',           en: '📐 Square footage' },
  { key: 'insuredValue', es: '💵 Valor asegurado',           en: '💵 Insured value' },
  { key: 'dwellingCoverage', es: '🏠 Cobertura de vivienda', en: '🏠 Dwelling coverage' },
  // Negocio.
  { key: 'businessName', es: '🏢 Nombre del negocio',        en: '🏢 Business name' },
  { key: 'businessType', es: '🏢 Tipo de negocio',           en: '🏢 Business type' },
  { key: 'businessAddress', es: '🏢 Dirección del negocio',  en: '🏢 Business address' },
  { key: 'numEmployees', es: '👥 N° de empleados',           en: '👥 # of employees' },
  { key: 'annualRevenue', es: '📊 Ingresos anuales del negocio', en: '📊 Annual revenue' },
  // Dental / visión / mascotas / gastos finales.
  { key: 'supplementType', es: '➕ Tipo de suplemento',        en: '➕ Supplement type' },
  // Medicare: SOA y HRA (Lissette 2026-09-13, "poder mapearla en la parte de
  // poliza y cliente mas polizas").
  { key: 'soaStatus',    es: '📝 Estatus del SOA',            en: '📝 SOA status' },
  { key: 'soaSigned',    es: '✍️ SOA firmado',                en: '✍️ SOA signed' },
  { key: 'hraStatus',    es: '🩺 Estatus del HRA',            en: '🩺 HRA status' },
  // Medicaid, fecha de firma del SOA y LIS (Lissette 2026-09-14).
  { key: 'medicaidEligibility', es: '🏥 Elegibilidad de Medicaid', en: '🏥 Medicaid eligibility' },
  { key: 'soaSignedDate', es: '🗓️ Fecha de firma del SOA',     en: '🗓️ SOA signature date' },
  { key: 'lisLevel',     es: '💊 LIS (Extra Help)',            en: '💊 LIS (Extra Help)' },
  { key: 'annualMaximum', es: '🦷 Máximo anual',             en: '🦷 Annual maximum' },
  { key: 'waitingPeriod', es: '⏳ Período de espera',         en: '⏳ Waiting period' },
  { key: 'networkType',  es: '🕸️ Tipo de red (DPPO/DHMO)',   en: '🕸️ Network type' },
  { key: 'coinsurance',  es: '➗ Coaseguro',                  en: '➗ Coinsurance' },
  { key: 'missingToothClause', es: '🦷 Cláusula de diente faltante', en: '🦷 Missing tooth clause' },
  { key: 'examCopay',    es: '👓 Copago del examen',         en: '👓 Exam copay' },
  { key: 'frameAllowance', es: '👓 Asignación de armazón',   en: '👓 Frame allowance' },
  { key: 'contactsAllowance', es: '👓 Lentes de contacto',   en: '👓 Contacts allowance' },
  { key: 'petName',      es: '🐾 Mascota — nombre',          en: '🐾 Pet name' },
  { key: 'petSpecies',   es: '🐾 Mascota — especie',         en: '🐾 Pet species' },
  { key: 'petBreed',     es: '🐾 Mascota — raza',            en: '🐾 Pet breed' },
  { key: 'petAge',       es: '🐾 Mascota — edad',            en: '🐾 Pet age' },
  { key: 'microchipNumber', es: '🐾 N° de microchip',        en: '🐾 Microchip #' },
  { key: 'reimbursementPercent', es: '🐾 % de reembolso',    en: '🐾 Reimbursement %' },
  { key: 'funeralHomeAssignment', es: '🕊️ Asignación a funeraria', en: '🕊️ Funeral home assignment' },
  // ── Datos del CLIENTE. Solo se usan en las fichas que esta importación CREA;
  //    a un cliente que ya existe no le tocan nada (Lissette 2026-09-14).
  { key: 'clientSsn',            es: '🔒 Cliente — SSN',                 en: '🔒 Client — SSN' },
  { key: 'clientAnnualIncome',   es: '💵 Cliente — ingreso anual',        en: '💵 Client — annual income' },
  { key: 'clientAssignedAgent',  es: '🧑‍💼 Cliente — agente',     en: '🧑‍💼 Client — agent' },
  { key: 'clientAttendedBy',     es: '🙋 Cliente — atiende / handle',     en: '🙋 Client — handled by' },
  { key: 'clientTags',           es: '🏷️ Cliente — etiquetas',           en: '🏷️ Client — tags' },
  { key: 'clientStatus',         es: '🚦 Cliente — estatus',             en: '🚦 Client — status' },
  { key: 'clientLanguage',       es: '🗣️ Cliente — idioma',              en: '🗣️ Client — language' },
  { key: 'clientEmployer',       es: '🏭 Cliente — empleador',            en: '🏭 Client — employer' },
  { key: 'clientHouseholdSize',  es: '👨‍👩‍👧 Cliente — tamaño del hogar',  en: '👨‍👩‍👧 Client — household size' },
  { key: 'clientMaritalStatus',  es: '💍 Cliente — estado civil',         en: '💍 Client — marital status' },
  { key: 'clientGender',         es: '⚧ Cliente — sexo',                 en: '⚧ Client — sex' },
  { key: 'clientImmigrationStatus', es: '🛂 Cliente — estatus migratorio', en: '🛂 Client — immigration status' },
  { key: 'clientCounty',         es: '📍 Cliente — condado',              en: '📍 Client — county' },
  { key: 'clientPhoneSecondary', es: '📱 Cliente — teléfono 2',           en: '📱 Client — phone 2' },
  { key: 'clientPlaceOfBirth',   es: '🌎 Cliente — lugar de nacimiento',  en: '🌎 Client — place of birth' },
  { key: 'clientSource',         es: '🎯 Cliente — origen',               en: '🎯 Client — source' },
  // ── El HOGAR: cónyuge y dependientes ──────────────────────────────────────
  //
  // Lissette, 2026-09-14: *"sí, también los cónyuges y dependientes"*. Van al
  // final de la lista porque son ciento diez opciones y empujarían fuera de la
  // vista a los campos que se usan en todas las importaciones.
  //
  // Como los del titular, SOLO se usan en las fichas que esta importación crea.
  ...camposDelConyuge(),
  ...camposDeDependientes(),
];

// Las claves añadidas en 2026-08-13 se vuelcan al payload del servidor de forma
// genérica: las fechas por polSerialToDate (Excel las guarda como número) y el
// resto como texto. Derivar la lista de POLICY_IMPORT_FIELDS sería más
// automático, pero estas dos listas dicen ADEMÁS cómo hay que limpiarlas.
const POLICY_IMPORT_DATE_KEYS = ['saleDate', 'processingDate', 'cancellationDate', 'soaSignedDate'];
const POLICY_IMPORT_TEXT_KEYS = [
  'supplementType', 'soaStatus', 'soaSigned', 'hraStatus',
  'medicaidEligibility', 'lisLevel',
  'policyNumber', 'processedBy', 'analystName', 'agentName', 'producerCode',
  'cancelReason', 'deductible', 'beneficiary', 'coverageType', 'liabilityCoverage',
  'familySize', 'membersActive', 'applicationNumber', 'subsidy', 'planTotalCost',
  'commissionPercent', 'commissionMonthly', 'commissionAmount',
  'paymentType', 'paymentMethod', 'payerName', 'billingDay',
  'cardBrand', 'cardLast4', 'cardExpMonth', 'cardExpYear', 'bankLast4',
  'vehicleYear', 'vehicleMake', 'vehicleModel', 'vin', 'licensePlate', 'driversOnPolicy',
  'propertyAddress', 'propertyType', 'yearBuilt', 'squareFootage', 'insuredValue', 'dwellingCoverage',
  'businessName', 'businessType', 'businessAddress', 'numEmployees', 'annualRevenue',
  'annualMaximum', 'waitingPeriod', 'networkType', 'coinsurance', 'missingToothClause',
  'examCopay', 'frameAllowance', 'contactsAllowance',
  'petName', 'petSpecies', 'petBreed', 'petAge', 'microchipNumber', 'reimbursementPercent',
  'funeralHomeAssignment',
];
const POLICY_IMPORT_ALIASES = {
  // 'name' belongs here, NOT under carrier: monday.com exports ALWAYS name the
  // primary (policyholder) column literally "Name". It used to be aliased to
  // carrier, which hijacked every monday import — the client name landed in the
  // Carrier field and NOTHING mapped to clientName, so the server matched no one
  // and silently dropped every policy (Lissette QA 2026-06-29: "no me creó
  // ninguna póliza, solo me duplicó el cliente").
  clientName: ['name', 'customer name', 'client name', 'nombre cliente', 'nombre del cliente', 'cliente', 'titular', 'nombre completo'],
  // Name parts. 'first name'/'last name' are EXACT (win over the substring
  // pass) so they never collide with clientName's aliases.
  clientFirstName: ['first name', 'firstname', 'first', 'primer nombre', 'nombre(s)', 'nombres', 'given name'],
  clientMiddleName: ['middle name', 'middlename', 'middle', 'segundo nombre', 'inicial', 'middle initial'],
  clientLastName: ['last name', 'lastname', 'apellido', 'apellidos', 'surname', 'family name', 'apellido paterno'],
  // Que la columna se reconozca sola al subir la hoja: sin esto hay que
  // buscarla a mano en una lista de mas de cien campos.
  soaStatus: ['soa', 'soa status', 'estatus del soa', 'estatus soa', 'status soa', 'scope of appointment', 'estado del soa'],
  soaSigned: ['soa firmado', 'soa signed', 'firma soa', 'soa firma', 'signed soa', 'soa sign'],
  hraStatus: ['hra', 'hra status', 'estatus del hra', 'estatus hra', 'status hra', 'health risk assessment', 'estado del hra'],
  // 'medicaid' a secas vale: en una hoja de Medicare no hay otra columna que
  // se llame asi, y es como la escribe casi todo el mundo.
  medicaidEligibility: ['medicaid', 'medicaid eligibility', 'medicaid elegibility', 'elegibilidad de medicaid', 'elegibilidad medicaid', 'categoria de medicaid', 'dual status', 'dual', 'nivel de medicaid'],
  soaSignedDate: ['soa signature date', 'soa signed date', 'fecha de firma del soa', 'fecha de firma soa', 'fecha firma soa', 'fecha soa', 'soa date'],
  lisLevel: ['lis', 'lis level', 'nivel de lis', 'nivel lis', 'extra help', 'low income subsidy', 'subsidio de bajos ingresos'],
  supplementType: ['tipo de suplemento', 'tipo suplemento', 'suplemento', 'supplement type', 'supplement', 'tipo de suplementos', 'supplemental type'],
  clientEmail: ['email', 'correo', 'e-mail', 'mail'],
  clientPhone: ['phone', 'telefono', 'teléfono', 'celular', 'mobile', 'cel'],
  clientDob: ['dob', 'date of birth', 'fecha de nacimiento', 'fecha nacimiento', 'nacimiento', 'birthdate', 'insured birth', 'birth'],
  // Client address. NOTE: 'estado' is intentionally NOT an alias for
  // addressState — it already maps to `status` (policy status) in monday
  // exports, and stealing it would break that mapping. English 'state' is
  // unambiguous; Spanish sheets rarely carry a separate state column.
  addressStreet: ['address', 'direccion', 'dirección', 'street', 'calle', 'domicilio', 'address line 1', 'address 1', 'addr'],
  addressCity: ['city', 'ciudad', 'municipio'],
  addressState: ['state', 'provincia'],
  addressZip: ['zip', 'zip code', 'zipcode', 'codigo postal', 'código postal', 'postal code', 'cp'],
  carrier: ['carrier', 'aseguradora', 'compania', 'compañia', 'insurer', 'company'],
  // The monday "Type" column holds the policy kind (Life / DENTAL / VISION…).
  // Aliases stay SPECIFIC so "TYPE OF DOCUMENT" never steals this mapping — the
  // two-phase auto-map gives exact 'type'/'tipo' priority, and we avoid a bare
  // 'type' substring that would also match "type of document".
  policyType: ['type', 'tipo', 'tipo de poliza', 'tipo de póliza', 'tipo de seguro', 'tipo de plan', 'typeofpolicy', 'type of policy', 'policy type', 'producto', 'product'],
  // 'product name' must be an EXACT alias here so it wins over policyType's
  // 'product' substring (the life PolicySummary carries BOTH "Product Name"
  // and "Product Type" — name is the plan, type is the policy kind).
  plan: ['plan', 'plan name', 'nombre del plan', 'product name'],
  // Member ID y # de póliza son campos DISTINTOS y cada uno reconoce lo suyo.
  // Antes las variantes de "policy number" caían aquí, en Member ID, y el
  // servidor copiaba además ese valor al # de póliza: de ahí venía que los dos
  // campos salieran con el mismo número y que borrar uno pareciera borrar el
  // otro (Lissette 2026-08-14). Una hoja que solo trae un número ahora aterriza
  // en la casilla que dice su cabecera, y el mapeo se puede cambiar a mano.
  memberId: ['member id', 'memberid', 'id member', 'id miembro', 'id de miembro', 'member #', 'member no', 'member number', 'subscriber id'],
  policyTags: ['etiqueta de poliza', 'etiquetas de poliza', 'policy tag', 'policy tags', 'tag de poliza', 'tags de poliza'],
  policyNumber: ['policy number', 'policy numer', 'policy numero', 'numero de poliza', 'número de póliza', 'numero poliza', 'no poliza', 'nro poliza', 'poliza', 'póliza', 'policy #', '# poliza', '# póliza', 'policy_number', 'policy no', 'policy num', 'pol number', 'pol #'],
  // Life "face amount" / death benefit — kept off generic 'coverage' to avoid
  // colliding with P&C coverage columns.
  faceAmount: ['face amount', 'faceamount', 'monto de cobertura', 'suma asegurada', 'death benefit', 'coverage amount'],
  premium: ['premium', 'prima', 'monthly premium', 'prima mensual', 'monto'],
  // 'issue date' / 'fecha de emision' = the LIFE inforce export's effective
  // date column ("ISSUE DATE POLIZA"). Without it the policy got stamped today.
  effectiveDate: ['start plan', 'effective date', 'eff date', 'eff. date', 'effective', 'issue date', 'issue date poliza', 'fecha de emision', 'fecha emision', 'inicio', 'fecha inicio', 'start date', 'vigencia'],
  renewalDate: ['end plan', 'expiration', 'renewal', 'renovacion', 'renovación', 'end date', 'expiration date', 'vencimiento', 'fin'],
  leadType: ['lead sources', 'lead source', 'sales source', 'origen', 'lead type', 'fuente'],
  agentLabel: ['agent assigned', 'agente', 'agent in market', 'agent', 'asesor'],
  // NOTE: do NOT alias 'active'/'activo' here — monday exports have BOTH an
  // "ACTIVE" checkmark column and a real "STATUS" column (Canceled/Verified/…).
  // 'active' would let the checkmark column win (it sorts first), so every
  // cancelled policy imported as active. Keeping only the STATUS aliases makes
  // the real status column win.
  status: ['status', 'estado', 'estatus', 'estado de la poliza', 'policy status'],
  members: ['members', 'miembros', 'asegurados', 'insured', 'dependientes'],
  notes: ['comments', 'observaciones', 'notas', 'nota', 'notes', 'comentarios', 'comentario', 'obs'],
  // Life PolicySummary columns (Lissette 2026-07-02). Exact-phase matching
  // keeps them from stealing generic columns: 'premium due date' beats the
  // bare 'premium' substring, 'agent number' beats agentLabel's 'agent', and
  // 'date of issue' coexists with effectiveDate's 'issue date' alias (kept
  // there for the inforce export whose ONLY date is "ISSUE DATE POLIZA").
  policyOwnerName: ['owner name', 'owner', 'propietario', 'dueno de la poliza', 'dueño de la póliza'],
  termDuration: ['term duration', 'term length', 'plazo', 'duracion del termino', 'duración del término'],
  issueDate: ['date of issue', 'fecha de emision', 'fecha de emisión'],
  premiumDueDate: ['premium due date', 'due date', 'fecha de pago de prima'],
  billablePremium: ['billable premium', 'prima facturable'],
  billingFrequency: ['billing frequency', 'frecuencia de pago', 'frecuencia de facturacion'],
  billingMethod: ['billing method', 'metodo de facturacion', 'método de facturación'],
  agentNumber: ['agent number', 'agent #', 'numero de agente', 'número de agente'],
  agencyNumber: ['agency number', 'agency #', 'numero de agencia', 'número de agencia'],
  // Medicare (Lissette 2026-08-09) — nombres tal como los mandó la agente en su
  // Excel, más las variantes en español. Todas van por coincidencia EXACTA a
  // propósito: 'initial premium' y 'current premium' llevan la palabra
  // "premium" dentro, y sin el paso exacto el pase por substring se las
  // robaría para la prima mensual. Por lo mismo NO se pone 'medicare' a secas
  // (chocaría con el tipo de póliza) ni 'company' (es el carrier).
  medicareNumber: ['medicare #', 'medicare number', 'medicare no', 'mbi', 'numero de medicare', 'número de medicare'],
  partADate: ['part a issue date', 'part a date', 'part a', 'fecha parte a', 'parte a'],
  partBDate: ['part b issue date', 'part b date', 'part b', 'fecha parte b', 'parte b'],
  companyId: ['company id', 'company #', 'id de la compania', 'id de la compañía'],
  planYear: ['plan current year', 'plan year', 'plan del ano', 'plan del año'],
  enrollmentCode: ['enrollment code', 'codigo de inscripcion', 'código de inscripción'],
  initialPremium: ['initial premium', 'prima inicial'],
  currentPremium: ['current premium', 'prima actual'],
  // Vida, segunda tanda (Lissette 2026-08-09). Igual que arriba, todos exactos:
  // 'delivery date' y 'placing date' llevan "date" dentro y 'commission paid'
  // lleva "commission"; sin el paso exacto el substring se los llevaría a
  // effectiveDate y a commissionAmount. NO se pone 'split' a secas ni 'address'
  // a secas por lo mismo.
  approvedDate: ['approved date', 'date approved', 'fecha de aprobacion', 'fecha de aprobación'],
  deliveryPapersDate: ['submission delivery papers', 'delivery papers', 'papeles de entrega'],
  deliveryDate: ['delivery date', 'date delivered', 'fecha de entrega'],
  placingDate: ['placing date', 'fecha de colocacion', 'fecha de colocación'],
  beneficiaryContingent: ['contingent beneficiary', 'beneficiario contingente', 'contingent'],
  riskClass: ['final risk class', 'risk class', 'clase de riesgo'],
  conversionAge: ['conversion age', 'edad de conversion', 'edad de conversión'],
  verificationForm: ['verification form', 'formulario de verificacion', 'formulario de verificación'],
  ownerAddress: ['address of owner', 'owner address', 'direccion del dueno', 'dirección del dueño'],
  commissionPaid: ['commission paid', 'comision pagada', 'comisión pagada'],
  commissionSplit: ['commission split', 'split (reparto)', 'reparto'],
  // ACA y P&C (Lissette 2026-08-09). Exactos por lo mismo de siempre: los tres
  // de prima llevan "premium" dentro y 'general agent' lleva "agent"; sin el
  // paso exacto se los quedarían la prima mensual y el agente de la póliza.
  enrollmentPeriod: ['oe', 'sep', 'oe/sep', 'oe / sep', 'enrollment period', 'periodo de inscripcion', 'período de inscripción'],
  marketplaceName: ['marketplace', 'marketplace name', 'nombre del marketplace', 'mercado', 'exchange'],
  premiumAnnual: ['premium (yearly)', 'yearly premium', 'annual premium', 'prima anual'],
  premiumSemiAnnual: ['premium (semi-annual)', 'semi-annual premium', 'semiannual premium', 'prima semestral'],
  generalAgent: ['general agent', 'agente general', 'mga'],

  // ── DATOS DEL CLIENTE, PARA LAS FICHAS QUE ESTA IMPORTACION CREA ─────────
  //
  // Lissette, 2026-09-14: *"en el importador de solo póliza, cuando existen
  // nuevos clientes necesito mapear para esos nuevos clientes toda la
  // información como si fuera cliente: SSN, income, agente asignado, handle…
  // y no me da la posibilidad"*.
  //
  // Hasta hoy este importador solo sabía del nombre, el correo, el teléfono, la
  // fecha de nacimiento y la dirección: un cliente creado desde aquí nacía casi
  // vacío y había que abrir su ficha a mano para completarlo, uno por uno.
  //
  // SOLO SE USAN AL CREAR. A un cliente que YA EXISTE no se le toca ni un campo
  // con estas columnas — es la otra mitad de lo que pidió (*"para esos nuevos,
  // no los actuales"*), y es lo que hace que reimportar una hoja no pueda pisar
  // lo que la agencia ya escribió a mano.
  //
  // VAN AL FINAL DEL OBJETO A PROPÓSITO. El emparejado automático hace dos
  // pasadas: una EXACTA (recorre todo, no depende del orden) y otra por
  // SUBCADENA (que sí depende). Puestas aquí, ninguna cabecera que hoy se
  // reconoce puede cambiar de destino: lo exacto sigue ganando y, en la de
  // subcadena, los campos de póliza conservan la prioridad que ya tenían. Lo
  // que no acierte solo se corrige en el desplegable, que para eso está.
  clientSsn: ['ssn', 'ssn del cliente', 'social security', 'social security number', 'seguro social', 'numero de seguro social', 'número de seguro social'],
  clientAnnualIncome: ['ingreso anual', 'ingresos anuales', 'ingreso del cliente', 'annual income', 'client income', 'yearly income'],
  clientAssignedAgent: ['agente asignado', 'agente asignado del cliente', 'assigned agent', 'asesor asignado', 'agente del cliente', 'responsable del cliente'],
  // "Atiende" NO es el agente asignado: son dos casillas distintas de la misma
  // tarjeta (Lissette 2026-09-03). Se resuelve contra los asientos de la
  // agencia; si ninguno responde a ese nombre, se guarda el texto tal cual.
  clientAttendedBy: ['atiende', 'handle', 'handled', 'handled by', 'quien atiende', 'quien lo atiende', 'encargado del cliente'],
  // Etiquetas DEL CLIENTE. No comparten alias con `policyTags`: una etiqueta de
  // póliza se va con la póliza, la del cliente se queda en la ficha.
  clientTags: ['etiqueta del cliente', 'etiquetas del cliente', 'client tag', 'client tags', 'etiquetas cliente'],
  clientStatus: ['estatus del cliente', 'estado del cliente', 'client status', 'estatus cliente'],
  clientLanguage: ['idioma', 'idioma del cliente', 'language', 'preferred language', 'lenguaje'],
  clientEmployer: ['empleador', 'employer', 'lugar de trabajo'],
  clientHouseholdSize: ['tamano del hogar', 'tamaño del hogar', 'household size', 'personas en el hogar', 'miembros del hogar'],
  clientMaritalStatus: ['estado civil', 'marital status'],
  clientGender: ['sexo', 'genero', 'género', 'gender'],
  clientImmigrationStatus: ['estatus migratorio', 'estado migratorio', 'immigration status', 'migratorio'],
  clientCounty: ['condado', 'county'],
  clientPhoneSecondary: ['telefono 2', 'teléfono 2', 'segundo telefono', 'segundo teléfono', 'telefono secundario', 'teléfono secundario', 'phone 2', 'secondary phone'],
  clientPlaceOfBirth: ['lugar de nacimiento', 'place of birth', 'pais de nacimiento', 'país de nacimiento'],
  clientSource: ['origen del cliente', 'fuente del cliente', 'client source', 'como nos conocio', 'cómo nos conoció'],
};
function polExactField(n) {
  for (const [field, aliases] of Object.entries(POLICY_IMPORT_ALIASES)) if (aliases.includes(n)) return field;
  return '';
}
function polSubstrField(n) {
  for (const [field, aliases] of Object.entries(POLICY_IMPORT_ALIASES)) if (aliases.some((a) => n.includes(a))) return field;
  return '';
}
function polImportAutoMap(header) {
  const n = String(header || '').trim().toLowerCase();
  if (!n) return '';
  return polExactField(n) || polSubstrField(n);
}
// Two-phase auto-map: assign EXACT header matches first (across all columns),
// then fill remaining columns with substring matches — and never reuse a field.
// This makes "CUSTOMER NAME" (exact → clientName) win over "CUSTOMER ID"
// (substring 'customer'), which previously stole the client-name mapping and
// broke every match.
// Campos que pueden venir de VARIAS columnas y se juntan en uno solo. Hoy solo
// las notas: una hoja suele repartir lo que el agente considera "la nota" entre
// dos o tres columnas (referido, comentarios, observaciones) y obligarle a
// elegir una perdia el resto (Lissette 2026-08-16).
const POLICY_COMBINABLE_FIELDS = new Set(['notes', 'policyNote']);

function polBuildAutoMap(headers, rows) {
  const lc = headers.map((h) => String(h || '').trim().toLowerCase());
  const map = {}; const used = new Map(); // campo -> columna que lo tiene
  // ¿Trae datos esta columna? Se miran las primeras filas, no solo la primera,
  // que suele venir en blanco. Sin `rows` (llamadas antiguas) se asume que sí,
  // con lo que el comportamiento es el de siempre.
  const tieneDatos = (i) => !rows || (rows || []).slice(0, 60).some((r) => String((r || [])[i] ?? '').trim());
  const poner = (i, f) => {
    const previa = used.get(f);
    if (previa === undefined) { map[i] = f; used.set(f, i); return; }
    // El campo ya estaba asignado. Si la columna que lo tenía viene VACÍA y
    // esta trae datos, se cambia: una hoja con "COMMENTS" (vacía) y "NOTES"
    // (con texto) asignaba las notas a COMMENTS solo por estar antes, y la
    // columna buena quedaba en "(ignorar)" (Lissette 2026-08-16).
    if (!tieneDatos(previa) && tieneDatos(i)) { delete map[previa]; map[i] = f; used.set(f, i); }
  };
  lc.forEach((n, i) => { if (!n) return; const f = polExactField(n); if (f) poner(i, f); });
  lc.forEach((n, i) => { if (!n || map[i] !== undefined) return; const f = polSubstrField(n); if (f) poner(i, f); });
  return map;
}
function polParseCsv(text) {
  const rows = []; let row = []; let cur = ''; let q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (q) { if (ch === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += ch; }
    else if (ch === '"') q = true;
    else if (ch === ',') { row.push(cur); cur = ''; }
    else if (ch === '\n') { row.push(cur); rows.push(row); row = []; cur = ''; }
    else if (ch !== '\r') cur += ch;
  }
  if (cur !== '' || row.length) { row.push(cur); rows.push(row); }
  return rows;
}
// Excel serial → YYYY-MM-DD; passes through real dates / ISO strings.
// Serial de Excel (época 1899-12-30) → 'YYYY-MM-DD'; el resto se parsea tal
// cual. El corte del ISO lo hace importDayIso, que además descarta los años
// imposibles: un "99999" en la columna de vigencia salía de aquí como
// "+099999-01" y tumbaba la importación entera en el servidor (E-070).
function polSerialToDate(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!isNaN(n) && n > 10000 && n < 80000) {
    return importDayIso(new Date(Date.UTC(1899, 11, 30) + n * 86400000));
  }
  return importDayIso(v);
}
const polNorm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
const polTokens = (s) => new Set(polNorm(s).split(' ').filter(Boolean));
const polDigits = (s) => String(s || '').replace(/\D/g, '').slice(-10);
const polLeadType = (v) => {
  const lc = polNorm(v);
  if (!lc) return '';
  const p = LEAD_TYPE_PRESETS.find((x) => x.id === lc || polNorm(x.es) === lc || polNorm(x.en) === lc);
  return p ? p.id : String(v).trim();
};
// Normalize a raw "Type"/"Product" cell from a policies export into a short
// policy-type id (matching the ids in policyTypes.js). Returns '' when it can't
// confidently classify the value, so the importer falls back to the chosen
// default type instead of guessing. Handles the messy monday values seen in the
// wild: "BLUE DENTAL COPAYMENT", "VISION WISE", "Term", "ACCIDENTE", "Life"…
// Order matters: "DENTAL COPAYMENT" must resolve to dental before a looser
// keyword can claim it.
const polPolicyType = (v) => {
  const t = polNorm(v);
  if (!t) return '';
  // Primero los tipos que se creó la agencia, por su nombre EXACTO (Lissette
  // 2026-10-19): una columna que dice "Seguro Privado" es ese tipo. Va antes
  // de las palabras sueltas para que "Vida Internacional" no caiga en Vida.
  const propio = tiposPropiosDeLaAgencia().find((p) => [p.id, p.label, p.labelEs].some((x) => x && polNorm(x) === t));
  if (propio) return propio.id;
  if (/dental|odont/.test(t)) return 'dental';
  if (/vision|optic/.test(t)) return 'vision';
  if (/accident/.test(t)) return 'accident';
  if (/final expense|gastos finales/.test(t)) return 'final';
  if (/medicare advantage|advantage/.test(t)) return 'ma';
  if (/supplement|medigap|medsup|suplement/.test(t)) return 'medsup';
  if (/part ?d/.test(t)) return 'partd';
  if (/\blife\b|vida|\bterm\b|whole life|permanent|\biul\b|universal life/.test(t)) return 'life';
  if (/renter|inquilino/.test(t)) return 'renters';
  if (/\bauto\b|vehicle|automovil|automóvil/.test(t)) return 'auto';
  if (/homeowner|\bhome\b|propietario|vivienda/.test(t)) return 'home';
  if (/commercial|comercial|\bbusiness\b|negocio/.test(t)) return 'commercial';
  if (/umbrella|paraguas/.test(t)) return 'umbrella';
  // Antes que 'commercial'/'aca': "general liability" y "hospital indemnity"
  // llevan palabras que caerían en esos dos si se miran despues.
  if (/liability|responsabilidad/.test(t)) return 'liability';
  if (/hospital|hospitaliz/.test(t)) return 'hosp';
  if (/\bpet\b|mascota/.test(t)) return 'pet';
  if (/travel|viaje/.test(t)) return 'travel';
  if (/health|salud|\baca\b|marketplace|obamacare|medical/.test(t)) return 'aca';
  return '';
};
const polStatus = (v) => {
  const t = polNorm(v);
  if (/cancel|baja/.test(t)) return 'cancelled';
  if (/pend|proceso|tramite/.test(t)) return 'pending';
  if (/inact|lapse|term|expir|vencid/.test(t)) return 'inactive';
  return 'active';
};
// Match one row to a client. Returns { client, status: 'matched'|'ambiguous'|'none' }.
function polMatchClient({ name, email, phone, dob }, clients) {
  const em = polNorm(email);
  const ph = polDigits(phone);
  const db = polSerialToDate(dob);
  const nameTok = polTokens(name);
  // Name candidates: token sets where one is a subset of the other (handles
  // "LAST FIRST" order and middle names). Match against EVERY person on the
  // household — principal, spouse, and each dependent — because an imported
  // policy is often listed under a family member (e.g. dental/vision for a
  // child) while the principal carries none. A hit on any of them returns the
  // principal's card. Token sets are kept per-person (not merged) so a
  // dependent's first name + the principal's last name can't false-match.
  // Directional: every token of `need` is in `have`. For the PRINCIPAL match so
  // a stray 1-token client can't swallow-match a full "LAST FIRST" row.
  const containsAll = (need, have) => { if (!need.size) return false; let ok = true; need.forEach((t) => { if (!have.has(t)) ok = false; }); return ok; };
  const setsEqual = (a, b) => a.size === b.size && containsAll(a, b);
  const principalTok = (c) => polTokens(`${c.firstName || ''} ${c.lastName || ''}`);
  const familyTokSets = (c) => {
    const sets = [];
    const sf = c.spouseFirstName || (c.spouse && c.spouse.firstName) || '';
    const sl = c.spouseLastName || (c.spouse && c.spouse.lastName) || '';
    if (sf || sl) sets.push(polTokens(`${sf} ${sl}`));
    if (Array.isArray(c.dependents)) {
      for (const d of c.dependents) {
        if (d && typeof d === 'object' && (d.firstName || d.lastName)) {
          sets.push(polTokens(`${d.firstName || ''} ${d.lastName || ''}`));
        }
      }
    }
    return sets.filter((s) => s && s.size);
  };
  const narrowBy = (cands) => {
    let n = cands;
    if (em) { const x = n.filter((c) => polNorm(c.email) === em); if (x.length) n = x; }
    if (n.length > 1 && ph) { const x = n.filter((c) => polDigits(c.phone) === ph); if (x.length) n = x; }
    if (n.length > 1 && db) { const x = n.filter((c) => (c.dob || '').slice(0, 10) === db); if (x.length) n = x; }
    return n;
  };
  if (nameTok.size) {
    // A LONE first name (1 token) is too weak to identify a person: it would
    // subset-match every client sharing that first name — or any client with a
    // spouse/dependent of that name — mis-linking the policy to a stranger. Real
    // case (Lissette 2026-07-10): a row that reduced to "Alfredo" matched
    // "Diogenes Mendez" because he has a dependent "Alfredo Tomas Mendez Vidal".
    // So subset + family matching require >=2 tokens; a single token can match
    // ONLY an exact 1-token client name.
    const multi = nameTok.size >= 2;
    // 1) PRINCIPAL takes priority; exact full-name token match (any word order →
    //    handles "LAST FIRST") always wins. Beyond exact, a >=2-token row may
    //    subset-match (client has extra middle names, or the row does).
    const principal = (clients || []).filter((c) => {
      const pt = principalTok(c);
      if (!pt.size) return false;
      if (setsEqual(nameTok, pt)) return true;                  // exact — any size
      if (!multi) return false;                                 // never subset-match a lone first name
      return containsAll(nameTok, pt) || (pt.size >= 2 && containsAll(pt, nameTok));
    });
    const exact = principal.filter((c) => setsEqual(nameTok, principalTok(c)));
    const pool = exact.length ? exact : principal;
    if (pool.length >= 1) {
      const n = narrowBy(pool);
      if (n.length === 1) return { client: n[0], status: 'matched' };
      if (n.length > 1) return { client: null, status: 'ambiguous' };
    }
    // 2) Fall back to spouse/dependent names (child policies), only if NO
    //    principal matched. DIRECTIONAL: every ROW token must be in the family
    //    member's name (row ⊆ dependent) and >=2 tokens, so a stranger sharing
    //    one token with a dependent can't attach.
    const household = multi ? (clients || []).filter((c) => familyTokSets(c).some((ct) => containsAll(nameTok, ct))) : [];
    if (household.length >= 1) {
      const n = narrowBy(household);
      if (n.length === 1) return { client: n[0], status: 'matched' };
      if (n.length > 1) return { client: null, status: 'ambiguous' };
    }
  }
  // No name candidates — try email then phone as a last resort.
  if (em) { const x = (clients || []).filter((c) => polNorm(c.email) === em); if (x.length === 1) return { client: x[0], status: 'matched' }; }
  if (ph) { const x = (clients || []).filter((c) => polDigits(c.phone) === ph); if (x.length === 1) return { client: x[0], status: 'matched' }; }
  return { client: null, status: 'none' };
}

// Parse a monday.com "HEALTH DEPARTMENT" policies workbook (array-of-arrays)
// into the bulk-import row shape. Detects the header row (skips banner rows),
// auto-maps columns, converts Excel serial dates. Shared so the Import view's
// "Policies Only" mode and the Policies-view "Importar pólizas" button both go
// through the same robust bulk endpoint.
export function parseMondayPolicyRows(arrRows) {
  const rows = (arrRows || []).filter((r) => (r || []).some((c) => String(c).trim()));
  if (!rows.length) return [];
  let hi = 0, best = -1;
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const s = (rows[i] || []).reduce((a, c) => a + (polImportAutoMap(c) ? 1 : 0), 0);
    if (s > best) { best = s; hi = i; }
  }
  const hdr = (rows[hi] || []).map((c) => String(c).trim());
  const map = polBuildAutoMap(hdr);
  const colOf = (f) => Object.keys(map).find((k) => map[k] === f);
  const out = [];
  for (let i = hi + 1; i < rows.length; i++) {
    const r = rows[i] || [];
    const get = (f) => { const c = colOf(f); return c == null ? '' : String(r[c] ?? '').trim(); };
    const cFirst = get('clientFirstName'), cMiddle = get('clientMiddleName'), cLast = get('clientLastName');
    const clientName = get('clientName') || [cFirst, cMiddle, cLast].filter(Boolean).join(' ').trim();
    const carrier = get('carrier');
    if (!clientName && !carrier) continue;
    const members = get('members');
    out.push({
      clientName,
      firstName: cFirst || undefined,
      middleName: cMiddle || undefined,
      lastName: cLast || undefined,
      email: get('clientEmail') || undefined,
      phone: get('clientPhone') || undefined,
      dob: polSerialToDate(get('clientDob')) || undefined,
      addressStreet: get('addressStreet') || undefined,
      addressCity: get('addressCity') || undefined,
      addressState: get('addressState') || undefined,
      addressZip: get('addressZip') || undefined,
      carrier: carrier || undefined,
      type: polPolicyType(get('policyType')) || undefined,
      memberId: get('memberId') || undefined,
      coverageAmount: get('faceAmount').replace(/[^0-9.]/g, '') || undefined,
      plan: get('plan') || undefined,
      premium: get('premium') || undefined,
      effectiveDate: polSerialToDate(get('effectiveDate')) || undefined,
      renewalDate: polSerialToDate(get('renewalDate')) || undefined,
      leadType: polLeadType(get('leadType')) || undefined,
      agentLabel: get('agentLabel') || undefined,
      status: get('status') || undefined,
      notes: get('notes') || undefined,
      members: members ? members.split(/[,;]+/).map((s) => s.trim()).filter(Boolean) : undefined,
      // Life PolicySummary extras → type_details on the server.
      policyOwnerName: get('policyOwnerName') || undefined,
      termDuration: get('termDuration') || undefined,
      issueDate: polSerialToDate(get('issueDate')) || undefined,
      premiumDueDate: polSerialToDate(get('premiumDueDate')) || undefined,
      billablePremium: get('billablePremium').replace(/[^0-9.]/g, '') || undefined,
      billingFrequency: get('billingFrequency') || undefined,
      billingMethod: get('billingMethod') || undefined,
      agentNumber: get('agentNumber') || undefined,
      agencyNumber: get('agencyNumber') || undefined,
      // Medicare extras → type_details on the server (Lissette 2026-08-09).
      medicareNumber: get('medicareNumber') || undefined,
      partADate: polSerialToDate(get('partADate')) || undefined,
      partBDate: polSerialToDate(get('partBDate')) || undefined,
      companyId: get('companyId') || undefined,
      planYear: get('planYear') || undefined,
      enrollmentCode: get('enrollmentCode') || undefined,
      initialPremium: get('initialPremium').replace(/[^0-9.]/g, '') || undefined,
      currentPremium: get('currentPremium').replace(/[^0-9.]/g, '') || undefined,
      // SOA y HRA (Lissette 2026-09-13). Van aqui A MANO, y no por el volcado
      // generico de POLICY_IMPORT_TEXT_KEYS, porque este camino --el de monday
      // y el de "solo polizas"-- arma su fila campo por campo: una clave que no
      // se nombre aqui no viaja, aunque este en las otras listas.
      soaStatus: get('soaStatus') || undefined,
      medicaidEligibility: normalizarElegibilidadDeMedicaid(get('medicaidEligibility')) || undefined,
      soaSignedDate: polSerialToDate(get('soaSignedDate')) || undefined,
      lisLevel: get('lisLevel') || undefined,
      soaSigned: normalizarFirmaDeSoa(get('soaSigned')) || undefined,
      hraStatus: get('hraStatus') || undefined,
      // Vida, segunda tanda → type_details (Lissette 2026-08-09).
      approvedDate: polSerialToDate(get('approvedDate')) || undefined,
      deliveryPapersDate: polSerialToDate(get('deliveryPapersDate')) || undefined,
      deliveryDate: polSerialToDate(get('deliveryDate')) || undefined,
      placingDate: polSerialToDate(get('placingDate')) || undefined,
      beneficiaryContingent: get('beneficiaryContingent') || undefined,
      riskClass: get('riskClass') || undefined,
      conversionAge: get('conversionAge') || undefined,
      verificationForm: get('verificationForm') || undefined,
      ownerAddress: get('ownerAddress') || undefined,
      commissionPaid: get('commissionPaid') || undefined,
      commissionSplit: get('commissionSplit') || undefined,
      // ACA y P&C → type_details (Lissette 2026-08-09).
      enrollmentPeriod: get('enrollmentPeriod') || undefined,
      marketplaceName: get('marketplaceName') || undefined,
      premiumAnnual: get('premiumAnnual').replace(/[^0-9.]/g, '') || undefined,
      premiumSemiAnnual: get('premiumSemiAnnual').replace(/[^0-9.]/g, '') || undefined,
      generalAgent: get('generalAgent') || undefined,
    });
  }
  return out;
}

export function PolicyImportModal({ lang, clients = [], existingPolicies = [], onClose, onImported, initialFile = null, seats = [] }) {
  const es = lang === 'es';
  const [step, setStep] = useState('file');
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [defaultType, setDefaultType] = useState('aca');
  const [createMissing, setCreateMissing] = useState(true);
  // Per-row client assignment chosen by the user in the preview, keyed by the
  // row's dataRows index. Values: a client id (link to that existing client),
  // '' (create a new client), or '__skip__' (don't import this row). When a row
  // has no entry here we fall back to the auto-detected match.
  const [assign, setAssign] = useState({});
  const [parsing, setParsing] = useState(false);
  const [err, setErr] = useState(null);
  const [prog, setProg] = useState({ done: 0, total: 0, linked: 0, createdClients: 0, createdPolicies: 0, ambiguous: 0, skipped: 0, duplicates: 0, stopped: false });
  // Las filas concretas detras de los contadores "duplicadas" y "omitidas",
  // para poder nombrarlas en la pantalla final en vez de solo contarlas.
  // Cuando el archivo resultó ser un export de Apizeal: se dice en pantalla,
  // porque el mapeo que se ve viene de un lector distinto y conviene saberlo.
  const [apizeal, setApizeal] = useState(null);
  const [dupRows, setDupRows] = useState([]);
  const [unmatchedRows, setUnmatchedRows] = useState([]);
  // Filas que llegaron al servidor pero la base de datos no aceptó.
  const [rejectedRows, setRejectedRows] = useState([]);
  // Duplicadas que el agente decide agregar igual. La deteccion no mira el
  // numero de poliza, asi que un DEPENDIENTE MAYOR con poliza propia del mismo
  // plan que el titular sale como repetida sin serlo (Lissette 2026-08-16).
  // Estado por fila: 'adding' | 'added' | 'ignored'. Se decide una por una,
  // al lado de cada nombre (Lissette 2026-08-16).
  const [dupState, setDupState] = useState({});
  // Valores de la columna de estado que el servidor no supo emparejar. Se
  // muestran al terminar para que la agente complete su catálogo y reimporte,
  // en vez de descubrir la hoja entera en "activa" (Lissette 2026-08-13).
  const [unknownStatuses, setUnknownStatuses] = useState([]);
  const fileRef = useRef(null);

  const detectHeaderRow = (rows) => {
    let best = 0, bestScore = -1;
    for (let i = 0; i < Math.min(rows.length, 15); i++) {
      const score = (rows[i] || []).reduce((s, c) => s + (polImportAutoMap(c) ? 1 : 0), 0);
      if (score > bestScore) { bestScore = score; best = i; }
    }
    return best;
  };

  const handleFile = async (file) => {
    if (!file) return;
    setErr(null); setParsing(true);
    try {
      const name = (file.name || '').toLowerCase();
      let rows;
      if (name.endsWith('.csv')) rows = polParseCsv(await file.text());
      else {
        const buf = new Uint8Array(await file.arrayBuffer());
        const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs');
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });
      }
      rows = (rows || []).filter((r) => (r || []).some((c) => String(c).trim()));
      if (!rows.length) { setErr(es ? 'El archivo está vacío.' : 'The file is empty.'); return; }

      // ── ¿ES UN EXPORT DE APIZEAL? ──────────────────────────────────────────
      //
      // Lissette, 2026-09-14: *"yo hablo de Solo Pólizas, que también reconozca
      // [el Excel de Apizeal], y que llene cónyuge y dependientes cuando es un
      // cliente nuevo"*.
      //
      // Hay que detectarlo, no basta con mapear a mano, porque en ese archivo
      // LAS CABECERAS SE REPITEN: "FIRST NAME" sale seis veces, una por miembro
      // del hogar. Un mapeador por nombre solo puede quedarse con una de las
      // seis, así que la familia entera era inalcanzable — no es que se mapeara
      // mal, es que no había forma de pedirla.
      //
      // `parseApizeal` lo lee POR POSICIÓN y devuelve filas con nuestras claves
      // (`spouseSsn`, `dep3Dob`…) más las columnas crudas con su nombre del
      // archivo, prefijadas "M1 · ", "M2 · " para que se distingan. Es el mismo
      // lector que usa Clientes + Pólizas: uno solo, no dos.
      const apz = isApizealWorkbook(rows);
      if (apz) {
        const leido = parseApizeal(rows, apz.headerRow, MAX_DEPENDIENTES_AL_IMPORTAR);
        if (leido.rows.length) {
          const crudas = new Set(leido.columnas || []);
          // Lo TRADUCIDO primero (sale ya mapeado), y detrás las columnas del
          // archivo con su nombre, disponibles para mapear a mano. Sin la
          // segunda mitad, una columna que este lector no sepa colocar no
          // aparecería por ningún lado y no habría forma de rescatarla.
          const traducidas = [...new Set(leido.rows.flatMap((r) => Object.keys(r)))]
            .filter((k) => !crudas.has(k));
          const hdrA = [...traducidas, ...(leido.columnas || [])];
          const mapA = {};
          hdrA.forEach((k, i) => { const campo = campoParaClaveDeApizeal(k); if (campo) mapA[i] = campo; });
          setHeaders(hdrA);
          setDataRows(leido.rows.map((r) => hdrA.map((k) => r[k] ?? '')));
          setMapping(mapA);
          setApizeal({ filas: leido.rows.length, miembros: leido.members, fuera: leido.skippedMembers, traducidas });
          setStep('map');
          return;
        }
      }
      setApizeal(null);

      const hi = detectHeaderRow(rows);
      const hdr = (rows[hi] || []).map((c) => String(c).trim());
      const map = {};
      Object.assign(map, polBuildAutoMap(hdr, rows.slice(hi + 1)));
      setHeaders(hdr); setDataRows(rows.slice(hi + 1)); setMapping(map); setStep('map');
    } catch (e) { setErr(e.message || String(e)); }
    finally { setParsing(false); }
  };

  // When opened with a file already chosen (dragged/selected on the import
  // screen), process it straight away so the user lands on the mapping step
  // instead of having to pick the file a second time.
  useEffect(() => { if (initialFile) handleFile(initialFile); /* eslint-disable-next-line */ }, []);

  // Equipo de la agencia, solo para resolver el NPN por nombre. Se pide aqui —y
  // no por props— porque ni esta ventana ni la pantalla que la abre usan el
  // equipo para nada mas, y arrastrarlo por dos capas para un solo dato es peor.
  // Si falla, el NPN simplemente no se autocompleta.
  const [teamUsers, setTeamUsers] = useState([]);
  useEffect(() => {
    let alive = true;
    listUsers().then(({ users: us }) => { if (alive) setTeamUsers(us || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const colOf = (field) => Object.keys(mapping).find((k) => mapping[k] === field);
  // Todas las columnas asignadas a un campo (para los combinables). Cada trozo
  // lleva delante el nombre de su columna, que si no la nota junta no se
  // entiende: "REFERIDO: Maria Lopez · COMMENTS: pago en efectivo".
  const colsOf = (field) => Object.keys(mapping).filter((k) => mapping[k] === field).sort((a, b) => Number(a) - Number(b));
  const joinCols = (fila, field) => colsOf(field)
    .map((c) => {
      const v = String(fila[c] ?? '').trim();
      if (!v) return '';
      const cab = String(headers[c] || '').trim();
      return cab ? `${cab}: ${v}` : v;
    })
    .filter(Boolean)
    .join(' \u00b7 ');
  // Nombre de colaborador -> NPN, para rellenar el Código Productor solo cuando
  // el agente que trae el Excel es uno de los colaboradores ya cargados en la
  // agencia. Es exactamente lo que hace la ficha al elegirlo en el desplegable
  // (Combobox de "Agente en póliza" -> set producerCode), y sin esto la agente
  // tenía que teclear el NPN póliza por póliza despues de importar
  // (Lissette 2026-08-13). Se compara sin acentos, sin mayusculas y con los
  // espacios colapsados, porque el nombre del Excel casi nunca viene idéntico.
  // Se reutiliza polNorm, el mismo normalizador con el que este importador ya
  // empareja nombres de clientes: sin acentos, sin may\u00fasculas y con los
  // espacios colapsados. Un normalizador propio aqu\u00ed acabar\u00eda divergiendo del
  // otro y emparejando distinto en la misma pantalla.
  const npnByCollaborator = useMemo(() => {
    const m = {};
    // El EQUIPO de la agencia primero (la dueña incluida): una cartera vendida
    // por ella entraba sin NPN porque aquí solo se miraban los colaboradores
    // (Lissette 2026-08-15). Los colaboradores van después para que, ante el
    // mismo nombre, mande la ficha del colaborador — que es la que la agente
    // mantiene a mano.
    for (const u of (teamUsers || [])) {
      const nm = u?.name || u?.displayName || '';
      if (nm && u?.npn) m[polNorm(nm)] = String(u.npn);
    }
    for (const co of getCollaboratorsPref()) {
      const label = collaboratorLabel(co);
      if (label && co?.npn) m[polNorm(label)] = String(co.npn);
    }
    return m;
  }, [teamUsers]);
  const npnForAgent = (name) => {
    const k = polNorm(name || '');
    return k ? (npnByCollaborator[k] || '') : '';
  };
  /**
   * LOS DATOS DEL CLIENTE QUE ESTA IMPORTACIÓN VA A CREAR.
   *
   * Lissette, 2026-09-14: *"cuando existen nuevos clientes necesito mapear para
   * esos nuevos clientes toda la información como si fuera cliente: SSN,
   * income, agente asignado, handle… y no me da la posibilidad"*.
   *
   * Se manda SIEMPRE, y el servidor decide: si la fila casa con un cliente que
   * ya existe, ni los mira. Esa es la otra mitad de lo que pidió (*"para esos
   * nuevos, NO los actuales"*), y por eso la regla vive en el servidor y no
   * aquí: la pantalla no sabe si una fila va a casar o no hasta que el
   * emparejador del servidor la mira.
   *
   * Los valores se traducen AQUÍ, con los mismos mapas que usa el importador de
   * clientes: una hoja trae "Casado", "Married", "EAD" o "Permiso de trabajo", y
   * guardar el texto crudo deja la casilla de la ficha en blanco porque no
   * coincide con ninguna opción del desplegable — un dato que se guarda y no se
   * ve es peor que uno que falta.
   */
  const datosDelClienteNuevo = (get) => {
    const txt = (k) => get(k) || undefined;
    // El SSN se manda solo si de verdad son nueve dígitos: media cifra o un
    // "pendiente" escrito en la casilla no es un SSN, y guardarlo dejaría la
    // ficha diciendo que lo tiene.
    const ssnDigitos = get('clientSsn').replace(/\D/g, '');
    const hogar = parseInt(get('clientHouseholdSize').replace(/\D/g, ''), 10);
    const etiquetas = get('clientTags')
      ? get('clientTags').split(/[,;|]+/).map((t) => t.trim()).filter(Boolean).slice(0, 30)
      : undefined;
    const atiende = get('clientAttendedBy')
      // Resuelve el nombre contra los ASIENTOS de la agencia; si ninguno
      // responde, devuelve `attendedByName` y la ficha lo enseña en gris. Es la
      // misma función que usa el importador de clientes.
      ? atiendeDesdeElArchivo(get('clientAttendedBy'), seats)
      : {};
    return {
      ...(ssnDigitos.length === 9 ? { ssn: ssnDigitos } : {}),
      annualIncome: txt('clientAnnualIncome'),
      assignedAgent: txt('clientAssignedAgent'),
      ...atiende,
      clientTags: etiquetas,
      clientStatus: STATUS_MAP[get('clientStatus').toLowerCase()] || undefined,
      preferredLanguage: normalizeEnum(get('clientLanguage'), LANG_MAP) || undefined,
      employer: txt('clientEmployer'),
      householdSize: Number.isFinite(hogar) && hogar > 0 && hogar <= 50 ? hogar : undefined,
      maritalStatus: normalizeEnum(get('clientMaritalStatus'), MARITAL_MAP) || undefined,
      gender: normalizeEnum(get('clientGender'), GENDER_MAP) || undefined,
      immigrationStatus: normalizeEnum(get('clientImmigrationStatus'), IMMIGRATION_MAP) || undefined,
      county: txt('clientCounty'),
      phoneSecondary: txt('clientPhoneSecondary'),
      placeOfBirth: txt('clientPlaceOfBirth'),
      clientSource: txt('clientSource'),
    };
  };

  /**
   * EL HOGAR DE UNA FICHA NUEVA: cónyuge y dependientes.
   *
   * Lissette, 2026-09-14: *"que llene cónyuge y dependientes cuando es un
   * cliente nuevo"*.
   *
   * Como los datos del titular, se manda SIEMPRE y el servidor decide: si la
   * fila casa con un cliente que ya existe, ni lo mira. Nadie pierde su familia
   * porque alguien reimporte una hoja.
   *
   * Las fechas pasan por `polSerialToDate` igual que las de la póliza: en un
   * .xlsx una celda de fecha llega como el número de serie de Excel (45678), no
   * como "12/25/1990".
   */
  const hogarDelClienteNuevo = (get) => {
    const t = (k) => get(k) || undefined;
    const conyuge = {
      spouseFirstName: t('spouseFirstName'),
      spouseMiddleName: t('spouseMiddleName'),
      spouseLastName: t('spouseLastName'),
      spouseDob: polSerialToDate(get('spouseDob')) || undefined,
      spouseSsn: get('spouseSsn').replace(/\D/g, '') || undefined,
      spouseEmail: t('spouseEmail'),
      spousePhone: t('spousePhone'),
      spouseGender: normalizeEnum(get('spouseGender'), GENDER_MAP) || undefined,
      spouseIncome: t('spouseIncome'),
      spouseImmigrationStatus: normalizeEnum(get('spouseImmigrationStatus'), IMMIGRATION_MAP) || undefined,
      // La palabra con la que el archivo nombró al cónyuge, TAL CUAL: el
      // servidor la usa para escribir el estado civil (casado/a o unión libre).
      // Sin estado civil el cónyuge se guarda y no lo enseña ninguna pantalla.
      spouseRelation: t('spouseRelation'),
    };
    // SIN NOMBRE NO HAY CÓNYUGE. El servidor guarda al cónyuge cuando tiene
    // nombre o apellido; mandar solo su SSN dejaría medio dato colgando de una
    // persona que no existe en la ficha.
    const hayConyuge = !!(conyuge.spouseFirstName || conyuge.spouseLastName);

    const dependientes = [];
    for (let n = 1; n <= MAX_DEPENDIENTES_AL_IMPORTAR; n++) {
      const nombre = get(`dep${n}FirstName`);
      const apellido = get(`dep${n}LastName`);
      const completo = get(`dep${n}FullName`);
      // Mismo criterio: una persona sin nombre no es una persona. Si la hoja
      // solo trae el nombre entero, se parte por el primer espacio -- sin IA,
      // que es un paso más que se puede caer y aquí no hace falta: el archivo
      // de Apizeal ya trae las dos columnas separadas.
      let fn = nombre, ln = apellido;
      if (!fn && !ln && completo) {
        const partes = completo.trim().split(/\s+/);
        fn = partes[0] || '';
        ln = partes.slice(1).join(' ');
      }
      if (!fn && !ln) continue;
      dependientes.push({
        firstName: fn || undefined,
        middleName: get(`dep${n}MiddleName`) || undefined,
        lastName: ln || undefined,
        dob: polSerialToDate(get(`dep${n}Dob`)) || undefined,
        ssn: get(`dep${n}Ssn`).replace(/\D/g, '') || undefined,
        relationship: get(`dep${n}Relation`) || undefined,
        gender: normalizeEnum(get(`dep${n}Gender`), GENDER_MAP) || undefined,
        income: get(`dep${n}Income`) || undefined,
        immigrationStatus: normalizeEnum(get(`dep${n}Immigration`), IMMIGRATION_MAP) || undefined,
      });
    }

    return {
      ...(hayConyuge ? conyuge : {}),
      ...(dependientes.length ? { dependents: dependientes } : {}),
    };
  };

  const buildRows = () => {
    const out = [];
    for (let idx = 0; idx < dataRows.length; idx++) {
      const r = dataRows[idx];
      const get = (f) => { const c = colOf(f); return c == null ? '' : String(r[c] ?? '').trim(); };
      // Name: prefer the full-name column; else join the separate parts.
      const cFirst = get('clientFirstName'), cMiddle = get('clientMiddleName'), cLast = get('clientLastName');
      const clientName = get('clientName') || [cFirst, cMiddle, cLast].filter(Boolean).join(' ').trim();
      const carrier = get('carrier');
      if (!clientName && !carrier) continue;
      const match = polMatchClient({ name: clientName, email: get('clientEmail'), phone: get('clientPhone'), dob: get('clientDob') }, clients);
      const members = get('members');
      out.push({
        idx, clientName, match,
        policy: {
          type: polPolicyType(get('policyType')) || defaultType || 'aca',
          carrier: carrier || 'N/A',
          plan: get('plan'),
          memberId: get('memberId'),
          monthlyPremium: get('premium').replace(/[^0-9.]/g, ''),
          effectiveDate: polSerialToDate(get('effectiveDate')),
          renewalDate: polSerialToDate(get('renewalDate')),
          leadType: polLeadType(get('leadType')),
          agentLabel: get('agentLabel'),
          processedBy: '',
          processingDate: '',
          status: polStatus(get('status')),
          notes: joinCols(r, 'notes'),
          policyInsured: [],
          policyInsuredExtra: members ? members.split(/[,;]+/).map((s) => s.trim()).filter(Boolean) : [],
        },
        // Exact payload the server import endpoint expects for this row.
        server: {
          ...datosDelClienteNuevo(get),
          ...hogarDelClienteNuevo(get),
          clientName,
          firstName: cFirst || undefined,
          middleName: cMiddle || undefined,
          lastName: cLast || undefined,
          email: get('clientEmail') || undefined,
          phone: get('clientPhone') || undefined,
          dob: polSerialToDate(get('clientDob')) || undefined,
          addressStreet: get('addressStreet') || undefined,
          addressCity: get('addressCity') || undefined,
          addressState: get('addressState') || undefined,
          addressZip: get('addressZip') || undefined,
          carrier: carrier || undefined,
          type: polPolicyType(get('policyType')) || undefined,
          memberId: get('memberId') || undefined,
          coverageAmount: get('faceAmount').replace(/[^0-9.]/g, '') || undefined,
          plan: get('plan') || undefined,
          premium: get('premium') || undefined,
          effectiveDate: polSerialToDate(get('effectiveDate')) || undefined,
          renewalDate: polSerialToDate(get('renewalDate')) || undefined,
          leadType: polLeadType(get('leadType')) || undefined,
          agentLabel: get('agentLabel') || undefined,
          status: get('status') || undefined,
          notes: joinCols(r, 'notes') || undefined,
          policyNote: joinCols(r, 'policyNote') || undefined,
          members: members ? members.split(/[,;]+/).map((s) => s.trim()).filter(Boolean) : undefined,
          // Life PolicySummary extras → type_details on the server.
          policyOwnerName: get('policyOwnerName') || undefined,
          termDuration: get('termDuration') || undefined,
          issueDate: polSerialToDate(get('issueDate')) || undefined,
          premiumDueDate: polSerialToDate(get('premiumDueDate')) || undefined,
          billablePremium: get('billablePremium').replace(/[^0-9.]/g, '') || undefined,
          billingFrequency: get('billingFrequency') || undefined,
          billingMethod: get('billingMethod') || undefined,
          agentNumber: get('agentNumber') || undefined,
          agencyNumber: get('agencyNumber') || undefined,
          // Medicare extras → type_details on the server (Lissette 2026-08-09).
          medicareNumber: get('medicareNumber') || undefined,
          partADate: polSerialToDate(get('partADate')) || undefined,
          partBDate: polSerialToDate(get('partBDate')) || undefined,
          companyId: get('companyId') || undefined,
          planYear: get('planYear') || undefined,
          enrollmentCode: get('enrollmentCode') || undefined,
          initialPremium: get('initialPremium').replace(/[^0-9.]/g, '') || undefined,
          currentPremium: get('currentPremium').replace(/[^0-9.]/g, '') || undefined,
          // Vida, segunda tanda → type_details (Lissette 2026-08-09).
          approvedDate: polSerialToDate(get('approvedDate')) || undefined,
          deliveryPapersDate: polSerialToDate(get('deliveryPapersDate')) || undefined,
          deliveryDate: polSerialToDate(get('deliveryDate')) || undefined,
          placingDate: polSerialToDate(get('placingDate')) || undefined,
          beneficiaryContingent: get('beneficiaryContingent') || undefined,
          riskClass: get('riskClass') || undefined,
          conversionAge: get('conversionAge') || undefined,
          verificationForm: get('verificationForm') || undefined,
          ownerAddress: get('ownerAddress') || undefined,
          commissionPaid: get('commissionPaid') || undefined,
          commissionSplit: get('commissionSplit') || undefined,
          // ACA y P&C → type_details (Lissette 2026-08-09).
          enrollmentPeriod: get('enrollmentPeriod') || undefined,
          marketplaceName: get('marketplaceName') || undefined,
          premiumAnnual: get('premiumAnnual').replace(/[^0-9.]/g, '') || undefined,
          premiumSemiAnnual: get('premiumSemiAnnual').replace(/[^0-9.]/g, '') || undefined,
          generalAgent: get('generalAgent') || undefined,
          // Paridad con la tarjeta (2026-08-13). El servidor decide cuáles van a
          // columna propia y cuáles a type_details; aquí solo se limpian.
          ...Object.fromEntries(
            POLICY_IMPORT_DATE_KEYS
              .map((k) => [k, polSerialToDate(get(k)) || undefined])
              .filter(([, v]) => v !== undefined),
          ),
          ...Object.fromEntries(
            POLICY_IMPORT_TEXT_KEYS
              .map((k) => [k, get(k) || undefined])
              .filter(([, v]) => v !== undefined),
          ),
          // El Código Productor de la hoja manda; solo si NO viene se deduce del
          // colaborador cuyo nombre coincide con el agente de la póliza. Va
          // DESPUÉS del volcado generico para poder sobrescribir ese undefined.
          producerCode: get('producerCode') || npnForAgent(get('agentLabel')) || undefined,
          tags: (() => {
            const raw = get('policyTags');
            if (!raw) return undefined;
            const out = [];
            for (const part of String(raw).split(/[,;|]/)) {
              const v = part.trim().slice(0, 40);
              if (v && !out.some((x) => x.toLowerCase() === v.toLowerCase())) out.push(v);
            }
            return out.length ? out : undefined;
          })(),
        },
      });
    }
    return out;
  };

  const built = step === 'map' ? buildRows() : [];
  // Effective per-row decision: the user's explicit choice if any, else the
  // auto-match (its client id when matched, '' = create new when not).
  // Default: matched → that client; AMBIGUOUS → '__skip__' (must choose — never
  // auto-create a duplicate for a name that matched several clients); 'none' →
  // '' (create new). The user can override any row.
  const effAssign = (b) => (assign[b.idx] !== undefined ? assign[b.idx] : (b.match.status === 'matched' ? b.match.client.id : (b.match.status === 'ambiguous' ? '__ambig__' : '')));
  const SENTINELS = new Set(['__skip__', '__ambig__']);
  const toLink = built.filter((b) => { const a = effAssign(b); return a && !SENTINELS.has(a); });
  const toCreate = built.filter((b) => effAssign(b) === '');
  const toSkip = built.filter((b) => effAssign(b) === '__skip__');
  const toAmbig = built.filter((b) => effAssign(b) === '__ambig__');

  // Raw rows for the server (the backend matches/creates in bulk, one request
  // per chunk — so hundreds of policies don't burn the 30/hour cap). Honors the
  // user's per-row assignment: a chosen client id links DIRECTLY (server skips
  // its own matching for that row); '' creates a new client; '__skip__' drops it.
  // Agrega UNA duplicada concreta: reenvia esa fila con allowDuplicate, que
  // salta la deteccion solo en ella. Una por una y al lado de su nombre, que es
  // como se decide mirando la lista.
  const addOneDuplicate = async (d) => {
    if (!d.row || dupState[d.uid]) return;
    setDupState((p) => ({ ...p, [d.uid]: 'adding' }));
    try {
      const res = await apiImportPolicies({
        defaultType: defaultType || 'aca', createMissing,
        rows: [{ ...d.row, allowDuplicate: true }],
      });
      const added = res.createdPolicies || 0;
      setDupState((p) => ({ ...p, [d.uid]: added ? 'added' : 'ignored' }));
      if (added) {
        setProg((p) => ({ ...p, createdPolicies: p.createdPolicies + added, duplicates: Math.max(0, p.duplicates - added) }));
        if (onImported) onImported();
      }
    } catch (e2) {
      // Se devuelve a su estado para poder reintentar, y el error se ve arriba.
      setDupState((p) => { const n = { ...p }; delete n[d.uid]; return n; });
      setErr((es ? 'No se pudo agregar: ' : 'Could not add it: ') + (e2.message || String(e2)));
    }
  };

  const buildServerRows = () => {
    const out = [];
    for (const b of built) {
      const a = effAssign(b);
      if (SENTINELS.has(a)) continue; // skip + unresolved-ambiguous never import
      out.push({ ...b.server, clientId: (a && a !== '') ? a : undefined });
    }
    return out;
  };

  const runImport = async () => {
    const serverRows = buildServerRows();
    if (!serverRows.length) { setErr(es ? 'No hay filas para importar. Revisa el mapeo.' : 'No rows to import. Check the mapping.'); return; }
    setErr(null); setStep('done');
    const CHUNK = 150;
    let linked = 0, createdClients = 0, createdPolicies = 0, ambiguous = 0, skipped = 0, duplicates = 0, failed = 0, stopped = false, firstErr = '';
    // Notas escritas sobre polizas que YA estaban y no tenian ninguna: sin este
    // contador, reimportar con las notas mapeadas parecia no hacer nada.
    let notesFilled = 0;
    let policyNotesAdded = 0;
    // Cuantas filas SALEN de este navegador con nota escrita. Es el corte que
    // separa "el texto nunca se leyo de la hoja" de "se envio y el servidor no
    // lo guardo". Sin este numero no habia forma de saber en cual de los dos
    // lados estaba el problema (Lissette 2026-08-16).
    //
    // Cuenta LOS DOS destinos. Contaba solo `notes`, asi que mapear a las notas
    // de la TARJETA daba "0 - la columna venia vacia o sin mapear" mientras se
    // enviaban 121 filas con nota: el contador acusaba al mapeo de un fallo que
    // estaba en otro sitio y mando a buscar donde no era.
    const rowsWithNotes = serverRows.filter((r) => String(r.notes || '').trim() || String(r.policyNote || '').trim()).length;
    const badStatuses = new Map();
    // QUE filas se cayeron, no solo cuantas. Un "6 duplicadas, omitidas" sin
    // mas obliga a comparar la hoja con el CRM a mano (Lissette 2026-08-15:
    // "no muestra cual es... no la conseguimos"). Se acumulan entre lotes: la
    // hoja se envia de 150 en 150.
    const dupRows = []; const unmatched = [];
    // Filas que la base de datos rechazo una a una. Van con las otras dos listas
    // porque son lo mismo: filas que NO entraron y hay que poder mirar.
    const rejected = [];
    let datesDropped = 0;
    setUnknownStatuses([]); setDupRows([]); setUnmatchedRows([]); setDupState({}); setRejectedRows([]);
    const total = serverRows.length;
    setProg({ done: 0, total, linked, createdClients, createdPolicies, ambiguous, skipped, duplicates, failed, stopped, notesFilled, policyNotesAdded, rowsWithNotes, datesDropped });
    for (let i = 0; i < serverRows.length; i += CHUNK) {
      const chunk = serverRows.slice(i, i + CHUNK);
      try {
        const res = await apiImportPolicies({ defaultType: defaultType || 'aca', createMissing, rows: chunk });
        linked += res.linked || 0; createdClients += res.createdClients || 0;
        createdPolicies += res.createdPolicies || 0; ambiguous += res.ambiguous || 0; skipped += res.skipped || 0;
        duplicates += res.duplicates || 0;
        notesFilled += res.notesFilled || 0;
        policyNotesAdded += res.policyNotesAdded || 0;
        datesDropped += res.datesDropped || 0;
        for (const rj of (res.rejectedRows || [])) rejected.push(rj);
        // Se guarda la FILA ORIGINAL junto a la descripcion: si se decide
        // agregarla, hay que reenviar la fila entera, no su resumen. rowIndex
        // es la posicion dentro de ESTE lote, de ahi el desplazamiento.
        for (const d of (res.duplicateRows || [])) {
          // uid propio: la lista se PARTE en dos grupos al pintarla, asi que el
          // indice dentro del grupo no coincide con el de dupRows. Guardar el
          // estado por indice mezclaba filas; por uid no puede pasar.
          dupRows.push({ ...d, row: chunk[d.rowIndex], uid: `${i}_${d.rowIndex}` });
        }
        for (const u of (res.unmatchedRows || [])) unmatched.push(u);
        // Estados que el servidor no reconoció, acumulados entre lotes: la hoja
        // se envía de 150 en 150 y el mismo valor puede repetirse en varios.
        for (const s of (res.statusesNotMatched || [])) {
          if (!s || !s.value) continue;
          badStatuses.set(s.value, (badStatuses.get(s.value) || 0) + (s.rows || 0));
        }
      } catch (e2) {
        const msg = e2.message || String(e2);
        if (/rate|429|too many|limit|límite/i.test(msg)) { stopped = true; setProg({ done: Math.min(i + CHUNK, total), total, linked, createdClients, createdPolicies, ambiguous, skipped, duplicates, failed, stopped, notesFilled }); break; }
        // A failed chunk is a REQUEST error (validation / server), NOT a "no
        // name" skip. Track it apart and surface the real message so we can see
        // exactly which field the server rejected.
        failed += chunk.length;
        if (!firstErr) firstErr = msg;
      }
      setProg({ done: Math.min(i + CHUNK, total), total, linked, createdClients, createdPolicies, ambiguous, skipped, duplicates, failed, stopped, notesFilled, policyNotesAdded, rowsWithNotes, datesDropped });
    }
    if (firstErr) setErr((es ? 'El servidor rechazó el envío: ' : 'Server rejected the import: ') + firstErr);
    setUnknownStatuses([...badStatuses.entries()].sort((a, b) => b[1] - a[1]).map(([value, rows]) => ({ value, rows })));
    setDupRows(dupRows); setUnmatchedRows(unmatched); setRejectedRows(rejected);
    if (onImported) onImported();
  };

  const sel = { background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 7, color: 'var(--c-text,#c8dff0)', padding: '.3rem .4rem', fontSize: '.78rem', width: '100%' };
  // El desplegable de tipo por defecto ofrecía SOLO los de salud/vida
  // (`!p.pc`), así que auto, hogar, comercial, inquilinos y sombrilla no se
  // podían elegir aunque el servidor lleva mapeándolos desde siempre
  // (POLICY_TYPE_IMPORT_MAP). Quien importaba una cartera de autos no tenía
  // forma de decirlo (Lissette 2026-08-17). Van en dos grupos porque son dos
  // ramos distintos y la lista junta se hace larga.
  const importSaludTypes = POLICY_TYPES.filter((p) => !p.pc);
  const importPCTypes = POLICY_TYPES.filter((p) => p.pc);

  return (
    <Modal title={es ? 'Importar pólizas (vincular a clientes)' : 'Import policies (link to clients)'} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
        {err && <div style={{ fontSize: '.8rem', color: 'var(--c-red,#ff3d6b)', background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 8, padding: '.6rem .8rem' }}>{err}</div>}

        {step === 'file' && (
          <>
            <p style={{ fontSize: '.82rem', color: 'var(--c-text2,#7aa3c0)', margin: 0, lineHeight: 1.5 }}>
              {es
                ? 'Sube tu Excel/CSV de pólizas. Cada fila se vincula a un cliente que ya exista (por nombre, y si hace falta email/teléfono/fecha de nacimiento). Las pólizas se crean en la ficha del cliente.'
                : 'Upload your policies Excel/CSV. Each row links to an existing client (by name, with email/phone/DOB fallback). Policies are created on the client record.'}
            </p>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={(e) => handleFile(e.target.files?.[0])} />
            <button type="button" disabled={parsing} onClick={() => fileRef.current?.click()}
              style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 9, padding: '.7rem 1rem', color: '#0a1628', fontWeight: 800, fontSize: '.85rem', cursor: parsing ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Upload size={16} /> {parsing ? (es ? 'Leyendo…' : 'Reading…') : (es ? 'Elegir archivo' : 'Choose file')}
            </button>
          </>
        )}

        {step === 'map' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.7rem' }}>
              <div>
                <label style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>{es ? 'Tipo por defecto (si no se detecta)' : 'Default type (when not detected)'}</label>
                <select value={defaultType} onChange={(e) => setDefaultType(e.target.value)} style={sel}>
                  <optgroup label={es ? 'Salud y Vida' : 'Health & Life'}>
                    {importSaludTypes.map((pt) => <option key={pt.id} value={pt.id}>{lang === 'es' ? pt.labelEs : pt.label}</option>)}
                  </optgroup>
                  {importPCTypes.length > 0 && (
                    <optgroup label={es ? 'Propiedad y Casualidad' : 'Property & Casualty'}>
                      {importPCTypes.map((pt) => <option key={pt.id} value={pt.id}>{lang === 'es' ? pt.labelEs : pt.label}</option>)}
                    </optgroup>
                  )}
                </select>
                <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', marginTop: 3, lineHeight: 1.3 }}>{es ? 'Cada fila usa su columna "Tipo de póliza" si está mapeada; este es solo el respaldo.' : 'Each row uses its mapped "Policy type" column; this is only the fallback.'}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', flexWrap: 'wrap', gap: '.1rem .5rem' }}>
                <span style={{ color: 'var(--c-green,#00ff88)' }}>✅ {toLink.length} {es ? 'a cliente existente' : 'to existing client'}</span>
                <span style={{ color: 'var(--c-amber,#ffb700)' }}>➕ {toCreate.length} {es ? 'cliente nuevo' : 'new client'}</span>
                {toAmbig.length > 0 && <span style={{ color: 'var(--c-red,#ff3d6b)', fontWeight: 700 }}>⚠️ {toAmbig.length} {es ? 'por elegir' : 'to choose'}</span>}
                {toSkip.length > 0 && <span style={{ color: 'var(--c-text3,#45688a)' }}>⏭️ {toSkip.length} {es ? 'sin importar' : 'skipped'}</span>}
              </div>
            </div>
            {/* SE RECONOCIÓ SOLO. Decirlo importa: el mapeo que se ve debajo
                no sale del detector de siempre sino del lector de Apizeal, que
                lee por POSICIÓN — es la única forma de distinguir las seis
                columnas llamadas "FIRST NAME", una por miembro del hogar. Y si
                una familia trae más gente de la que caben, se dice con su
                número en vez de perderla en silencio. */}
            {apizeal && (
              <div style={{ background: 'rgba(0,212,255,.08)', border: '1px solid rgba(0,212,255,.3)', borderRadius: 9, padding: '.5rem .7rem', fontSize: '.74rem', color: 'var(--c-cyan,#00d4ff)', lineHeight: 1.45 }}>
                {es
                  ? `📗 Reconocido como export de Apizeal: ${apizeal.filas} pólizas, con hasta ${apizeal.miembros} personas por hogar. El cónyuge y los dependientes ya vienen mapeados y se guardarán en las fichas que se CREEN (a las que ya existen no se les toca nada).`
                  : `📗 Recognised as an Apizeal export: ${apizeal.filas} policies, up to ${apizeal.miembros} people per household. Spouse and dependents come pre-mapped and are saved on the clients this import CREATES (existing ones are left untouched).`}
                {apizeal.fuera > 0 && (
                  <div style={{ color: 'var(--c-amber,#ffb700)', marginTop: 3 }}>
                    {es
                      ? `⚠️ ${apizeal.fuera} persona(s) del hogar no caben (el tope es ${MAX_DEPENDIENTES_AL_IMPORTAR} dependientes) y no se importarán.`
                      : `⚠️ ${apizeal.fuera} household member(s) don't fit (the cap is ${MAX_DEPENDIENTES_AL_IMPORTAR} dependents) and won't be imported.`}
                  </div>
                )}
              </div>
            )}
            <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Mapea cada columna (deja "Nombre del cliente" para el match):' : 'Map each column (keep "Client name" for matching):'}</div>
            {/* 200px enseñaban CINCO columnas de las catorce que trae una hoja
                normal, con el modal medio vacío por debajo: había que adivinar
                que aquello se desplazaba. Se sube a 340 (Lissette 2026-09-14,
                al pedir poder mapear los datos del cliente). Sigue habiendo
                barra, que es lo correcto — una hoja puede traer cincuenta. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem', maxHeight: 340, overflowY: 'auto' }}>
              {headers.map((h, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem', alignItems: 'center' }}>
                  {/* El nombre de la columna y UN EJEMPLO de lo que trae. Sin el
                      ejemplo no habia forma de ver que una columna venia vacia:
                      se mapeaba, se importaba y no llegaba nada, sin ningun
                      aviso (Lissette 2026-08-16, las notas). Se busca el primer
                      valor no vacio, no el de la primera fila, que suele serlo. */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={h}>{(apizeal?.traducidas?.includes(h) ? etiquetaDeClaveApizeal(h, es ? 'es' : 'en') : h) || `(col ${i + 1})`}</div>
                    {(() => {
                      const muestra = (dataRows || []).slice(0, 60).map((r) => String((r || [])[i] ?? '').trim()).find(Boolean);
                      return muestra
                        ? <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={muestra}>{muestra.slice(0, 60)}</div>
                        : <div style={{ fontSize: '.66rem', color: 'var(--c-amber,#ffb700)' }}>{es ? 'columna vacía' : 'empty column'}</div>;
                    })()}
                  </div>
                  {/* Un campo solo puede venir de UNA columna. Al elegirlo aqui
                      se quita de cualquier otra: si quedaban dos columnas con el
                      mismo campo, el importador leia la de indice MAS BAJO --
                      normalmente la que el detector automatico habia asignado
                      sola, a menudo vacia-- y la elegida a mano se ignoraba en
                      silencio. Asi se mapeaban las notas y llegaban vacias
                      (Lissette 2026-08-16: "salen vacias y el me confirmo que si
                      tiene notas"). "Ignorar" (valor vacio) puede repetirse. */}
                  <select value={mapping[i] || ''} onChange={(e) => setMapping((m) => {
                    const v = e.target.value;
                    const next = { ...m, [i]: v };
                    // Las NOTAS se pueden alimentar de VARIAS columnas a la vez
                    // (p. ej. "REFERIDO" + "COMMENTS"): se juntan en una sola
                    // nota, cada trozo con el nombre de su columna delante. El
                    // resto de campos sigue siendo de una sola columna.
                    if (v && !POLICY_COMBINABLE_FIELDS.has(v)) {
                      for (const k of Object.keys(next)) if (k !== String(i) && next[k] === v) next[k] = '';
                    }
                    return next;
                  })} style={sel}>
                    {POLICY_IMPORT_FIELDS.map((f) => <option key={f.key} value={f.key}>{es ? f.es : f.en}</option>)}
                  </select>
                </div>
              ))}
            </div>
            {!colOf('clientName') && <div style={{ fontSize: '.74rem', color: 'var(--c-amber,#ffb700)' }}>⚠️ {es ? 'Asigna una columna a "Nombre del cliente" para poder vincular.' : 'Map a column to "Client name" to link policies.'}</div>}
            {/* Per-row review: choose the client for EACH policy (assign an existing
                client, create a new one, or skip the row). */}
            {built.length > 0 && (
              <>
                <div style={{ fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
                  <span>{es ? 'Revisa cada póliza y elige el cliente:' : 'Review each policy and choose the client:'}</span>
                  <span style={{ display: 'flex', gap: '.4rem' }}>
                    <button type="button" onClick={() => setAssign({})} style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 7, padding: '.25rem .55rem', color: 'var(--c-text2,#7aa3c0)', fontSize: '.7rem', fontWeight: 700, cursor: 'pointer' }}>{es ? 'Auto' : 'Auto'}</button>
                    <button type="button" onClick={() => { const n = {}; built.forEach((b) => { if (b.match.status !== 'matched') n[b.idx] = '__skip__'; }); setAssign((s) => ({ ...s, ...n })); }} style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 7, padding: '.25rem .55rem', color: 'var(--c-text2,#7aa3c0)', fontSize: '.7rem', fontWeight: 700, cursor: 'pointer' }} title={es ? 'No importar las que no coinciden' : 'Skip the ones without a match'}>{es ? 'Saltar sin match' : 'Skip unmatched'}</button>
                  </span>
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, display: 'flex', flexDirection: 'column' }}>
                  {built.map((b) => {
                    const a = effAssign(b);
                    const skip = a === '__skip__';
                    const ambig = a === '__ambig__';
                    const isNew = a === '';
                    const chip = skip
                      ? { t: es ? '⏭️ no importar' : '⏭️ skip', c: 'var(--c-text3,#45688a)' }
                      : ambig
                        ? { t: es ? '⚠️ varios — elige' : '⚠️ multiple — choose', c: 'var(--c-red,#ff3d6b)' }
                        : isNew
                          ? { t: es ? '➕ cliente nuevo' : '➕ new client', c: 'var(--c-amber,#ffb700)' }
                          : { t: es ? '✅ vincular' : '✅ link', c: 'var(--c-green,#00ff88)' };
                    return (
                      <div key={b.idx} style={{ padding: '.5rem .6rem', borderBottom: '1px solid var(--c-border,#1a3550)', opacity: skip ? .55 : 1, background: ambig ? 'rgba(255,61,107,.06)' : 'transparent' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.76rem', marginBottom: '.35rem' }}>
                          <span style={{ flex: 1, color: 'var(--c-text,#c8dff0)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <b>{b.policy.carrier}</b>{b.policy.plan ? ` · ${b.policy.plan}` : ''} <span style={{ color: 'var(--c-text3,#45688a)' }}>· {es ? 'archivo' : 'file'}: “{b.clientName || (es ? 'sin nombre' : 'no name')}”</span>
                          </span>
                          <span style={{ fontSize: '.68rem', fontWeight: 800, color: chip.c, whiteSpace: 'nowrap' }}>{chip.t}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
                          <div style={{ flex: 1, opacity: skip ? .5 : 1, pointerEvents: skip ? 'none' : 'auto' }}>
                            <ClientSearchSelect
                              clients={clients}
                              value={(skip || ambig || isNew) ? '' : a}
                              onChange={(e) => setAssign((s) => ({ ...s, [b.idx]: e.target.value }))}
                              lang={lang}
                              noneLabel={es ? '➕ Crear cliente nuevo' : '➕ Create new client'}
                              selectStyle={{ background: 'var(--c-surface,#0a1929)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 7, color: 'var(--c-text,#c8dff0)', padding: '.3rem .4rem', fontSize: '.76rem' }}
                            />
                          </div>
                          <button type="button" onClick={() => setAssign((s) => ({ ...s, [b.idx]: skip ? '' : '__skip__' }))}
                            style={{ flex: '0 0 auto', background: skip ? 'var(--c-cyan,#00d4ff)' : 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 7, padding: '.3rem .55rem', color: skip ? '#0a1628' : 'var(--c-text2,#7aa3c0)', fontSize: '.7rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            {skip ? (es ? 'Importar' : 'Import') : (es ? 'No importar' : 'Skip')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.4 }}>
                  {es
                    ? '“➕ Crear cliente nuevo” crea la ficha del cliente. Si la persona YA existe pero con el nombre distinto, búscala en la lista y selecciónala para vincular — así NO se duplica.'
                    : '“➕ Create new client” creates the client record. If the person ALREADY exists under a different name, search the list and pick them to link — that way nothing is duplicated.'}
                </div>
                {toAmbig.length > 0 && (
                  <div style={{ fontSize: '.74rem', color: 'var(--c-red,#ff3d6b)', background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 8, padding: '.5rem .7rem', lineHeight: 1.4 }}>
                    ⚠️ {es
                      ? `${toAmbig.length} póliza(s) coinciden con VARIOS clientes (marcadas “⚠️ varios — elige”). NO se importarán hasta que elijas el cliente correcto o las marques “crear nuevo”. Así evitamos duplicados.`
                      : `${toAmbig.length} policy(ies) match MULTIPLE clients (marked “⚠️ multiple — choose”). They will NOT import until you pick the right client or mark them “create new”. This prevents duplicates.`}
                  </div>
                )}
              </>
            )}
            <div style={{ display: 'flex', gap: '.5rem' }}>
              <button type="button" onClick={() => { setStep('file'); setErr(null); }} style={{ flex: '0 0 auto', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '.6rem .9rem', color: 'var(--c-text2,#7aa3c0)', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer' }}>{es ? 'Atrás' : 'Back'}</button>
              {(() => { const total = toLink.length + toCreate.length; const disabled = total === 0 || !colOf('clientName'); return (
              <button type="button" disabled={disabled} onClick={runImport}
                style={{ flex: 1, background: disabled ? 'var(--c-surface3,#162d44)' : 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 9, padding: '.6rem 1rem', color: disabled ? 'var(--c-text3,#45688a)' : '#0a1628', fontWeight: 800, fontSize: '.85rem', cursor: disabled ? 'not-allowed' : 'pointer' }}>
                {es ? `Importar ${total} póliza(s)` : `Import ${total} policy(ies)`}
              </button>
              ); })()}
            </div>
          </>
        )}

        {step === 'done' && (
          <>
            <div style={{ fontSize: '.85rem', color: 'var(--c-text,#c8dff0)' }}>{prog.done < prog.total ? (es ? `Importando… ${prog.done}/${prog.total}` : `Importing… ${prog.done}/${prog.total}`) : (es ? '¡Listo!' : 'Done!')}</div>
            <div style={{ height: 8, background: 'var(--c-surface2,#112538)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${prog.total ? Math.round((prog.done / prog.total) * 100) : 0}%`, background: 'var(--c-cyan,#00d4ff)', transition: 'width .2s' }} />
            </div>
            <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span>📄 {es ? 'Pólizas creadas' : 'Policies created'}: <b style={{ color: 'var(--c-text,#c8dff0)' }}>{prog.createdPolicies}</b></span>
              <span>🔗 {es ? 'Vinculadas a clientes existentes' : 'Linked to existing clients'}: {prog.linked}</span>
              {prog.createdClients > 0 && <span>🆕 {es ? 'Clientes nuevos creados' : 'New clients created'}: {prog.createdClients}</span>}
              {prog.ambiguous > 0 && <span style={{ color: 'var(--c-amber,#ffb700)' }}>⚠️ {es ? 'Ambiguas (varios clientes, omitidas)' : 'Ambiguous (multiple clients, skipped)'}: {prog.ambiguous}</span>}
              {prog.duplicates > 0 && <span>♻️ {es ? 'Duplicadas (ya existían, omitidas)' : 'Duplicates (already on file, skipped)'}: {prog.duplicates}</span>}
              {prog.skipped > 0 && <span>⏭️ {es ? 'Omitidas (sin nombre)' : 'Skipped (no name)'}: {prog.skipped}</span>}
              {prog.notesFilled > 0 && <span style={{ color: 'var(--c-green,#00e58a)' }}>📝 {es ? 'Notas puestas en pólizas que ya existían' : 'Notes filled in on policies already on file'}: {prog.notesFilled}</span>}
              {prog.policyNotesAdded > 0 && <span style={{ color: 'var(--c-green,#00e58a)' }}>📄 {es ? 'Notas añadidas en la tarjeta (botón Notas)' : 'Notes added on the card (Notes button)'}: {prog.policyNotesAdded}</span>}
              {/* De donde viene la nota, dicho en numeros: si esto sale 0, el
                  texto no se leyo de la hoja (columna vacia o sin mapear) y no
                  hay nada que buscar del lado del servidor. */}
              {prog.rowsWithNotes !== undefined && (
                <span style={{ color: prog.rowsWithNotes > 0 ? 'var(--c-text2,#7aa3c0)' : 'var(--c-amber,#ffb700)' }}>
                  📤 {es ? 'Filas enviadas con nota' : 'Rows sent carrying a note'}: {prog.rowsWithNotes}
                  {prog.rowsWithNotes === 0 && (es ? ' — la columna de notas venía vacía o sin mapear' : ' — the notes column was empty or unmapped')}
                </span>
              )}
              {/* Una celda con algo escrito que no era una fecha. La póliza entra
                  igual, pero sin esa fecha — y eso se dice, no se calla. */}
              {prog.datesDropped > 0 && (
                <span style={{ color: 'var(--c-amber,#ffb700)' }}>
                  📅 {es ? 'Fechas que no se pudieron leer (la póliza entró sin esa fecha)' : "Dates that couldn't be read (the policy was imported without them)"}: {prog.datesDropped}
                </span>
              )}
              {prog.failed > 0 && <span style={{ color: 'var(--c-red,#ff3d6b)' }}>❌ {es ? 'Fallaron (el servidor rechazó el envío — ver mensaje arriba)' : 'Failed (server rejected the request — see message above)'}: {prog.failed}</span>}
              {prog.stopped && <span style={{ color: 'var(--c-amber,#ffb700)' }}>{es ? 'Se detuvo por el límite de seguridad. Espera ~1 hora y reimporta (no duplica).' : 'Stopped by the security rate limit. Wait ~1 hour and re-import (no duplicates).'}</span>}
            </div>
            {/* QUE filas se cayeron. Un contador solo ("6 duplicadas") obliga a
                cotejar la hoja con el CRM a mano; aqui salen con nombre. */}
            {[
              { rows: dupRows.filter((r) => r.against !== 'sheet'), icon: '♻️', color: 'var(--c-text2,#7aa3c0)',
                es: 'Ya estaban en Deskte (no se volvieron a crear)', en: 'Already in Deskte (not created again)',
                esHelp: 'Tu archivo está bien: estas pólizas YA existían en Deskte antes de esta importación (mismo cliente, tipo, compañía, plan y fecha efectiva). Se indica el número y la fecha en que se creó la que ya estaba.',
                enHelp: 'Your file is fine: these policies ALREADY existed in Deskte before this import (same client, type, carrier, plan and effective date). The number and creation date of the existing one are shown.' },
              { rows: dupRows.filter((r) => r.against === 'sheet'), icon: '📄', color: 'var(--c-amber,#ffb700)',
                es: 'Repetidas dentro del archivo', en: 'Repeated inside the file',
                esHelp: 'Estas filas venían dos veces en la MISMA hoja. Solo se importó la primera.',
                enHelp: 'These rows appeared twice in the SAME sheet. Only the first one was imported.' },
              { rows: unmatchedRows, icon: '⏭️', color: 'var(--c-amber,#ffb700)',
                es: 'Omitidas (no se encontró el cliente)', en: 'Skipped (no matching client)',
                esHelp: 'No hubo un cliente al que enlazarlas. Revisa el nombre en la hoja o créalo antes de reimportar.',
                enHelp: 'There was no client to link them to. Check the name in the sheet or create the client before re-importing.' },
              // Va la ULTIMA a proposito: los botones Agregar/Ignorar se pintan
              // con `gi === 0`, asi que anadir grupos por detras no los mueve.
              { rows: rejectedRows, icon: '❌', color: 'var(--c-red,#ff3d6b)',
                es: 'La base de datos no las aceptó', en: 'The database would not accept them',
                esHelp: 'El resto del archivo SÍ entró. Estas filas traen algún dato que el sistema no pudo guardar — casi siempre una celda que parece fecha pero no lo es. Corrige esas filas en la hoja y vuelve a importar solo esas.',
                enHelp: 'The rest of the file DID import. These rows carry a value the system could not store — most often a cell that looks like a date but is not. Fix those rows in the sheet and re-import just those.' },
            ].filter((g) => g.rows.length > 0).map((g, gi) => (
              <div key={g.en} style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9, padding: '.6rem .75rem' }}>
                <div style={{ fontSize: '.76rem', fontWeight: 800, color: g.color, marginBottom: '.15rem' }}>
                  {g.icon} {es ? g.es : g.en} — {g.rows.length}
                </div>
                <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginBottom: '.4rem' }}>{es ? g.esHelp : g.enHelp}</div>
                <div style={{ maxHeight: 190, overflowY: 'auto' }}>
                  {g.rows.map((r, i) => {
                    const st = r.uid ? dupState[r.uid] : undefined;
                    const decidible = gi === 0 && r.row && !st;
                    return (
                    <div key={r.uid || i} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.72rem', color: 'var(--c-text,#c8dff0)', padding: '.28rem 0', borderBottom: i < g.rows.length - 1 ? '1px solid var(--c-border,#1a3550)' : 'none', opacity: st === 'ignored' ? .45 : 1 }}>
                      <div style={{ flex: 1, minWidth: 0, textDecoration: st === 'ignored' ? 'line-through' : 'none' }}>
                        <b>{r.clientName || (es ? '(sin nombre)' : '(no name)')}</b>
                        <span style={{ color: 'var(--c-text2,#7aa3c0)' }}>
                          {[r.carrier, r.plan, r.policyNumber && `#${r.policyNumber}`, r.effectiveDate]
                            .filter(Boolean).map((x) => ` \u00b7 ${x}`).join('')}
                        </span>
                        {r.against === 'deskte' && r.existingCreatedAt && (
                          <span style={{ color: 'var(--c-text3,#45688a)' }}>
                            {' \u2014 '}{es ? 'la que ya estaba' : 'the one on file'}
                            {r.existingPolicyNumber ? ` #${r.existingPolicyNumber}` : ''}
                            {es ? `, creada el ${r.existingCreatedAt}` : `, created ${r.existingCreatedAt}`}
                          </span>
                        )}
                      </div>
                      {decidible && (
                        <div style={{ display: 'flex', gap: '.3rem', flexShrink: 0 }}>
                          <button type="button" onClick={() => addOneDuplicate(r)}
                            title={es ? 'No es duplicada: agregarla igual' : 'Not a duplicate: add it anyway'}
                            style={{ background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none', color: '#fff', fontSize: '.68rem', fontWeight: 700, padding: '.22rem .6rem', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                            {es ? 'Agregar' : 'Add'}
                          </button>
                          <button type="button" onClick={() => setDupState((p) => ({ ...p, [r.uid]: 'ignored' }))}
                            title={es ? 'Si es duplicada: dejarla fuera' : 'It is a duplicate: leave it out'}
                            style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', fontSize: '.68rem', fontWeight: 700, padding: '.22rem .6rem', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                            {es ? 'Ignorar' : 'Ignore'}
                          </button>
                        </div>
                      )}
                      {st === 'adding' && <span style={{ color: 'var(--c-cyan,#00d4ff)', fontSize: '.68rem', flexShrink: 0 }}>{es ? 'Agregando\u2026' : 'Adding\u2026'}</span>}
                      {st === 'added' && <span style={{ color: 'var(--c-green,#00e58a)', fontSize: '.68rem', fontWeight: 700, flexShrink: 0 }}>{es ? '\u2705 Agregada' : '\u2705 Added'}</span>}
                      {st === 'ignored' && <span style={{ color: 'var(--c-text3,#45688a)', fontSize: '.68rem', flexShrink: 0 }}>{es ? 'Ignorada' : 'Ignored'}</span>}
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {unknownStatuses.length > 0 && (
              <div style={{ fontSize: '.78rem', color: 'var(--c-amber,#ffb700)', background: 'rgba(255,183,0,.08)', border: '1px solid rgba(255,183,0,.3)', borderRadius: 8, padding: '.7rem .85rem', display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                <strong>⚠️ {es ? 'Estados que no reconocí — esas pólizas entraron como "Activa"' : 'Statuses I did not recognize — those policies came in as "Active"'}</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.3rem' }}>
                  {unknownStatuses.map((s) => (
                    <span key={s.value} style={{ background: 'rgba(255,183,0,.14)', border: '1px solid rgba(255,183,0,.3)', borderRadius: 100, padding: '.15rem .55rem', fontWeight: 700 }}>
                      {s.value} · {s.rows}
                    </span>
                  ))}
                </div>
                <span style={{ color: 'var(--c-text3,#45688a)' }}>
                  {es
                    ? 'Créalos con ESE MISMO nombre en los estados personalizados de la agencia y vuelve a importar: las que ya entraron no se corrigen solas.'
                    : 'Create them with the SAME name in the agency custom statuses and re-import: the ones already in do not fix themselves.'}
                </span>
              </div>
            )}
            {prog.done >= prog.total && <button type="button" onClick={() => { onClose(); if (prog.createdPolicies > 0 && typeof window !== 'undefined') window.location.reload(); }} style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', borderRadius: 9, padding: '.6rem 1rem', color: '#0a1628', fontWeight: 800, fontSize: '.85rem', cursor: 'pointer' }}>{es ? 'Cerrar y recargar' : 'Close & reload'}</button>}
          </>
        )}
      </div>
    </Modal>
  );
}

export function PoliciesView({ policies, clients, onPoliciesChange, onClientClick, t, lang, users = [], seatLabels = {}, currentUser, policyTagLibrary = [], onPolicyTagLibraryChange, policyTagColors = {}, onPolicyTagColorsChange }) {
  const [typeFilter,setTypeFilter]=useStickyFilter("deskte:pol:type");
  // "Atiende" (handled-by seat, from the policy's CLIENT) + "Analista" (from
  // the policy itself) filters — Lissette 2026-07-02.
  const [attendFilter,setAttendFilter]=useStickyFilter("deskte:pol:attend");
  const [analystFilter,setAnalystFilter]=useStickyFilter("deskte:pol:analyst");
  // Filtro por ETIQUETA de póliza (Lissette 2026-08-15). 'all' = todas,
  // 'none' = las que no tienen ninguna, o el nombre exacto de una etiqueta.
  const [tagFilter,setTagFilter]=useStickyFilter("deskte:pol:tag");
  // Las etiquetas que de verdad hay puestas en alguna póliza, con cuántas. El
  // desplegable NO se arma con el catálogo de la agencia: ofrecer etiquetas que
  // no tiene nadie llenaría la lista de opciones que solo pueden dar cero
  // resultados.
  const tagCounts = useMemo(() => contarEtiquetas(policies), [policies]);
  const untaggedCount = tagCounts.sinEtiqueta;
  // "Agente" = the CLIENT's assigned agent (Client.assignedAgent). "Agente en la
  // póliza" = the agent on the policy itself (policy.agentLabel). Two separate
  // filters (Lissette 2026-07-15).
  const [assignedAgentFilter,setAssignedAgentFilter]=useStickyFilter("deskte:pol:agent");
  const [agentLabelFilter,setAgentLabelFilter]=useStickyFilter("deskte:pol:agentLabel");
  // Date-range filter (Lissette 2026-07-02): Desde/Hasta over a pickable date
  // field — Efectiva (default), Renovación or Venta.
  const [dateField,setDateField]=useState("effective"); // effective | renewal | sale
  const [dateFrom,setDateFrom]=useState("");
  const [dateTo,setDateTo]=useState("");
  // Status bucket: 'all' shows everything, 'active' hides cancelled, 'cancelled'
  // shows only cancelled (legacy "inactive" counts as cancelled). Default 'all'.
  const [statusFilter,setStatusFilter]=useStickyFilter("deskte:pol:status");
  const [yearFilter,setYearFilter]=useStickyFilter("deskte:pol:year");
  // La regla vive en policyStatuses.js: de ella depende ademas cuantos meses
  // dio dinero una poliza en Comisiones por Poliza.
  const isCancelled = (p)=> esEstatusCancelado(p.status);
  // Custom-status manager (create/manage the agency's own policy statuses) —
  // brought into the main Pólizas view so statuses can be created right here
  // (Lissette 2026-07-01). statusVer bumps to re-read the catalog after a change.
  const [statusMgr,setStatusMgr]=useState(false);
  const [statusVer,setStatusVer]=useState(0);
  // Carrier filter is scoped to the currently selected type — switching
  // type clears it (handled by the useEffect below) so a stale carrier
  // like "Anthem" doesn't silently hide all Auto policies when the user
  // flips type → Auto. "all" disables the filter.
  // Varias aseguradoras a la vez (Lissette 2026-08-28). Lista vacia = todas.
  const [carrierFilter,setCarrierFilter]=useStickyFilterList("deskte:pol:carrier");
  useEffect(()=>{ setCarrierFilter("all"); },[typeFilter]);
  // Marketplace (Lissette 2026-09-29): solo existe con el tipo Salud (Mercado)
  // elegido, y como la aseguradora, se vacía al cambiar de tipo para que un
  // valor viejo no esconda las pólizas de otro tipo. Ver filtroDeMarketplace.js.
  const [mktFilter,setMktFilter]=useStickyFilter("deskte:pol:mkt");
  useEffect(()=>{ if(typeFilter!=="aca") setMktFilter("all"); },[typeFilter]);
  // US-state filter (Lissette 2026-06-22: "por estado de Florida, Maryland…").
  // A policy's state = its client's state; we group case-insensitively so
  // "FL" and "fl" merge. "all" disables it.
  const [stateFilter,setStateFilter]=useStickyFilter("deskte:pol:state");
  const stateOf=(p)=>estadoDeCliente(clients.find(cl=>cl.id===p.clientId));
  const [search,setSearch]=useState("");
  const [editPol,setEditPol]=useState(null);
  const [delPol,setDelPol]=useState(null);
  // La poliza que se esta moviendo a otro cliente (Maritza, 2026-09-10).
  const [movPol,setMovPol]=useState(null);
  // Multi-select / bulk-delete: a toggle puts each row into a checkbox mode so
  // the user can pick policies and remove them in one confirmed action.
  const [selectMode,setSelectMode]=useState(false);
  const [selectedIds,setSelectedIds]=useState(()=>new Set());
  const [confirmBulk,setConfirmBulk]=useState(false);
  // Cambiar el estatus de las seleccionadas (Luis Rodriguez, 2026-09-10).
  const [bulkEstatus,setBulkEstatus]=useState(false);
  // Cambio masivo de analista / agente / handle / estado (Lissette 2026-10-20).
  const [bulkCampos,setBulkCampos]=useState(false);
  // Quitar una etiqueta en masa: { ids:[...] } de las seleccionadas, o { ids:null, inicial } de todas (2026-10-22).
  const [quitarEtq,setQuitarEtq]=useState(null);
  const [bulkBusy,setBulkBusy]=useState(false);
  const isApiModeP = typeof window !== "undefined" && !!window.__deskteAuth;
  // Etiquetas de poliza desde la tarjeta. Se manda la poliza entera con las
  // etiquetas nuevas, igual que hace el guardado de asegurados: mapPolicyToApi
  // omite `tags` cuando no viaja, asi que ningun otro guardado las pisa.
  const updatePolicyTags = async (policy, nextTags) => {
    if (isApiModeP) {
      try { await apiUpdatePolicy(policy.id, { ...policy, tags: nextTags }); }
      catch (err) {
        toast.error((lang === "es" ? "No se pudieron guardar las etiquetas: " : "Could not save the tags: ") + err.message);
        return;
      }
    }
    onPoliciesChange(prev => prev.map(p => (p.id === policy.id ? { ...p, tags: nextTags } : p)));
  };
  const [showTypeManager,setShowTypeManager]=useState(false);
  const [showFieldsManager,setShowFieldsManager]=useState(false);
  const [showDuplicates,setShowDuplicates]=useState(false);
  const [showDupAccepted,setShowDupAccepted]=useState(false);
  const [, bumpTypes]=useState(0);
  // Duplicate detector: groups of 2+ DISTINCT policies (different ids) for the
  // SAME client that look like the same policy — same policyNumber, or (when
  // there's no number) same carrier+type+premium. Helps clean up double-imports
  // (Lissette 2026-07-09). This is about REAL duplicate rows; the phantom
  // "same-id-twice" pagination bug was fixed separately (#1209).
  const duplicateGroups = useMemo(() => {
    const byKey = new Map();
    (policies || []).forEach((p) => {
      if (!p || !p.clientId) return;
      const num = String(p.policyNumber || '').trim().toLowerCase();
      const key = num
        ? `${p.clientId}|#${num}`
        : `${p.clientId}|${String(p.carrier || '').trim().toLowerCase()}|${String(p.type || '').trim().toLowerCase()}|${String(p.monthlyPremium || '').trim()}`;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(p);
    });
    const groups = [];
    for (const arr of byKey.values()) {
      const uniqIds = [...new Map(arr.map((p) => [p.id, p])).values()];
      if (uniqIds.length >= 2) groups.push(uniqIds);
    }
    return groups;
  }, [policies]);
  // Un grupo deja de avisar cuando TODAS sus pólizas están marcadas como
  // revisadas-y-correctas. Marcar solo una no lo resuelve: el par sigue sin
  // decidir, y esconderlo a medias sería peor que no esconderlo. Si mañana
  // entra una tercera igual, el grupo vuelve a salir — porque la nueva no
  // está marcada (Lissette 2026-08-17: "solo da la opción de eliminar").
  const isDupOk = (p) => !!p?.dupOk;
  const dupPending = useMemo(() => duplicateGroups.filter((g) => !g.every(isDupOk)), [duplicateGroups]);
  const dupAccepted = useMemo(() => duplicateGroups.filter((g) => g.every(isDupOk)), [duplicateGroups]);
  // Guardar la decisión en las pólizas del grupo. Se manda la póliza ENTERA
  // (`...p`), como el resto de la vista: un patch parcial vaciaría campos.
  const [dupBusy, setDupBusy] = useState(false);
  const markDupOk = async (list, ok) => {
    if (dupBusy) return;
    setDupBusy(true);
    const val = ok ? '1' : '';
    let failed = 0;
    for (const p of list) {
      if (isApiModeP) {
        try { await apiUpdatePolicy(p.id, { ...p, dupOk: val }); }
        catch { failed++; continue; }
      }
      onPoliciesChange((prev) => prev.map((x) => (x.id === p.id ? { ...x, dupOk: val } : x)));
    }
    setDupBusy(false);
    if (failed) toast.error(lang === 'es' ? `No se pudo guardar ${failed} póliza(s)` : `Could not save ${failed} policy(ies)`);
    else if (ok) toast.success(lang === 'es' ? 'Marcada como correcta' : 'Marked as fine');
  };
  // Sidebar click on "Policies" closes any open editor / confirm modal.
  useEffect(()=>{
    const h=(e)=>{ if(e?.detail?.view==="policies"){ setEditPol(null); setDelPol(null); setShowTypeManager(false); setShowFieldsManager(false); setSelectMode(false); setSelectedIds(new Set()); setConfirmBulk(false); } };
    window.addEventListener("deskte:nav-reset",h);
    return ()=>window.removeEventListener("deskte:nav-reset",h);
  },[]);
  const [sortBy, setSortBy] = useState("default"); // default | expiringSoon | anioDesc | anioAsc | azAsc | azDesc
  // Pagination — 15/page default (Lissette's preference 2026-05-19 — even
  // the 25 from clients felt long for policies which take more vertical room
  // per row). Persisted in localStorage.
  //
  // Migration: previous releases defaulted to 25, so existing browsers have
  // 25 cached even if the user never explicitly picked it. On first load
  // after this change, wipe the cached value (forces the new 15 default)
  // and stamp a one-shot flag so we never touch it again. Users who genuinely
  // prefer 25/50/100/200 just pick from the dropdown once.
  const [pageSize, setPageSize] = useState(() => {
    try {
      if (!localStorage.getItem("deskte:policiesPageSizeMigratedV2")) {
        localStorage.removeItem("deskte:policiesPageSize");
        localStorage.setItem("deskte:policiesPageSizeMigratedV2", "1");
      }
      const stored = parseInt(prefsGet("deskte:policiesPageSize"), 10);
      return [15, 25, 50, 100, 200].includes(stored) ? stored : 15;
    } catch { return 15; }
  });
  const [page, setPage] = useState(1);
  useEffect(() => {
    try { prefsSet("deskte:policiesPageSize", String(pageSize)); } catch {}
  }, [pageSize]);
  // Distinct carrier names already in this agency's policy book. Powers
  // the autocomplete dropdown on the carrier field of PolicyForm.
  const carrierSuggestions = useMemo(
    () => [...new Set(policies.map(p => p.carrier).filter(Boolean))].sort(),
    [policies],
  );
  // Los tipos de suplemento que la agencia YA escribio en sus polizas. El
  // catalogo crece solo: lo que entra por un importe aparece en el
  // desplegable de la siguiente poliza, sin que nadie lo configure.
  const supplementSuggestions = useMemo(() => suplementosUsados(policies), [policies]);
  // Lo que la agencia ya escribio en SOA y HRA: asi el desplegable "se va
  // llenando y va quedando" sin una pantalla de ajustes que mantener.
  const soaStatusSuggestions = useMemo(() => valoresUsados(policies, 'soaStatus'), [policies]);
  const productTypeSuggestions = useMemo(() => valoresUsados(policies, 'productType'), [policies]);
  const hraStatusSuggestions = useMemo(() => valoresUsados(policies, 'hraStatus'), [policies]);
  const medicaidSuggestions = useMemo(() => valoresUsados(policies, 'medicaidEligibility'), [policies]);
  const lisSuggestions = useMemo(() => valoresUsados(policies, 'lisLevel'), [policies]);
  const daysUntilExp = (p) => {
    const d = p.expirationDate || p.renewalDate;
    if (!d) return null;
    const exp = new Date(d + 'T00:00:00');
    if (isNaN(exp.getTime())) return null;
    const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
    return Math.round((exp.getTime() - todayMid.getTime()) / 86400000);
  };
  // Carriers actually used within the currently-selected type. This is what
  // populates the carrier filter dropdown — restricting by type so the
  // user only sees carriers that would actually return results (e.g. when
  // type=Auto, you don't see "Anthem" because it's a health carrier in
  // this book).
  const carrierOptions = useMemo(() => {
    const scoped = typeFilter === "all"
      ? policies
      : policies.filter(p => p.type === typeFilter);
    return [...new Set(scoped.map(p => p.carrier).filter(Boolean))].sort();
  }, [policies, typeFilter]);
  // Distinct client states across the book (case-insensitive, first-seen
  // display) for the state dropdown.
  const stateOptions = useMemo(() => {
    const seen = new Map();
    policies.forEach(p => { const s = stateOf(p); if (s) { const k = s.toUpperCase(); if (!seen.has(k)) seen.set(k, s); } });
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [policies, clients]);
  // "Atiende" of a policy = the handling seat of its CLIENT (same source as
  // the clients-list filter). Analysts come from the policies themselves.
  const attendOf=(p)=>{ const c=clients.find(cl=>cl.id===p.clientId); return (c&&c.attendedBySeatId)||""; };
  // Date used by the Desde/Hasta range filter. Values may be 'YYYY-MM-DD' or a
  // full ISO timestamp — slice(0,10) normalizes both for string comparison.
  // "cancellation" (Lissette 2026-08-26): filtrar las bajas por rango de fechas
  // — cuantas se cayeron en julio, en el trimestre... Se suma al selector que ya
  // existia en vez de anadir un filtro aparte: es la MISMA pregunta (Desde/Hasta
  // sobre una fecha), solo cambia cual.
  //
  // No hace falta cruzarlo con el filtro de estado: una poliza sin fecha de
  // cancelacion no entra en el rango (ver `md` mas abajo), asi que eligiendo
  // esta fecha ya solo salen las canceladas.
  const rangeDateOf=(p)=> String((dateField==="renewal" ? (p.expirationDate||p.renewalDate) : dateField==="sale" ? p.saleDate : dateField==="cancellation" ? p.cancellationDate : p.effectiveDate) || "").slice(0,10);
  const analystOptions = useMemo(
    () => [...new Set(policies.map(p => (p.analystName||"").trim()).filter(Boolean))].sort((a,b)=>a.localeCompare(b)),
    [policies],
  );
  // "Agente en la póliza" options come from each policy's agentLabel; "Agente"
  // (assigned) resolves through the policy's client.
  const assignedAgentOf=(p)=>agenteDeCliente(clients.find(cl=>cl.id===p.clientId));
  const agentLabelOptions = useMemo(
    () => [...new Set(policies.map(p => (p.agentLabel||"").trim()).filter(Boolean))].sort((a,b)=>a.localeCompare(b)),
    [policies],
  );
  // Los años que hay en la cartera, con cuántas pólizas tiene cada uno.
  const yearOptions = useMemo(() => aniosDePolizas(policies), [policies]);
  const sinAnioCount = useMemo(() => polizasSinAnio(policies), [policies]);
  const assignedAgentOptions = useMemo(
    () => [...new Set(policies.map(p => assignedAgentOf(p)).filter(Boolean))].sort((a,b)=>a.localeCompare(b)),
    [policies, clients],
  );
  // Names of the people INSURED on a policy — EXACT mirror of the 🛡️ INSURED
  // badge on the card (PoliciesView ~877-887): ONLY the primary/spouse/deps
  // EXPLICITLY selected in policyInsured + the free-text policyInsuredExtra.
  // Lets the search find a policy by the DEPENDENT or SPOUSE who's the insured,
  // even when the titular isn't on it (Lissette 2026-07-01).
  // Fix 2026-07-01: an earlier version defaulted legacy policies (policyInsured
  // == null) to primary+spouse — that made a policy match by a spouse's name
  // even when NOBODY was marked insured (badge shows 0 / "no abre"), bringing up
  // unrelated clients. Now: no defaulting — match only what's actually set, so
  // the search agrees with the badge.
  // Array of the people insured on the policy (titular/cónyuge/dependientes/extra).
  // insuredNamesOf keeps returning the joined string used by the buscador; the
  // list version feeds the "Asegurados:" line shown under the titular so the
  // agent isn't confused when a search by a DEPENDENT surfaces the primary's
  // name (Lissette 2026-07-08 #1a).
  const insuredNamesList = (p, c) => {
    const ids = Array.isArray(p.policyInsured) ? p.policyInsured : [];
    const names = [];
    if (c) {
      if (ids.includes("primary")) names.push(`${c.firstName||""} ${c.lastName||""}`.trim());
      if (c.spouse && c.spouse.firstName && ids.includes("spouse")) names.push(`${c.spouse.firstName} ${c.spouse.lastName||""}`.trim());
      (Array.isArray(c.dependents) ? c.dependents : []).forEach((d, di) => {
        if (d && ids.includes(d.id || ("dep" + di))) names.push(`${d.firstName||""} ${d.lastName||""}`.trim());
      });
    }
    (Array.isArray(p.policyInsuredExtra) ? p.policyInsuredExtra : []).forEach(n => { if (n) names.push(String(n).trim()); });
    return names.filter(Boolean);
  };
  const insuredNamesOf = (p, c) => insuredNamesList(p, c).join(" ");
  // Todos los filtros MENOS el de estatus. Lo usan la lista y los contadores de
  // Todas/Activas/Pendientes/Canceladas: si los contadores solo miraban tipo,
  // aseguradora y búsqueda, "Canceladas" enseñaba el total de siempre aunque se
  // eligiera un año (Lissette 2026-10-13).
  const pasaFiltrosSinEstatus=(p)=>{
    const c=clients.find(cl=>cl.id===p.clientId);
    const name=c?`${c.firstName} ${c.lastName}`:"";
    const q=search.toLowerCase();
    const ms=!q||`${name} ${p.carrier} ${p.plan} ${p.policyNumber} ${insuredNamesOf(p,c)}`.toLowerCase().includes(q);
    const mt=typeFilter==="all"||p.type===typeFilter;
    const mc=coincideConSeleccion(p.carrier,carrierFilter);
    const mstate=stateFilter==="all"||stateOf(p).toUpperCase()===stateFilter.toUpperCase();
    const mat=attendFilter==="all"||(attendFilter==="none"?!attendOf(p):attendOf(p)===attendFilter);
    const man=analystFilter==="all"||(analystFilter==="none"?!(p.analystName||"").trim():(p.analystName||"").trim()===analystFilter);
    // Etiqueta de póliza. Se compara sin distinguir mayúsculas, igual que se
    // deduplica al ponerlas, para que "Renovar" y "renovar" no se separen.
    const tg=pasaElFiltroDeEtiqueta(p, tagFilter);
    const magl=agentLabelFilter==="all"||(agentLabelFilter==="none"?!(p.agentLabel||"").trim():(p.agentLabel||"").trim()===agentLabelFilter);
    const maa=assignedAgentFilter==="all"||(assignedAgentFilter==="none"?!assignedAgentOf(p):assignedAgentOf(p)===assignedAgentFilter);
    // Date range: with a Desde/Hasta set, a policy without that date doesn't match.
    const md=(!dateFrom&&!dateTo)||(()=>{ const d=rangeDateOf(p); if(!d) return false; if(dateFrom&&d<dateFrom) return false; if(dateTo&&d>dateTo) return false; return true; })();
    // Año de inicio. Convive con Desde/Hasta en vez de sustituirlo: el rango
    // sirve para "de marzo a junio", y esto para la pregunta de todos los
    // dias, que es "enseñame las de 2025" sin teclear dos fechas.
    const my=pasaElFiltroDeAnio(p, yearFilter);
    const mmk=typeFilter!=="aca"||pasaElFiltroDeMarketplace(p, mktFilter);
    return ms&&mt&&mc&&mstate&&mat&&man&&tg&&magl&&maa&&md&&my&&mmk;
  };
  const filtered=useMemo(()=>{
    const list = policies.filter(p=>{
      const mst = statusFilter==="all" ? true
        : statusFilter==="active" ? (!isCancelled(p) && p.status!=="pending")
        : statusFilter==="pending" ? p.status==="pending"
        : statusFilter==="cancelled" ? isCancelled(p)
        : p.status === statusFilter; // estatus específico: gracia / personalizado
      return mst&&pasaFiltrosSinEstatus(p);
    });
    // POR AÑO (Lissette 2026-09-13: "si quiero el orden por año también"). La
    // cuenta vive en anioDePoliza.js, la misma que el filtro y la que enseña la
    // tarjeta: dos formas de leer el año darian un orden que no se explica con
    // lo que se ve escrito en cada fila.
    // DE LA A A LA Z POR CLIENTE (Lissette 2026-09-15: "en poliza colocar
    // filtro de a a la z" -> "clientes"). El nombre sale de ordenPorCliente.js,
    // que lo arma IGUAL que lo pinta la fila de aqui abajo: ordenar por un
    // nombre distinto del que se lee en el renglon es un orden que parece roto.
    if (sortBy === "azAsc" || sortBy === "azDesc") {
      return ordenarPorCliente(list, sortBy === "azDesc" ? "desc" : "asc",
        (id) => clients.find((cl) => cl.id === id), lang === "es");
    }
    if (sortBy === "anioDesc" || sortBy === "anioAsc") {
      return ordenarPorAnio(list, sortBy === "anioAsc" ? "asc" : "desc");
    }
    if (sortBy === "expiringSoon") {
      const FAR = 1e9;
      return [...list].sort((a, b) => {
        const da = daysUntilExp(a); const db = daysUntilExp(b);
        return (da === null ? FAR : da) - (db === null ? FAR : db);
      });
    }
    return list;
  },[policies,typeFilter,mktFilter,carrierFilter,statusFilter,stateFilter,attendFilter,analystFilter,tagFilter,agentLabelFilter,assignedAgentFilter,yearFilter,dateField,dateFrom,dateTo,search,clients,sortBy,lang]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  useEffect(() => { setPage(1); }, [search, typeFilter, mktFilter, carrierFilter, statusFilter, stateFilter, attendFilter, analystFilter, tagFilter, agentLabelFilter, assignedAgentFilter, yearFilter, dateField, dateFrom, dateTo, sortBy, pageSize]);
  const pageStart = (page - 1) * pageSize;
  const paginated = filtered.slice(pageStart, pageStart + pageSize);

  // ── LIMPIAR FILTROS ─────────────────────────────────────────────────────
  //
  // Lissette 2026-09-11: *"en polizas coloca la opcion de limpiar filtro"*.
  //
  // Hace falta JUSTO AQUI y no en otras pantallas porque estos filtros se
  // QUEDAN PUESTOS —son sticky por persona desde el 2026-08-19, a peticion
  // suya— y siguen a cada uno entre sesiones y aparatos. Un filtro que
  // sobrevive al cierre del navegador es un filtro que un lunes por la manana
  // nadie recuerda haber puesto: la lista sale corta y parece que faltan
  // polizas. Ahi el boton deja de ser una comodidad.
  //
  // Lo que NO limpia, a proposito: el ORDEN y el tamano de pagina. No esconden
  // ninguna fila, asi que no son lo que hay que deshacer cuando "faltan
  // polizas" — y reordenar la lista sin que nadie lo pida desorienta mas.
  const FILTROS_POR_DEFECTO = { fecha: "effective" };
  const hayFiltros = typeFilter!=="all" || mktFilter!=="all" || statusFilter!=="all" || stateFilter!=="all"
    || attendFilter!=="all" || analystFilter!=="all" || tagFilter!=="all"
    || agentLabelFilter!=="all" || assignedAgentFilter!=="all"
    || (Array.isArray(carrierFilter) && carrierFilter.length>0)
    || yearFilter!=="all"
    || !!dateFrom || !!dateTo || dateField!==FILTROS_POR_DEFECTO.fecha || !!search;
  const limpiarFiltros = () => {
    setTypeFilter("all"); setMktFilter("all"); setStatusFilter("all"); setStateFilter("all");
    setAttendFilter("all"); setAnalystFilter("all"); setTagFilter("all");
    setAgentLabelFilter("all"); setAssignedAgentFilter("all"); setYearFilter("all");
    // Lista vacia = todas, que es la convencion de useStickyFilterList.
    setCarrierFilter([]);
    setDateField(FILTROS_POR_DEFECTO.fecha); setDateFrom(""); setDateTo("");
    setSearch("");
  };

  // ── Multi-select helpers ────────────────────────────────────────────────
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const exitSelect = () => { setSelectMode(false); setSelectedIds(new Set()); setConfirmBulk(false); setBulkEstatus(false); setBulkCampos(false); };
  // "Select all" acts on the whole FILTERED set (every page), so the user can
  // wipe an entire bad import in one go — not just the visible page.
  const allFilteredSelected = filtered.length > 0 && filtered.every(p => selectedIds.has(p.id));
  const toggleSelectAll = () => setSelectedIds(allFilteredSelected ? new Set() : new Set(filtered.map(p => p.id)));
  const runBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) { setConfirmBulk(false); return; }
    setBulkBusy(true);
    try {
      if (isApiModeP) {
        // Server caps at 500/request — chunk so large clean-ups still go through.
        for (let i = 0; i < ids.length; i += 200) {
          await apiBulkDeletePolicies(ids.slice(i, i + 200));
        }
      }
      const gone = new Set(ids);
      onPoliciesChange(prev => prev.filter(p => !gone.has(p.id)));
      toast.success(lang === "es" ? `${ids.length} póliza(s) eliminada(s)` : `${ids.length} policy(ies) deleted`);
      exitSelect();
    } catch (err) {
      toast.error((lang === "es" ? "No se pudieron eliminar: " : "Could not delete: ") + err.message);
    } finally { setBulkBusy(false); }
  };

  return <div>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"1rem",gap:".5rem",flexWrap:"wrap"}}>
      <div className="dkt-sec-title" style={{margin:0}}>{t.policies.allPolicies}</div>
      <div style={{display:"flex",gap:".5rem",flexWrap:"wrap"}}>
        {!selectMode && <button onClick={()=>setSelectMode(true)}
          style={{display:"flex",alignItems:"center",gap:".4rem",padding:".45rem .75rem",borderRadius:8,
            background:"rgba(0,212,255,.1)",border:"1px solid rgba(0,212,255,.35)",
            color:"var(--c-cyan,#00d4ff)",fontSize:".72rem",fontWeight:700,cursor:"pointer",transition:"all .15s"}}
          onMouseEnter={e=>{e.currentTarget.style.background="rgba(0,212,255,.2)";}}
          onMouseLeave={e=>{e.currentTarget.style.background="rgba(0,212,255,.1)";}}>
          <CheckSquare size={13}/>{lang==="es"?"Seleccionar":"Select"}
        </button>}
        {selectMode && <>
          <button onClick={toggleSelectAll}
            style={{display:"flex",alignItems:"center",gap:".4rem",padding:".45rem .75rem",borderRadius:8,
              background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",
              color:"var(--c-text2,#7aa3c0)",fontSize:".72rem",fontWeight:700,cursor:"pointer"}}>
            <CheckSquare size={13}/>{allFilteredSelected?(lang==="es"?"Quitar todas":"Unselect all"):(lang==="es"?`Seleccionar todas (${filtered.length})`:`Select all (${filtered.length})`)}
          </button>
          <button onClick={()=>{ if(selectedIds.size) setBulkEstatus(true); }} disabled={selectedIds.size===0}
            style={{display:"flex",alignItems:"center",gap:".4rem",padding:".45rem .75rem",borderRadius:8,
              background:selectedIds.size?"rgba(0,212,255,.12)":"var(--c-surface2,#112538)",
              border:`1px solid ${selectedIds.size?"rgba(0,212,255,.4)":"var(--c-border,#1a3550)"}`,
              color:selectedIds.size?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)",fontSize:".72rem",fontWeight:700,
              cursor:selectedIds.size?"pointer":"not-allowed",transition:"all .15s"}}>
            <RefreshCw size={13}/>{lang==="es"?`Cambiar estatus (${selectedIds.size})`:`Change status (${selectedIds.size})`}
          </button>
          <button onClick={()=>{ if(selectedIds.size) setBulkCampos(true); }} disabled={selectedIds.size===0}
            title={lang==="es"?"Analista, agente, handle o estado":"Analyst, agent, handle or state"}
            style={{display:"flex",alignItems:"center",gap:".4rem",padding:".45rem .75rem",borderRadius:8,
              background:selectedIds.size?"rgba(0,212,255,.12)":"var(--c-surface2,#112538)",
              border:`1px solid ${selectedIds.size?"rgba(0,212,255,.4)":"var(--c-border,#1a3550)"}`,
              color:selectedIds.size?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)",fontSize:".72rem",fontWeight:700,
              cursor:selectedIds.size?"pointer":"not-allowed",transition:"all .15s"}}>
            <Users size={13}/>{lang==="es"?`Cambio masivo (${selectedIds.size})`:`Bulk change (${selectedIds.size})`}
          </button>
          <button onClick={()=>{ if(selectedIds.size) setQuitarEtq({ids:[...selectedIds]}); }} disabled={selectedIds.size===0}
            title={lang==="es"?"Quitar una etiqueta de las seleccionadas":"Remove a tag from the selected"}
            style={{display:"flex",alignItems:"center",gap:".4rem",padding:".45rem .75rem",borderRadius:8,
              background:selectedIds.size?"rgba(0,212,255,.12)":"var(--c-surface2,#112538)",
              border:`1px solid ${selectedIds.size?"rgba(0,212,255,.4)":"var(--c-border,#1a3550)"}`,
              color:selectedIds.size?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)",fontSize:".72rem",fontWeight:700,
              cursor:selectedIds.size?"pointer":"not-allowed",transition:"all .15s"}}>
            <Tag size={13}/>{lang==="es"?`Quitar etiqueta (${selectedIds.size})`:`Remove tag (${selectedIds.size})`}
          </button>
          <button onClick={()=>{ if(selectedIds.size) setConfirmBulk(true); }} disabled={selectedIds.size===0}
            style={{display:"flex",alignItems:"center",gap:".4rem",padding:".45rem .75rem",borderRadius:8,
              background:selectedIds.size?"rgba(255,61,107,.12)":"var(--c-surface2,#112538)",
              border:`1px solid ${selectedIds.size?"rgba(255,61,107,.4)":"var(--c-border,#1a3550)"}`,
              color:selectedIds.size?"#ff3d6b":"var(--c-text3,#45688a)",fontSize:".72rem",fontWeight:700,
              cursor:selectedIds.size?"pointer":"not-allowed",transition:"all .15s"}}>
            <Trash2 size={13}/>{lang==="es"?`Borrar (${selectedIds.size})`:`Delete (${selectedIds.size})`}
          </button>
          <button onClick={exitSelect}
            style={{display:"flex",alignItems:"center",gap:".4rem",padding:".45rem .75rem",borderRadius:8,
              background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",
              color:"var(--c-text2,#7aa3c0)",fontSize:".72rem",fontWeight:700,cursor:"pointer"}}>
            <X size={13}/>{lang==="es"?"Cancelar":"Cancel"}
          </button>
        </>}
        {!selectMode && <button onClick={()=>setShowTypeManager(true)}
          style={{display:"flex",alignItems:"center",gap:".4rem",padding:".45rem .75rem",borderRadius:8,
            background:"rgba(139,92,246,.1)",border:"1px solid rgba(139,92,246,.3)",
            color:"#a78bfa",fontSize:".72rem",fontWeight:700,cursor:"pointer",transition:"all .15s"}}
          onMouseEnter={e=>{e.currentTarget.style.background="rgba(139,92,246,.2)";}}
          onMouseLeave={e=>{e.currentTarget.style.background="rgba(139,92,246,.1)";}}>
          <Plus size={13}/>{lang==="es"?"Gestionar tipos":"Manage types"}
        </button>}
        {!selectMode && <button onClick={()=>setShowFieldsManager(true)}
          title={lang==="es"?"Renombrar o eliminar campos personalizados de cada tipo":"Rename or delete custom fields per type"}
          style={{display:"flex",alignItems:"center",gap:".4rem",padding:".45rem .75rem",borderRadius:8,
            background:"rgba(0,212,255,.1)",border:"1px solid rgba(0,212,255,.3)",
            color:"#00d4ff",fontSize:".72rem",fontWeight:700,cursor:"pointer",transition:"all .15s"}}
          onMouseEnter={e=>{e.currentTarget.style.background="rgba(0,212,255,.2)";}}
          onMouseLeave={e=>{e.currentTarget.style.background="rgba(0,212,255,.1)";}}>
          🧩 {lang==="es"?"Gestionar campos":"Manage fields"}
        </button>}
        {/* En ámbar solo cuenta lo que falta por DECIDIR: un grupo ya revisado
            y dado por bueno no puede seguir avisando, o el aviso nunca se
            apaga y deja de significar nada. Pero el botón no desaparece
            mientras queden grupos aceptados — se queda en gris — porque si no,
            no habría por dónde volver a revisarlos. */}
        {!selectMode && (dupPending.length>0 || dupAccepted.length>0) && (()=>{
          const pend=dupPending.length>0;
          const bg=pend?"rgba(245,158,11,.12)":"var(--c-surface2,#112538)";
          return <button onClick={()=>{setShowDuplicates(true);if(!pend)setShowDupAccepted(true);}}
            title={pend
              ? (lang==="es"?"Revisar pólizas que parecen duplicadas":"Review policies that look duplicated")
              : (lang==="es"?"Ya revisaste todas — ver las que marcaste como correctas":"All reviewed — see the ones you marked as fine")}
            style={{display:"flex",alignItems:"center",gap:".4rem",padding:".45rem .75rem",borderRadius:8,
              background:bg,border:pend?"1px solid rgba(245,158,11,.4)":"1px solid var(--c-border,#1a3550)",
              color:pend?"#f59e0b":"var(--c-text3,#45688a)",fontSize:".72rem",fontWeight:700,cursor:"pointer",transition:"all .15s"}}
            onMouseEnter={e=>{e.currentTarget.style.background=pend?"rgba(245,158,11,.22)":"var(--c-surface,#0d1b2a)";}}
            onMouseLeave={e=>{e.currentTarget.style.background=bg;}}>
            <Copy size={13}/>{pend
              ? (lang==="es"?`Duplicadas (${dupPending.length})`:`Duplicates (${dupPending.length})`)
              : (lang==="es"?`Duplicadas ✓`:`Duplicates ✓`)}
          </button>;
        })()}
      </div>
    </div>
    <div style={{position:"relative",marginBottom:".875rem"}}><Search size={13} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--c-text3,#45688a)",pointerEvents:"none"}}/><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search policies..." className="dkt-search" style={{paddingLeft:32}}/></div>
    <div className="flex flex-wrap gap-2 mb-5" style={{alignItems:"center"}}>
      {/* TIPO DE PÓLIZA en un desplegable, como en Clientes (Lissette
          2026-09-30: "que sea como es en cliente, un drop down"). Antes era una
          fila de botones, uno por tipo, que ocupaba dos renglones. Cada opción
          lleva cuántas pólizas tiene. */}
      <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)} className="dkt-inp"
        style={{fontSize:".73rem",padding:".4rem .75rem",width:"auto",cursor:"pointer"}}
        title={lang==="es"?"Filtrar por tipo de póliza":"Filter by policy type"}>
        <option value="all">{lang==="es"?`📋 Todo Tipo (${policies.length})`:`📋 All Types (${policies.length})`}</option>
        {POLICY_TYPES.map(pt=>{ const count=policies.filter(p=>p.type===pt.id).length; return count>0
          ? <option key={pt.id} value={pt.id}>{pt.icon} {lang==="es"?pt.labelEs:pt.label} ({count})</option>
          : null; })}
      </select>
      {/* Marketplace — solo con Salud (Mercado) elegido (Lissette 2026-09-29). */}
      {typeFilter==="aca" && (()=>{
        const opciones = opcionesDeMarketplace(policies.filter(p=>p.type==="aca"));
        if (!opciones.length) return null;
        return (
          <select value={mktFilter} onChange={e=>setMktFilter(e.target.value)} className="dkt-inp"
            style={{fontSize:".73rem",padding:".4rem .75rem",width:"auto",cursor:"pointer"}}
            title={lang==="es"?"Filtrar por marketplace":"Filter by marketplace"}>
            <option value="all">{lang==="es"?`🏛️ Todo Marketplace (${opciones.length})`:`🏛️ All Marketplaces (${opciones.length})`}</option>
            {opciones.map(o=>(
              <option key={o.valor} value={o.valor}>
                {o.valor===SIN_MARKETPLACE ? (lang==="es"?"Sin marketplace":"No marketplace") : o.valor} ({o.cuenta})
              </option>
            ))}
          </select>
        );
      })()}
      {/* Carrier dropdown — only shown when there's more than one carrier
          to pick from within the selected type. Otherwise it's a no-op
          control that clutters the toolbar. "Limpiar" returns to the
          default "all" option without forcing the user to scroll the
          select to its first entry. */}
      {carrierOptions.length > 1 && (
        <FiltroMultiple
          opciones={carrierOptions}
          seleccion={carrierFilter}
          onChange={setCarrierFilter}
          contar={(car) => policies.filter(p =>
            (typeFilter === "all" || p.type === typeFilter) && p.carrier === car
          ).length}
          textoTodas={lang === "es" ? `Toda Aseguradora (${carrierOptions.length})` : `All Carriers (${carrierOptions.length})`}
          titulo={lang === "es" ? "Filtrar por aseguradora" : "Filter by carrier"}
          lang={lang} />
      )}
      {/* US-state dropdown — a policy's state comes from its client. Only shown
          when there's more than one state in the book (Lissette 2026-06-22). */}
      {stateOptions.length > 1 && (
        <select
          value={stateFilter}
          onChange={e => setStateFilter(e.target.value)}
          className="dkt-inp"
          style={{fontSize:".73rem",padding:".4rem .75rem",width:"auto",cursor:"pointer"}}
          title={lang==="es" ? "Filtrar por estado" : "Filter by state"}>
          <option value="all">
            {lang === "es" ? `📍 Todo Estado (${stateOptions.length})` : `📍 All States (${stateOptions.length})`}
          </option>
          {stateOptions.map(st => {
            const count = policies.filter(p => stateOf(p).toUpperCase() === st.toUpperCase()).length;
            return <option key={st} value={st}>{st} ({count})</option>;
          })}
        </select>
      )}
      {stateFilter !== "all" && (
        <button onClick={()=>setStateFilter("all")}
          style={{display:"inline-flex",alignItems:"center",gap:3,padding:".35rem .6rem",borderRadius:7,cursor:"pointer",border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontSize:".7rem",fontWeight:600,fontFamily:"inherit"}}>
          <X size={11}/>{lang === "es" ? "Limpiar" : "Clear"}
        </button>
      )}
      {/* "Atiende" (handle) — the seat that handles the policy's client. Same
          semantics as the clients-list filter (Lissette 2026-07-02). */}
      {attendOptions(users).length > 0 && (
        <select value={attendFilter} onChange={e=>setAttendFilter(e.target.value)} className="dkt-inp"
          style={{fontSize:".73rem",padding:".4rem .75rem",width:"auto",cursor:"pointer"}}
          title={lang==="es"?"Filtrar por quién atiende al cliente":"Filter by who handles the client"}>
          <option value="all">{lang==="es"?"👤 Atiende: Todos":"👤 Handled: All"}</option>
          <option value="none">{lang==="es"?"Sin asignar":"Unassigned"}</option>
          {attendOptions(users).map(u => { const l = seatLabelFor(u.id, seatLabels, users); return <option key={u.id} value={u.id}>{l.name}</option>; })}
        </select>
      )}
      {attendFilter !== "all" && (
        <button onClick={()=>setAttendFilter("all")}
          style={{display:"inline-flex",alignItems:"center",gap:3,padding:".35rem .6rem",borderRadius:7,cursor:"pointer",border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontSize:".7rem",fontWeight:600,fontFamily:"inherit"}}>
          <X size={11}/>{lang === "es" ? "Limpiar" : "Clear"}
        </button>
      )}
      {/* Analista — the analystName recorded on each policy. ALWAYS visible
          (hiding it when no policy had an analyst yet read as "the filter is
          missing" — Lissette 2026-07-02). */}
      <select value={analystFilter} onChange={e=>setAnalystFilter(e.target.value)} className="dkt-inp"
        style={{fontSize:".73rem",padding:".4rem .75rem",width:"auto",cursor:"pointer"}}
        title={lang==="es"?"Filtrar por analista (el campo Analista de cada póliza)":"Filter by analyst (each policy's Analyst field)"}>
        <option value="all">{lang==="es"?"🔎 Analista: Todos":"🔎 Analyst: All"}</option>
        <option value="none">{lang==="es"?"Sin analista":"No analyst"}</option>
        {analystOptions.map(a => { const count = policies.filter(p => (p.analystName||"").trim() === a).length; return <option key={a} value={a}>{a} ({count})</option>; })}
      </select>
      {analystFilter !== "all" && (
        <button onClick={()=>setAnalystFilter("all")}
          style={{display:"inline-flex",alignItems:"center",gap:3,padding:".35rem .6rem",borderRadius:7,cursor:"pointer",border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontSize:".7rem",fontWeight:600,fontFamily:"inherit"}}>
          <X size={11}/>{lang === "es" ? "Limpiar" : "Clear"}
        </button>
      )}
      {/* Etiqueta de PÓLIZA — las que se ven en la tarjeta junto al estado. No
          son las del cliente. Siempre visible, aunque todavía no haya ninguna
          puesta: esconder un filtro cuando está vacío se lee como que falta
          (la misma razón que el de Analista, 2026-07-02). */}
      <select value={tagFilter} onChange={e=>setTagFilter(e.target.value)} className="dkt-inp"
        style={{fontSize:".73rem",padding:".4rem .75rem",width:"auto",cursor:"pointer"}}
        title={lang==="es"?"Filtrar por etiqueta de la póliza":"Filter by policy tag"}>
        <option value="all">{lang==="es"?"🏷️ Etiqueta: Todas":"🏷️ Tag: All"}</option>
        {untaggedCount>0 && <option value="none">{textoSinEtiqueta(untaggedCount, lang)}</option>}
        {tagCounts.opciones.map(t => <option key={t.label} value={t.label}>{t.label} ({t.n})</option>)}
      </select>
      {tagFilter !== "all" && tagFilter !== "none" && (
        <button onClick={()=>setQuitarEtq({ids:null, inicial:tagFilter})}
          title={lang==="es"?"Quitar esta etiqueta de todas las pólizas que la tienen":"Remove this tag from every policy that has it"}
          style={{display:"inline-flex",alignItems:"center",gap:3,padding:".35rem .6rem",borderRadius:7,cursor:"pointer",border:"1px solid rgba(239,68,68,.45)",background:"rgba(239,68,68,.1)",color:"var(--c-red,#ef4444)",fontSize:".7rem",fontWeight:700,fontFamily:"inherit"}}>
          <Tag size={11}/>{lang === "es" ? "Quitar de todas" : "Remove from all"}
        </button>
      )}
      {tagFilter !== "all" && (
        <button onClick={()=>setTagFilter("all")}
          style={{display:"inline-flex",alignItems:"center",gap:3,padding:".35rem .6rem",borderRadius:7,cursor:"pointer",border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontSize:".7rem",fontWeight:600,fontFamily:"inherit"}}>
          <X size={11}/>{lang === "es" ? "Limpiar" : "Clear"}
        </button>
      )}
      {/* "Agente" = the client's assigned agent (Client.assignedAgent). Lissette 2026-07-15. */}
      {assignedAgentOptions.length > 0 && (
        <select value={assignedAgentFilter} onChange={e=>setAssignedAgentFilter(e.target.value)} className="dkt-inp"
          style={{fontSize:".73rem",padding:".4rem .75rem",width:"auto",cursor:"pointer"}}
          title={lang==="es"?"Filtrar por el agente del cliente":"Filter by the client's agent"}>
          <option value="all">{lang==="es"?"👤 Agente: Todos":"👤 Agent: All"}</option>
          <option value="none">{lang==="es"?"Sin agente":"No agent"}</option>
          {assignedAgentOptions.map(a => { const count = policies.filter(p => assignedAgentOf(p) === a).length; return <option key={a} value={a}>{a} ({count})</option>; })}
        </select>
      )}
      {assignedAgentFilter !== "all" && (
        <button onClick={()=>setAssignedAgentFilter("all")}
          style={{display:"inline-flex",alignItems:"center",gap:3,padding:".35rem .6rem",borderRadius:7,cursor:"pointer",border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontSize:".7rem",fontWeight:600,fontFamily:"inherit"}}>
          <X size={11}/>{lang === "es" ? "Limpiar" : "Clear"}
        </button>
      )}
      {/* "Agente en la póliza" = the agent on the policy (policy.agentLabel). Lissette 2026-07-15. */}
      {agentLabelOptions.length > 0 && (
        <select value={agentLabelFilter} onChange={e=>setAgentLabelFilter(e.target.value)} className="dkt-inp"
          style={{fontSize:".73rem",padding:".4rem .75rem",width:"auto",cursor:"pointer"}}
          title={lang==="es"?"Filtrar por el agente en la póliza":"Filter by the agent on the policy"}>
          <option value="all">{lang==="es"?"📄 Agente en póliza: Todos":"📄 Agent on policy: All"}</option>
          <option value="none">{lang==="es"?"Sin agente en póliza":"No agent on policy"}</option>
          {agentLabelOptions.map(a => { const count = policies.filter(p => (p.agentLabel||"").trim() === a).length; return <option key={a} value={a}>{a} ({count})</option>; })}
        </select>
      )}
      {agentLabelFilter !== "all" && (
        <button onClick={()=>setAgentLabelFilter("all")}
          style={{display:"inline-flex",alignItems:"center",gap:3,padding:".35rem .6rem",borderRadius:7,cursor:"pointer",border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontSize:".7rem",fontWeight:600,fontFamily:"inherit"}}>
          <X size={11}/>{lang === "es" ? "Limpiar" : "Clear"}
        </button>
      )}
      {/* AÑO DE INICIO (Lissette 2026-09-13: "un filtro de año dependiendo del
          año que comenzó la póliza"). Va ANTES del rango Desde/Hasta porque es
          la pregunta gruesa —"enseñame las de 2025"— y el rango es la fina. Cada
          opción lleva cuántas pólizas hay detrás, para no hacer clic a ciegas. */}
      {yearOptions.length > 0 && (
        <select value={yearFilter} onChange={e=>setYearFilter(e.target.value)} className="dkt-inp"
          style={{fontSize:".73rem",padding:".4rem .6rem",width:"auto",cursor:"pointer"}}
          title={lang==="es"?"Filtrar por el año en que empezó la póliza (fecha efectiva)":"Filter by the year the policy started (effective date)"}>
          <option value="all">{lang==="es"?`🗓️ Todo año (${policies.length})`:`🗓️ All years (${policies.length})`}</option>
          {yearOptions.map(({anio,cuantas})=><option key={anio} value={anio}>{anio} ({cuantas})</option>)}
          {sinAnioCount>0 && <option value="none">{(lang==="es"?"Sin fecha efectiva":"No effective date")+` (${sinAnioCount})`}</option>}
        </select>
      )}
      {/* Date range — Desde/Hasta over a pickable date field (Lissette 2026-07-02). */}
      <span style={{display:"inline-flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
        <select value={dateField} onChange={e=>setDateField(e.target.value)} className="dkt-inp"
          style={{fontSize:".73rem",padding:".4rem .5rem",width:"auto",cursor:"pointer"}}
          title={lang==="es"?"Qué fecha se filtra con Desde/Hasta":"Which date the From/To range filters"}>
          <option value="effective">{lang==="es"?"📅 F. Efectiva":"📅 Effective"}</option>
          <option value="renewal">{lang==="es"?"📅 F. Renovación":"📅 Renewal"}</option>
          <option value="sale">{lang==="es"?"📅 F. Venta":"📅 Sale date"}</option>
          <option value="cancellation">{lang==="es"?"📅 F. Cancelación":"📅 Cancellation date"}</option>
        </select>
        <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="dkt-inp"
          style={{fontSize:".73rem",padding:".35rem .5rem",width:"auto",cursor:"pointer",colorScheme:"dark"}}
          title={lang==="es"?"Desde":"From"}/>
        <span style={{color:"var(--c-text3,#45688a)",fontWeight:800,fontSize:".72rem"}}>→</span>
        <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className="dkt-inp"
          style={{fontSize:".73rem",padding:".35rem .5rem",width:"auto",cursor:"pointer",colorScheme:"dark"}}
          title={lang==="es"?"Hasta":"To"}/>
        {(dateFrom||dateTo) && (
          <button onClick={()=>{setDateFrom("");setDateTo("");}}
            style={{display:"inline-flex",alignItems:"center",gap:3,padding:".35rem .6rem",borderRadius:7,cursor:"pointer",border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontSize:".7rem",fontWeight:600,fontFamily:"inherit"}}>
            <X size={11}/>{lang === "es" ? "Limpiar" : "Clear"}
          </button>
        )}
      </span>
      {/* Status buckets — active vs cancelled, with live counts. Scoped to
          every other filter (year, dates, agent…) so the numbers match what's shown. */}
      {(()=>{
        const scoped=policies.filter(pasaFiltrosSinEstatus);
        const cancCount=scoped.filter(isCancelled).length;
        const pendCount=scoped.filter(p=>!isCancelled(p)&&p.status==="pending").length;
        const actCount=scoped.length-cancCount-pendCount;
        return <>
          <button onClick={()=>setStatusFilter("all")} className={`dkt-tab${statusFilter==="all"?" on":""}`}>{lang==="es"?"Todas":"All"} ({scoped.length})</button>
          <button onClick={()=>setStatusFilter("active")} className={`dkt-tab${statusFilter==="active"?" on":""}`} style={{color:statusFilter==="active"?undefined:"#00ff88"}}>✅ {lang==="es"?"Activas":"Active"} ({actCount})</button>
          <button onClick={()=>setStatusFilter("pending")} className={`dkt-tab${statusFilter==="pending"?" on":""}`} style={{color:statusFilter==="pending"?undefined:"#f59e0b"}}>⏳ {lang==="es"?"Pendientes":"Pending"} ({pendCount})</button>
          <button onClick={()=>setStatusFilter("cancelled")} className={`dkt-tab${statusFilter==="cancelled"?" on":""}`} style={{color:statusFilter==="cancelled"?undefined:"#ff3d6b"}}>🚫 {lang==="es"?"Canceladas":"Cancelled"} ({cancCount})</button>
        </>;
      })()}
      {/* Filtro por estatus específico — Pendiente, Período de gracia y los
          estatus PERSONALIZADOS de la agencia (Lissette 2026-07-01). statusVer
          fuerza re-leer el catálogo tras crear/quitar un estatus. */}
      {(()=>{ void statusVer; return (
        <select value={["all","active","pending","cancelled"].includes(statusFilter)?"":statusFilter}
          onChange={(e)=>setStatusFilter(e.target.value||"all")}
          className="dkt-search" style={{width:'auto',minWidth:140}}
          title={lang==="es"?"Filtrar por estatus (Gracia, personalizados)":"Filter by status (Grace, custom)"}>
          <option value="">{lang==="es"?"Estatus…":"Status…"}</option>
          {getAllPolicyStatuses().filter((s)=>s.id!=="pending").map((s)=><option key={s.id} value={s.id}>{lang==="es"?(s.labelEs||s.label):s.label}</option>)}
        </select>
      ); })()}
      <button onClick={()=>setStatusMgr(true)} className="dkt-tab" style={{display:"inline-flex",alignItems:"center",gap:4}}
        title={lang==="es"?"Crear o administrar estatus personalizados":"Create or manage custom statuses"}>⚙️ {lang==="es"?"Estatus":"Statuses"}</button>
      {/* Solo cuando hay algo que limpiar: un boton que siempre esta y casi
          nunca hace nada se deja de ver a los dos dias. Dice CUANTAS quedan
          para que se entienda que la lista estaba recortada. */}
      {hayFiltros && <button onClick={limpiarFiltros} className="dkt-tab"
        style={{display:"inline-flex",alignItems:"center",gap:4,color:"var(--c-amber,#f59e0b)",borderColor:"rgba(245,158,11,.4)"}}
        title={lang==="es"?"Quitar todos los filtros y ver la lista completa":"Clear every filter and see the whole list"}>
        <X size={12}/>{lang==="es"?`Limpiar filtros (${filtered.length} de ${policies.length})`:`Clear filters (${filtered.length} of ${policies.length})`}
      </button>}
      <div style={{flex:1}}/>
      {/* ORDENAR POR AÑO. Un solo boton con tres pasos —nuevas primero, viejas
          primero, sin ordenar— y la flecha DICE en cual esta: un orden que no
          se ve desde fuera es un orden que nadie sabe si esta puesto. Es
          hermano del de "Por vencer" y comparte `sortBy`, asi que elegir uno
          apaga el otro solo; dos ordenes a la vez no existen. */}
      <button onClick={()=>setSortBy(sortBy==="anioDesc"?"anioAsc":sortBy==="anioAsc"?"default":"anioDesc")}
        className={`dkt-tab${sortBy==="anioDesc"||sortBy==="anioAsc"?" on":""}`}
        style={{display:"flex",alignItems:"center",gap:4}}
        title={lang==="es"
          ? (sortBy==="anioDesc" ? "Las más nuevas primero · clic para las más viejas primero"
            : sortBy==="anioAsc" ? "Las más viejas primero · clic para quitar el orden"
            : "Ordenar por el año en que empezó la póliza")
          : (sortBy==="anioDesc" ? "Newest first · click for oldest first"
            : sortBy==="anioAsc" ? "Oldest first · click to clear the sort"
            : "Sort by the year the policy started")}>
        🗓️ {lang==="es" ? "Por año" : "By year"}{sortBy==="anioDesc"?" ↓":sortBy==="anioAsc"?" ↑":""}
      </button>
      {/* DE LA A A LA Z POR CLIENTE. Tercer hermano de "Por ano" y "Por
          vencer": comparten `sortBy`, asi que elegir uno apaga los otros dos
          solo. Tres pasos —A-Z, Z-A, sin ordenar— y la flecha dice en cual
          esta. */}
      <button onClick={()=>setSortBy(sortBy==="azAsc"?"azDesc":sortBy==="azDesc"?"default":"azAsc")}
        className={`dkt-tab${sortBy==="azAsc"||sortBy==="azDesc"?" on":""}`}
        style={{display:"flex",alignItems:"center",gap:4}}
        title={lang==="es"
          ? (sortBy==="azAsc" ? "De la A a la Z por cliente · clic para la Z a la A"
            : sortBy==="azDesc" ? "De la Z a la A por cliente · clic para quitar el orden"
            : "Ordenar por el nombre del cliente, de la A a la Z")
          : (sortBy==="azAsc" ? "A to Z by client · click for Z to A"
            : sortBy==="azDesc" ? "Z to A by client · click to clear the sort"
            : "Sort by client name, A to Z")}>
        🔤 {lang==="es" ? "Cliente A-Z" : "Client A-Z"}{sortBy==="azAsc"?" ↑":sortBy==="azDesc"?" ↓":""}
      </button>
      <button onClick={()=>setSortBy(sortBy==="expiringSoon"?"default":"expiringSoon")}
        className={`dkt-tab${sortBy==="expiringSoon"?" on":""}`}
        style={{display:"flex",alignItems:"center",gap:4}}
        title={lang==="es"?"Ordenar por días para vencer":"Sort by days to expire"}>
        ⏰ {lang==="es" ? "Por vencer" : "Expiring"}
      </button>
    </div>
    {statusMgr && <PolicyStatusManager lang={lang} onClose={()=>setStatusMgr(false)} onChanged={()=>setStatusVer((v)=>v+1)}/>}
    {/* La lista y su paginacion bajan en su propia caja; lo de arriba se
        queda quieto (Lissette 2026-09-25, CajaConScroll.jsx). */}
    <CajaConScroll>
    <div className="space-y-2">
      {filtered.length===0&&<div className="dkt-card" style={{textAlign:"center",padding:"3rem",color:"var(--c-text3,#45688a)"}}>No policies found</div>}
      {paginated.map(p=>{
        const c=clients.find(cl=>cl.id===p.clientId);
        const days = daysUntilExp(p);
        const dColor = days === null ? null : days < 0 ? '#ef4444' : days <= 7 ? '#ef4444' : days <= 30 ? '#f97316' : days <= 90 ? '#ffb700' : '#7aa3c0';
        const dLabel = days === null ? null
          : days < 0 ? (lang==='es' ? `Venció hace ${Math.abs(days)}d` : `${Math.abs(days)}d overdue`)
          : (lang==='es' ? `${days}d para vencer` : `${days}d to expire`);
        const isSel = selectedIds.has(p.id);
        return <div key={p.id} className="dkt-card" onClick={selectMode?()=>toggleSelect(p.id):undefined}
          style={{padding:".875rem 1rem",display:"flex",alignItems:"flex-start",gap:"1rem",
            cursor:selectMode?"pointer":undefined,
            ...(selectMode&&isSel?{borderColor:"rgba(0,212,255,.5)",background:"rgba(0,212,255,.06)"}:{})}}>
        {selectMode && <input type="checkbox" checked={isSel} onChange={()=>toggleSelect(p.id)} onClick={e=>e.stopPropagation()}
          style={{width:18,height:18,marginTop:2,cursor:"pointer",accentColor:"#00d4ff",flex:"0 0 auto"}}/>}
        <div className="flex-1">
          <div style={{display:"flex",alignItems:"center",gap:".65rem",marginBottom:".6rem",flexWrap:"wrap"}}>
            {c&&<>
              <Avatar client={c} size={7}/>
              {/* ENLACE de verdad, no un botón: así el clic derecho ofrece
                  "abrir en pestaña nueva", y la rueda o Ctrl la abren solas
                  (Lissette 2026-08-19). El clic normal sigue yendo a la ficha
                  en la misma pantalla — por eso el preventDefault, que solo se
                  aplica al clic simple. */}
              <a href={`#client/${c.id}`}
                onClick={(e)=>{ if(e.metaKey||e.ctrlKey||e.shiftKey||e.button===1) return; e.preventDefault(); if(onClientClick) onClientClick(c); }}
                style={{background:"none",border:"none",cursor:"pointer",fontWeight:700,fontSize:".92rem",
                  color:"var(--c-white,#eef6ff)",padding:0,transition:"color .2s",textAlign:"left",textDecoration:"none"}}
                onMouseEnter={e=>e.currentTarget.style.color="var(--c-cyan,#00d4ff)"}
                onMouseLeave={e=>e.currentTarget.style.color="var(--c-white,#eef6ff)"}>
                {/* El nombre EN EL ORDEN QUE TENGA PUESTO LA AGENCIA
                    ("Nombre Apellido" o "Apellido, Nombre"), igual que la lista
                    de Clientes y la de Pagos del Mes. Antes esta fila era la
                    unica que lo escribia siempre al derecho, asi que con la
                    agencia configurada por apellido el orden A-Z ordenaba por
                    una palabra que aqui no se veia (Lissette 2026-09-15). */}
                {formatClientName(c)}
              </a>
            </>}
            {dLabel && (
              <span style={{fontSize:".62rem",fontWeight:700,padding:"2px 8px",borderRadius:100,background:dColor+"22",color:dColor,border:`1px solid ${dColor}55`,fontFamily:"var(--font-mono)"}}>
                ⏰ {dLabel}
              </span>
            )}
          </div>
          {(()=>{ const covered=insuredNamesList(p,c); return covered.length>0 && (
            <div style={{fontSize:".68rem",color:"var(--c-text2,#7aa3c0)",marginTop:"-.35rem",marginBottom:".55rem",display:"flex",alignItems:"flex-start",gap:5,lineHeight:1.35}}>
              <span style={{flexShrink:0}}>🛡️</span>
              <span><span style={{fontWeight:700}}>{lang==="es"?"Asegurados:":"Insured:"}</span> {covered.join(", ")}</span>
            </div>
          ); })()}
          <PolicyCard policy={p} t={t} lang={lang} onEdit={p=>setEditPol(p)} onDelete={id=>setDelPol(id)} onMove={isApiModeP?(pol=>setMovPol(pol)):undefined} compact client={c} dependents={c?.dependents||[]} currentUserId={currentUser?.id}
            onUpdateTags={updatePolicyTags} policyTagLibrary={policyTagLibrary} onPolicyTagLibraryChange={onPolicyTagLibraryChange}
            policyTagColors={policyTagColors} onPolicyTagColorsChange={onPolicyTagColorsChange}/>
        </div>
      </div>; })}
    </div>
    {filtered.length > 0 && (
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        gap:'.75rem', flexWrap:'wrap',
        marginTop:'1rem', padding:'.6rem .85rem',
        background:'var(--c-surface2,#112538)', border:'1px solid var(--c-border,#1a3550)',
        borderRadius:8, fontSize:'.72rem', color:'var(--c-text2,#7aa3c0)',
      }}>
        <div>
          {lang === 'es'
            ? `Mostrando ${pageStart + 1}–${Math.min(pageStart + pageSize, filtered.length)} de ${filtered.length}`
            : `Showing ${pageStart + 1}–${Math.min(pageStart + pageSize, filtered.length)} of ${filtered.length}`}
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'.5rem', flexWrap:'wrap'}}>
          <label style={{display:'flex', alignItems:'center', gap:'.35rem', fontSize:'.7rem'}}>
            <span>{lang === 'es' ? 'Por página:' : 'Per page:'}</span>
            <select
              value={pageSize}
              onChange={e => setPageSize(parseInt(e.target.value, 10))}
              style={{padding:'2px 6px', borderRadius:6, border:'1px solid var(--c-border,#1a3550)', background:'var(--c-surface,#0d1c2e)', color:'var(--c-text,#c8dff0)', fontFamily:'inherit', cursor:'pointer'}}
            >
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </label>
          <div style={{display:'flex', alignItems:'center', gap:'.4rem'}}>
            <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              style={{padding:'.3rem .7rem', borderRadius:6, fontFamily:'inherit', fontSize:'.7rem', fontWeight:700,
                border:'1px solid var(--c-border,#1a3550)',
                background: page <= 1 ? 'var(--c-surface,#0d1c2e)' : 'var(--c-surface2,#112538)',
                color: page <= 1 ? 'var(--c-text3,#45688a)' : 'var(--c-text,#c8dff0)',
                cursor: page <= 1 ? 'not-allowed' : 'pointer'}}>
              ◀ {lang === 'es' ? 'Anterior' : 'Prev'}
            </button>
            <span style={{fontSize:'.7rem', fontWeight:700, color:'var(--c-text,#c8dff0)', minWidth:90, textAlign:'center'}}>
              {lang === 'es' ? `Página ${page} de ${totalPages}` : `Page ${page} of ${totalPages}`}
            </span>
            <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              style={{padding:'.3rem .7rem', borderRadius:6, fontFamily:'inherit', fontSize:'.7rem', fontWeight:700,
                border:'1px solid var(--c-border,#1a3550)',
                background: page >= totalPages ? 'var(--c-surface,#0d1c2e)' : 'var(--c-surface2,#112538)',
                color: page >= totalPages ? 'var(--c-text3,#45688a)' : 'var(--c-text,#c8dff0)',
                cursor: page >= totalPages ? 'not-allowed' : 'pointer'}}>
              {lang === 'es' ? 'Siguiente' : 'Next'} ▶
            </button>
          </div>
        </div>
      </div>
    )}
    </CajaConScroll>
    {editPol&&<Modal title={t.policies.editTitle} onClose={()=>setEditPol(null)} wide>
      <PolicyForm policy={editPol} onSave={async form=>{
        if (isApiModeP) {
          try { await apiUpdatePolicy(editPol.id, { ...editPol, ...form }); }
          catch (err) { toast.error((lang==="es"?"No se pudo actualizar la póliza: ":"Failed to update policy: ")+err.message); return; }
        }
        onPoliciesChange(prev=>prev.map(p=>p.id===editPol.id?{...editPol,...form}:p));
        toast.success(lang==="es"?"Guardado ✓":"Saved ✓");
        setEditPol(null);
      }} onClose={()=>setEditPol(null)} t={t} lang={lang} carrierSuggestions={carrierSuggestions} soaStatusSuggestions={soaStatusSuggestions} hraStatusSuggestions={hraStatusSuggestions} medicaidSuggestions={medicaidSuggestions} lisSuggestions={lisSuggestions} productTypeSuggestions={productTypeSuggestions} supplementSuggestions={supplementSuggestions}
      client={(clients||[]).find(c=>c.id===editPol.clientId)}
      dependents={((clients||[]).find(c=>c.id===editPol.clientId)||{}).dependents||[]}
      users={users} currentUser={currentUser}
      knownAgents={[...new Set((policies||[]).map(p=>p.agentLabel).filter(Boolean))]}
      knownAnalysts={[...new Set((policies||[]).map(p=>p.analystName).filter(Boolean))]}
      knownProcessors={[...new Set((policies||[]).map(p=>p.processedBy).filter(Boolean))]} />
    </Modal>}
    {delPol&&<Modal title={t.common.confirm} onClose={()=>setDelPol(null)}>
      <p style={{color:"var(--c-text2,#7aa3c0)",marginBottom:"1.25rem",fontSize:".88rem"}}>{lang==="es"?"¿Eliminar esta póliza?":"Delete this policy?"}</p>
      <div style={{display:"flex",gap:".75rem"}}>
        <button type="button" onClick={async()=>{
          if (isApiModeP) {
            try { await apiDeletePolicy(delPol); }
            catch (err) { toast.error((lang==="es"?"No se pudo eliminar la póliza: ":"Failed to delete policy: ")+err.message); return; }
          }
          onPoliciesChange(prev=>prev.filter(p=>p.id!==delPol));
          setDelPol(null);
        }} className="dkt-btn-d" style={{flex:1,justifyContent:"center"}}>{t.common.yes}</button>
        <button type="button" onClick={()=>setDelPol(null)} className="dkt-btn-s" style={{flex:1,justifyContent:"center"}}>{t.common.no}</button>
      </div>
    </Modal>}
    {movPol&&<MoverPolizaModal policy={movPol} clients={clients} lang={lang}
      clienteActual={(clients||[]).find(c=>c.id===movPol.clientId)}
      onClose={()=>setMovPol(null)}
      onMoved={(movida)=>onPoliciesChange(prev=>prev.map(p=>p.id===movida.id?movida:p))}/>}
    {showDuplicates && <Modal title={lang==="es"?"Pólizas duplicadas":"Duplicate policies"} onClose={()=>setShowDuplicates(false)}>
      {dupPending.length===0 ? (
        <div style={{textAlign:'center',padding:'1.5rem 1rem',color:'var(--c-text2,#7aa3c0)'}}>
          <div style={{fontSize:'1.8rem',marginBottom:'.4rem'}}>✅</div>
          {lang==="es"?"No queda nada por revisar.":"Nothing left to review."}
          {dupAccepted.length>0 && (
            <div style={{fontSize:'.72rem',color:'var(--c-text3,#45688a)',marginTop:'.5rem'}}>
              {lang==="es"
                ? `${dupAccepted.length} grupo(s) marcados como correctos.`
                : `${dupAccepted.length} group(s) marked as fine.`}
            </div>
          )}
        </div>
      ) : (<>
        {/* El detector SOSPECHA, no sabe. Un dependiente mayor con póliza
            propia del mismo plan que el titular se ve exactamente igual que
            una duplicada de verdad, así que la decisión es de la agente y
            tiene que poder decir las DOS cosas. Antes solo se podía borrar
            (Lissette 2026-08-17). */}
        <div style={{fontSize:'.76rem',color:'var(--c-text2,#7aa3c0)',marginBottom:'.9rem',lineHeight:1.5}}>
          {lang==="es"
            ? `${dupPending.length} grupo(s) parecen la misma póliza (mismo cliente y número, o mismo cliente + aseguradora + tipo + prima). Si de verdad sobra una, bórrala; si las dos son buenas —por ejemplo un dependiente mayor con su propia póliza— marca "Se queda" y dejan de avisarte.`
            : `${dupPending.length} group(s) look like the same policy (same client and number, or same client + carrier + type + premium). If one really is extra, delete it; if both are legitimate — say, an adult dependent with their own policy — mark "Keep" and they stop being flagged.`}
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:'.85rem',maxHeight:'60vh',overflowY:'auto'}}>
          {dupPending.map((group,gi)=>{
            const c=(clients||[]).find(x=>x.id===group[0].clientId);
            const cname=c?`${c.firstName||''} ${c.lastName||''}`.trim():(lang==="es"?'Cliente':'Client');
            return (
              <div key={gi} style={{border:'1px solid rgba(245,158,11,.35)',borderRadius:10,padding:'.7rem',background:'rgba(245,158,11,.05)'}}>
                <div style={{fontSize:'.8rem',fontWeight:800,color:'var(--c-white,#eef6ff)',marginBottom:'.5rem',display:'flex',alignItems:'center',gap:'.5rem',flexWrap:'wrap'}}>
                  👤 {cname}
                  {onClientClick && c && <button type="button" onClick={()=>{onClientClick(c);setShowDuplicates(false);}} className="dkt-btn ghost xs">{lang==="es"?"Abrir ficha":"Open client"}</button>}
                  {/* Atajo para el caso frecuente: las dos son buenas. */}
                  <button type="button" disabled={dupBusy} onClick={()=>markDupOk(group,true)} className="dkt-btn ghost xs"
                    title={lang==="es"?"Ninguna sobra: dejar de avisar de este grupo":"Neither is extra: stop flagging this group"}
                    style={{marginLeft:'auto'}}>
                    ✓ {lang==="es"?"No son duplicadas":"Not duplicates"}
                  </button>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:'.4rem'}}>
                  {group.map((p)=>{
                    const pt=getPolicyType(p.type);
                    return (
                      <div key={p.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'.6rem',background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',borderRadius:8,padding:'.45rem .6rem'}}>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:'.76rem',fontWeight:700,color:'var(--c-text,#c8dff0)'}}>{p.carrier||'—'}{p.policyNumber?` · #${p.policyNumber}`:''}</div>
                          <div style={{fontSize:'.64rem',color:'var(--c-text3,#45688a)'}}>{(pt&&(pt.label||pt.name))||p.type} · {p.monthlyPremium?`$${p.monthlyPremium}`:'—'} · {p.effectiveDate||'—'} · {p.status}</div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:'.35rem',flexShrink:0}}>
                          {/* "Se queda" al lado de "Borrar": las dos salidas a
                              la misma altura, para no empujar hacia la
                              destructiva por ser la única que hay. */}
                          <button type="button" disabled={dupBusy} onClick={()=>markDupOk([p],!isDupOk(p))}
                            className={isDupOk(p)?"dkt-btn xs":"dkt-btn-s xs"}
                            title={isDupOk(p)
                              ? (lang==="es"?"Ya marcada como correcta — pulsa para deshacer":"Already marked as fine — click to undo")
                              : (lang==="es"?"Esta póliza es buena: no la borres":"This policy is legitimate: don't delete it")}>
                            {isDupOk(p)?"✓ ":""}{lang==="es"?"Se queda":"Keep"}
                          </button>
                          <button type="button" disabled={dupBusy} onClick={async()=>{
                            const ok=await confirmDialog({message:lang==="es"?"¿Borrar ESTA póliza? (la otra del grupo se queda)":"Delete THIS policy? (the other one stays)",danger:true});
                            if(!ok) return;
                            if(isApiModeP){ try{ await apiDeletePolicy(p.id); }catch(err){ toast.error((lang==="es"?"No se pudo eliminar: ":"Failed to delete: ")+err.message); return; } }
                            onPoliciesChange(prev=>prev.filter(x=>x.id!==p.id));
                          }} className="dkt-btn-d">
                            <Trash2 size={13}/>{lang==="es"?"Borrar":"Delete"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </>)}
      {/* La vuelta atrás. Marcar "se queda" esconde el grupo, y esconder algo
          sin camino de regreso es como se pierde un aviso para siempre: si se
          pulsó por error, desde aquí se recupera. */}
      {dupAccepted.length>0 && (
        <div style={{marginTop:'.9rem',borderTop:'1px solid var(--c-border,#1a3550)',paddingTop:'.7rem'}}>
          <button type="button" onClick={()=>setShowDupAccepted(v=>!v)} className="dkt-btn ghost xs">
            {showDupAccepted?'▾':'▸'} {lang==="es"
              ? `Marcadas como correctas (${dupAccepted.length})`
              : `Marked as fine (${dupAccepted.length})`}
          </button>
          {showDupAccepted && (
            <div style={{display:'flex',flexDirection:'column',gap:'.4rem',marginTop:'.5rem',maxHeight:'30vh',overflowY:'auto'}}>
              {dupAccepted.map((group,gi)=>{
                const c=(clients||[]).find(x=>x.id===group[0].clientId);
                const cname=c?`${c.firstName||''} ${c.lastName||''}`.trim():(lang==="es"?'Cliente':'Client');
                return (
                  <div key={gi} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'.6rem',fontSize:'.72rem',color:'var(--c-text2,#7aa3c0)',background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',borderRadius:8,padding:'.4rem .6rem'}}>
                    <span style={{minWidth:0}}>
                      <b style={{color:'var(--c-text,#c8dff0)'}}>{cname}</b>
                      {` · ${group[0].carrier||'—'} · ${group.length} ${lang==="es"?'pólizas':'policies'}`}
                    </span>
                    <button type="button" disabled={dupBusy} onClick={()=>markDupOk(group,false)} className="dkt-btn-s xs" style={{flexShrink:0}}>
                      {lang==="es"?"Volver a revisar":"Review again"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Modal>}
    {showTypeManager && <Modal title={lang==="es"?"Gestionar tipos de póliza":"Manage policy types"} onClose={()=>setShowTypeManager(false)}>
      <PolicyTypeManager lang={lang} onChange={()=>bumpTypes(n=>n+1)} policies={policies}/>
    </Modal>}
    {showFieldsManager && <Modal title={lang==="es"?"Gestionar campos personalizados":"Manage custom fields"} onClose={()=>setShowFieldsManager(false)}>
      <PolicyCustomFieldsManager lang={lang} policies={policies} onPoliciesChange={onPoliciesChange}/>
    </Modal>}
    {bulkEstatus && <CambiarEstatusModal
      policies={policies.filter(p=>selectedIds.has(p.id))}
      lang={lang}
      apiMode={isApiModeP}
      onClose={()=>setBulkEstatus(false)}
      onDone={({ids,destino,fecha})=>{
        // Lo de la pantalla se pone al dia sin recargar. El servidor manda
        // ademas la fila entera por SSE, asi que si alguna se quedo fuera de
        // alcance vuelve a su sitio sola.
        const tocadas=new Set(ids);
        onPoliciesChange(prev=>prev.map(p=>tocadas.has(p.id)
          ? {...p,status:destino,cancellationDate:destino==="cancelled"?fecha:"",...(destino==="cancelled"?{renewalDate:""}:{})}
          : p));
        exitSelect();
      }}/>}
    {bulkCampos && <CambioMasivoModal
      policies={policies.filter(p=>selectedIds.has(p.id))}
      users={users}
      seatLabels={seatLabels}
      analystNames={[...new Set([...analystOptions, ...(users||[]).map(u=>u.name)].filter(Boolean))].sort((a,b)=>a.localeCompare(b))}
      agentNames={[...new Set([...agentLabelOptions, ...(users||[]).filter(u=>["agency_owner","agency_admin","agent","solo_agent"].includes(u.role)).map(u=>u.name), ...getCollaboratorsPref().map(collaboratorLabel)].filter(Boolean))].sort((a,b)=>a.localeCompare(b))}
      lang={lang}
      apiMode={isApiModeP}
      onClose={()=>setBulkCampos(false)}
      onDone={({ids,campo,valor})=>{
        // Los de la POLIZA se ponen al dia aqui; los del CLIENTE llegan por
        // SSE (client.updated) a la ficha, que es de donde los lee la lista.
        if(campo==="analyst"||campo==="agent"){
          const tocadas=new Set(ids);
          onPoliciesChange(prev=>prev.map(p=>tocadas.has(p.id)?{...p,...(campo==="analyst"?{analystName:valor}:{agentLabel:valor})}:p));
        }
        exitSelect();
      }}/>}
    {quitarEtq && <QuitarEtiquetaModal
      de="polizas"
      ids={quitarEtq.ids}
      inicial={quitarEtq.inicial || ""}
      opciones={quitarEtq.ids ? contarEtiquetas((policies||[]).filter(p=>quitarEtq.ids.includes(p.id))).opciones : tagCounts.opciones}
      lang={lang}
      onClose={()=>setQuitarEtq(null)}
      onDone={({etiqueta,ids})=>{
        const tocadas=new Set(ids);
        onPoliciesChange(prev=>prev.map(p=>tocadas.has(p.id)?{...p,tags:sinEtiqueta(p.tags,etiqueta)}:p));
        if(String(tagFilter).toLowerCase()===String(etiqueta).toLowerCase()) setTagFilter("all");
        if(quitarEtq.ids) exitSelect();
      }}/>}
    {confirmBulk && <Modal title={lang==="es"?"Confirmar borrado":"Confirm delete"} onClose={()=>!bulkBusy&&setConfirmBulk(false)}>
      <p style={{color:"var(--c-text2,#7aa3c0)",marginBottom:".5rem",fontSize:".9rem",fontWeight:700}}>
        {lang==="es"?`¿Borrar ${selectedIds.size} póliza(s) seleccionada(s)?`:`Delete ${selectedIds.size} selected policy(ies)?`}
      </p>
      <p style={{color:"var(--c-text3,#45688a)",marginBottom:"1.25rem",fontSize:".78rem",lineHeight:1.45}}>
        {lang==="es"?"Se quitan de tu lista. Si borras de más, se pueden restaurar — dime y lo recupero.":"They are removed from your list. If you delete too many, they can be restored — just ask."}
      </p>
      <div style={{display:"flex",gap:".75rem"}}>
        <button type="button" disabled={bulkBusy} onClick={runBulkDelete} className="dkt-btn-d" style={{flex:1,justifyContent:"center",opacity:bulkBusy?.6:1,cursor:bulkBusy?"wait":"pointer"}}>
          {bulkBusy?(lang==="es"?"Borrando…":"Deleting…"):(lang==="es"?`Sí, borrar ${selectedIds.size}`:`Yes, delete ${selectedIds.size}`)}
        </button>
        <button type="button" disabled={bulkBusy} onClick={()=>setConfirmBulk(false)} className="dkt-btn-s" style={{flex:1,justifyContent:"center"}}>{lang==="es"?"Cancelar":"Cancel"}</button>
      </div>
    </Modal>}
  </div>;
}

export function PolicyTypeManager({ lang, onChange, policies }) {
  const [, bump] = useState(0);
  const [draft, setDraft] = useState({ name:"", icon:"📄", pc:false });
  const customTypes = POLICY_TYPES.filter(p=>p.custom);
  const defaultTypes = POLICY_TYPES.filter(p=>!p.custom);
  const handleAdd = () => {
    const name = draft.name.trim();
    if (!name) return;
    const newType = {
      id: "custom_" + Date.now(),
      label: name,
      labelEs: name,
      icon: draft.icon || "📄",
      color: draft.pc ? "#0ea5e9" : "#8b5cf6",
      pc: !!draft.pc,
      custom: true,
    };
    addCustomPolicyType(newType);
    setDraft({ name:"", icon:"📄", pc:false });
    bump(n=>n+1);
    onChange && onChange();
    // Persist to the agency (shared across seats/devices), like custom statuses.
    apiUpdateAgencySettings({ policyTypes: getCustomPolicyTypes() }).catch(e=>{
      toast.error((lang==="es"?"No se pudo guardar el tipo: ":"Could not save the type: ")+(e.message||""));
    });
  };
  const handleDelete = async (id) => {
    const inUse = (policies||[]).some(p=>p.type===id);
    if (inUse) {
      toast.warn(lang==="es" ? "No se puede eliminar: hay pólizas usando este tipo." : "Cannot delete: policies are using this type.");
      return;
    }
    if (!(await confirmDialog({ message: lang==="es" ? "¿Eliminar este tipo personalizado?" : "Delete this custom type?", danger: true }))) return;
    removeCustomPolicyType(id);
    bump(n=>n+1);
    onChange && onChange();
    apiUpdateAgencySettings({ policyTypes: getCustomPolicyTypes() }).catch(e=>{
      toast.error((lang==="es"?"No se pudo guardar el cambio: ":"Could not save the change: ")+(e.message||""));
    });
  };
  return <div style={{display:"flex",flexDirection:"column",gap:"1rem"}}>
    <div style={{padding:"1rem",borderRadius:12,border:"1px solid rgba(139,92,246,.3)",background:"rgba(139,92,246,.05)"}}>
      <div style={{fontSize:".78rem",fontWeight:700,color:"#a78bfa",marginBottom:".75rem",textTransform:"uppercase",letterSpacing:".05em"}}>
        ➕ {lang==="es"?"Nuevo tipo de póliza":"New policy type"}
      </div>
      <div style={{marginBottom:".6rem"}}>
        <label style={{display:"block",fontSize:".65rem",fontWeight:700,color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:".25rem"}}>{lang==="es"?"Nombre":"Name"}</label>
        <input type="text" value={draft.name} onChange={e=>setDraft(d=>({...d,name:e.target.value}))}
          placeholder={lang==="es"?"ej. Seguro de Cosecha":"e.g. Crop Insurance"}
          style={{width:"100%",padding:".45rem .6rem",fontSize:".78rem",borderRadius:6,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface,#0a1929)",color:"var(--c-white,#eef6ff)"}}/>
      </div>
      <div style={{marginBottom:".6rem"}}>
        <label style={{display:"block",fontSize:".65rem",fontWeight:700,color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:".25rem"}}>{lang==="es"?"Elige un icono":"Choose an icon"}</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:".25rem",padding:".5rem",borderRadius:6,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface,#0a1929)",maxHeight:"100px",overflowY:"auto"}}>
          {CUSTOM_ICON_CHOICES.map((ic,i)=>(
            <button key={i} type="button" onClick={()=>setDraft(d=>({...d,icon:ic}))}
              style={{width:28,height:28,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1rem",
                background:draft.icon===ic?"rgba(139,92,246,.25)":"transparent",
                border:draft.icon===ic?"2px solid #a78bfa":"1px solid transparent",cursor:"pointer"}}>
              {ic}
            </button>
          ))}
        </div>
        <div style={{marginTop:".35rem",display:"flex",alignItems:"center",gap:".5rem"}}>
          <span style={{fontSize:".65rem",color:"var(--c-text3,#45688a)"}}>{lang==="es"?"O escribe un emoji:":"Or type an emoji:"}</span>
          <input type="text" maxLength={4} value={draft.icon} onChange={e=>setDraft(d=>({...d,icon:e.target.value}))}
            style={{width:50,padding:".25rem",textAlign:"center",fontSize:".9rem",borderRadius:5,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface,#0a1929)",color:"var(--c-white,#eef6ff)"}}/>
        </div>
      </div>
      <label style={{display:"flex",alignItems:"center",gap:".4rem",fontSize:".72rem",color:"var(--c-text2,#7aa3c0)",marginBottom:".6rem",cursor:"pointer"}}>
        <input type="checkbox" checked={draft.pc} onChange={e=>setDraft(d=>({...d,pc:e.target.checked}))}/>
        {lang==="es" ? "Es una póliza P&C (Propiedad y Accidentes)" : "This is a P&C (Property & Casualty) policy"}
      </label>
      <button type="button" onClick={handleAdd}
        disabled={!draft.name.trim()}
        style={{width:"100%",padding:".55rem",borderRadius:8,border:"none",cursor:(draft.name.trim()?"pointer":"not-allowed"),
          background:(draft.name.trim()?"#8b5cf6":"var(--c-surface2,#112538)"),
          color:(draft.name.trim()?"#fff":"var(--c-text3,#45688a)"),
          fontSize:".78rem",fontWeight:700}}>
        {lang==="es"?"Agregar tipo":"Add type"}
      </button>
    </div>
    <div>
      <div style={{fontSize:".7rem",fontWeight:700,color:"var(--c-text3,#45688a)",marginBottom:".5rem",textTransform:"uppercase",letterSpacing:".05em"}}>
        {lang==="es"?"Tus tipos personalizados":"Your custom types"} ({customTypes.length})
      </div>
      {customTypes.length===0 && <div style={{padding:".75rem",textAlign:"center",fontSize:".72rem",color:"var(--c-text3,#45688a)",background:"var(--c-surface2,#112538)",borderRadius:8,border:"1px dashed var(--c-border,#1a3550)"}}>
        {lang==="es"?"Aún no has creado tipos personalizados":"No custom types yet"}
      </div>}
      <div style={{display:"flex",flexDirection:"column",gap:".35rem"}}>
        {customTypes.map(pt=><div key={pt.id} style={{display:"flex",alignItems:"center",gap:".6rem",padding:".5rem .6rem",borderRadius:8,background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)"}}>
          <span style={{fontSize:"1.2rem"}}>{pt.icon}</span>
          <div style={{flex:1}}>
            <div style={{fontSize:".78rem",fontWeight:600,color:"var(--c-white,#eef6ff)"}}>{lang==="es"?pt.labelEs:pt.label}</div>
            <div style={{fontSize:".62rem",color:"var(--c-text3,#45688a)"}}>{pt.pc?"P&C":"Health / Life"} . {(policies||[]).filter(p=>p.type===pt.id).length} {lang==="es"?"pólizas":"policies"}</div>
          </div>
          <button onClick={()=>handleDelete(pt.id)} title={lang==="es"?"Eliminar":"Delete"}
            style={{padding:".3rem",borderRadius:6,border:"none",background:"rgba(255,61,107,.1)",color:"#ff3d6b",cursor:"pointer",display:"flex",alignItems:"center"}}>
            <Trash2 size={13}/>
          </button>
        </div>)}
      </div>
    </div>
    <div style={{fontSize:".62rem",color:"var(--c-text3,#45688a)",padding:".5rem",background:"var(--c-surface2,#112538)",borderRadius:6,border:"1px solid var(--c-border,#1a3550)"}}>
      💡 {lang==="es"
        ? `${defaultTypes.length} tipos predeterminados están incluidos y no se pueden eliminar. Los tipos personalizados aparecen en todos los módulos (Pólizas, Comisiones, Recursos, Filtros).`
        : `${defaultTypes.length} default types are included and cannot be removed. Custom types appear across all modules (Policies, Commissions, Resources, Filters).`}
    </div>
  </div>;
}

// Manager for the agency-shared custom-field NAMES per policy type — the catalog
// behind PolicyForm's "Campos personalizados". Rename or delete a field for a
// whole type here and it propagates to every policy of that type. Renames and
// deletes also MIGRATE the per-policy values: a rename keeps each value under the
// new name; a delete strips the field + its value from those policies. Adding a
// name is supported too, so fields can be defined upfront without opening a
// policy. Returns a bare div, wrapped in a Modal by the caller (like
// PolicyTypeManager). Needs `policies` + `onPoliciesChange` for the migration.
export function PolicyCustomFieldsManager({ lang, policies = [], onPoliciesChange }) {
  const es = lang === "es";
  const isApi = typeof window !== "undefined" && !!window.__deskteAuth;
  const [, bump] = useState(0);
  const [busy, setBusy] = useState(false);
  const typesWithFields = Object.keys(getAllPolicyCustomFieldDefs());
  const [selType, setSelType] = useState(typesWithFields[0] || "aca");
  const [newLabel, setNewLabel] = useState("");
  const [drafts, setDrafts] = useState({}); // { originalLabel: editedText }

  const fields = getPolicyCustomFieldDefs(selType);
  const typeLabel = (id) => { const p = getPolicyType(id); return p ? `${p.icon} ${es ? (p.labelEs || p.label) : p.label}` : id; };
  const lc = (s) => String(s || "").trim().toLowerCase();

  const persist = async (map) => {
    if (isApi && map) await apiUpdateAgencySettings({ policyCustomFieldDefs: map });
  };
  // Patch every policy of `typeId` carrying a customField named `matchLabel`,
  // applying `xform` to its customFields. Persists each (API) + updates state once.
  const migrate = async (typeId, matchLabel, xform) => {
    const target = lc(matchLabel);
    const affected = (policies || []).filter((p) => p.type === typeId && Array.isArray(p.customFields)
      && p.customFields.some((f) => f && lc(f.label) === target));
    let failed = 0;
    for (const p of affected) {
      if (isApi) { try { await apiUpdatePolicy(p.id, { ...p, customFields: xform(p.customFields) }); } catch { failed++; } }
    }
    if (affected.length && onPoliciesChange) {
      const ids = new Set(affected.map((p) => p.id));
      onPoliciesChange((prev) => prev.map((p) => (ids.has(p.id) ? { ...p, customFields: xform(p.customFields) } : p)));
    }
    return { count: affected.length, failed };
  };

  const handleAdd = async () => {
    const l = newLabel.trim(); if (!l) return;
    setBusy(true);
    try {
      const map = addPolicyCustomFieldDefs(selType, [l]);
      if (map) await persist(map);
      setNewLabel(""); bump((n) => n + 1);
    } catch (e) { toast.error(e.message || (es ? "No se pudo guardar" : "Could not save")); }
    finally { setBusy(false); }
  };
  const handleRename = async (oldLabel) => {
    const next = (drafts[oldLabel] ?? oldLabel).trim();
    if (!next || next === oldLabel) return;
    setBusy(true);
    try {
      const map = renamePolicyCustomFieldDef(selType, oldLabel, next);
      if (!map) { toast.error(es ? "Ese nombre ya existe o no cambió" : "That name already exists or is unchanged"); return; }
      await persist(map);
      const res = await migrate(selType, oldLabel, (cf) => cf.map((f) => (f && lc(f.label) === lc(oldLabel)) ? { ...f, label: next } : f));
      setDrafts((d) => { const n = { ...d }; delete n[oldLabel]; return n; });
      bump((n) => n + 1);
      toast.success(es ? `Renombrado${res.count ? ` en ${res.count} póliza(s)` : ""}` : `Renamed${res.count ? ` on ${res.count} policy(ies)` : ""}`);
    } catch (e) { toast.error(e.message || (es ? "No se pudo renombrar" : "Could not rename")); }
    finally { setBusy(false); }
  };
  const handleDelete = async (label) => {
    const ok = await confirmDialog({
      title: es ? "Eliminar campo" : "Delete field",
      message: es
        ? `¿Eliminar el campo "${label}" de TODAS las pólizas de tipo ${typeLabel(selType)}? Se borrará el campo y su valor en esas pólizas.`
        : `Delete the "${label}" field from ALL ${typeLabel(selType)} policies? The field and its value will be removed from those policies.`,
      danger: true, confirmLabel: es ? "Eliminar" : "Delete",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const map = removePolicyCustomFieldDef(selType, label);
      if (map) await persist(map);
      const res = await migrate(selType, label, (cf) => cf.filter((f) => !(f && lc(f.label) === lc(label))));
      bump((n) => n + 1);
      toast.success(es ? `Eliminado${res.count ? ` de ${res.count} póliza(s)` : ""}` : `Deleted${res.count ? ` from ${res.count} policy(ies)` : ""}`);
    } catch (e) { toast.error(e.message || (es ? "No se pudo eliminar" : "Could not delete")); }
    finally { setBusy(false); }
  };

  const inp = { width: "100%", padding: ".5rem .6rem", borderRadius: 8, background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text,#eef6ff)", fontSize: ".82rem", boxSizing: "border-box" };
  const iconBtn = (color) => ({ padding: ".4rem", borderRadius: 6, border: "none", background: `${color}1a`, color, cursor: busy ? "not-allowed" : "pointer", display: "flex", alignItems: "center", opacity: busy ? 0.5 : 1 });

  return <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 360 }}>
    <div style={{ fontSize: ".78rem", color: "var(--c-text2,#7aa3c0)", lineHeight: 1.4 }}>
      {es
        ? "Estos son los campos personalizados que aparecen en la tarjeta de póliza, agrupados por tipo. Renombrar o eliminar aquí afecta a TODAS las pólizas de ese tipo (los valores ya guardados se mueven o se borran con el campo)."
        : "These are the custom fields shown on the policy card, grouped by type. Renaming or deleting here affects ALL policies of that type (saved values move or are deleted with the field)."}
    </div>
    <div>
      <label style={{ display: "block", fontSize: ".66rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>{es ? "Tipo de póliza" : "Policy type"}</label>
      <select value={selType} onChange={(e) => { setSelType(e.target.value); setDrafts({}); setNewLabel(""); }} style={inp}>
        {POLICY_TYPES.map((pt) => {
          const n = getPolicyCustomFieldDefs(pt.id).length;
          return <option key={pt.id} value={pt.id}>{typeLabel(pt.id)}{n ? ` (${n})` : ""}</option>;
        })}
      </select>
    </div>
    <div>
      <div style={{ fontSize: ".66rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
        {es ? `Campos de ${typeLabel(selType)}` : `${typeLabel(selType)} fields`} ({fields.length})
      </div>
      {fields.length === 0 && <div style={{ fontSize: ".78rem", color: "var(--c-text3,#45688a)", padding: ".4rem 0" }}>{es ? "Este tipo aún no tiene campos personalizados." : "This type has no custom fields yet."}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {fields.map((label) => {
          const edited = (drafts[label] ?? label);
          const changed = edited.trim() && edited.trim() !== label;
          return <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input value={edited} disabled={busy} onChange={(e) => setDrafts((d) => ({ ...d, [label]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter" && changed) handleRename(label); }} style={inp} />
            {changed && <button onClick={() => handleRename(label)} disabled={busy} title={es ? "Renombrar" : "Rename"} style={iconBtn("#00d4ff")}><Check size={15} /></button>}
            <button onClick={() => handleDelete(label)} disabled={busy} title={es ? "Eliminar" : "Delete"} style={iconBtn("#ff3d6b")}><Trash2 size={14} /></button>
          </div>;
        })}
      </div>
    </div>
    <div style={{ display: "flex", gap: 8, alignItems: "center", borderTop: "1px solid var(--c-border,#1a3550)", paddingTop: 10 }}>
      <input value={newLabel} disabled={busy} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
        placeholder={es ? "Nombre del campo nuevo (ej. Nº de grupo)" : "New field name (e.g. Group #)"} style={inp} />
      <button onClick={handleAdd} disabled={busy || !newLabel.trim()}
        style={{ padding: ".45rem .9rem", borderRadius: 8, border: "none", whiteSpace: "nowrap", fontWeight: 700, fontSize: ".8rem", display: "flex", alignItems: "center", gap: 4, cursor: (busy || !newLabel.trim()) ? "not-allowed" : "pointer", background: (busy || !newLabel.trim()) ? "var(--c-surface2,#112538)" : "#00d4ff", color: (busy || !newLabel.trim()) ? "var(--c-text3,#45688a)" : "#06243a" }}>
        <Plus size={14} />{es ? "Agregar" : "Add"}
      </button>
    </div>
  </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
// PaymentTrackerView — manual monthly payment tracking. One row per active
// policy for the selected month; the agent marks each ✅ paid / ❌ not paid.
// Status is stored per (policy, "YYYY-MM") on the backend (PolicyMonthlyPayment)
// and records who verified it. No carrier integration — fully manual.
// ─────────────────────────────────────────────────────────────────────────────
const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ── Payment-roster import (carrier CSV → match clients → mark this month) ─────
// Each carrier reports payment differently, so we read the right column per
// carrier and DERIVE a single status (paid / grace / unpaid):
//   • Ambetter → "Paid Through Date": covers the selected month ⇒ paid, else unpaid.
//   • Oscar    → "Policy status": Active ⇒ paid · Grace period ⇒ grace · other ⇒ unpaid.
// `map` is logical-field → header (lowercased). Unknown files fall back to the
// manual-mapping step (with an optional AI assist).
const PAYMENT_CARRIERS = [
  {
    id: 'ambetter', label: 'Ambetter',
    detect: (h) => h.includes('paid through date'),
    map: {
      firstName: 'insured first name', lastName: 'insured last name', dob: 'member date of birth',
      policyNumber: 'policy number', memberId: 'exchange subscriber id',
      paidThroughDate: 'paid through date', premium: 'monthly premium amount',
    },
  },
  {
    id: 'oscar', label: 'Oscar',
    detect: (h) => h.includes('policy status') && h.includes('member id'),
    map: {
      insuredName: 'member name', dob: 'date of birth', memberId: 'member id',
      policyStatus: 'policy status', premium: 'premium amount',
    },
  },
];
const PAYMENT_FIELD_DEFS = [
  { key: 'insuredName', es: 'Nombre completo', en: 'Full name' },
  { key: 'firstName', es: 'Nombre', en: 'First name' },
  { key: 'lastName', es: 'Apellido', en: 'Last name' },
  { key: 'dob', es: 'Fecha de nacimiento', en: 'Date of birth' },
  { key: 'policyNumber', es: 'Número de póliza', en: 'Policy number' },
  { key: 'memberId', es: 'Member ID', en: 'Member ID' },
  { key: 'paidThroughDate', es: 'Pagado hasta (fecha) — Ambetter', en: 'Paid through date — Ambetter' },
  { key: 'policyStatus', es: 'Estado de la póliza — Oscar', en: 'Policy status — Oscar' },
  { key: 'premium', es: 'Prima mensual', en: 'Monthly premium' },
];
function paymentDetectCarrier(headersLc) {
  return PAYMENT_CARRIERS.find((c) => { try { return c.detect(headersLc); } catch { return false; } }) || null;
}
// "5/4/1982" | "05/04/1982" | "1982-05-04" → Date (local midnight); null if bad.
function paymentParseLooseDate(v) {
  const s = String(v || '').trim();
  if (!s) return null;
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) { let y = +m[3]; if (y < 100) y += y > 40 ? 1900 : 2000; return new Date(y, +m[1] - 1, +m[2]); }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
function paymentLastDayOfPeriod(period) {
  const [y, mo] = String(period).split('-').map(Number);
  return new Date(y, mo, 0); // day 0 of next month = last day of `mo` (1-based)
}
// Single source of truth for the carrier-specific rule → { paid, statusLabel, raw }.
function derivePaymentStatus({ paidThroughDate, policyStatus }, period) {
  if (paidThroughDate) {
    const pt = paymentParseLooseDate(paidThroughDate);
    const last = paymentLastDayOfPeriod(period);
    return { paid: !!(pt && pt >= last), statusLabel: null, raw: String(paidThroughDate).trim() };
  }
  if (policyStatus) {
    const t = String(policyStatus).toLowerCase();
    if (/grace|graci/.test(t)) return { paid: false, statusLabel: 'grace', raw: String(policyStatus).trim() };
    if (/active|current|vigente|al d[ií]a|in.?force|paid/.test(t)) return { paid: true, statusLabel: null, raw: String(policyStatus).trim() };
    return { paid: false, statusLabel: null, raw: String(policyStatus).trim() };
  }
  return { paid: false, statusLabel: null, raw: '' };
}
// Turn the raw sheet into normalized, classified rows the backend can match.
function buildPaymentRows({ headers, dataRows, fieldMap, period }) {
  const headersLc = headers.map((h) => String(h || '').toLowerCase().trim());
  const idxOf = (logical) => {
    const hdr = fieldMap[logical];
    if (!hdr) return -1;
    return headersLc.indexOf(String(hdr).toLowerCase().trim());
  };
  const stripQ = (s) => String(s || '').replace(/^['"]+|['"]+$/g, '').trim();
  const out = [];
  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i] || [];
    const get = (logical) => { const j = idxOf(logical); return j < 0 ? '' : String(row[j] ?? '').trim(); };
    const name = get('insuredName') || `${get('firstName')} ${get('lastName')}`.trim();
    const policyNumber = stripQ(get('policyNumber'));
    const memberId = stripQ(get('memberId'));
    if (!name && !policyNumber && !memberId) continue;
    const premiumRaw = get('premium');
    const amount = premiumRaw ? premiumRaw.replace(/[^0-9.]/g, '') : '';
    const st = derivePaymentStatus({ paidThroughDate: get('paidThroughDate'), policyStatus: get('policyStatus') }, period);
    out.push({ index: i, name, dob: get('dob'), policyNumber, memberId, paid: st.paid, statusLabel: st.statusLabel, amount: amount || null, rawStatus: st.raw });
  }
  return out;
}
function paymentRowChip(d, es) {
  if (d.paid) return { label: es ? '✅ Pagó' : '✅ Paid', color: '#10b981' };
  if (d.statusLabel === 'grace') return { label: es ? '🟡 Gracia' : '🟡 Grace', color: '#f59e0b' };
  return { label: es ? '🔴 No pagó' : '🔴 Unpaid', color: '#ef4444' };
}

// PaymentImportModal — upload a carrier roster, auto-match each row to a client,
// review/fix the matches, then write paid/grace/unpaid for the selected month.
function PaymentImportModal({ period, policyOptions = [], onClose, onImported, lang }) {
  const es = lang === 'es';
  const [step, setStep] = useState('file'); // file | map | review | done
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [carrier, setCarrier] = useState('');
  const [carrierLabel, setCarrierLabel] = useState('');
  const [fieldMap, setFieldMap] = useState({});
  const [preview, setPreview] = useState([]);
  const [summary, setSummary] = useState({});
  const [include, setInclude] = useState(() => new Set());
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const monthLabel = fmtMonth(period);

  const policyIdFor = (d) => overrides[d.index] || d.match?.policyId || '';
  const setOverride = (index, pid) => {
    setOverrides((o) => ({ ...o, [index]: pid }));
    setInclude((s) => { const n = new Set(s); if (pid) n.add(index); else n.delete(index); return n; });
  };
  const toggleInclude = (index) => setInclude((s) => { const n = new Set(s); n.has(index) ? n.delete(index) : n.add(index); return n; });

  const runPreview = async (nrows, carrierId) => {
    if (!nrows.length) { setErr(es ? 'No se encontraron filas con datos.' : 'No data rows found.'); return; }
    setBusy(true); setErr(null);
    try {
      const { data, summary: sm } = await importPaymentsPreview({ period, carrier: carrierId || carrier, rows: nrows });
      setPreview(data); setSummary(sm);
      setInclude(new Set(data.filter((d) => d.status === 'matched').map((d) => d.index)));
      setOverrides({});
      setStep('review');
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const parseFile = async (file) => {
    if (!file) return;
    setErr(null); setBusy(true); setFileName(file.name || '');
    try {
      const name = (file.name || '').toLowerCase();
      let rows;
      if (name.endsWith('.csv')) rows = polParseCsv(await file.text());
      else {
        const buf = new Uint8Array(await file.arrayBuffer());
        const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs');
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false });
      }
      rows = (rows || []).filter((r) => (r || []).some((c) => String(c).trim()));
      if (!rows.length) { setErr(es ? 'El archivo está vacío.' : 'The file is empty.'); return; }
      const hdr = (rows[0] || []).map((c) => String(c).trim());
      const body = rows.slice(1);
      setHeaders(hdr); setDataRows(body);
      const det = paymentDetectCarrier(hdr.map((h) => h.toLowerCase().trim()));
      if (det) {
        setCarrier(det.id); setCarrierLabel(det.label); setFieldMap(det.map);
        await runPreview(buildPaymentRows({ headers: hdr, dataRows: body, fieldMap: det.map, period }), det.id);
      } else {
        setCarrier('custom'); setCarrierLabel(es ? 'Formato nuevo' : 'New format');
        setFieldMap({}); setStep('map');
      }
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const runAiMap = async () => {
    setAiBusy(true); setErr(null);
    try {
      const sample = dataRows.slice(0, 2).map((r) => (r || []).map((c) => String(c ?? '')));
      const map = await aiMapPaymentColumns({ headers, sample });
      if (!map) { setErr(es ? 'La IA no está disponible ahora. Mapea las columnas a mano.' : 'AI is unavailable. Map the columns manually.'); return; }
      const fm = {};
      for (const f of PAYMENT_FIELD_DEFS) {
        const v = map[f.key];
        if (v && headers.some((h) => h.toLowerCase().trim() === String(v).toLowerCase().trim())) fm[f.key] = String(v).toLowerCase().trim();
      }
      setFieldMap(fm);
      toast.success(es ? 'La IA propuso un mapeo — revísalo abajo' : 'AI proposed a mapping — review it below');
    } catch (e) { setErr(e.message || String(e)); }
    finally { setAiBusy(false); }
  };

  const continueFromMap = async () => {
    const hasName = fieldMap.insuredName || (fieldMap.firstName && fieldMap.lastName) || fieldMap.firstName;
    const hasStatus = fieldMap.paidThroughDate || fieldMap.policyStatus;
    if (!hasName) { setErr(es ? 'Indica qué columna tiene el nombre.' : 'Tell me which column has the name.'); return; }
    if (!hasStatus) { setErr(es ? 'Indica la columna de "Pagado hasta" o "Estado de la póliza".' : 'Map the "Paid through" or "Policy status" column.'); return; }
    await runPreview(buildPaymentRows({ headers, dataRows, fieldMap, period }), 'custom');
  };

  const doCommit = async () => {
    const items = preview.filter((d) => include.has(d.index)).map((d) => {
      const policyId = policyIdFor(d);
      if (!policyId) return null;
      const note = `${es ? 'Importado de' : 'Imported from'} ${carrierLabel}${d.rawStatus ? ` · ${d.rawStatus}` : ''}`.slice(0, 500);
      return { policyId, paid: d.paid, statusLabel: d.statusLabel || '', amount: d.amount, note };
    }).filter(Boolean);
    if (!items.length) { toast.error(es ? 'No hay filas listas para aplicar.' : 'No rows ready to apply.'); return; }
    setBusy(true);
    try {
      const res = await importPaymentsCommit({ period, items });
      setResult(res);
      setStep('done');
      onImported && onImported();
    } catch (e) { toast.error(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const undoThis = async () => {
    setBusy(true);
    try {
      const { deleted } = await undoPaymentImport({ period });
      toast.success(es ? `${deleted} marca(s) importada(s) eliminada(s)` : `${deleted} imported mark(s) removed`);
      onImported && onImported();
      onClose();
    } catch (e) { toast.error(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const willApply = preview.filter((d) => include.has(d.index) && policyIdFor(d)).length;
  const inp = { background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 7, color: 'var(--c-text,#eef6ff)', fontSize: '.74rem', padding: '.35rem .5rem', fontFamily: 'inherit' };

  // One review row.
  const ReviewRow = (d) => {
    const chip = paymentRowChip(d, es);
    const pid = policyIdFor(d);
    const inc = include.has(d.index) && !!pid;
    const matchName = d.match?.clientName;
    return (
      <div key={d.index} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '.5rem .6rem', borderRadius: 9, background: 'var(--c-surface,#0d1f33)', border: `1px solid ${inc ? 'rgba(16,185,129,.3)' : 'var(--c-border,#1a3550)'}` }}>
        <input type="checkbox" checked={inc} disabled={!pid} onChange={() => toggleInclude(d.index)} title={es ? 'Aplicar' : 'Apply'} style={{ width: 15, height: 15, flexShrink: 0, cursor: pid ? 'pointer' : 'not-allowed', accentColor: '#10b981' }} />
        <span style={{ fontSize: '.7rem', fontWeight: 800, color: chip.color, whiteSpace: 'nowrap', flexShrink: 0 }}>{chip.label}</span>
        <div style={{ flex: '1 1 130px', minWidth: 0 }}>
          <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-text,#eef6ff)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || '—'}</div>
          <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)' }}>
            {d.dob ? `🎂 ${d.dob} · ` : ''}{d.amount ? `${fmtMoney(d.amount)} · ` : ''}{d.rawStatus || ''}
          </div>
        </div>
        {d.status === 'matched' && !overrides[d.index] ? (
          <span style={{ fontSize: '.72rem', color: '#10b981', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 1, minWidth: 0, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={matchName}>
            <Check size={12} /> {matchName}
          </span>
        ) : null}
        {/* Desplegable CON BUSCADOR. Era un <select> pelado con TODAS las
            polizas de la agencia dentro: para asignar una fila del archivo
            habia que bajar por la lista a ojo, una por una (Lissette
            2026-09-02, punto 65). Se busca por nombre, compania y numero de
            poliza a la vez, sin acentos y sin importar el orden. */}
        <div style={{ flex: '0 1 240px', minWidth: 150, maxWidth: 260 }}>
          <ClientSearchSelect
            clients={policyOptions}
            value={pid}
            onChange={(e) => setOverride(d.index, e.target.value)}
            lang={lang}
            optionLabel={(o) => o.label}
            haystack={(o) => o.label}
            placeholder={d.status === 'matched'
              ? (es ? '\ud83d\udd0d cambiar de póliza…' : '\ud83d\udd0d change policy…')
              : (es ? '\ud83d\udd0d buscar cliente o póliza…' : '\ud83d\udd0d search client or policy…')}
            noneLabel={es ? '— sin asignar —' : '— unassigned —'}
            selectStyle={{ ...inp, width: '100%' }}
          />
        </div>
      </div>
    );
  };

  const needsPick = preview.filter((d) => d.status !== 'matched');
  const matchedRows = preview.filter((d) => d.status === 'matched');

  return <Modal title={es ? '📤 Subir archivo de pagos' : '📤 Upload payment file'} onClose={onClose} wide>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', boxSizing: 'border-box' }}>
      <div style={{ fontSize: '.74rem', color: 'var(--c-text2,#7da0c4)' }}>
        {es ? 'Aplicar al mes:' : 'Apply to month:'} <strong style={{ color: 'var(--c-cyan,#00d4ff)' }}>{monthLabel}</strong>
        {carrierLabel ? <> · {es ? 'Compañía:' : 'Carrier:'} <strong style={{ color: 'var(--c-text,#eef6ff)' }}>{carrierLabel}</strong></> : null}
        {fileName ? <> · <span style={{ color: 'var(--c-text3,#45688a)' }}>{fileName}</span></> : null}
      </div>

      {err && <div style={{ padding: '.6rem .75rem', borderRadius: 8, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', color: '#ef4444', fontSize: '.74rem' }}>{err}</div>}

      {step === 'file' && (
        <div>
          <div onClick={() => fileRef.current?.click()}
            style={{ border: '2px dashed var(--c-border,#1a3550)', borderRadius: 12, padding: '2rem 1rem', textAlign: 'center', cursor: 'pointer', background: 'var(--c-surface,#0d1f33)' }}>
            <Upload size={26} style={{ color: 'var(--c-cyan,#00d4ff)', marginBottom: 8 }} />
            <div style={{ fontSize: '.84rem', fontWeight: 700, color: 'var(--c-text,#eef6ff)' }}>{busy ? (es ? 'Leyendo…' : 'Reading…') : (es ? 'Haz clic para elegir el archivo' : 'Click to choose the file')}</div>
            <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: 4 }}>CSV · Excel (.xlsx, .xls) — Ambetter, Oscar{es ? ' u otra compañía' : ' or another carrier'}</div>
          </div>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; parseFile(f); }} />
          <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginTop: 10, lineHeight: 1.6 }}>
            {es
              ? 'Leo cada fila y calculo Pagó / Gracia / No pagó con la regla de la compañía, luego empato con tu cliente. Nada se guarda hasta que confirmes.'
              : 'I read each row, compute Paid / Grace / Unpaid with the carrier rule, then match your client. Nothing is saved until you confirm.'}
          </div>
        </div>
      )}

      {step === 'map' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: '.74rem', color: 'var(--c-text2,#7da0c4)' }}>
              {es ? 'No reconocí esta compañía. Indica qué columna es cada cosa:' : 'I did not recognize this carrier. Map each column:'}
            </div>
            <button onClick={runAiMap} disabled={aiBusy} className="dkt-tab" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              🤖 {aiBusy ? (es ? 'Pensando…' : 'Thinking…') : (es ? 'Auto-detectar con IA' : 'Auto-detect with AI')}
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {PAYMENT_FIELD_DEFS.map((f) => (
              <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontWeight: 700 }}>{es ? f.es : f.en}</span>
                <select value={fieldMap[f.key] || ''} onChange={(e) => setFieldMap((m) => ({ ...m, [f.key]: e.target.value }))} style={inp}>
                  <option value="">{es ? '— ninguna —' : '— none —'}</option>
                  {headers.map((h, i) => <option key={i} value={h.toLowerCase().trim()}>{h}</option>)}
                </select>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <button onClick={() => { setStep('file'); setErr(null); }} className="dkt-tab">{es ? '‹ Atrás' : '‹ Back'}</button>
            <button onClick={continueFromMap} disabled={busy} className="dkt-btn-primary" style={{ padding: '.45rem .9rem', borderRadius: 8, fontWeight: 800, fontSize: '.78rem' }}>
              {busy ? (es ? 'Emparejando…' : 'Matching…') : (es ? 'Continuar ›' : 'Continue ›')}
            </button>
          </div>
        </div>
      )}

      {step === 'review' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: '.72rem', fontWeight: 700 }}>
            <span style={{ padding: '.25rem .55rem', borderRadius: 7, background: 'rgba(16,185,129,.12)', border: '1px solid rgba(16,185,129,.3)', color: '#10b981' }}>✓ {es ? 'Emparejados' : 'Matched'}: {summary.matched || 0}</span>
            <span style={{ padding: '.25rem .55rem', borderRadius: 7, background: 'rgba(245,158,11,.12)', border: '1px solid rgba(245,158,11,.3)', color: '#f59e0b' }}>? {es ? 'Dudosos' : 'Ambiguous'}: {summary.ambiguous || 0}</span>
            <span style={{ padding: '.25rem .55rem', borderRadius: 7, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', color: '#ef4444' }}>✗ {es ? 'Sin emparejar' : 'Unmatched'}: {summary.unmatched || 0}</span>
            <span style={{ marginLeft: 'auto', padding: '.25rem .55rem', borderRadius: 7, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text,#eef6ff)' }}>{es ? 'Se aplicarán' : 'Will apply'}: {willApply}</span>
          </div>

          <div style={{ maxHeight: '46vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 2 }}>
            {needsPick.length > 0 && (
              <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 2 }}>
                {es ? 'Revisar (elige el cliente)' : 'Review (pick the client)'}
              </div>
            )}
            {needsPick.map(ReviewRow)}
            {matchedRows.length > 0 && (
              <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 6 }}>
                {es ? 'Emparejados automáticamente' : 'Auto-matched'}
              </div>
            )}
            {matchedRows.map(ReviewRow)}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <button onClick={() => { setStep('file'); setPreview([]); setErr(null); }} className="dkt-tab">{es ? '‹ Otro archivo' : '‹ Another file'}</button>
            <button onClick={doCommit} disabled={busy || willApply === 0} className="dkt-btn-primary"
              style={{ padding: '.45rem 1rem', borderRadius: 8, fontWeight: 800, fontSize: '.8rem', opacity: (busy || willApply === 0) ? .55 : 1 }}>
              {busy ? (es ? 'Guardando…' : 'Saving…') : (es ? `Aplicar ${willApply} pago(s)` : `Apply ${willApply} payment(s)`)}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && result && (
        <div style={{ textAlign: 'center', padding: '1rem .5rem' }}>
          <div style={{ fontSize: '2rem' }}>✅</div>
          <div style={{ fontSize: '.95rem', fontWeight: 800, color: 'var(--c-text,#eef6ff)', marginTop: 6 }}>
            {es ? `${result.applied} pago(s) aplicados a ${monthLabel}` : `${result.applied} payment(s) applied to ${monthLabel}`}
          </div>
          {result.skipped > 0 && (
            <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginTop: 4 }}>
              {es ? `${result.skipped} omitidos (fuera de tu alcance)` : `${result.skipped} skipped (out of your scope)`}
            </div>
          )}
          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {result.applied > 0 && (
              <button onClick={undoThis} disabled={busy} className="dkt-tab" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#ef4444', fontWeight: 700 }}>
                <Trash2 size={13} />{es ? 'Deshacer importación' : 'Undo import'}
              </button>
            )}
            <button onClick={onClose} className="dkt-btn-primary" style={{ padding: '.45rem 1.2rem', borderRadius: 8, fontWeight: 800, fontSize: '.8rem' }}>{es ? 'Listo' : 'Done'}</button>
          </div>
        </div>
      )}
    </div>
  </Modal>;
}

// El nombre del cliente en "Pagos del mes" como ENLACE de verdad (#client/<id>),
// igual que en Pólizas y Renovaciones: el clic derecho ofrece "abrir en pestaña
// nueva" y Ctrl o la rueda la abren solas. El clic simple sigue abriendo la
// ficha en la misma pantalla. (Asistente de Dirección, 2026-10-28: "en pagos
// del mes no se pueden abrir los clientes en ventanas diferentes y se debe
// volver atrás y filtrar de nuevo", E-406.) Sin cliente resuelto, solo texto.
function NombreConEnlace({ c, onClientClick, cancelled, es, children }) {
  const estilo = { color: 'var(--c-text,#eef6ff)', fontWeight: 700, fontSize: '.85rem', textAlign: 'left', textDecoration: cancelled ? 'line-through' : 'none' };
  if (!c) return <span style={estilo}>{children}</span>;
  return (
    <a href={`#client/${c.id}`}
      onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; e.preventDefault(); if (onClientClick) onClientClick(c); }}
      title={es ? 'Abrir la ficha del cliente (clic derecho: abrir en pestaña nueva)' : 'Open the client profile (right-click: open in new tab)'}
      style={{ ...estilo, cursor: 'pointer' }}>
      {children}
    </a>
  );
}

export function PaymentTrackerView({ clients = [], policies = [], users = [], seatLabels = {}, currentUser, onClientClick, lang }) {
  const es = lang === 'es';
  const [period, setPeriod] = useState(currentPeriod);
  // Month-RANGE mode (Lissette 2026-07-02): pick Desde/Hasta and see every
  // month's status per policy side by side. The single-month mode above stays
  // untouched; this is a separate toggle. Capped at 12 months per load.
  const [rangeOn, setRangeOn] = useState(false);
  const [rangeFrom, setRangeFrom] = useState(currentPeriod);
  const [rangeTo, setRangeTo] = useState(currentPeriod);
  const [rangeRows, setRangeRows] = useState([]); // policy rows with payByPeriod {'YYYY-MM': payment}
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [carrierFilter, setCarrierFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all'); // US state of the client
  const [statusFilter, setStatusFilter] = useState('all'); // all | paid | unpaid | pending
  // Estado de la PÓLIZA (activo / pendiente / gracia / cancelada / custom) —
  // distinto del estado de PAGO de arriba (Lissette 2026-07-18).
  const [polStatusFilter, setPolStatusFilter] = useState('all');
  const [attendFilter, setAttendFilter] = useState('all'); // handling seat of the client
  const [analystFilter, setAnalystFilter] = useState('all'); // analystName on the policy
  // Monto Desde/Hasta sobre la columna Monto (Lissette 2026-08-26). Se guarda
  // como TEXTO, no como número: mientras se escribe "1" de "150" el campo pasa
  // por estados intermedios, y un Number() prematuro haría saltar la tabla.
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  // LOS FILTROS QUE FALTABAN (Lissette 2026-09-01, punto 9): las columnas
  // Forma, Metodo, Nota y las personalizadas se VEIAN pero no se podian
  // preguntar. "Quienes estan en autopay y no han pagado" no tenia respuesta
  // sin leerse la tabla entera a ojo.
  const [formaFilter, setFormaFilter] = useState('all');
  const [metodoFilter, setMetodoFilter] = useState('all');
  const [notaFilter, setNotaFilter] = useState('all');
  // Columnas propias: {id, texto}. Una sola a la vez — filtrar por varias
  // columnas de texto libre seria una pantalla de busqueda avanzada, y aqui lo
  // que hace falta es "ensename las que dicen X".
  const [colFilter, setColFilter] = useState({ id: '', texto: '' });
  // Orden por columna (Lissette 2026-09-01: "para ordenarlo ascendente o
  // descendente, menor a mayor"). Un toque sube, otro baja, el tercero vuelve
  // al orden de siempre — el mismo gesto que en Tareas.
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const tocarCabecera = (key) => {
    const n = siguienteOrden(sortBy, sortDir, key);
    setSortBy(n.sortBy); setSortDir(n.sortDir); setPage(1);
  };
  // AGENTE EN PÓLIZA (Lissette 2026-09-01). Es el campo `agentLabel` de la
  // ficha —quien figura en la poliza ante la aseguradora—, y NO es lo mismo que
  // "Atiende" (quien lleva al cliente en el CRM) ni que "Analista". La vista de
  // Polizas ya filtraba por el; Pagos del Mes no.
  const [agenteFilter, setAgenteFilter] = useState('all');
  const [search, setSearch] = useState('');
  // Lo que se escribe se pinta al instante; el filtrado de miles de filas va
  // detras, sin bloquear la tecla (E-345). Mientras alcanza, la lista vieja
  // sigue a la vista en vez de congelar el cuadro de busqueda.
  const busqueda = useDeferredValue(search);
  const [savingId, setSavingId] = useState(null);
  const [remindRow, setRemindRow] = useState(null);
  // Note THREAD per (policy, month) — Lissette 2026-07-03: "la nota en su
  // propia columna, como una burbuja que al tocarla despliega todos los
  // mensajes y las imágenes". The 💬 bubble opens a modal with every message;
  // each message can carry several pasted (Ctrl+V) or picked (📷) images.
  const [noteThreadRow, setNoteThreadRow] = useState(null); // row whose thread is open
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [msgImgs, setMsgImgs] = useState([]); // draft blobUrls not yet sent
  const [msgSending, setMsgSending] = useState(false);
  const [noteImgBusy, setNoteImgBusy] = useState(false);
  // ESPEJO EN LAS NOTAS DEL CLIENTE (Lissette 2026-09-15: *"si quieres hacer
  // espejo o no, no obligatorio"*).
  //
  // Arranca APAGADO en cada nota, y no se recuerda la eleccion anterior a
  // proposito: una casilla que se queda marcada de la vez pasada copia a la
  // ficha una nota que nadie decidio copiar, y eso no se descubre hasta que
  // alguien la lee alli. Marcarla cuesta un clic; quitar una nota copiada por
  // sorpresa cuesta encontrarla primero.
  const [espejoEnCliente, setEspejoEnCliente] = useState(false);
  /**
   * "SIEMPRE REFLEJAR": la casilla del espejo nace marcada en cada nota nueva.
   *
   * Lissette, 2026-09-16: *"un check para todas las notas, tambien deja el
   * individual"*. Este es el de "todas": se guarda como preferencia y decide
   * con que valor ABRE la casilla individual. La individual sigue mandando en
   * la nota que se esta escribiendo — desmarcarla para una no apaga la
   * preferencia, que es justo lo que "tambien deja el individual" pide.
   */
  /**
   * EL ESPEJO VIENE ENCENDIDO. Lissette lo pidio tres veces con palabras
   * distintas —*"necesito que sea un espejo"*, *"un check para todas las
   * notas"*, *"sigue sin verse"*— y las tres veces la respuesta fue una casilla
   * que habia que acordarse de marcar en CADA nota.
   *
   * La base de datos lo conto sin lugar a dudas (2026-09-16): de las notas
   * escritas ese dia, 3 se reflejaron —las que se escribieron con la casilla
   * marcada, y funcionaron perfectamente— y 22 salieron sin espejo. No estaba
   * roto: estaba APAGADO, y apagado por defecto en cada nota nueva.
   *
   * Asi que se invierte: nace encendido, y quien no lo quiera para una nota la
   * desmarca. Es lo que se pidio desde el principio; lo otro era pedirle a una
   * persona que se acuerde de una casilla treinta veces al dia.
   *
   * `!== "0"` y no `=== "1"` a proposito: sin preferencia guardada el valor es
   * `null`, y eso tiene que significar ENCENDIDO. Solo un "0" explicito —que
   * solo se escribe cuando alguien la desmarca a mano— lo apaga.
   */
  const [espejoSiempre, setEspejoSiempre] = useState(() => {
    try { return prefsGet("deskte:pagosEspejoSiempre") !== "0"; } catch { return true; }
  });
  // NO se guarda en el primer pintado. Guardarlo ahi reescribia la preferencia
  // con lo que hubiera leido al montar — y si el componente monta ANTES de que
  // las preferencias del servidor bajen a este navegador, eso borraba el "si"
  // de la usuaria y lo mandaba al servidor como un "no". Una preferencia que se
  // apaga sola es indistinguible de una funcion rota.
  const espejoYaGuardado = useRef(false);
  useEffect(() => {
    if (!espejoYaGuardado.current) { espejoYaGuardado.current = true; return; }
    try { prefsSet("deskte:pagosEspejoSiempre", espejoSiempre ? "1" : "0"); } catch {}
  }, [espejoSiempre]);
  const [reflejandoTodas, setReflejandoTodas] = useState(false);
  // Editar una nota del hilo (autor-only) — Lissette 2026-07-08.
  const [editMsgId, setEditMsgId] = useState(null);
  const [editMsgText, setEditMsgText] = useState('');
  const [editMsgImgs, setEditMsgImgs] = useState([]); // photos while editing (blobUrls)
  const [editMsgSaving, setEditMsgSaving] = useState(false);
  const noteImgFileRef = useRef(null);
  const editMsgImgFileRef = useRef(null);
  const threadBodyRef = useRef(null);
  const msgInputRef = useRef(null);
  const editMsgTaRef = useRef(null);
  // Scroll horizontal de la tabla (para llegar a las columnas del final) con
  // botones ◀ ▶ y teclas ← →, igual que el timeline de Comisiones.
  const tableScrollRef = useRef(null);
  // La tabla ya tenia scroll propio con una altura fija (72vh). Ahora llega
  // justo al borde de abajo de la pantalla y lo de arriba no se mueve
  // (Lissette 2026-09-25, CajaConScroll.jsx). Un solo ref para las dos cosas:
  // las flechas siguen moviendo la tabla.
  const [refAlto, altoTabla] = useAltoHastaAbajo(0);
  const refTabla = useCallback((n) => { tableScrollRef.current = n; refAlto(n); }, [refAlto]);
  const scrollTable = (dx) => tableScrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' });
  const [selected, setSelected] = useState(() => new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  // Saved reminder templates (per policy type + carrier), agency-shared.
  const [reminderTpls, setReminderTpls] = useState([]);
  const loadReminderTpls = () => { listReminderTemplates().then(setReminderTpls).catch(() => {}); };
  useEffect(() => { loadReminderTpls(); }, []);
  // Ensure the agency's custom policy statuses are loaded so the per-row status
  // menu + filter include them even if Monthly Payment mounted before the
  // app-level agency-settings hydration ran. The version bump recomputes
  // extraStatuses once they arrive.
  const [statusVer, setStatusVer] = useState(0);
  // Agency-shared custom columns for this table ([{ id, name }]) — same model as
  // the Vault/Company/Followups tables. The per-row VALUES live on each policy's
  // customData ({ columnId: text }). Loaded with the agency settings below.
  const [customColumns, setCustomColumns] = useState([]);
  // Estados personalizados del PAGO (prima $0, pago procesado...), aparte de
  // los de la poliza.
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [colMgr, setColMgr] = useState(false);
  useEffect(() => {
    apiGetAgencySettings().then((s) => {
      if (Array.isArray(s?.policyStatuses)) { setCustomPolicyStatuses(s.policyStatuses); setStatusVer((v) => v + 1); }
      // Estados PROPIOS del pago del mes. Columna nueva: en una agencia que
      // todavia no la tenga llega vacia y manda el rescate de abajo.
      if (Array.isArray(s?.paymentStatuses)) { setPaymentStatuses(s.paymentStatuses); setPaymentStatusCatalog(s.paymentStatuses); }
      if (Array.isArray(s?.marketplaces)) setAgencyMarketplaces(s.marketplaces);
      // Los motivos de cancelacion que anadio la agencia. Se asigna SIEMPRE,
      // tambien vacio: vacio es "solo los de fabrica", que es una respuesta.
      setMotivosDeAgencia(Array.isArray(s?.cancelReasons) ? s.cancelReasons : []);
      setCustomColumns(normalizeColumns(Array.isArray(s?.paymentCustomColumns) ? s.paymentCustomColumns : []));
    }).catch(() => {});
  }, []);
  // Persist the column DEFINITIONS to agency settings. Routed to the seat-writable
  // /seat-config so a seat's edits stick too (Lissette 2026-07-08: "que se guarden
  // sea quien sea — seat o agente").
  //
  // Solo se borra lo que se borró en la ventana (ticket de Marilyn Sequera,
  // 2026-10-28). Ver crm-app/columnasDeTabla.js.
  const persistColumns = async (cols) => {
    setCustomColumns(await guardarColumnasDeAjuste('paymentCustomColumns', customColumns, cols));
  };
  const [tplManager, setTplManager] = useState(false);
  const [statusMgr, setStatusMgr] = useState(false); // custom-status manager (moved here from the policy card)
  const [importOpen, setImportOpen] = useState(false); // carrier-roster payment importer
  // Marks that came from a file import carry a note "Importado de…/Imported from…".
  const importedCount = useMemo(() => rows.filter((r) => /^(Importado de|Imported from)/.test(r.payment?.note || '')).length, [rows]);
  const undoImport = async () => {
    if (!importedCount) return;
    const ok = await confirmDialog({
      title: es ? 'Borrar importación' : 'Delete import',
      message: es
        ? `Se quitarán las ${importedCount} marca(s) de pago que vinieron de un archivo en ${fmtMonth(period)}. Las marcas que pusiste a mano se conservan. ¿Continuar?`
        : `This removes the ${importedCount} payment mark(s) that came from a file in ${fmtMonth(period)}. Marks you set by hand are kept. Continue?`,
      danger: true, confirmLabel: es ? 'Sí, borrar' : 'Yes, delete',
    });
    if (!ok) return;
    try {
      const { deleted } = await undoPaymentImport({ period });
      toast.success(es ? `${deleted} marca(s) importada(s) eliminada(s)` : `${deleted} imported mark(s) removed`);
      load();
    } catch (e) { toast.error(e.message || (es ? 'No se pudo borrar' : 'Could not delete')); }
  };
  const [page, setPage] = useState(1);
  // Rows per page in Pagos del Mes — configurable (Lissette 2026-07-10: "poder
  // ampliar ese número"). Persisted so the choice sticks across sessions.
  const [pageSize, setPageSize] = useState(() => {
    try { const s = parseInt(prefsGet("deskte:paymentsPageSize"), 10); return [15, 25, 50, 100, 200, 500].includes(s) ? s : 15; } catch { return 15; }
  });
  useEffect(() => { try { prefsSet("deskte:paymentsPageSize", String(pageSize)); } catch {} }, [pageSize]);

  useEffect(() => { setCarrierFilter('all'); }, [typeFilter]);

  // Cronometro de la carga (Lissette 2026-09-02, queja 39). Se enseña abajo del
  // todo para poder mandar una captura: sin saber DONDE se van los segundos no
  // se puede decidir si la reforma va en el servidor o en la pantalla, y ya se
  // fallo una vez por suponerlo (se iba a proponer un indice sobre una tabla de
  // 3.459 filas que no era el problema).
  const [medicion, setMedicion] = useState(null);

  // ── LAS POLIZAS YA ESTAN AQUI: NO HAY QUE VOLVER A BAJARLAS ──────────────
  //
  // Medido el 2026-09-02 en una agencia de 201 polizas: 0,9 s, de los que
  // 266 ms son la consulta de polizas y 259 ms el viaje. Los dos crecen en
  // linea recta, asi que una agencia de 1.000 se va a ~4 s y una de 2.000 a
  // ~8 s. Y lo que viajaba era la poliza ENTERA por fila (53 columnas, siete
  // JSON, dos de texto largo) — las mismas que el CRM ya se trajo al entrar.
  //
  // Ahora se le pide al servidor solo lo que cambia por mes y el cuerpo se
  // saca de memoria. El servidor sigue decidiendo QUE filas salen (agencia,
  // activas + canceladas, fichas borradas, alcance del agente): de aqui solo
  // sale el CUERPO, nunca la lista.
  const porId = useMemo(() => {
    const m = new Map();
    for (const p of policies || []) if (p && p.id != null) m.set(p.id, p);
    return m;
  }, [policies]);

  // Vive en crm-app/pegarCuerposDePagos.js para poder llamarlo desde las
  // pruebas sin montar React ni arrastrar MSAL.
  const pegarCuerpos = (filas) => pegarCuerposDePagos(filas, porId);

  const load = () => {
    setLoading(true); setError(null);
    const reloj = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const t0 = reloj();
    // Solo se pide ligero si de verdad hay libro en memoria contra el que
    // casarlo. Recien entrando todavia no lo hay.
    const puedoLigero = porId.size > 0;
    getPaymentTracker({ period, ligero: puedoLigero })
      .then(async (res) => {
        let filas = res.rows || [];
        // `res.ligero` lo dice el SERVIDOR. Durante el despliegue la pantalla
        // nueva puede hablar con el servidor viejo, que devuelve las filas
        // completas aunque se le pida ligero.
        if (res.ligero) {
          const pegadas = pegarCuerpos(filas);
          if (pegadas) {
            filas = pegadas;
          } else {
            // A alguna fila no le encontramos el cuerpo (el libro de memoria
            // se quedo corto, o una pagina fallo al cargar). Se pide completo
            // y ya esta: mejor un viaje de mas que una tabla incompleta.
            const completo = await getPaymentTracker({ period });
            filas = completo.rows || [];
          }
        }
        setRows(filas);
        const msServidor = res.meta?.ms?.total ?? null;
        const msRespuesta = res.tiempos?.msRespuesta ?? 0;
        // El viaje = todo lo que tardo la peticion menos lo que el servidor dice
        // que tardo el. Nunca negativo: los dos relojes no son el mismo.
        const msViaje = msServidor == null ? null : Math.max(0, msRespuesta - msServidor);
        // Lo que tarda en PINTARSE se mide despues del siguiente fotograma, que
        // es cuando el navegador ya dibujo la tabla con las filas nuevas.
        const tPintado = reloj();
        const marcar = () => setMedicion({
          filas: res.meta?.filas ?? filas.length,
          ligero: res.ligero === true,
          intentos: res.tiempos?.intentos ?? 1,
          msServidor, msViaje,
          msMapeo: res.tiempos?.msMapeo ?? null,
          msPintado: Math.round(reloj() - tPintado),
          msTotal: Math.round(reloj() - t0),
          detalle: res.meta?.ms || null,
        });
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(marcar));
        else marcar();
      })
      .catch((e) => { setError(e.message || 'Error'); setRows([]); setMedicion(null); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  // Months covered by the Desde→Hasta range (ascending, inclusive). Hard cap
  // at 12 so a stray "2024 → 2026" pick doesn't fire 24 requests.
  const rangeInfo = useMemo(() => {
    const parse = (s) => { const [y, m] = String(s).split('-').map(Number); return y * 12 + ((m || 1) - 1); };
    let a = parse(rangeFrom), b = parse(rangeTo);
    if (a > b) { const t = a; a = b; b = t; }
    const months = [];
    for (let i = a; i <= b && months.length < 12; i++) months.push(`${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`);
    return { months, clamped: b - a + 1 > 12 };
  }, [rangeFrom, rangeTo]);
  const rangeMonths = rangeInfo.months;

  // Range mode fetches one month at a time (same endpoint as single-month) and
  // folds the results into one row per policy with payByPeriod['YYYY-MM'].
  const loadRange = () => {
    setLoading(true); setError(null);
    // UNA peticion para todo el rango, no una por mes. Antes esto era
    // `Promise.all(rangeMonths.map(...))`: hasta DOCE cargas completas del
    // listado de la agencia -- las mismas polizas, las mismas fichas de
    // cliente, los mismos descifrados, doce veces -- solo para cambiar de que
    // mes era el pago. Ahora el servidor hace una pasada de polizas y trae los
    // pagos de todos los meses pedidos; las filas de pago son diminutas al lado
    // de una poliza. (2026-09-02, queja 39.)
    const puedoLigero = porId.size > 0;
    getPaymentTracker({ period: rangeMonths[0], periods: rangeMonths, ligero: puedoLigero })
      .then(async (res) => {
        if (res.ligero) {
          const pegadas = pegarCuerpos(res.rows || []);
          // Mismo criterio que arriba: o estan todas, o se pide completo.
          res = pegadas
            ? { ...res, rows: pegadas }
            : await getPaymentTracker({ period: rangeMonths[0], periods: rangeMonths });
        }
        return res;
      })
      .then((res) => {
        // `payByPeriod` ya viene armado por mes desde el servidor. Se deja el
        // respaldo por si un navegador con la version vieja en cache hablara
        // con el servidor nuevo durante el despliegue: sin el, la tabla saldria
        // en blanco esos minutos.
        setRangeRows((res.rows || []).map((r) => (
          r.payByPeriod ? r : { ...r, payByPeriod: { [rangeMonths[0]]: r.payment || { paid: false } } }
        )));
      })
      .catch((e) => { setError(e.message || 'Error'); setRangeRows([]); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (rangeOn) loadRange(); /* eslint-disable-next-line */ }, [rangeOn, rangeMonths]);

  // Live update when another seat marks a payment for the same month.
  useEffect(() => {
    const h = (e) => {
      const d = e?.detail;
      if (!d) return;
      if (d.period === period) {
        setRows((prev) => prev.map((r) => r.id === d.policyId
          ? { ...r, payment: { ...(r.payment || {}), paid: !!d.paid } }
          : r));
      }
      if (rangeOn && rangeMonths.includes(d.period)) {
        setRangeRows((prev) => prev.map((r) => r.id === d.policyId
          ? { ...r, payByPeriod: { ...r.payByPeriod, [d.period]: { ...(r.payByPeriod?.[d.period] || {}), paid: !!d.paid } } }
          : r));
      }
    };
    window.addEventListener('deskte:policy-payment', h);
    return () => window.removeEventListener('deskte:policy-payment', h);
  }, [period, rangeOn, rangeMonths]);

  // In range mode every derived list (options, filters, counts) works over the
  // folded range rows; otherwise over the single-month rows.
  const baseRows = rangeOn ? rangeRows : rows;

  // Lo que ofrece cada columna de tipo LISTA: lo que la agencia ya escribio en
  // ella (Lissette 2026-09-10), asi el desplegable se va creando solo. Sale de
  // `baseRows` y NO de las filas filtradas: un filtro puesto no puede recortar
  // las opciones que se ofrecen, o al filtrar se dejaria de poder escribir lo
  // mismo que se escribio ayer.
  const opcionesPorColumna = useMemo(() => {
    const m = {};
    for (const col of customColumns) {
      if (tipoDeColumna(col) !== 'list') continue;
      m[col.id] = opcionesDeColumna(col, baseRows.map((r) => r.customData && r.customData[col.id]));
    }
    return m;
  }, [customColumns, baseRows]);

  // Indice de fichas por id (Mayra y Jennifer, agencia Pomares, 2026-10-03:
  // "al buscar es demasiado lento; al borrar un nombre y buscar a otro se queda
  // cargando"). `clientOf`, `stateOf` y `attendOf` hacian cada uno un
  // `clients.find` -- recorrer TODAS las fichas -- y se llaman unas siete veces
  // por fila en cada tecla (filtro, contadores, fila pintada). Con miles de
  // polizas y miles de fichas eran decenas de millones de comparaciones por
  // letra. El Map se arma una vez por cambio de `clients` (E-345).
  const clientesPorId = useMemo(() => {
    const m = new Map();
    for (const c of clients || []) if (c && c.id != null) m.set(c.id, c);
    return m;
  }, [clients]);
  const clientOf = (r) => clientesPorId.get(r.clientId) || null;
  // Full name of the principal (Lissette 2026-07-28): first + middle + last, as
  // stored in the profile, not truncated to first+last. (A Spanish second
  // surname, if entered, already lives inside lastName.)
  const nameOf = (r) => {
    const c = clientOf(r);
    return c ? [c.firstName, c.middleName, c.lastName].filter(Boolean).join(' ').trim() : (r.clientName || '—');
  };
  // Names of the people INSURED on the policy — mirrors the Pólizas search
  // (PoliciesView.insuredNamesOf) so the buscador finds a row by its DEPENDENT or
  // SPOUSE, not only the titular (Lissette 2026-07-08). Matches what's actually in
  // policyInsured / policyInsuredExtra, same as the 🛡️ insured badge on the card.
  const insuredNamesList = (r, c) => {
    const ids = Array.isArray(r.policyInsured) ? r.policyInsured : [];
    const names = [];
    if (c) {
      if (ids.includes('primary')) names.push(`${c.firstName || ''} ${c.lastName || ''}`.trim());
      if (c.spouse && c.spouse.firstName && ids.includes('spouse')) names.push(`${c.spouse.firstName} ${c.spouse.lastName || ''}`.trim());
      (Array.isArray(c.dependents) ? c.dependents : []).forEach((d, di) => {
        if (d && ids.includes(d.id || ('dep' + di))) names.push(`${d.firstName || ''} ${d.lastName || ''}`.trim());
      });
    }
    (Array.isArray(r.policyInsuredExtra) ? r.policyInsuredExtra : []).forEach((n) => { if (n) names.push(String(n).trim()); });
    return names.filter(Boolean);
  };
  const insuredNamesOf = (r, c) => insuredNamesList(r, c).join(' ');
  // Save one custom-column cell for a policy. Optimistic on the row; persisted
  // per policy (not per month). Any seat with policies.edit can save.
  const [savingCellId, setSavingCellId] = useState(null);
  // `propagar` es para las casillas que pinta `CeldaPropia` (lista y marcar):
  // ese componente ya avisa y deshace por su cuenta cuando el guardado revienta,
  // asi que necesita que el fallo LE LLEGUE. Sin la bandera se lo tragaria aqui
  // y la casilla se quedaria enseñando algo que no esta guardado — con dos
  // avisos si ademas se avisara desde los dos sitios.
  const saveCustomCell = async (r, columnId, value, { propagar = false } = {}) => {
    const prev = (r.customData && typeof r.customData === 'object') ? r.customData : {};
    const nextData = { ...prev, [columnId]: value };
    const patch = (list) => list.map((x) => (x.id === r.id ? { ...x, customData: nextData } : x));
    setRows(patch); setRangeRows(patch);
    setSavingCellId(r.id + columnId);
    try {
      await savePaymentCustomData({ policyId: r.id, customData: { [columnId]: value } });
    } catch (e) {
      if (!propagar) toast.error(e.message || (es ? 'No se pudo guardar' : 'Could not save'));
      // Roll back to the prior value on failure.
      const rollback = (list) => list.map((x) => (x.id === r.id ? { ...x, customData: prev } : x));
      setRows(rollback); setRangeRows(rollback);
      if (propagar) throw e;
    } finally {
      setSavingCellId(null);
    }
  };

  // Photos in a photo-enabled custom column (col.allowPhotos). Images live under
  // a companion "<columnId>::img" key as a JSON array (Lissette 2026-07-28). The
  // backend stores RAW blob URLs and signs them on read; a freshly-added image
  // shows instantly via a local objectURL, mapped back to its raw blob URL when
  // persisting so it survives reload (re-signed by the server).
  const objUrlToRaw = useRef({});
  const [cellImgBusy, setCellImgBusy] = useState(null);
  const [cellLightbox, setCellLightbox] = useState(null);
  const cellImgs = (r, col) => {
    try { const a = JSON.parse((r.customData && r.customData[col.id + '::img']) || '[]'); return Array.isArray(a) ? a : []; }
    catch { return []; }
  };
  const persistCellImgs = async (r, col, displayArr) => {
    const key = col.id + '::img';
    const prev = (r.customData && typeof r.customData === 'object') ? r.customData : {};
    const nextData = { ...prev, [key]: JSON.stringify(displayArr) };
    const patch = (list) => list.map((x) => (x.id === r.id ? { ...x, customData: nextData } : x));
    setRows(patch); setRangeRows(patch);
    try {
      // Persist RAW blob URLs: strip the SAS query from signed URLs, and map a
      // just-added objectURL back to the blob URL returned by the uploader.
      const raw = displayArr.map((u) => (String(u).startsWith('blob:') ? (objUrlToRaw.current[u] || '') : String(u).split('?')[0])).filter(Boolean);
      await savePaymentCustomData({ policyId: r.id, customData: { [key]: JSON.stringify(raw) } });
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo guardar la foto' : 'Could not save the photo'));
      const rollback = (list) => list.map((x) => (x.id === r.id ? { ...x, customData: prev } : x));
      setRows(rollback); setRangeRows(rollback);
    }
  };
  const addCellImage = async (r, col, file) => {
    if (!file || !/^image\//.test(file.type || '')) { toast.error(es ? 'Solo imágenes' : 'Images only'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error(es ? 'La imagen supera 5 MB' : 'Image over 5 MB'); return; }
    const cellKey = r.id + col.id;
    setCellImgBusy(cellKey);
    try {
      const objUrl = URL.createObjectURL(file);
      const named = file.name ? file : new File([file], 'pasted-image.png', { type: file.type || 'image/png' });
      const blobUrl = await uploadPaymentNoteImage(named);
      objUrlToRaw.current[objUrl] = blobUrl;
      await persistCellImgs(r, col, [...cellImgs(r, col), objUrl]);
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo subir' : 'Upload failed'));
    } finally { setCellImgBusy(null); }
  };
  const removeCellImage = (r, col, idx) => persistCellImgs(r, col, cellImgs(r, col).filter((_, j) => j !== idx));
  // Cancelled policies now appear in the tracker (Lissette 2026-07-06) but stay
  // OUT of the money/paid/unpaid totals — they're informational, badged rows.
  // La MISMA regla que la lista de polizas y que Comisiones por Poliza
  // (polizaEstado.js): un estatus propio de la agencia llamado "Cancelada"
  // cuenta como baja aqui tambien. Antes esta copia solo miraba dos ids.
  const isCancelledRow = (r) => esPolizaCancelada(r);

  // Forma de pago (junto al nombre) + Método de pago (columna) — traídos de la
  // ficha de cada póliza (Lissette 2026-07-08). paymentType/paymentMethod ya
  // vienen en la fila (mapPolicyFromApi los aplana desde type_details).
  const payTypeMeta = (r) => {
    if (r.paymentType === 'autopay') return { label: es ? '🔄 Autopay' : '🔄 Autopay', color: '#10b981', bg: 'rgba(16,185,129,.12)', border: 'rgba(16,185,129,.35)' };
    if (r.paymentType === 'client') return { label: es ? '👤 Cliente paga' : '👤 Client pays', color: '#ffb700', bg: 'rgba(255,183,0,.12)', border: 'rgba(255,183,0,.35)' };
    if (r.paymentType === 'force') return { label: es ? '⚡ Forzar' : '⚡ Force', color: '#a855f7', bg: 'rgba(168,85,247,.12)', border: 'rgba(168,85,247,.35)' };
    if (r.paymentType === 'zero') return { label: es ? '🆓 Prima $0' : '🆓 $0 Premium', color: '#10b981', bg: 'rgba(16,185,129,.12)', border: 'rgba(16,185,129,.35)' };
    return null; // sin definir → no chip
  };
  const payMethodLabel = (r) => {
    const base = {
      credit_card: es ? 'Tarjeta de Crédito' : 'Credit Card',
      debit_card: es ? 'Tarjeta de Débito' : 'Debit Card',
      bank_account: es ? 'Cuenta Bancaria (ACH)' : 'Bank Account (ACH)',
    }[r.paymentMethod];
    if (!base) return '';
    const last4 = r.paymentMethod === 'bank_account' ? r.bankLast4 : r.cardLast4;
    const brand = r.paymentMethod === 'bank_account' ? '🏦' : '💳';
    return `${brand} ${base}${last4 ? ` ••••${last4}` : ''}`;
  };

  const carrierOptions = useMemo(() => {
    const scoped = typeFilter === 'all' ? baseRows : baseRows.filter((r) => r.type === typeFilter);
    return [...new Set(scoped.map((r) => r.carrier).filter(Boolean))].sort();
  }, [baseRows, typeFilter]);

  // Quien figura como agente EN LA POLIZA. `agentName` es el respaldo de las
  // filas viejas, igual que hace la tarjeta de poliza al pintarlo.
  const agenteOf = (r) => String(r.agentLabel || r.agentName || '').trim();
  const agenteOptions = useMemo(
    () => [...new Set(baseRows.map((r) => agenteOf(r)).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [baseRows],
  );

  // US state of the policy's client (from the client's address). Used by the
  // new "Estado USA" filter.
  const stateOf = (r) => (clientOf(r)?.state || '').trim().toUpperCase();
  const stateOptions = useMemo(
    () => [...new Set(baseRows.map((r) => stateOf(r)).filter(Boolean))].sort(),
    [baseRows, clients],
  );

  // "Atiende" (handling seat of the policy's client) + "Analista" (from the
  // policy) filters — Lissette 2026-07-02.
  const attendOf = (r) => (clientOf(r)?.attendedBySeatId) || '';
  const analystOf = (r) => (r.analystName || '').trim();
  const analystOptions = useMemo(
    () => [...new Set(baseRows.map(analystOf).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [baseRows],
  );

  const statusOfPay = (pay) => {
    // An explicit extra status (grace / custom) wins over paid/unpaid.
    if (pay?.statusLabel) return pay.statusLabel;
    // Reviewed = somebody actually MARKED it. A row that exists only because a
    // note was saved (markedByUserId null) still shows as "Sin revisar".
    const reviewed = !!pay?.markedByUserId;
    if (!reviewed) return 'pending';
    return pay?.paid ? 'paid' : 'unpaid';
  };
  const statusOf = (r) => statusOfPay(r.payment);
  // Estados de PAGO del mes, más allá de pagó / no pagó. Solo el período de
  // gracia, que sí es una situación de pago.
  //
  // Los estados personalizados de PÓLIZA ya NO salen aquí: son otra cosa
  // (Lissette 2026-08-15). Un estado como "Pendiente de firma" habla del trámite
  // de la póliza, no de si el cliente pagó el mes, y ofrecerlo en este
  // desplegable mezclaba dos preguntas distintas en la misma casilla.
  //
  // RESCATE de los que ya se usaban. Al separar los dos mundos se quitaron del
  // desplegable los personalizados de POLIZA, y con ellos desaparecieron los
  // que la agencia usaba para PAGOS -- "Prima $0", "Pago procesado" -- porque
  // hasta entonces no habia otro sitio donde crearlos (ticket de Maria
  // Fernanda, 2026-08-18). No se borro ninguno: siguen guardados.
  //
  // Para devolverlos NO se copian todos los de poliza: eso volveria a mezclar
  // las dos preguntas, que es justo lo que se arreglo. Se devuelven los que
  // estan PROBADOS por el uso -- los que aparecen marcados en algun pago real
  // de esta agencia. Un "Pendiente de firma" que solo se uso en polizas no
  // vuelve; un "Prima $0" que se marco en pagos, si.
  // Cuantas veces se usa cada estatus, para poder avisar antes de moverlo o
  // quitarlo: en el estado de la POLIZA y en pagos marcados de esta agencia.
  const statusUsage = useMemo(() => {
    const pol = {}, pay = {};
    for (const r of baseRows) {
      if (r.status) pol[r.status] = (pol[r.status] || 0) + 1;
      const own = r.payment?.statusLabel;
      if (own) pay[own] = (pay[own] || 0) + 1;
      for (const m of Object.keys(r.payByPeriod || {})) {
        const lbl = r.payByPeriod[m]?.statusLabel;
        if (lbl) pay[lbl] = (pay[lbl] || 0) + 1;
      }
    }
    return { pol, pay };
  }, [baseRows]);

  const paymentStatusesInUse = useMemo(() => {
    const usados = new Set();
    for (const r of baseRows) {
      for (const m of Object.keys(r.payByPeriod || {})) {
        const lbl = r.payByPeriod[m]?.statusLabel;
        if (lbl && lbl !== 'grace') usados.add(lbl);
      }
      const own = r.payment?.statusLabel;
      if (own && own !== 'grace') usados.add(own);
    }
    if (!usados.size) return [];
    return getCustomPolicyStatuses()
      .filter((st) => usados.has(st.id))
      .map((st) => ({ id: st.id, label: lang === 'es' ? (st.labelEs || st.label) : st.label, color: st.color || '#64748b' }));
  }, [baseRows, lang, statusVer]);

  const extraStatuses = useMemo(() => {
    const grace = getPolicyStatusMeta('grace', lang);
    const propios = (paymentStatuses || []).map((st) => ({
      id: st.id,
      label: lang === 'es' ? (st.labelEs || st.label) : (st.label || st.labelEs),
      color: st.color || '#64748b',
    }));
    // Los rescatados solo se ofrecen mientras no haya catalogo propio: en
    // cuanto la agencia crea los suyos, mandan los suyos y no se duplican.
    const rescatados = propios.length ? [] : paymentStatusesInUse;
    const vistos = new Set(['grace', ...propios.map((x) => x.id)]);
    return [
      { id: 'grace', label: grace.label, color: grace.color },
      ...propios,
      ...rescatados.filter((x) => !vistos.has(x.id)),
    ];
  }, [lang, statusVer, paymentStatuses, paymentStatusesInUse]);
  // Los personalizados se siguen LEYENDO —con su nombre y su color— para que un
  // mes marcado antes de este cambio no aparezca como un id crudo.
  const legacyStatusLabels = useMemo(
    () => getCustomPolicyStatuses().map((s) => ({ id: s.id, label: lang === 'es' ? (s.labelEs || s.label) : s.label, color: s.color || '#64748b' })),
    [lang, statusVer],
  );
  const statusMetaFor = (id) => {
    if (id === 'paid') return { label: es ? 'Pagó' : 'Paid', color: '#10b981' };
    if (id === 'unpaid') return { label: es ? 'No pagó' : 'Unpaid', color: '#ef4444' };
    if (id === 'pending') return { label: es ? 'Sin revisar' : 'Not reviewed', color: '#7da0c4' };
    return extraStatuses.find((s) => s.id === id)
      || legacyStatusLabels.find((s) => s.id === id)
      || { label: id, color: '#64748b' };
  };

  // Estado de la PÓLIZA de una fila. Cualquier fila cancelada (enum crudo o
  // status inactivo/lapse) cae en 'cancelled'; el resto usa su status id
  // (active/pending/grace/<custom>). Default 'active' si no viene status.
  const polStatusOf = (r) => (isCancelledRow(r) ? 'cancelled' : (r.status || 'active'));
  // "Activa" cuenta IGUAL que la pestaña Activas de Pólizas: todo lo que no está
  // cancelado ni pendiente (Gracia y los estatus propios incluidos). Contar solo
  // el status 'active' daba 308 aquí contra 553 allí para la misma agencia
  // (Lissette 2026-10-13). Los demás estatus siguen contando el suyo.
  const pasaEstatusDePoliza = (r, st) => (st === 'active'
    ? !isCancelledRow(r) && r.status !== 'pending'
    : polStatusOf(r) === st);
  // Todos los filtros MENOS el estatus de la póliza. Lo usan la lista y los
  // números del desplegable de estatus: así, con "Salud" puesto, "Activa" dice
  // cuántas de Salud están activas y no las de todos los tipos (749 contra
  // 553, Lissette 2026-10-14). Es lo mismo que ya hace Pólizas.
  const pasaFiltrosSinEstatusDePoliza = (r, q) => {
    const mt = typeFilter === 'all' || r.type === typeFilter;
    const mc = carrierFilter === 'all' || r.carrier === carrierFilter;
    const msa = stateFilter === 'all' || stateOf(r) === stateFilter;
    const mat = attendFilter === 'all' || (attendFilter === 'none' ? !attendOf(r) : attendOf(r) === attendFilter);
    const man = analystFilter === 'all' || (analystFilter === 'none' ? !analystOf(r) : analystOf(r) === analystFilter);
    // In range mode a status matches when ANY month in the range has it —
    // "unpaid over Jan–Mar" reads as "didn't pay in at least one of them".
    const mst = statusFilter === 'all' || (rangeOn
      ? rangeMonths.some((m) => statusOfPay(r.payByPeriod?.[m]) === statusFilter)
      : statusOf(r) === statusFilter);
    // Monto: la MISMA prima mensual que pinta la columna Monto, en los dos
    // modos (un mes y rango), porque la columna enseña ese mismo dato en los
    // dos. Filtrar por otra cifra distinta de la que se ve seria peor que no
    // filtrar.
    const mm = amountInRange(r.monthlyPremium, amountMin, amountMax);
    const ms = !q || `${nameOf(r)} ${r.carrier} ${r.plan} ${r.policyNumber} ${insuredNamesOf(r, clientOf(r))}`.toLowerCase().includes(q);
    // Las cuatro columnas que faltaban. La regla de cada una vive en
    // `filtrosDePagosDelMes.js` para poder probarla sin montar React.
    const mfo = pasaFiltroDeForma(r, formaFilter);
    const mme = pasaFiltroDeMetodo(r, metodoFilter);
    const mno = pasaFiltroDeNota(r, notaFilter);
    const mcol = !colFilter.id || pasaFiltroDeColumna(r, colFilter.id, colFilter.texto);
    // "Sin agente" es una opcion: las polizas sin agente puesto son las que
    // hay que encontrar para completarlas, igual que en Forma y Metodo.
    const mag = agenteFilter === 'all'
      || (agenteFilter === 'none' ? !agenteOf(r) : agenteOf(r) === agenteFilter);
    return mt && mc && msa && mat && man && mst && mm && ms && mfo && mme && mno && mcol && mag;
  };
  const enAlcance = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return baseRows.filter((r) => pasaFiltrosSinEstatusDePoliza(r, q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRows, typeFilter, carrierFilter, stateFilter, statusFilter, attendFilter, analystFilter, amountMin, amountMax, busqueda, clientesPorId, rangeOn, rangeMonths, formaFilter, metodoFilter, notaFilter, colFilter, agenteFilter]);
  const polStatusCounts = useMemo(() => {
    const m = {};
    enAlcance.forEach((r) => { const s = polStatusOf(r); m[s] = (m[s] || 0) + 1; });
    m.active = enAlcance.filter((r) => pasaEstatusDePoliza(r, 'active')).length;
    return m;
  }, [enAlcance]);

  const filtered = useMemo(
    () => (polStatusFilter === 'all' ? enAlcance : enAlcance.filter((r) => pasaEstatusDePoliza(r, polStatusFilter))),
    [enAlcance, polStatusFilter],
  );

  // El orden va DESPUES de filtrar y ANTES de paginar: ordenar solo la pagina
  // que se ve daria un "menor a mayor" que cambia de sentido al pasar de
  // pagina, que es peor que no ordenar.
  const ordenadas = useMemo(() => {
    if (!sortBy) return filtered;
    return [...filtered].sort(comparadorDePagos(sortBy, sortDir, {
      es, nombreDe: nameOf, estadoDe: statusOf, etiquetaDeEstado: (id) => statusMetaFor(id).label,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sortBy, sortDir, es, extraStatuses]);

  // Paginate 15 per page over the filtered view.
  const totalPages = Math.max(1, Math.ceil(ordenadas.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = ordenadas.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Single-month view is a REAL bordered <table> (like the Comisiones timeline
  // Lissette pointed to): one box, gridlines between every column, sticky first
  // column (Cliente) + sticky header, horizontal scroll for extra columns
  // (Lissette 2026-07-08: "un cuadro con columna independiente, con líneas").
  // Header + body cell base styles (borders give the Excel-like gridlines).
  const thBase = { textAlign: 'left', padding: '.5rem .55rem', fontWeight: 800, fontSize: '.58rem', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--c-text3,#45688a)', background: 'var(--c-surface,#0d1f33)', borderRight: '1px solid var(--c-border,#1a3550)', borderBottom: '2px solid var(--c-border,#1a3550)', position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' };
  // Lo que se le anade a una cabecera para que se vea que ordena.
  const thOrden = { cursor: 'pointer', userSelect: 'none' };
  const tituloDeOrden = es
    ? 'Ordenar por esta columna (un toque de menor a mayor, otro al reves, el tercero quita el orden)'
    : 'Sort by this column (one click low→high, another high→low, a third clears it)';
  const tdBase = { padding: '.45rem .55rem', borderRight: '1px solid var(--c-border,#1a3550)', verticalAlign: 'top' };
  // La fila de filtros: pegada DEBAJO de la cabecera (que es sticky en top:0),
  // asi que va a la altura de esa cabecera o se le montaria encima al bajar.
  const thFiltro = {
    padding: '.28rem .35rem', background: 'var(--c-surface,#0d1f33)',
    borderRight: '1px solid var(--c-border,#1a3550)',
    borderBottom: '2px solid var(--c-border,#1a3550)',
    position: 'sticky', top: 34, zIndex: 2,
  };
  const inpFiltro = {
    width: '100%', boxSizing: 'border-box', minWidth: 0,
    background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)',
    borderRadius: 6, color: 'var(--c-text,#c8dff0)', fontSize: '.68rem',
    padding: '.22rem .3rem', fontFamily: 'inherit',
  };

  // Counts: single month → policies; range → policy-months (one policy over
  // 3 months contributes 3). Cancelled rows are shown but never counted in the
  // paid/unpaid/money summaries (they distort "what's still owed").
  const activeFiltered = useMemo(() => filtered.filter((r) => !isCancelledRow(r)), [filtered]);
  const cancelledCount = filtered.length - activeFiltered.length;
  const paidMonthsOf = (r) => rangeMonths.filter((m) => !!r.payByPeriod?.[m]?.paid).length;
  const paidCount = rangeOn
    ? activeFiltered.reduce((s, r) => s + paidMonthsOf(r), 0)
    : activeFiltered.filter((r) => r.payment?.paid).length;
  const unpaidCount = rangeOn
    ? activeFiltered.length * rangeMonths.length - paidCount
    : activeFiltered.length - paidCount;

  // Status counts across the type/carrier/search scope (ignoring statusFilter
  // itself) so the status chips show how many fall in each bucket.
  const statusScope = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return baseRows.filter((r) => {
      if (isCancelledRow(r)) return false; // status chips reflect active policies only
      const mt = typeFilter === 'all' || r.type === typeFilter;
      const mc = carrierFilter === 'all' || r.carrier === carrierFilter;
      const msa = stateFilter === 'all' || stateOf(r) === stateFilter;
      const mat = attendFilter === 'all' || (attendFilter === 'none' ? !attendOf(r) : attendOf(r) === attendFilter);
      const man = analystFilter === 'all' || (analystFilter === 'none' ? !analystOf(r) : analystOf(r) === analystFilter);
      const ms = !q || `${nameOf(r)} ${r.carrier} ${r.plan} ${r.policyNumber}`.toLowerCase().includes(q);
      return mt && mc && msa && mat && man && ms;
    });
  }, [baseRows, typeFilter, carrierFilter, stateFilter, attendFilter, analystFilter, busqueda, clientesPorId]);
  const cntFor = (st) => rangeOn
    ? statusScope.reduce((s, r) => s + rangeMonths.filter((m) => statusOfPay(r.payByPeriod?.[m]) === st).length, 0)
    : statusScope.filter((r) => statusOf(r) === st).length;
  const cntPaid = cntFor('paid');
  const cntUnpaid = cntFor('unpaid');
  const cntPending = cntFor('pending');

  // Amount totals (monthly premium) over the current filtered view. In range
  // mode the total spans every month in the range; collected counts each paid
  // policy-month at its monthly premium.
  const money = (n) => Number(n) || 0;
  const totalPremium = activeFiltered.reduce((s, r) => s + money(r.monthlyPremium), 0) * (rangeOn ? rangeMonths.length : 1);
  const collected = rangeOn
    ? activeFiltered.reduce((s, r) => s + money(r.monthlyPremium) * paidMonthsOf(r), 0)
    : activeFiltered.filter((r) => r.payment?.paid).reduce((s, r) => s + money(r.monthlyPremium), 0);
  const pendingAmount = totalPremium - collected;

  // Bulk selection — keyed by policy id, scoped to the filtered view.
  // Volver a la pagina 1 al tocar CUALQUIER filtro. Los cuatro nuevos entran
  // aqui a proposito: sin esto, filtrar estando en la pagina 4 deja la tabla en
  // blanco y parece que el filtro no encontro nada.
  useEffect(() => { setSelected(new Set()); setPage(1); setNoteThreadRow(null); }, [period, typeFilter, carrierFilter, stateFilter, polStatusFilter, statusFilter, attendFilter, analystFilter, amountMin, amountMax, search, rangeOn, rangeFrom, rangeTo, formaFilter, metodoFilter, notaFilter, colFilter, agenteFilter]);
  const allSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someSelected = selected.size > 0;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((r) => r.id)));
  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const bulkSetPaid = async (next) => {
    const targets = filtered.filter((r) => selected.has(r.id));
    if (targets.length === 0 || bulkSaving) return;
    setBulkSaving(true);
    const nowIso = new Date().toISOString();
    // optimistic — apply to every selected row at once
    setRows((prev) => prev.map((x) => selected.has(x.id)
      ? { ...x, payment: { ...(x.payment || {}), id: x.payment?.id || 'pending', paid: next,
          markedByUserId: currentUser?.id || x.payment?.markedByUserId || 'optimistic',
          markedByName: currentUser?.name || x.payment?.markedByName || null,
          markedAt: nowIso, paidAt: next ? nowIso : null,
          history: [{ paid: next, markedByName: currentUser?.name || null, markedAt: nowIso },
                    ...((x.payment && x.payment.history) || [])] } }
      : x));
    let ok = 0, fail = 0;
    for (const r of targets) {
      try {
        await markPolicyPayment({ policyId: r.id, period, paid: next });
        window.dispatchEvent(new CustomEvent('deskte:policy-payment', { detail: { policyId: r.id, period, paid: next } }));
        ok++;
      } catch { fail++; }
    }
    setBulkSaving(false);
    setSelected(new Set());
    if (fail === 0) {
      toast.success(es ? `${ok} póliza(s) actualizada(s)` : `${ok} policy(ies) updated`);
    } else {
      toast.error(es ? `${fail} fallaron — recargando` : `${fail} failed — reloading`);
      load(); // re-fetch truth for the rows whose optimistic update was wrong
    }
  };

  const setPaidState = async (r, next) => {
    const wasReviewed = !!r.payment?.markedByUserId;
    const prevPaid = !!r.payment?.paid;
    if (wasReviewed && next === prevPaid) return;
    const prevPayment = r.payment ? { ...r.payment } : null;
    setSavingId(r.id);
    const nowIso = new Date().toISOString();
    const evt = { paid: next, markedByName: currentUser?.name || null, markedAt: nowIso };
    // optimistic — record who + when, and prepend a history event (keeps both reviews)
    setRows((prev) => prev.map((x) => x.id === r.id
      ? { ...x, payment: { ...(x.payment || {}), id: x.payment?.id || 'pending', paid: next,
          markedByUserId: currentUser?.id || x.payment?.markedByUserId || 'optimistic',
          markedByName: currentUser?.name || x.payment?.markedByName || null,
          markedAt: nowIso,
          paidAt: next ? nowIso : null,
          history: [evt, ...((x.payment && x.payment.history) || [])] } } : x));
    try {
      await markPolicyPayment({ policyId: r.id, period, paid: next });
      window.dispatchEvent(new CustomEvent('deskte:policy-payment', { detail: { policyId: r.id, period, paid: next } }));
    } catch (e) {
      // revert to the prior payment state
      setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, payment: prevPayment } : x));
      toast.error(e.message || (es ? 'No se pudo guardar' : 'Could not save'));
    } finally {
      setSavingId(null);
    }
  };

  // Set ANY per-month status: 'paid' | 'unpaid' | 'grace' | '<custom id>'.
  // paid/unpaid clear the extra label; grace/custom store the label (paid=false).
  const setRowStatus = async (r, status) => {
    const paid = status === 'paid';
    const statusLabel = (status === 'paid' || status === 'unpaid') ? '' : status;
    if (statusOf(r) === status) return;
    const prevPayment = r.payment ? { ...r.payment } : null;
    setSavingId(r.id);
    const nowIso = new Date().toISOString();
    setRows((prev) => prev.map((x) => x.id === r.id
      ? { ...x, payment: { ...(x.payment || {}), id: x.payment?.id || 'pending', paid, statusLabel: statusLabel || null,
          markedByUserId: currentUser?.id || x.payment?.markedByUserId || 'optimistic',
          markedByName: currentUser?.name || x.payment?.markedByName || null, markedAt: nowIso, paidAt: paid ? nowIso : null,
          history: [{ paid, statusLabel: statusLabel || null, markedByName: currentUser?.name || null, markedAt: nowIso }, ...((x.payment && x.payment.history) || [])] } } : x));
    try {
      await markPolicyPayment({ policyId: r.id, period, paid, statusLabel });
      window.dispatchEvent(new CustomEvent('deskte:policy-payment', { detail: { policyId: r.id, period, paid } }));
    } catch (e) {
      setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, payment: prevPayment } : x));
      toast.error(e.message || (es ? 'No se pudo guardar' : 'Could not save'));
    } finally {
      setSavingId(null);
    }
  };

  // Range-mode variant of setRowStatus: marks ONE month (m) of one policy.
  // savingId is keyed r.id+m so only the touched month's select shows busy.
  const setRangeStatus = async (r, m, status) => {
    const paid = status === 'paid';
    const statusLabel = (status === 'paid' || status === 'unpaid') ? '' : status;
    const prev = r.payByPeriod?.[m] ? { ...r.payByPeriod[m] } : null;
    if (statusOfPay(prev) === status) return;
    setSavingId(r.id + m);
    const nowIso = new Date().toISOString();
    setRangeRows((prevRows) => prevRows.map((x) => x.id === r.id
      ? { ...x, payByPeriod: { ...x.payByPeriod, [m]: { ...(x.payByPeriod?.[m] || {}), id: x.payByPeriod?.[m]?.id || 'pending', paid, statusLabel: statusLabel || null,
          markedByUserId: currentUser?.id || x.payByPeriod?.[m]?.markedByUserId || 'optimistic',
          markedByName: currentUser?.name || null, markedAt: nowIso, paidAt: paid ? nowIso : null } } }
      : x));
    try {
      await markPolicyPayment({ policyId: r.id, period: m, paid, statusLabel });
      window.dispatchEvent(new CustomEvent('deskte:policy-payment', { detail: { policyId: r.id, period: m, paid } }));
    } catch (e) {
      setRangeRows((prevRows) => prevRows.map((x) => x.id === r.id ? { ...x, payByPeriod: { ...x.payByPeriod, [m]: prev } } : x));
      toast.error(e.message || (es ? 'No se pudo guardar' : 'Could not save'));
    } finally {
      setSavingId(null);
    }
  };

  // ── Note thread: open the 💬 bubble, send a message, delete an own message.
  // Sending never touches paid/markedBy (note-only endpoints), so an
  // unreviewed row keeps showing "Sin revisar".
  const openThread = async (r) => {
    setNoteThreadRow(r); setThread([]); setMsgText(''); setMsgImgs([]);
    // Nace con la preferencia, no en falso: eso es lo que hace que no haya que
    // marcarla nota a nota.
    setEspejoEnCliente(espejoSiempre && !!r.clientId); setThreadLoading(true);
    try {
      setThread(await listPolicyPaymentNotes({ policyId: r.id, period }));
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudieron cargar las notas' : 'Could not load the notes'));
    } finally {
      setThreadLoading(false);
    }
  };
  /**
   * Reflejar de una vez todas las del hilo que aun no lo estan.
   *
   * Se pregunta ANTES con el numero: crea una nota en la ficha por cada una, y
   * eso no se deshace con un boton. Y despues se dice lo que de verdad paso —
   * cuantas se reflejaron, cuantas ya estaban y cuantas son de otra persona—
   * en vez de un "listo" sobre un hilo a medias reflejar.
   */
  // Cuantas del hilo faltan por reflejar, contando SOLO las que esta persona
  // puede reflejar: las suyas. Las de otro no se tocan nunca, asi que contarlas
  // dejaria el boton con un "(1)" eterno que al pulsarlo no hace nada — y eso
  // se lee como que el boton esta roto. Misma regla de autoria que el lapiz de
  // editar, unas lineas mas abajo.
  // TODAS las del hilo, las haya escrito quien las haya escrito.
  //
  // Hasta el 2026-09-17 esto contaba solo las propias, por analogia con el
  // lapiz de editar. Lissette, delante del hilo de su clienta: *"¿por que no
  // puso, si no es mia? ¿que es eso?"*. Tenia razon: EDITAR cambia lo que otro
  // escribio; REFLEJAR no toca la nota de pago, crea una nota nueva en la
  // ficha. El unico dano era la atribucion, y eso se arregla firmando el espejo
  // con el autor original — que es lo que hace ahora el servidor.
  const pendientesDeReflejar = thread.filter((m) => !m.legacy && !m.espejo).length;
  // LAS NOTAS VIEJAS DEL MES cuentan tambien (Lissette 2026-09-16: *"sigue sin
  // reflejarse la nota de pagos"*). Son las que tiene la cartera que lleva
  // meses —el hilo es posterior— y se quedaban fuera de "todas" en silencio.
  // No llevan autor: esa nota no es un mensaje firmado, es el campo del mes, y
  // el servidor nunca guardo quien lo escribio. Por eso no se les aplica el
  // "solo las mias" y por eso el aviso de abajo lo dice antes de pulsar.
  const viejasPendientes = thread.filter(
    (m) => m.legacy && !m.espejo && (m.text || '').trim(),
  ).length;
  const porReflejar = pendientesDeReflejar + viejasPendientes;
  // Cuantas son de otra persona — ya NO para excluirlas, sino para decir en la
  // confirmacion que en la ficha van a salir con SU nombre y no con el tuyo.
  const deOtrosEnElHilo = thread.filter(
    (m) => !m.legacy && !m.espejo && m.authorUserId && m.authorUserId !== currentUser?.id,
  ).length;

  const reflejarTodas = async () => {
    const r = noteThreadRow;
    if (!r || reflejandoTodas) return;
    const pendientes = porReflejar;
    if (!pendientes) {
      toast.success(es ? 'Todas las notas de este hilo ya están reflejadas.' : 'Every note in this thread is already mirrored.');
      return;
    }
    const ok = await confirmDialog({
      title: es ? '¿Reflejar todas en la ficha?' : 'Mirror them all onto the record?',
      message: (es
        ? `Se crearán hasta ${pendientes} nota${pendientes === 1 ? '' : 's'} en la ficha del cliente, una por cada nota de este hilo que todavía no esté reflejada.`
        : `Up to ${pendientes} note${pendientes === 1 ? '' : 's'} will be created on the client record, one per thread note that is not mirrored yet.`)
        // Las de otra persona SE REFLEJAN, y salen con SU nombre. Decirlo antes
        // evita la duda de "¿va a parecer que lo escribí yo?".
        + (deOtrosEnElHilo
          ? (es
            ? ` ${deOtrosEnElHilo === 1 ? 'Una es' : `${deOtrosEnElHilo} son`} de otra persona: en la ficha ${deOtrosEnElHilo === 1 ? 'saldrá' : 'saldrán'} con su nombre, no con el tuyo.`
            : ` ${deOtrosEnElHilo === 1 ? 'One was' : `${deOtrosEnElHilo} were`} written by someone else: on the record ${deOtrosEnElHilo === 1 ? 'it keeps their name' : 'they keep their names'}, not yours.`)
          : '')
        + (viejasPendientes
          ? (es
            ? ` De esas, ${viejasPendientes} ${viejasPendientes === 1 ? 'es una nota antigua del mes' : 'son notas antiguas del mes'}: en esas el sistema no guardó quién las escribió, así que se reflejan a tu nombre.`
            : ` Of those, ${viejasPendientes} ${viejasPendientes === 1 ? 'is an old monthly note' : 'are old monthly notes'}: the system never recorded who wrote them, so they are mirrored under your name.`)
          : ''),
      confirmLabel: es ? 'Reflejar todas' : 'Mirror all',
    });
    if (!ok) return;
    setReflejandoTodas(true);
    try {
      const res = await mirrorAllPaymentNotes(r.id, lang);
      await openThread(r);
      const partes = [];
      if (res.reflejadas) partes.push(es ? `${res.reflejadas} reflejada${res.reflejadas === 1 ? '' : 's'}` : `${res.reflejadas} mirrored`);
      if (res.yaEstaban) partes.push(es ? `${res.yaEstaban} ya lo estaban` : `${res.yaEstaban} already were`);
      if (res.deOtros) partes.push(es ? `${res.deOtros} de otra persona (sin tocar)` : `${res.deOtros} by someone else (left alone)`);
      if (res.viejas) partes.push(es ? `${res.viejas} antigua${res.viejas === 1 ? '' : 's'} del mes` : `${res.viejas} old monthly`);
      if (res.fallaron) partes.push(es ? `${res.fallaron} no se pudieron` : `${res.fallaron} could not be done`);
      const texto = partes.join(' · ') || (es ? 'No había ninguna por reflejar.' : 'There was nothing to mirror.');
      if (res.fallaron) toast.error(texto); else toast.success(texto);
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudieron reflejar' : 'Could not mirror them'));
    } finally {
      setReflejandoTodas(false);
    }
  };

  const closeThread = () => { setNoteThreadRow(null); setThread([]); setMsgText(''); setMsgImgs([]); setEspejoEnCliente(false); };

  const sendThreadMsg = async () => {
    const r = noteThreadRow;
    const text = msgText.trim();
    if (!r || msgSending || noteImgBusy || (!text && msgImgs.length === 0)) return;
    setMsgSending(true);
    try {
      // EL ESPEJO LO HACE EL SERVIDOR (Lissette 2026-09-16: *"pero necesito que
      // sea un espejo"*). Antes eran dos llamadas desde aqui y la copia quedaba
      // suelta: editar una no cambiaba la otra, y a los dos dias decian cosas
      // distintas. Ahora las dos notas van enlazadas, y editar o borrar esta se
      // lleva a la de la ficha.
      const msg = await addPolicyPaymentNote({
        policyId: r.id, period, text, imageUrls: msgImgs, espejo: espejoEnCliente, lang,
      });
      setThread((prev) => [msg, ...prev]);
      // LO QUE SE CONSIGUIO DE VERDAD, no lo que se pidio: si se marco el espejo
      // y el servidor no pudo crearlo, `msg.espejo` vuelve en `false` y se dice.
      // Callarlo dejaria a la agente creyendo que la nota esta en la ficha
      // (regla de oro 83: un recorte que no se cuenta es una mentira).
      if (espejoEnCliente && msg && msg.espejo) {
        toast.success(es ? 'Nota guardada y reflejada en las notas del cliente ✅' : 'Note saved and mirrored into the client notes ✅');
      } else if (espejoEnCliente) {
        toast.error(es
          ? 'La nota se guardó en Pagos del Mes, pero NO se pudo reflejar en las notas del cliente.'
          : "The note was saved in Monthly Payments, but it could NOT be mirrored into the client's notes.");
      }
      setMsgText(''); setMsgImgs([]);
      setRows((prev) => prev.map((x) => x.id === r.id
        ? { ...x, payment: { ...(x.payment || {}), notesCount: (x.payment?.notesCount || 0) + 1,
            lastNoteText: text || x.payment?.lastNoteText || null } } : x));
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo enviar la nota' : 'Could not send the note'));
    } finally {
      setMsgSending(false);
    }
  };

  const deleteThreadMsg = async (m) => {
    const r = noteThreadRow;
    if (!r || m.legacy) return;
    try {
      await deletePolicyPaymentNote(m.id);
      setThread((prev) => prev.filter((x) => x.id !== m.id));
      setRows((prev) => prev.map((x) => x.id === r.id
        ? { ...x, payment: { ...(x.payment || {}), notesCount: Math.max(0, (x.payment?.notesCount || 1) - 1) } } : x));
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo borrar la nota' : 'Could not delete the note'));
    }
  };

  // Pin/unpin a payment note (any agent). Legacy first message isn't pinnable.
  const togglePayNotePin = async (m) => {
    if (m.legacy) return;
    const next = !m.isPinned;
    setThread((prev) => prev.map((x) => x.id === m.id ? { ...x, isPinned: next } : x));
    try { await togglePaymentNotePin(m.id, next); if (noteThreadRow) setThread(await listPolicyPaymentNotes({ policyId: noteThreadRow.id, period })); }
    catch (e) { setThread((prev) => prev.map((x) => x.id === m.id ? { ...x, isPinned: m.isPinned } : x)); toast.error(e.message || (es ? 'No se pudo fijar' : 'Failed to pin')); }
  };

  // Editar una nota del hilo (solo el autor). Guarda vía PATCH texto Y fotos, y
  // refleja los cambios en el hilo + el preview de la fila. Al editar se pueden
  // agregar/quitar fotos (Lissette 2026-07-13).
  // Al editar se arrastra el estado del espejo: la casilla sale como esta la
  // nota, y cambiarla lo enciende o lo apaga (Lissette 2026-09-16).
  const [editEspejo, setEditEspejo] = useState(false);
  const startEditMsg = (m) => {
    setEditMsgId(m.id); setEditMsgText(m.text || ''); setEditMsgImgs(m.imageUrls || []);
    setEditEspejo(!!m.espejo);
  };
  const cancelEditMsg = () => { setEditMsgId(null); setEditMsgText(''); setEditMsgImgs([]); setEditEspejo(false); };
  const saveEditMsg = async (m) => {
    const r = noteThreadRow;
    const text = editMsgText.trim();
    if (!r || m.legacy || editMsgSaving) return;
    if (!text && editMsgImgs.length === 0) { toast.error(es ? 'La nota debe tener texto o una foto.' : 'The note must have text or a photo.'); return; }
    setEditMsgSaving(true);
    try {
      // `editEspejo` viaja SIEMPRE: si no cambio, el servidor reescribe la nota
      // de la ficha con el texto nuevo; si cambio, la crea o la borra.
      const updated = await updatePolicyPaymentNote(m.id, text, editMsgImgs, editEspejo, lang);
      const newImgs = (updated && updated.imageUrls) || [];
      const espejoReal = !!(updated && updated.espejo);
      setThread((prev) => prev.map((x) => (x.id === m.id ? { ...x, text: text || null, imageUrls: newImgs, espejo: espejoReal } : x)));
      // La nota reflejada pudo haberla borrado alguien desde la ficha: entonces
      // el servidor limpia el enlace y aqui se dice, en vez de dejar una casilla
      // marcada que ya no refleja nada.
      if (editEspejo && !espejoReal) {
        toast.error(es
          ? 'La nota se guardó, pero ya no hay nota en la ficha que reflejar (alguien la borró).'
          : 'The note was saved, but there is no mirrored note on the record any more (someone deleted it).');
      }
      setRows((prev) => prev.map((x) => x.id === r.id
        ? { ...x, payment: { ...(x.payment || {}), lastNoteText: text || x.payment?.lastNoteText || null } } : x));
      cancelEditMsg();
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo editar la nota' : 'Could not edit the note'));
    } finally {
      setEditMsgSaving(false);
    }
  };

  // Upload pasted (Ctrl+V) or picked images / PDFs into a draft (compose or
  // edit). PDF was added 2026-07-21: receipts and carrier letters arrive as
  // PDF more often than as screenshots, and the old guard dropped them in
  // total silence — the agent picked a file and simply nothing happened.
  const NOTE_MAX_BYTES = 5 * 1024 * 1024;
  const isNotePdf = (u) => /\.pdf(\?|$)/i.test(String(u || ''));
  const addNoteImage = async (file, setter = setMsgImgs) => {
    if (!file) return;
    const pdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
    if (!pdf && !/^image\//.test(file.type || '')) {
      toast.error(es ? 'Solo se pueden adjuntar imágenes o PDF' : 'Only images or PDF can be attached');
      return;
    }
    if (file.size > NOTE_MAX_BYTES) {
      toast.error(es ? 'El archivo supera 5 MB' : 'The file is larger than 5 MB');
      return;
    }
    setNoteImgBusy(true);
    try {
      const fallbackName = pdf ? 'documento.pdf' : 'pasted-image.png';
      const named = file.name ? file : new File([file], fallbackName, { type: file.type || (pdf ? 'application/pdf' : 'image/png') });
      const blobUrl = await uploadPaymentNoteImage(named);
      setter((prev) => [...prev, blobUrl]);
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo subir el archivo' : 'Could not upload the file'));
    } finally {
      setNoteImgBusy(false);
    }
  };
  const onNotePaste = (e) => {
    const items = [...(e.clipboardData?.items || [])].filter((it) => it.type && it.type.startsWith('image/'));
    if (!items.length) return;
    e.preventDefault();
    items.forEach((it) => addNoteImage(it.getAsFile()));
  };
  const onEditMsgPaste = (e) => {
    const items = [...(e.clipboardData?.items || [])].filter((it) => it.type && it.type.startsWith('image/'));
    if (!items.length) return;
    e.preventDefault();
    items.forEach((it) => addNoteImage(it.getAsFile(), setEditMsgImgs));
  };

  // Keep the thread scrolled to the newest message. Newest is now at the TOP
  // (oldest sinks to the bottom), so scroll to the top.
  useEffect(() => {
    if (threadBodyRef.current) threadBodyRef.current.scrollTop = 0;
  }, [thread, noteThreadRow]);

  return <div>
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1rem',gap:'.5rem',flexWrap:'wrap'}}>
      <div className="dkt-sec-title" style={{margin:0,display:'flex',alignItems:'center',gap:8}}>
        <CreditCard size={18}/>{es ? 'Pagos del Mes' : 'Monthly Payments'}
      </div>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        {/* Month-range toggle — the single-month select stays the default;
            range shows Desde/Hasta pickers instead (Lissette 2026-07-02). */}
        <button onClick={()=>setRangeOn((v)=>!v)} className={`dkt-tab${rangeOn?' on':''}`}
          style={{display:'inline-flex',alignItems:'center',gap:5,fontWeight:700}}
          title={es?'Ver varios meses a la vez (Desde → Hasta)':'View several months at once (From → To)'}>
          📆 {es?'Rango de meses':'Month range'}
        </button>
        {!rangeOn && (
          <select value={period} onChange={(e)=>setPeriod(e.target.value)} className="dkt-search" style={{width:'auto',minWidth:160}}>
            {MONTHS_LIST(12).map((m)=><option key={m} value={m}>{fmtMonth(m)}</option>)}
          </select>
        )}
        {rangeOn && <>
          <select value={rangeFrom} onChange={(e)=>setRangeFrom(e.target.value)} className="dkt-search" style={{width:'auto',minWidth:140}} title={es?'Desde':'From'}>
            {MONTHS_LIST(12).map((m)=><option key={m} value={m}>{fmtMonth(m)}</option>)}
          </select>
          <span style={{color:'var(--c-text3,#45688a)',fontWeight:800}}>→</span>
          <select value={rangeTo} onChange={(e)=>setRangeTo(e.target.value)} className="dkt-search" style={{width:'auto',minWidth:140}} title={es?'Hasta':'To'}>
            {MONTHS_LIST(12).map((m)=><option key={m} value={m}>{fmtMonth(m)}</option>)}
          </select>
          {rangeInfo.clamped && (
            <span style={{fontSize:'.68rem',color:'#f59e0b',fontWeight:700}}>{es?'Máx. 12 meses':'Max 12 months'}</span>
          )}
        </>}
        {!rangeOn && (
          <button onClick={()=>setImportOpen(true)} className="dkt-tab" style={{display:'inline-flex',alignItems:'center',gap:5,fontWeight:700}}
            title={es?'Subir archivo de la compañía (Ambetter, Oscar…) y marcar pagos automáticamente':'Upload a carrier file (Ambetter, Oscar…) and mark payments automatically'}>
            <Upload size={13}/>{es?'Subir archivo':'Upload file'}
          </button>
        )}
        {!rangeOn && importedCount>0 && (
          <button onClick={undoImport} className="dkt-tab" style={{display:'inline-flex',alignItems:'center',gap:5,fontWeight:700,color:'#ef4444'}}
            title={es?`Quitar las ${importedCount} marca(s) que vinieron de un archivo este mes`:`Remove the ${importedCount} mark(s) that came from a file this month`}>
            <Trash2 size={13}/>{es?'Borrar importación':'Delete import'}
          </button>
        )}
        <button onClick={rangeOn?loadRange:load} className="dkt-tab" title={es?'Recargar':'Reload'}>↻</button>
      </div>
    </div>

    {/* Hidden picker for the note attach button — several files at once */}
    <input ref={noteImgFileRef} type="file" accept="image/*,application/pdf,.pdf" multiple style={{display:'none'}}
      onChange={(e)=>{ const fs=[...(e.target.files||[])]; if(noteImgFileRef.current) noteImgFileRef.current.value=''; fs.forEach((f)=>addNoteImage(f)); }}/>

    {/* Summary chips */}
    <div style={{display:'flex',gap:8,marginBottom:'.875rem',flexWrap:'wrap'}}>
      <span style={{padding:'.3rem .6rem',borderRadius:8,background:'rgba(16,185,129,.12)',border:'1px solid rgba(16,185,129,.3)',color:'#10b981',fontSize:'.72rem',fontWeight:700}}>
        ✅ {es?'Pagaron':'Paid'}: {paidCount}
      </span>
      <span style={{padding:'.3rem .6rem',borderRadius:8,background:'rgba(239,68,68,.12)',border:'1px solid rgba(239,68,68,.3)',color:'#ef4444',fontSize:'.72rem',fontWeight:700}}>
        ❌ {es?'Falta':'Unpaid'}: {unpaidCount}
      </span>
      <span style={{padding:'.3rem .6rem',borderRadius:8,background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',color:'var(--c-text2,#7da0c4)',fontSize:'.72rem',fontWeight:700}}>
        {es?'Total activas':'Active total'}: {activeFiltered.length}
      </span>
      {cancelledCount>0 && (
        <span title={es?'Pólizas canceladas — se muestran pero no cuentan en los totales':'Cancelled policies — shown but not counted in the totals'} style={{padding:'.3rem .6rem',borderRadius:8,background:'rgba(148,163,184,.12)',border:'1px solid rgba(148,163,184,.3)',color:'#94a3b8',fontSize:'.72rem',fontWeight:700}}>
          🚫 {es?'Canceladas':'Cancelled'}: {cancelledCount}
        </span>
      )}
      <span style={{marginLeft:'auto',display:'flex',gap:8,flexWrap:'wrap'}}>
        <span title={es?'Prima mensual cobrada':'Monthly premium collected'} style={{padding:'.3rem .6rem',borderRadius:8,background:'rgba(16,185,129,.12)',border:'1px solid rgba(16,185,129,.3)',color:'#10b981',fontSize:'.72rem',fontWeight:700}}>
          💵 {es?'Cobrado':'Collected'}: {fmtMoney(collected)}
        </span>
        <span title={es?'Prima mensual pendiente':'Monthly premium pending'} style={{padding:'.3rem .6rem',borderRadius:8,background:'rgba(245,158,11,.12)',border:'1px solid rgba(245,158,11,.3)',color:'#f59e0b',fontSize:'.72rem',fontWeight:700}}>
          ⏳ {es?'Pendiente':'Pending'}: {fmtMoney(pendingAmount)}
        </span>
        <span title={rangeOn?(es?`Prima total del rango (${rangeMonths.length} mes(es))`:`Total premium over the range (${rangeMonths.length} month(s))`):(es?'Prima mensual total':'Total monthly premium')} style={{padding:'.3rem .6rem',borderRadius:8,background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',color:'var(--c-text,#eef6ff)',fontSize:'.72rem',fontWeight:800}}>
          Σ {fmtMoney(totalPremium)}{rangeOn?(es?` · ${rangeMonths.length} mes(es)`:` · ${rangeMonths.length} month(s)`):'/mo'}
        </span>
      </span>
    </div>

    <div style={{position:'relative',marginBottom:'.875rem'}}>
      <Search size={13} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--c-text3,#45688a)',pointerEvents:'none'}}/>
      <input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder={es?'Buscar cliente, dependiente, cónyuge, carrier, plan...':'Search client, dependent, spouse, carrier, plan...'} className="dkt-search" style={{paddingLeft:32}}/>
    </div>

    <div className="flex flex-wrap gap-2 mb-5" style={{alignItems:'center'}}>
      {/* Tipo de póliza */}
      <select value={typeFilter} onChange={(e)=>setTypeFilter(e.target.value)} className="dkt-search" style={{width:'auto',minWidth:150}} title={es?'Tipo de póliza':'Policy type'}>
        <option value="all">{es?`📋 Todo tipo (${rows.length})`:`📋 All types (${rows.length})`}</option>
        {POLICY_TYPES.map((pt)=>{ const count=rows.filter((r)=>r.type===pt.id).length; return count>0
          ? <option key={pt.id} value={pt.id}>{(pt.icon?pt.icon+' ':'')+(es?pt.labelEs:pt.label)} ({count})</option> : null; })}
      </select>
      {/* Compañía (scoped al tipo) */}
      <select value={carrierFilter} onChange={(e)=>setCarrierFilter(e.target.value)} className="dkt-search" style={{width:'auto',minWidth:150}} title={es?'Compañía':'Company'}>
        <option value="all">{es?`🏢 Toda compañía${carrierOptions.length?` (${carrierOptions.length})`:''}`:`🏢 All companies${carrierOptions.length?` (${carrierOptions.length})`:''}`}</option>
        {carrierOptions.map((c)=><option key={c} value={c}>{c}</option>)}
      </select>
      {/* Estado de USA */}
      <select value={stateFilter} onChange={(e)=>setStateFilter(e.target.value)} className="dkt-search" style={{width:'auto',minWidth:130}} title={es?'Estado (USA)':'State (US)'}>
        <option value="all">{es?`📍 Todo estado${stateOptions.length?` (${stateOptions.length})`:''}`:`📍 All states${stateOptions.length?` (${stateOptions.length})`:''}`}</option>
        {stateOptions.map((s)=><option key={s} value={s}>{s}</option>)}
      </select>
      {/* Estado de pago */}
      <select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} className="dkt-search" style={{width:'auto',minWidth:150}} title={es?'Estado de pago':'Payment status'}>
        <option value="all">{es?'🚦 Todos':'🚦 All statuses'}</option>
        <option value="paid">{es?`✅ Pagaron (${cntPaid})`:`✅ Paid (${cntPaid})`}</option>
        <option value="unpaid">{es?`❌ No pagaron (${cntUnpaid})`:`❌ Unpaid (${cntUnpaid})`}</option>
        <option value="pending">{es?`◻ Sin revisar (${cntPending})`:`◻ Not reviewed (${cntPending})`}</option>
        {extraStatuses.map((s)=>{ const n=cntFor(s.id); return <option key={s.id} value={s.id}>● {s.label} ({n})</option>; })}
      </select>
      {/* Estado de la PÓLIZA (activo / pendiente / gracia / cancelada / custom) */}
      <select value={polStatusFilter} onChange={(e)=>setPolStatusFilter(e.target.value)} className="dkt-search" style={{width:'auto',minWidth:150}} title={es?'Estado de la póliza':'Policy status'}>
        <option value="all">{es?`🗂️ Toda póliza (${baseRows.length})`:`🗂️ All policies (${baseRows.length})`}</option>
        {getAllPolicyStatuses().map((s)=>{ const n=polStatusCounts[s.id]||0; return n>0
          ? <option key={s.id} value={s.id}>{(es?(s.labelEs||s.label):s.label)} ({n})</option> : null; })}
      </select>
      {/* Atiende (handling seat of the client) — mirrors the clients-list filter. */}
      {attendOptions(users).length > 0 && (
        <select value={attendFilter} onChange={(e)=>setAttendFilter(e.target.value)} className="dkt-search" style={{width:'auto',minWidth:150}}
          title={es?'Filtrar por quién atiende al cliente':'Filter by who handles the client'}>
          <option value="all">{es?'👤 Atiende: Todos':'👤 Handled: All'}</option>
          <option value="none">{es?'Sin asignar':'Unassigned'}</option>
          {attendOptions(users).map((u)=>{ const l=seatLabelFor(u.id, seatLabels, users); return <option key={u.id} value={u.id}>{l.name}</option>; })}
        </select>
      )}
      {/* Analista — the analystName recorded on each policy. ALWAYS visible. */}
      <select value={analystFilter} onChange={(e)=>setAnalystFilter(e.target.value)} className="dkt-search" style={{width:'auto',minWidth:150}}
        title={es?'Filtrar por analista (el campo Analista de cada póliza)':'Filter by analyst (each policy\'s Analyst field)'}>
        <option value="all">{es?'🔎 Analista: Todos':'🔎 Analyst: All'}</option>
        <option value="none">{es?'Sin analista':'No analyst'}</option>
        {analystOptions.map((a)=><option key={a} value={a}>{a}</option>)}
      </select>
      {/* AGENTE EN PÓLIZA. Va en la barra y no en la cabecera porque NO es una
          columna de esta tabla — es un dato de la ficha de la poliza. La
          cabecera filtra lo que se ve; la barra, lo que no se ve. */}
      <select value={agenteFilter} onChange={(e)=>setAgenteFilter(e.target.value)} className="dkt-search" style={{width:'auto',minWidth:170}}
        title={es?'Filtrar por el agente que figura en la póliza':'Filter by the agent on the policy'}>
        <option value="all">{es?'🧑‍💼 Agente en póliza: Todos':'🧑‍💼 Agent on policy: All'}</option>
        <option value="none">{(es?'Sin agente':'No agent')+` (${baseRows.filter((r)=>!agenteOf(r)).length})`}</option>
        {agenteOptions.map((a)=>{
          const n = baseRows.filter((r)=>agenteOf(r)===a).length;
          return <option key={a} value={a}>{a} ({n})</option>;
        })}
      </select>
      {/* Forma, Metodo, Nota y las columnas propias YA NO estan aqui: viven en
          la FILA DE FILTROS de la cabecera de la tabla, cada una debajo de su
          columna (Lissette 2026-09-01: "yo queria que aqui yo podria tener
          filtro", senalando las cabeceras).

          No se dejan en los dos sitios a proposito. Duplicar un control que
          comparte estado no anade nada y llena la barra; y si algun dia
          dejaran de compartirlo, la barra diria una cosa y la tabla otra
          (E-138). La regla que queda: LA CABECERA FILTRA LO QUE SE VE EN LA
          TABLA; LA BARRA, LO QUE NO ES UNA COLUMNA.

          Buscar, Estado y Monto SI siguen en los dos: ya estaban en la barra
          antes de esto y quitarlos seria arrebatarle algo que ya usa. */}
      {/* Monto Desde/Hasta — sobre la columna Monto (prima mensual). Cualquiera
          de los dos extremos puede ir solo: "de $200 en adelante" o "hasta $50". */}
      <span style={{display:'inline-flex',alignItems:'center',gap:4}}
        title={es?'Filtrar por el monto de la columna Monto (prima mensual). Puede llenar solo uno de los dos.':'Filter by the Amount column (monthly premium). Either end can be left blank.'}>
        <span style={{fontSize:'.72rem',color:'var(--c-text3,#45688a)',fontWeight:700}}>💵</span>
        <input type="number" inputMode="decimal" min="0" step="0.01" value={amountMin} onChange={(e)=>setAmountMin(e.target.value)}
          placeholder={es?'Desde':'From'} className="dkt-search" style={{width:88}} aria-label={es?'Monto desde':'Amount from'}/>
        <span style={{fontSize:'.72rem',color:'var(--c-text3,#45688a)'}}>–</span>
        <input type="number" inputMode="decimal" min="0" step="0.01" value={amountMax} onChange={(e)=>setAmountMax(e.target.value)}
          placeholder={es?'Hasta':'To'} className="dkt-search" style={{width:88}} aria-label={es?'Monto hasta':'Amount to'}/>
      </span>
      {/* Al reves no se corrige solo: se avisa. Corregirlo en silencio cambiaria
          lo que la agente escribio; dejarlo mudo la dejaria con la tabla vacia
          sin saber por que. */}
      {(()=>{ const lo=parseMoney(amountMin), hi=parseMoney(amountMax);
        return (lo!==null && hi!==null && lo>hi) ? (
          <span style={{fontSize:'.68rem',fontWeight:700,color:'#f59e0b'}}>
            {es?'El "Desde" es mayor que el "Hasta"':'"From" is higher than "To"'}
          </span>
        ) : null; })()}
      {(typeFilter!=='all'||carrierFilter!=='all'||stateFilter!=='all'||polStatusFilter!=='all'||statusFilter!=='all'||attendFilter!=='all'||analystFilter!=='all'||amountMin!==''||amountMax!=='') && (
        <button onClick={()=>{setTypeFilter('all');setCarrierFilter('all');setStateFilter('all');setPolStatusFilter('all');setStatusFilter('all');setAttendFilter('all');setAnalystFilter('all');setAmountMin('');setAmountMax('');}}
          className="dkt-tab" style={{display:'inline-flex',alignItems:'center',gap:3}}><X size={11}/>{es?'Limpiar':'Clear'}</button>
      )}
      <span style={{flex:1}}/>
      <button onClick={()=>setStatusMgr(true)} className="dkt-tab" style={{display:'inline-flex',alignItems:'center',gap:4}}
        title={es?'Estatus del pago del mes (aparte de los de la póliza)':"Monthly payment statuses (separate from the policy's)"}>
        ⚙️ {es?'Estatus de pago':'Payment statuses'}
      </button>
      <button onClick={()=>setColMgr(true)} className="dkt-tab" style={{display:'inline-flex',alignItems:'center',gap:4}} title={es?'Agregar columnas personalizadas a esta tabla':'Add custom columns to this table'}>
        ➕ {es?'Columnas':'Columns'}
      </button>
      {/* Mover la tabla ◀ ▶ para llegar a las columnas del final (como el timeline
          de Comisiones). Solo cuando hay columnas personalizadas + un mes. */}
      {!rangeOn && customColumns.length > 0 && (
        <span style={{display:'inline-flex',gap:'.25rem'}}>
          <button type="button" onClick={()=>{tableScrollRef.current?.focus({preventScroll:true});scrollTable(-260);}} className="dkt-tab" title={es?'Mover a la izquierda (tecla ←)':'Scroll left (← key)'} style={{fontWeight:800,padding:'.4rem .6rem'}}>◀</button>
          <button type="button" onClick={()=>{tableScrollRef.current?.focus({preventScroll:true});scrollTable(260);}} className="dkt-tab" title={es?'Mover a la derecha (tecla →) — ver columnas del final':'Scroll right (→ key) — see the last columns'} style={{fontWeight:800,padding:'.4rem .6rem'}}>▶</button>
        </span>
      )}
      <button onClick={()=>setTplManager(true)} className="dkt-tab" style={{display:'inline-flex',alignItems:'center',gap:4}} title={es?'Plantillas de recordatorio':'Reminder templates'}>
        <Bell size={12}/>{es?'Plantillas':'Templates'}
      </button>
    </div>

    {loading && <div style={{padding:'2rem',textAlign:'center',color:'var(--c-text3,#45688a)'}}>{es?'Cargando…':'Loading…'}</div>}
    {error && !loading && <div style={{padding:'1rem',borderRadius:8,background:'rgba(239,68,68,.1)',border:'1px solid rgba(239,68,68,.3)',color:'#ef4444',fontSize:'.78rem'}}>{error}</div>}
    {/* Vacio por FILTRO y vacio de verdad no dicen lo mismo (regla 89). Con el
        monto esto pasa a ser critico: "de $200 a $300" sin coincidencias diria
        "no hay polizas activas este mes", y la agente creeria que se le
        perdieron las polizas del mes. */}
    {!loading && !error && filtered.length === 0 && (
      <div style={{padding:'2rem',textAlign:'center',color:'var(--c-text3,#45688a)',fontSize:'.82rem'}}>
        {baseRows.length === 0
          ? (es?'No hay pólizas activas para este mes.':'No active policies for this month.')
          : (<>
              <div>{es?`Ningún resultado con estos filtros (hay ${baseRows.length} póliza(s) este mes).`:`No matches with these filters (${baseRows.length} policy/policies this month).`}</div>
              <button onClick={()=>{setTypeFilter('all');setCarrierFilter('all');setStateFilter('all');setPolStatusFilter('all');setStatusFilter('all');setAttendFilter('all');setAnalystFilter('all');setAmountMin('');setAmountMax('');setSearch('');}}
                className="dkt-tab" style={{marginTop:'.7rem',display:'inline-flex',alignItems:'center',gap:4}}><X size={11}/>{es?'Limpiar filtros':'Clear filters'}</button>
            </>)}
      </div>
    )}

    {!loading && !error && filtered.length > 0 && (
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        {/* Bulk action bar — single-month only (bulk marking is per-period) */}
        {!rangeOn && (
        <div style={{display:'flex',alignItems:'center',gap:12,padding:'.45rem .75rem',borderRadius:10,
          background:someSelected?'rgba(59,130,246,.1)':'transparent',
          border:`1px solid ${someSelected?'rgba(59,130,246,.35)':'transparent'}`,minHeight:38}}>
          <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:'.74rem',fontWeight:700,color:'var(--c-text2,#7da0c4)'}}>
            <input type="checkbox" checked={allSelected} ref={(el)=>{ if(el) el.indeterminate = someSelected && !allSelected; }}
              onChange={toggleAll} style={{width:16,height:16,cursor:'pointer',accentColor:'#3b82f6'}}/>
            {someSelected ? (es?`${selected.size} seleccionada(s)`:`${selected.size} selected`) : (es?'Seleccionar todo':'Select all')}
          </label>
          {someSelected && (
            <div style={{display:'flex',gap:8,marginLeft:'auto'}}>
              <button onClick={()=>bulkSetPaid(true)} disabled={bulkSaving}
                style={{display:'flex',alignItems:'center',gap:4,padding:'.35rem .7rem',borderRadius:8,background:'rgba(16,185,129,.15)',border:'1px solid rgba(16,185,129,.4)',color:'#10b981',fontSize:'.74rem',fontWeight:800,cursor:bulkSaving?'wait':'pointer',opacity:bulkSaving?.6:1}}>
                <Check size={13}/>{es?'Marcar pagado':'Mark paid'}
              </button>
              <button onClick={()=>bulkSetPaid(false)} disabled={bulkSaving}
                style={{display:'flex',alignItems:'center',gap:4,padding:'.35rem .7rem',borderRadius:8,background:'rgba(239,68,68,.12)',border:'1px solid rgba(239,68,68,.35)',color:'#ef4444',fontSize:'.74rem',fontWeight:800,cursor:bulkSaving?'wait':'pointer',opacity:bulkSaving?.6:1}}>
                <X size={13}/>{es?'Marcar no pagado':'Mark unpaid'}
              </button>
            </div>
          )}
        </div>
        )}
        {/* SINGLE-MONTH = tabla real: fila de encabezados + filas en grid que
            comparten gridCols, así cada columna (incl. las personalizadas) baja
            alineada como columna completa. Scroll horizontal si sobran columnas.
            RANGE MODE mantiene su vista de meses lado a lado (abajo). */}
        {!rangeOn ? (
          <div ref={refTabla} tabIndex={0}
            onKeyDown={(e)=>{ if(e.key==='ArrowRight'){e.preventDefault();scrollTable(200);} else if(e.key==='ArrowLeft'){e.preventDefault();scrollTable(-200);} else if(e.key==='Home'){e.preventDefault();tableScrollRef.current?.scrollTo({left:0,behavior:'smooth'});} else if(e.key==='End'){e.preventDefault();tableScrollRef.current?.scrollTo({left:tableScrollRef.current.scrollWidth,behavior:'smooth'});} }}
            style={{overflow:'auto',maxHeight:altoTabla||'72vh',border:'1px solid var(--c-border,#1a3550)',borderRadius:12,outline:'none'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'.78rem'}}>
              <thead>
                <tr>
                  {/* Tocar el nombre de la columna ordena: sube, baja, y a la
                      tercera vuelve al orden de siempre. El <th> entero es el
                      boton para que se pueda dar donde sea, no solo en la
                      palabra (Lissette 2026-09-01). */}
                  <th onClick={()=>tocarCabecera('cliente')} title={tituloDeOrden} style={{...thBase,...thOrden,left:0,zIndex:3,minWidth:250}}>{(es?'Cliente':'Client')+flechaDeOrden(sortBy,sortDir,'cliente')}</th>
                  <th onClick={()=>tocarCabecera('forma')} title={tituloDeOrden} style={{...thBase,...thOrden,minWidth:112}}>{(es?'Forma':'Form')+flechaDeOrden(sortBy,sortDir,'forma')}</th>
                  <th onClick={()=>tocarCabecera('metodo')} title={tituloDeOrden} style={{...thBase,...thOrden,minWidth:170}}>{(es?'Método':'Method')+flechaDeOrden(sortBy,sortDir,'metodo')}</th>
                  <th onClick={()=>tocarCabecera('nota')} title={tituloDeOrden} style={{...thBase,...thOrden,minWidth:64,textAlign:'center'}}>{(es?'Nota':'Note')+flechaDeOrden(sortBy,sortDir,'nota')}</th>
                  <th onClick={()=>tocarCabecera('monto')} title={tituloDeOrden} style={{...thBase,...thOrden,minWidth:92,textAlign:'right'}}>{(es?'Monto':'Amount')+flechaDeOrden(sortBy,sortDir,'monto')}</th>
                  <th onClick={()=>tocarCabecera('estado')} title={tituloDeOrden} style={{...thBase,...thOrden,minWidth:150}}>{(es?'Estado':'Status')+flechaDeOrden(sortBy,sortDir,'estado')}</th>
                  {customColumns.map((col)=>{ const k=claveDeColumnaPropia(col.id); return (
                    <th key={col.id} onClick={()=>tocarCabecera(k)} title={tituloDeOrden} style={{...thBase,...thOrden,minWidth:210,whiteSpace:'normal'}}>{col.name+flechaDeOrden(sortBy,sortDir,k)}</th>
                  ); })}
                </tr>
                {/* FILA DE FILTROS, DEBAJO DE CADA CABECERA (Lissette
                    2026-09-01: los queria AQUI, no solo en la barra de arriba).
                    Estilo Excel: cada columna se filtra desde su propia
                    columna, que es donde una la busca.

                    COMPARTEN EL MISMO ESTADO que los controles de la barra —no
                    son filtros nuevos—, asi que mover uno mueve el otro. Dos
                    estados para el mismo filtro se separarian y la agente veria
                    la barra diciendo una cosa y la tabla otra (E-138).

                    Va `top: 34` porque la cabecera de arriba es sticky: sin
                    eso, al bajar por la tabla esta fila se le montaria encima. */}
                <tr>
                  <th style={{...thFiltro,left:0,zIndex:3}}>
                    <input value={search} onChange={(e)=>setSearch(e.target.value)} style={inpFiltro}
                      placeholder={es?'Buscar cliente…':'Search client…'} />
                  </th>
                  <th style={thFiltro}>
                    <select value={formaFilter} onChange={(e)=>setFormaFilter(e.target.value)} style={inpFiltro}
                      title={es?'Filtrar por forma de pago':'Filter by payment form'}>
                      <option value="all">{es?'Todas':'All'}</option>
                      {[...FORMAS_DE_PAGO, SIN_DEFINIR].map((v)=>{
                        const n = baseRows.filter((r)=>pasaFiltroDeForma(r, v)).length;
                        return n>0 ? <option key={v} value={v}>{nombreDeForma(v, es?'es':'en')} ({n})</option> : null;
                      })}
                    </select>
                  </th>
                  <th style={thFiltro}>
                    <select value={metodoFilter} onChange={(e)=>setMetodoFilter(e.target.value)} style={inpFiltro}
                      title={es?'Filtrar por método de pago':'Filter by payment method'}>
                      <option value="all">{es?'Todos':'All'}</option>
                      {[...METODOS_DE_PAGO, SIN_DEFINIR].map((v)=>{
                        const n = baseRows.filter((r)=>pasaFiltroDeMetodo(r, v)).length;
                        return n>0 ? <option key={v} value={v}>{nombreDeMetodo(v, es?'es':'en')} ({n})</option> : null;
                      })}
                    </select>
                  </th>
                  <th style={thFiltro}>
                    <select value={notaFilter} onChange={(e)=>setNotaFilter(e.target.value)} style={inpFiltro}
                      title={es?'Filtrar por si la fila tiene notas':'Filter by whether the row has notes'}>
                      <option value="all">{es?'Todas':'All'}</option>
                      <option value="con">{(es?'Con':'With')+` (${baseRows.filter((r)=>pasaFiltroDeNota(r,'con')).length})`}</option>
                      <option value="sin">{(es?'Sin':'No')+` (${baseRows.filter((r)=>pasaFiltroDeNota(r,'sin')).length})`}</option>
                    </select>
                  </th>
                  <th style={thFiltro}>
                    {/* Monto: dos casillas, min y max — el mismo par que ya
                        estaba en la barra. */}
                    <div style={{display:'flex',gap:3}}>
                      <input type="number" inputMode="decimal" min="0" step="0.01" value={amountMin}
                        onChange={(e)=>setAmountMin(e.target.value)} style={{...inpFiltro,minWidth:0}}
                        placeholder={es?'mín':'min'} title={es?'Monto mínimo':'Minimum amount'} />
                      <input type="number" inputMode="decimal" min="0" step="0.01" value={amountMax}
                        onChange={(e)=>setAmountMax(e.target.value)} style={{...inpFiltro,minWidth:0}}
                        placeholder={es?'máx':'max'} title={es?'Monto máximo':'Maximum amount'} />
                    </div>
                  </th>
                  <th style={thFiltro}>
                    <select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} style={inpFiltro}
                      title={es?'Filtrar por estado del pago':'Filter by payment status'}>
                      {/* Las MISMAS opciones que el selector de la barra, con
                          los mismos contadores: dos listas distintas para el
                          mismo filtro se separarian (E-138). */}
                      <option value="all">{es?'Todos':'All'}</option>
                      <option value="paid">{(es?'✅ Pagaron':'✅ Paid')+` (${cntPaid})`}</option>
                      <option value="unpaid">{(es?'❌ No pagaron':'❌ Unpaid')+` (${cntUnpaid})`}</option>
                      <option value="pending">{(es?'◻ Sin revisar':'◻ Not reviewed')+` (${cntPending})`}</option>
                      {extraStatuses.map((st)=><option key={st.id} value={st.id}>● {st.label} ({cntFor(st.id)})</option>)}
                    </select>
                  </th>
                  {customColumns.map((col)=>(
                    <th key={col.id} style={thFiltro}>
                      {/* Cada columna propia filtra la SUYA: elegirla en el
                          desplegable de la barra dejaba de hacer falta en
                          cuanto el filtro vive en su propia columna. */}
                      {tipoDeColumna(col)==='check' ? (
                        // Una columna de marcar no se busca escribiendo: lo que
                        // se guarda es un "1" que nadie sabe que hay que teclear.
                        // Se filtra por marcada / sin marcar, que es lo unico que
                        // puede valer.
                        <select
                          value={colFilter.id===col.id ? colFilter.texto : ''}
                          onChange={(e)=>setColFilter({ id: col.id, texto: e.target.value })}
                          style={inpFiltro}
                          title={es?'Filtra por marcadas o sin marcar.':'Filter by ticked or unticked.'}>
                          <option value="">{es?'Todas':'All'}</option>
                          <option value={MARCA_SI}>{es?'☑ Marcadas':'☑ Ticked'}</option>
                          <option value={SIN_DEFINIR}>{es?'☐ Sin marcar':'☐ Unticked'}</option>
                        </select>
                      ) : (
                        <input
                          value={colFilter.id===col.id ? colFilter.texto : ''}
                          onChange={(e)=>setColFilter({ id: col.id, texto: e.target.value })}
                          style={inpFiltro}
                          placeholder={es?'contiene… / none':'contains… / none'}
                          title={es?'Busca dentro de esta columna. Escribe none para ver las vacías.':'Searches inside this column. Type none to see the empty ones.'} />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((r,ri)=>{
                  const pt = getPolicyType(r.type);
                  const paid = !!r.payment?.paid;
                  const reviewed = !!r.payment?.markedByUserId;
                  const cancelled = isCancelledRow(r);
                  const c = clientOf(r);
                  const isSel = selected.has(r.id);
                  const ml = payMethodLabel(r);
                  const fm = payTypeMeta(r);
                  const st = statusOf(r); const meta = statusMetaFor(st); const reviewedNow = st!=='pending';
                  const n = r.payment?.notesCount || 0;
                  // Fondo OPACO por fila (raya alterna + estado). La celda Cliente
                  // sticky lo reusa para tapar lo que se desplaza por debajo.
                  // Usa variables de tema para que en MODO CLARO las filas (canceladas,
                  // seleccionadas, impares) NO queden oscuras + texto ilegible (Lissette 07-08).
                  const rowBg = isSel ? 'var(--c-cyan-dim,rgba(0,212,255,.12))' : (cancelled ? 'var(--c-surface3,#141b26)' : (ri%2===0 ? 'var(--c-surface,#0d1f33)' : 'var(--c-surface2,#0b1a2c)'));
                  const accent = paid ? '3px solid rgba(16,185,129,.55)' : (isSel ? '3px solid #3b82f6' : '3px solid transparent');
                  return <tr key={r.id} style={{background:rowBg,borderBottom:'1px solid var(--c-border,#1a3550)'}}>
                    {/* Cliente — columna CONGELADA (sticky) a la izquierda */}
                    <td style={{...tdBase,position:'sticky',left:0,zIndex:1,background:rowBg,minWidth:250,borderLeft:accent,boxShadow:'6px 0 8px -6px rgba(0,0,0,.5)'}}>
                      <div style={{display:'flex',alignItems:'flex-start',gap:8}}>
                        <input type="checkbox" checked={isSel} onChange={()=>toggleOne(r.id)}
                          title={es?'Seleccionar':'Select'} style={{width:16,height:16,flexShrink:0,marginTop:2,cursor:'pointer',accentColor:'#3b82f6'}}/>
                        <span style={{fontSize:'1.1rem',flexShrink:0,lineHeight:1.1}}>{pt.icon}</span>
                        <div style={{minWidth:0}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                            <NombreConEnlace c={c} onClientClick={onClientClick} cancelled={cancelled} es={es}>{nameOf(r)}</NombreConEnlace>
                            <span style={{fontSize:'.68rem',color:'var(--c-text3,#45688a)'}}>{es?pt.labelEs:pt.label}</span>
                            {cancelled && (
                              <span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'.08rem .4rem',borderRadius:100,background:'rgba(239,68,68,.12)',border:'1px solid rgba(239,68,68,.4)',color:'#ff6b8a',fontSize:'.6rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.03em'}}>
                                🚫 {es?'Cancelada':'Cancelled'}
                              </span>
                            )}
                          </div>
                          <div style={{fontSize:'.68rem',color:'var(--c-text2,#7da0c4)',marginTop:1}}>
                            {r.carrier || '—'}{r.policyNumber?` · #${r.policyNumber}`:''}
                          </div>
                          {(()=>{ const covered=insuredNamesList(r,c); return covered.length>0 && (
                            <div style={{fontSize:'.64rem',color:'var(--c-text2,#7da0c4)',marginTop:2,display:'flex',alignItems:'flex-start',gap:4,lineHeight:1.3}}>
                              <span style={{flexShrink:0}}>🛡️</span>
                              <span><span style={{fontWeight:700}}>{es?'Asegurados:':'Insured:'}</span> {covered.join(', ')}</span>
                            </div>
                          ); })()}
                          {(r.payment?.history && r.payment.history.length > 0) ? (
                            <div style={{display:'flex',flexDirection:'column',gap:1,marginTop:3}}>
                              {r.payment.history.map((h, i) => {
                                const hMeta = h.statusLabel ? statusMetaFor(h.statusLabel) : null;
                                const hLabel = hMeta ? `● ${hMeta.label}` : (h.paid?(es?'Pagó':'Paid'):(es?'No pagó':'Unpaid'));
                                const hColor = hMeta ? hMeta.color : (h.paid?'#10b981':'#f59e0b');
                                return (
                                <div key={i} style={{fontSize:'.66rem',color: hColor,display:'flex',alignItems:'flex-start',gap:4}}>
                                  <Check size={11} style={{flexShrink:0,marginTop:2}}/>
                                  <span>{hLabel+(h.markedByName?(es?' · por '+h.markedByName:' · by '+h.markedByName):'')}{h.markedAt?` · ${new Date(h.markedAt).toLocaleString('en-US',{month:'short',day:'numeric',year:'numeric',hour:'numeric',minute:'2-digit'})}`:''}</span>
                                </div>
                                );
                              })}
                            </div>
                          ) : (reviewed && r.payment?.markedByName && (
                            <div style={{fontSize:'.66rem',color: paid?'#10b981':'#f59e0b',marginTop:3,display:'flex',alignItems:'flex-start',gap:4}}>
                              <Check size={11} style={{flexShrink:0,marginTop:2}}/>
                              <span>{(paid?(es?'Pagó · ':'Paid · '):(es?'No pagó · ':'Unpaid · '))+(es?'por ':'by ')+r.payment.markedByName}{r.payment.markedAt?` · ${new Date(r.payment.markedAt).toLocaleString('en-US',{month:'short',day:'numeric',year:'numeric',hour:'numeric',minute:'2-digit'})}`:''}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                    {/* Forma de pago (de la ficha) */}
                    <td style={tdBase} title={es?'Forma de pago (de la ficha de la póliza)':'Payment form (from the policy record)'}>
                      {fm ? (
                        <span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'.1rem .45rem',borderRadius:100,background:fm.bg,border:`1px solid ${fm.border}`,color:fm.color,fontSize:'.62rem',fontWeight:800,whiteSpace:'nowrap'}}>{fm.label}</span>
                      ) : <span style={{color:'var(--c-text3,#45688a)',fontSize:'.72rem'}}>—</span>}
                    </td>
                    {/* Método de pago (de la ficha) */}
                    <td style={{...tdBase,fontSize:'.72rem',fontWeight:700,color: ml?'var(--c-text,#c8dff0)':'var(--c-text3,#45688a)'}} title={ml||''}>{ml||'—'}</td>
                    {/* Nota */}
                    <td style={{...tdBase,textAlign:'center'}}>
                      <button onClick={()=>openThread(r)}
                        title={n>0 ? (r.payment?.lastNoteText || (es?`${n} nota(s)`:`${n} note(s)`)) : (es?'Agregar nota de este mes':'Add a note for this month')}
                        style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:4,
                          minWidth:52,padding:'.3rem .5rem',borderRadius:100,cursor:'pointer',fontFamily:'inherit',fontSize:'.74rem',fontWeight:800,
                          background:n>0?'rgba(0,212,255,.1)':'none',
                          border:n>0?'1px solid rgba(0,212,255,.45)':'1px dashed var(--c-border,#1a3550)',
                          color:n>0?'var(--c-cyan,#00d4ff)':'var(--c-text3,#45688a)'}}>
                        💬{n>0?` ${n}`:' +'}
                      </button>
                    </td>
                    {/* Monto */}
                    <td style={{...tdBase,textAlign:'right',whiteSpace:'nowrap'}}>
                      <div style={{fontSize:'.85rem',fontWeight:800,color: paid?'#10b981':'var(--c-text,#eef6ff)'}}>{r.monthlyPremium ? fmtMoney(r.monthlyPremium) : '—'}</div>
                      {r.monthlyPremium ? <div style={{fontSize:'.6rem',color:'var(--c-text3,#45688a)'}}>{es?'/mes':'/mo'}</div> : null}
                    </td>
                    {/* Estado (select + recordatorio) */}
                    <td style={tdBase}>
                      <div style={{display:'flex',flexDirection:'column',gap:4,alignItems:'stretch'}}>
                        <select value={st==='pending'?'':st} onChange={(e)=>{ if(e.target.value==='') return; setRowStatus(r, e.target.value); }} disabled={savingId===r.id}
                          title={es?'Estado de pago':'Payment status'}
                          style={{padding:'.4rem .5rem',borderRadius:8,width:'100%',fontSize:'.74rem',fontWeight:800,
                            cursor:savingId===r.id?'wait':'pointer',opacity:savingId===r.id?.6:1,appearance:'auto',boxSizing:'border-box',
                            background: !reviewedNow?'var(--c-bg2,#0a1828)':`${meta.color}22`,
                            border:`1px solid ${!reviewedNow?'var(--c-border,#1a3550)':`${meta.color}66`}`,
                            color: !reviewedNow?'var(--c-text2,#7da0c4)':meta.color}}>
                          {!reviewedNow && <option value="">{es?'— Seleccionar —':'— Select —'}</option>}
                          <option value="unpaid">{es?'❌ No pagó':'❌ Unpaid'}</option>
                          <option value="paid">{es?'✅ Pagó':'✅ Paid'}</option>
                          {extraStatuses.map((s)=><option key={s.id} value={s.id}>● {s.label}</option>)}
                        </select>
                        {/* Recordatorio SOLO para "No pagó" (st==='unpaid'): no para
                            gracia ni estatus personalizados, que tienen statusLabel y
                            por tanto st!=='unpaid' aunque paid sea false (Lissette 07-14). */}
                        {st==='unpaid' && !cancelled && (
                          <button onClick={()=>setRemindRow(r)} title={es?'Enviar recordatorio':'Send reminder'}
                            style={{display:'flex',alignItems:'center',justifyContent:'center',gap:4,padding:'.3rem .5rem',borderRadius:8,
                              background:'rgba(245,158,11,.12)',border:'1px solid rgba(245,158,11,.35)',color:'#f59e0b',fontSize:'.68rem',fontWeight:700,cursor:'pointer'}}>
                            <Bell size={11}/>{es?'Recordatorio':'Reminder'}
                          </button>
                        )}
                      </div>
                    </td>
                    {/* Columnas personalizadas — celda tipo Excel: TEXTAREA que
                        envuelve el texto y crece de alto (texto largo completo). */}
                    {customColumns.map((col)=>{ const tipoCol=tipoDeColumna(col); if(tipoCol!=='text') return (
                      // Lista y marcar usan la MISMA casilla que Renovaciones y
                      // Prospecto y Referido (`CeldaPropia`): tres copias del
                      // desplegable que se llena solo es como una gana un arreglo
                      // y las otras dos se quedan atras sin que nada avise.
                      <td key={col.id} style={{...tdBase,padding:'.3rem .4rem'}}>
                        <CeldaPropia
                          valor={(r.customData && r.customData[col.id]) || ''}
                          lang={lang}
                          columna={col}
                          opciones={opcionesPorColumna[col.id]||[]}
                          disabled={savingCellId===r.id+col.id}
                          estilo={tipoCol==='list'?{background:'var(--c-bg2,#0a1828)',border:'1px solid var(--c-border,#1a3550)',borderRadius:7,padding:'.3rem .45rem',color:'var(--c-white,#eef6ff)',fontSize:'.74rem'}:{}}
                          onGuardar={(txt)=>saveCustomCell(r, col.id, txt, { propagar: true })} />
                      </td>
                    ); return (
                      <td key={col.id} style={{...tdBase,padding:'.3rem .4rem'}}>
                        <textarea rows={1} defaultValue={(r.customData && r.customData[col.id]) || ''}
                          ref={(el)=>{ if(el){ el.style.height='auto'; el.style.height=el.scrollHeight+'px'; } }}
                          onInput={(e)=>{ e.target.style.height='auto'; e.target.style.height=e.target.scrollHeight+'px'; }}
                          onBlur={(e)=>{ const v=e.target.value.trim(); const cur=(r.customData && r.customData[col.id])||''; if(v!==cur) saveCustomCell(r, col.id, v); }}
                          onPaste={col.allowPhotos ? (e)=>{ const items=[...(e.clipboardData?.items||[])].filter(it=>it.type&&it.type.startsWith('image/')); if(items.length){ e.preventDefault(); items.forEach(it=>addCellImage(r,col,it.getAsFile())); } } : undefined}
                          disabled={savingCellId===r.id+col.id}
                          placeholder="—"
                          style={{width:'100%',minHeight:'1.9rem',resize:'vertical',overflow:'hidden',lineHeight:1.35,fontFamily:'inherit',
                            background:'var(--c-bg2,#0a1828)',border:'1px solid var(--c-border,#1a3550)',borderRadius:7,padding:'.3rem .45rem',
                            color:'var(--c-white,#eef6ff)',fontSize:'.74rem',outline:'none',boxSizing:'border-box',
                            whiteSpace:'pre-wrap',wordBreak:'break-word',opacity:savingCellId===r.id+col.id?.6:1}}/>
                        {col.allowPhotos && (
                          <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:4}}>
                            {cellImgs(r,col).map((u,i)=>(
                              <span key={i} style={{position:'relative',display:'inline-block'}}>
                                <img src={u} alt="" onClick={()=>setCellLightbox({src:u})} style={{width:38,height:38,objectFit:'cover',borderRadius:5,border:'1px solid var(--c-border,#1a3550)',cursor:'zoom-in'}}/>
                                <button type="button" onClick={()=>removeCellImage(r,col,i)} title={es?'Quitar':'Remove'} style={{position:'absolute',top:-5,right:-5,background:'var(--c-red,#ff3d6b)',border:'none',color:'#fff',borderRadius:'50%',width:14,height:14,fontSize:9,lineHeight:'14px',cursor:'pointer',padding:0}}>×</button>
                              </span>
                            ))}
                            <label title={es?'Agregar foto (o pega con Ctrl+V en el texto)':'Add photo (or paste with Ctrl+V in the text)'} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:38,height:38,borderRadius:5,border:'1px dashed var(--c-cyan,#00d4ff)',color:'var(--c-cyan,#00d4ff)',cursor:'pointer',fontSize:'.85rem'}}>
                              {cellImgBusy===r.id+col.id ? '…' : '📷'}
                              <input type="file" accept="image/*" style={{display:'none'}} onChange={(e)=>{const f=e.target.files&&e.target.files[0]; e.target.value=''; if(f) addCellImage(r,col,f);}}/>
                            </label>
                          </div>
                        )}
                      </td>
                    ); })}
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <CajaConScroll>{paged.map((r)=>{
            const pt = getPolicyType(r.type);
            const cancelled = isCancelledRow(r);
            const c = clientOf(r);
            const fm = payTypeMeta(r);
            return <div key={r.id} style={{display:'flex',alignItems:'center',gap:12,padding:'.6rem .75rem',borderRadius:10,
              background: cancelled?'rgba(148,163,184,.06)':'var(--c-surface,#0d1f33)', opacity:cancelled?.7:1,
              border:`1px solid ${cancelled?'rgba(148,163,184,.35)':'var(--c-border,#1a3550)'}`}}>
              <span style={{fontSize:'1.1rem',width:24,textAlign:'center',flexShrink:0}}>{pt.icon}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                  <NombreConEnlace c={c} onClientClick={onClientClick} cancelled={cancelled} es={es}>{nameOf(r)}</NombreConEnlace>
                  <span style={{fontSize:'.7rem',color:'var(--c-text3,#45688a)'}}>{es?pt.labelEs:pt.label}</span>
                  {fm && (<span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'.08rem .45rem',borderRadius:100,background:fm.bg,border:`1px solid ${fm.border}`,color:fm.color,fontSize:'.62rem',fontWeight:800}}>{fm.label}</span>)}
                  {cancelled && (<span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'.08rem .4rem',borderRadius:100,background:'rgba(239,68,68,.12)',border:'1px solid rgba(239,68,68,.4)',color:'#ff6b8a',fontSize:'.62rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.03em'}}>🚫 {es?'Cancelada':'Cancelled'}</span>)}
                </div>
                <div style={{fontSize:'.7rem',color:'var(--c-text2,#7da0c4)',marginTop:2}}>
                  {r.carrier || '—'}{r.policyNumber?` · #${r.policyNumber}`:''}
                </div>
                {(()=>{ const covered=insuredNamesList(r,c); return covered.length>0 && (
                  <div style={{fontSize:'.65rem',color:'var(--c-text2,#7da0c4)',marginTop:2,display:'flex',alignItems:'flex-start',gap:4,lineHeight:1.3}}>
                    <span style={{flexShrink:0}}>🛡️</span>
                    <span><span style={{fontWeight:700}}>{es?'Asegurados:':'Insured:'}</span> {covered.join(', ')}</span>
                  </div>
                ); })()}
              </div>
              <div style={{flexShrink:0,textAlign:'right',minWidth:74}}>
                <div style={{fontSize:'.85rem',fontWeight:800,color:'var(--c-text,#eef6ff)'}}>{r.monthlyPremium ? fmtMoney(r.monthlyPremium) : '—'}</div>
                {r.monthlyPremium ? <div style={{fontSize:'.62rem',color:'var(--c-text3,#45688a)'}}>{es?'/mes':'/mo'}</div> : null}
              </div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap',justifyContent:'flex-end',maxWidth:560}}>
                {rangeMonths.map((m)=>{
                  const st = statusOfPay(r.payByPeriod?.[m]);
                  const meta = statusMetaFor(st);
                  const reviewedNow = st!=='pending';
                  const busy = savingId===r.id+m;
                  const [yy,mm] = m.split('-');
                  const abbr = new Date(Number(yy), Number(mm)-1, 1).toLocaleDateString(es?'es':'en-US',{month:'short'}) + ' ' + String(yy).slice(2);
                  return (
                    <div key={m} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                      <span style={{fontSize:'.58rem',fontWeight:800,color:'var(--c-text3,#45688a)',textTransform:'capitalize',fontFamily:'var(--font-mono)'}}>{abbr}</span>
                      <select value={st==='pending'?'':st} onChange={(e)=>{ if(e.target.value==='') return; setRangeStatus(r, m, e.target.value); }} disabled={busy}
                        title={`${fmtMonth(m)} — ${meta.label}`}
                        style={{padding:'.25rem .3rem',borderRadius:7,width:86,fontSize:'.66rem',fontWeight:800,
                          cursor:busy?'wait':'pointer',opacity:busy?.6:1,appearance:'auto',
                          background: !reviewedNow?'var(--c-bg2,#0a1828)':`${meta.color}22`,
                          border:`1px solid ${!reviewedNow?'var(--c-border,#1a3550)':`${meta.color}66`}`,
                          color: !reviewedNow?'var(--c-text2,#7da0c4)':meta.color}}>
                        {!reviewedNow && <option value="">◻ —</option>}
                        <option value="unpaid">{es?'❌ No pagó':'❌ Unpaid'}</option>
                        <option value="paid">{es?'✅ Pagó':'✅ Paid'}</option>
                        {extraStatuses.map((s)=><option key={s.id} value={s.id}>● {s.label}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>;
          })}</CajaConScroll>
        )}
        {/* Pagination — 15 per page */}
        {filtered.length > 15 && (
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8,marginTop:8}}>
            <span style={{fontSize:'.72rem',color:'var(--c-text3,#45688a)'}}>
              {es?`Mostrando ${(safePage-1)*pageSize+1}–${Math.min(safePage*pageSize,filtered.length)} de ${filtered.length}`
                 :`Showing ${(safePage-1)*pageSize+1}–${Math.min(safePage*pageSize,filtered.length)} of ${filtered.length}`}
            </span>
            <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'.72rem',color:'var(--c-text2,#7da0c4)'}}>
                {es?'Por página:':'Per page:'}
                <select value={pageSize} onChange={(e)=>{setPageSize(parseInt(e.target.value,10));setPage(1);}}
                  style={{padding:'2px 6px',borderRadius:6,border:'1px solid var(--c-border,#1a3550)',background:'var(--c-surface,#0d1c2e)',color:'var(--c-text,#c8dff0)',fontFamily:'inherit',cursor:'pointer'}}>
                  {[15,25,50,100,200,500].map((n)=><option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              {totalPages>1 && <div style={{display:'flex',alignItems:'center',gap:6}}>
                <button onClick={()=>setPage((p)=>Math.max(1,p-1))} disabled={safePage<=1} className="dkt-tab"
                  style={{opacity:safePage<=1?.4:1,cursor:safePage<=1?'not-allowed':'pointer'}}>‹ {es?'Anterior':'Prev'}</button>
                <span style={{fontSize:'.74rem',color:'var(--c-text2,#7da0c4)',fontFamily:'var(--font-mono)',minWidth:54,textAlign:'center'}}>{safePage} / {totalPages}</span>
                <button onClick={()=>setPage((p)=>Math.min(totalPages,p+1))} disabled={safePage>=totalPages} className="dkt-tab"
                  style={{opacity:safePage>=totalPages?.4:1,cursor:safePage>=totalPages?'not-allowed':'pointer'}}>{es?'Siguiente':'Next'} ›</button>
              </div>}
            </div>
          </div>
        )}
        {/* Cronometro de la carga -- Lissette 2026-09-02, queja 39.
            Esta linea existe para PODER DECIDIR: dice cuantos milisegundos se
            fueron en el servidor, en el viaje por internet, en convertir las
            filas y en pintarlas. Con eso se sabe si la reforma va en el servidor
            o en la pantalla, en vez de suponerlo -- que ya salio mal una vez.
            Solo numeros, ni un dato de ningun cliente. Se quita cuando la
            reforma este hecha. */}
        {medicion && (
          <div style={{marginTop:10,paddingTop:8,borderTop:'1px dashed var(--c-border,#1a3550)',fontSize:'.68rem',color:'var(--c-text3,#45688a)',fontFamily:'var(--font-mono)',lineHeight:1.6}}>
            <div style={{fontWeight:700,marginBottom:2}}>
              {es?'Tiempo de carga':'Load time'}: {(medicion.msTotal/1000).toFixed(1)} s · {medicion.filas} {es?'pólizas':'policies'}
            </div>
            <div>
              {es?'servidor':'server'} {medicion.msServidor ?? '?'} ms
              {' · '}{es?'viaje':'transfer'} {medicion.msViaje ?? '?'} ms
              {' · '}{es?'preparar filas':'mapping'} {medicion.msMapeo ?? '?'} ms
              {' · '}{es?'pintar':'painting'} {medicion.msPintado} ms
              {medicion.ligero ? (' · ' + (es ? 'ligero' : 'light')) : ''}
            </div>
            {/* Los intentos son lo que hace que el "viaje" deje de ser ambiguo:
                apiFetch espera hasta 88 s reintentando los 502/503 de un
                contenedor que arranca, y esas esperas caen DENTRO del viaje.
                Un intento = red de verdad. Varios = habia un despliegue en
                marcha. (Lissette, 2026-09-02: 290 ms de servidor y 36.812 de
                viaje.) Solo sale cuando hubo mas de uno, para no ensuciar la
                linea el 99 % de las veces. */}
            {medicion.intentos > 1 && (
              <div style={{color:'#f59e0b',fontWeight:700}}>
                {es
                  ? `El servidor no contestó a la primera: ${medicion.intentos} intentos. Casi todo ese "viaje" son las esperas entre intento e intento, no tu conexión — suele significar que había una actualización en marcha.`
                  : `The server didn't answer on the first try: ${medicion.intentos} attempts. Most of that "transfer" is the waiting between retries, not your connection — it usually means a deploy was running.`}
              </div>
            )}
            {medicion.detalle && (
              <div style={{opacity:.75}}>
                {es?'dentro del servidor':'inside server'} — {es?'pólizas':'policies'} {medicion.detalle.polizas} ms
                {' · '}{es?'eventos':'events'} {medicion.detalle.eventos} ms
                {' · '}{es?'notas':'notes'} {medicion.detalle.notas} ms
                {' · '}{es?'usuarios':'users'} {medicion.detalle.usuarios} ms
                {' · '}{es?'firmas de fotos':'photo signing'} {medicion.detalle.firmas} ms
              </div>
            )}
          </div>
        )}
      </div>
    )}

    {/* Note-thread modal: every message for (policy, month) with its images,
        plus a composer that accepts several pasted/picked images per message. */}
    {noteThreadRow && (
      <Modal xl onClose={closeThread}
        title={`🗒️ ${es?'Notas de pago':'Payment notes'} — ${nameOf(noteThreadRow)} · ${es?'todos los meses':'all months'}`}>
        <div ref={threadBodyRef} style={{maxHeight:'64vh',minHeight:'40vh',overflowY:'auto',display:'flex',flexDirection:'column',gap:8,paddingRight:2}}>
          {threadLoading && (
            <div style={{padding:'1.2rem',textAlign:'center',color:'var(--c-text3,#45688a)',fontSize:'.78rem'}}>{es?'Cargando…':'Loading…'}</div>
          )}
          {!threadLoading && thread.length === 0 && (
            <div style={{padding:'1.2rem',textAlign:'center',color:'var(--c-text3,#45688a)',fontSize:'.78rem'}}>
              {es?'Sin notas todavía — escribe la primera abajo.':'No notes yet — write the first one below.'}
            </div>
          )}
          {!threadLoading && [...thread].sort((a,b)=>((b.isPinned?1:0)-(a.isPinned?1:0))||String(b.createdAt||'').localeCompare(String(a.createdAt||''))).map((m)=>(
            <div key={m.id} style={{background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',borderRadius:10,padding:'.5rem .65rem'}}>
              <div style={{display:'flex',alignItems:'center',gap:6,fontSize:'.64rem',color:'var(--c-text3,#45688a)',fontWeight:700,flexWrap:'wrap'}}>
                {/* Quién · cuándo · a qué hora se puso la nota */}
                <span style={{color:'var(--c-cyan,#00d4ff)'}}>{m.isPinned && <span title={es?'Fijada':'Pinned'} style={{marginRight:3}}>📌</span>}{m.legacy ? (es?'Nota anterior':'Earlier note') : (m.authorName || '—')}</span>
                {m.period && <span title={es?'Mes de la nota':'Note month'} style={{background:'var(--c-surface3,#162d44)',borderRadius:100,padding:'1px 7px',color:'var(--c-text2,#7aa3c0)'}}>{fmtMonth(m.period)}</span>}
                {/* Se ve CUÁLES están reflejadas: sin la marca, "la reflejé o no"
                    solo se sabría abriendo la ficha del cliente a mirar. */}
                {m.espejo && <span title={es?'También está en las notas del cliente. Si editas o borras ésta, aquélla cambia igual.':'Also on the client notes. Editing or deleting this one changes that one too.'}
                  style={{background:'rgba(0,212,255,.12)',border:'1px solid rgba(0,212,255,.3)',borderRadius:100,padding:'1px 7px',color:'var(--c-cyan,#00d4ff)'}}>🔁 {es?'en la ficha':'on the record'}</span>}
                {m.createdAt && <span>· {new Date(m.createdAt).toLocaleString('en-US',{month:'short',day:'numeric',year:'numeric',hour:'numeric',minute:'2-digit'})}</span>}
                {editMsgId !== m.id && (
                  <span style={{marginLeft:'auto',display:'inline-flex',gap:2,alignItems:'center'}}>
                    {!m.legacy && (
                      <button onClick={()=>togglePayNotePin(m)} title={m.isPinned?(es?'Desfijar':'Unpin'):(es?'Fijar':'Pin')}
                        style={{border:'none',background:'none',color:m.isPinned?'var(--c-cyan,#00d4ff)':'var(--c-text3,#45688a)',cursor:'pointer',padding:'0 .2rem',lineHeight:0,display:'inline-flex'}}><Pin size={12}/></button>
                    )}
                    {!m.legacy && m.authorUserId && m.authorUserId === currentUser?.id && (
                      <>
                        <button onClick={()=>startEditMsg(m)} title={es?'Editar esta nota':'Edit this note'}
                          style={{border:'none',background:'none',color:'var(--c-text3,#45688a)',cursor:'pointer',fontSize:'.7rem',padding:'0 .2rem'}}>✏️</button>
                        <button onClick={()=>deleteThreadMsg(m)} title={es?'Borrar esta nota':'Delete this note'}
                          style={{border:'none',background:'none',color:'var(--c-text3,#45688a)',cursor:'pointer',fontSize:'.7rem',padding:'0 .2rem'}}>🗑️</button>
                      </>
                    )}
                  </span>
                )}
              </div>
              {editMsgId === m.id ? (
                <div style={{marginTop:6}} onPaste={onEditMsgPaste}>
                  <NoteFormatButtons taRef={editMsgTaRef} value={editMsgText} onChange={setEditMsgText} lang={lang} compact style={{marginBottom:6}} />
                  <RichNoteBox boxRef={editMsgTaRef} value={editMsgText} onChange={setEditMsgText} lang={lang} autoFocus rows={3}
                    placeholder={es?'Edita la nota… pega fotos con Ctrl/⌘+V':'Edit the note… paste photos with Ctrl/⌘+V'}
                    style={{width:'100%',boxSizing:'border-box',resize:'vertical',fontFamily:'inherit',fontSize:'.78rem',lineHeight:1.4,
                      background:'var(--c-bg2,#0a1828)',border:'1px solid var(--c-border,#1a3550)',borderRadius:8,padding:'.45rem .55rem',color:'var(--c-white,#eef6ff)',outline:'none'}}/>
                  {editMsgImgs.length > 0 && (
                    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:6}}>
                      {editMsgImgs.map((u,i)=>(
                        <div key={i} style={{position:'relative'}}>
                          {isNotePdf(u)
                            ? <a href={u} target="_blank" rel="noopener noreferrer" title="PDF"
                                style={{height:56,width:56,borderRadius:6,border:'1px solid var(--c-border,#1a3550)',background:'var(--c-surface2,#112538)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--c-text2,#7aa3c0)'}}>
                                <FileText size={20}/>
                              </a>
                            : <img src={u} alt="" style={{height:56,width:56,objectFit:'cover',borderRadius:6,border:'1px solid var(--c-border,#1a3550)'}}/>}
                          <button type="button" onClick={()=>setEditMsgImgs((prev)=>prev.filter((_,j)=>j!==i))} title={es?'Quitar':'Remove'}
                            style={{position:'absolute',top:-6,right:-6,width:18,height:18,borderRadius:'50%',background:'var(--c-red,#ff3d6b)',color:'#fff',border:'none',cursor:'pointer',fontSize:'.7rem',lineHeight:1,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* El espejo, al editar: sale como está la nota y se puede
                      encender o apagar aquí mismo (Lissette 2026-09-16). Apagarlo
                      borra la nota de la ficha, no ésta. */}
                  <label style={{display:'flex',alignItems:'center',gap:6,marginTop:7,cursor:noteThreadRow.clientId?'pointer':'not-allowed',fontSize:'.72rem',color:'var(--c-text2,#7aa3c0)'}}>
                    <input type="checkbox" checked={editEspejo && !!noteThreadRow.clientId} disabled={!noteThreadRow.clientId}
                      onChange={(e)=>setEditEspejo(e.target.checked)} style={{cursor:'inherit'}}/>
                    <span>{es?'Reflejada en las notas del cliente':'Mirrored into the client notes'}</span>
                  </label>
                  <div style={{display:'flex',gap:6,justifyContent:'space-between',alignItems:'center',marginTop:6}}>
                    <button type="button" onClick={()=>editMsgImgFileRef.current && editMsgImgFileRef.current.click()} disabled={noteImgBusy}
                      style={{display:'inline-flex',alignItems:'center',gap:4,padding:'.3rem .6rem',borderRadius:8,background:'var(--c-surface3,#162d44)',border:'1px solid var(--c-border,#1a3550)',color:'var(--c-text2,#7aa3c0)',fontSize:'.72rem',fontWeight:700,cursor:noteImgBusy?'wait':'pointer'}}>
                      <ImageIcon size={12}/> {noteImgBusy?(es?'Subiendo…':'Uploading…'):(es?'Foto/PDF':'Photo/PDF')}
                    </button>
                    <input ref={editMsgImgFileRef} type="file" accept="image/*,application/pdf,.pdf" multiple style={{display:'none'}} onChange={(e)=>{[...e.target.files].forEach((f)=>addNoteImage(f,setEditMsgImgs)); e.target.value='';}}/>
                    <div style={{display:'flex',gap:6}}>
                      <button onClick={cancelEditMsg} disabled={editMsgSaving}
                        style={{padding:'.3rem .7rem',borderRadius:8,background:'var(--c-surface3,#162d44)',border:'1px solid var(--c-border,#1a3550)',color:'var(--c-text2,#7aa3c0)',fontSize:'.72rem',fontWeight:700,cursor:'pointer'}}>{es?'Cancelar':'Cancel'}</button>
                      <button onClick={()=>saveEditMsg(m)} disabled={editMsgSaving}
                        style={{padding:'.3rem .8rem',borderRadius:8,background:'var(--c-cyan,#00d4ff)',border:'none',color:'#0a1628',fontSize:'.72rem',fontWeight:800,cursor:editMsgSaving?'wait':'pointer',opacity:editMsgSaving?.6:1}}>{editMsgSaving?(es?'Guardando…':'Saving…'):(es?'Guardar':'Save')}</button>
                    </div>
                  </div>
                </div>
              ) : (
                m.text && <div style={{fontSize:'.78rem',color:'var(--c-text,#eef6ff)',marginTop:4}}><RichNote text={m.text} /></div>
              )}
              {editMsgId !== m.id && (m.imageUrls||[]).length > 0 && (
                <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:6}}>
                  {m.imageUrls.map((u,i)=>(
                    isNotePdf(u)
                      ? <a key={i} href={u} target="_blank" rel="noopener noreferrer" title={es?'Abrir PDF':'Open PDF'}
                          style={{display:'inline-flex',alignItems:'center',gap:6,padding:'.4rem .6rem',borderRadius:8,
                            background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',
                            color:'var(--c-cyan,#00d4ff)',fontSize:'.72rem',fontWeight:700,textDecoration:'none'}}>
                          <FileText size={14}/> {es?`PDF ${i+1}`:`PDF ${i+1}`}
                        </a>
                      : <img key={i} src={u} alt="" onClick={()=>window.open(u,'_blank','noopener')}
                          title={es?'Ver en grande':'View full size'}
                          style={{height:150,maxWidth:260,objectFit:'cover',borderRadius:8,border:'1px solid var(--c-border,#1a3550)',cursor:'zoom-in'}}/>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Draft images waiting to be sent with the next message */}
        {msgImgs.length > 0 && (
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:10}}>
            {msgImgs.map((u,i)=>(
              <span key={i} style={{position:'relative',display:'inline-block'}}>
                <span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'.25rem .5rem',borderRadius:8,
                  background:'rgba(236,72,153,.08)',border:'1px dashed rgba(236,72,153,.45)',color:'#ec4899',fontSize:'.68rem',fontWeight:700}}>
                  {isNotePdf(u) ? '📕' : '🖼️'} {isNotePdf(u) ? `PDF ${i+1}` : (es?`Imagen ${i+1}`:`Image ${i+1}`)}
                  <button onClick={()=>setMsgImgs((prev)=>prev.filter((_,j)=>j!==i))} title={es?'Quitar':'Remove'}
                    style={{border:'none',background:'none',color:'#ef4444',cursor:'pointer',fontWeight:800,fontSize:'.7rem',padding:0}}>✕</button>
                </span>
              </span>
            ))}
          </div>
        )}
        <NoteFormatButtons taRef={msgInputRef} value={msgText} onChange={setMsgText} lang={lang} compact style={{marginTop:10}} />
        {/* ESPEJO EN LAS NOTAS DEL CLIENTE (Lissette 2026-09-15, y el 16: *"pero
            necesito que sea un espejo"*). Opcional y apagado por defecto: se
            decide nota por nota. La letra pequeña dice las dos cosas que pueden
            sorprender —que van enganchadas y que las fotos no viajan— ANTES de
            marcar, no después. */}
        <label style={{display:'flex',alignItems:'flex-start',gap:7,marginTop:8,cursor:'pointer',fontSize:'.73rem',color:'var(--c-text2,#7aa3c0)'}}>
          <input type="checkbox" checked={espejoEnCliente && !!noteThreadRow.clientId} disabled={!noteThreadRow.clientId}
            onChange={(e)=>setEspejoEnCliente(e.target.checked)}
            style={{marginTop:2,cursor:noteThreadRow.clientId?'pointer':'not-allowed'}}/>
          <span>
            <b style={{color:'var(--c-text,#eef6ff)'}}>{es?'Reflejar también en las notas del cliente':'Also mirror into the client notes'}</b>
            <span style={{display:'block',color:'var(--c-text3,#45688a)',fontSize:'.68rem',marginTop:1}}>
              {!noteThreadRow.clientId
                ? (es ? 'Esta fila no tiene cliente en el que reflejarla.' : 'This row has no client to mirror it into.')
                : (es
                  ? `Van enganchadas: si editas o borras esta nota, la de la ficha cambia igual${msgImgs.length?' · las fotos se quedan aquí, la de la ficha dice cuántas son':''}.`
                  : `They stay linked: editing or deleting this note changes the one on the record too${msgImgs.length?' · photos stay here; the mirrored note says how many there are':''}.`)}
            </span>
          </span>
        </label>
        {/* LO DE "TODAS", que es lo que pidio Lissette el 2026-09-16. Son dos
            cosas distintas y por eso son dos controles:

              · "Siempre" decide con que valor NACE la casilla de arriba en cada
                nota nueva, para no tener que marcarla una por una. Se recuerda.
              · "Reflejar todas" es para lo que YA esta escrito en el hilo.

            La casilla individual sigue mandando en la nota que se escribe: si
            se desmarca para una, la preferencia no se apaga. */}
        {!!noteThreadRow.clientId && (
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginTop:6,flexWrap:'wrap'}}>
            <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:'.7rem',color:'var(--c-text3,#45688a)'}}>
              <input type="checkbox" checked={espejoSiempre}
                onChange={(e)=>{ setEspejoSiempre(e.target.checked); setEspejoEnCliente(e.target.checked); }}/>
              {es?'Reflejar siempre, sin tener que marcarlo cada vez':'Always mirror, without ticking it each time'}
            </label>
            <button type="button" onClick={reflejarTodas} disabled={reflejandoTodas || threadLoading}
              title={es?'Reflejar en la ficha las notas de este hilo que aún no lo están':'Mirror the thread notes that are not on the record yet'}
              style={{background:'var(--c-surface3,#162d44)',border:'1px solid var(--c-border,#1a3550)',borderRadius:7,
                padding:'.25rem .6rem',cursor:(reflejandoTodas||threadLoading)?'wait':'pointer',fontSize:'.68rem',
                fontWeight:700,color:'var(--c-cyan,#00d4ff)',fontFamily:'inherit',opacity:(reflejandoTodas||threadLoading)?0.6:1}}>
              {reflejandoTodas
                ? (es?'Reflejando…':'Mirroring…')
                : `🔁 ${es?'Reflejar todas':'Mirror them all'}${porReflejar?` (${porReflejar})`:''}`}
            </button>
          </div>
        )}
        <div style={{display:'flex',alignItems:'center',gap:6,marginTop:6}}>
          <input ref={msgInputRef} value={msgText} maxLength={1000} autoFocus
            onChange={(e)=>setMsgText(e.target.value)}
            onKeyDown={(e)=>{ if(e.key==='Enter' && !e.shiftKey) { e.preventDefault(); sendThreadMsg(); } }}
            onPaste={onNotePaste}
            placeholder={es?'Escribe un mensaje, pega imágenes (Ctrl+V) o adjunta un PDF…':'Type a message, paste images (Ctrl+V) or attach a PDF…'}
            style={{flex:1,minWidth:0,padding:'.45rem .6rem',borderRadius:8,
              background:'var(--c-bg2,#0a1828)',border:'1px solid var(--c-border,#1a3550)',
              color:'var(--c-text,#eef6ff)',fontSize:'.78rem',fontFamily:'inherit'}}/>
          <button onClick={()=>{ if(noteImgFileRef.current) noteImgFileRef.current.click(); }} disabled={noteImgBusy}
            title={es?'Adjuntar imágenes o PDF':'Attach images or PDF'}
            style={{border:'1px solid var(--c-border,#1a3550)',background:'var(--c-surface2,#112538)',borderRadius:8,padding:'.4rem .55rem',cursor:noteImgBusy?'wait':'pointer',fontSize:'.8rem'}}>
            {noteImgBusy?'⏳':'📷'}
          </button>
          <button onClick={sendThreadMsg} disabled={msgSending || noteImgBusy || (!msgText.trim() && msgImgs.length===0)}
            className="dkt-btn-primary"
            style={{padding:'.45rem .9rem',borderRadius:8,fontWeight:800,fontSize:'.78rem',
              opacity:(msgSending||noteImgBusy||(!msgText.trim()&&msgImgs.length===0))?.5:1,
              cursor:(msgSending||noteImgBusy)?'wait':'pointer'}}>
            {msgSending?(es?'Enviando…':'Sending…'):(es?'Enviar':'Send')}
          </button>
        </div>
      </Modal>
    )}
    {remindRow && (
      <ReminderModal row={remindRow} client={clientOf(remindRow)} period={period}
        templates={reminderTpls} onTemplatesChange={loadReminderTpls}
        onClose={()=>setRemindRow(null)} lang={lang}/>
    )}
    {tplManager && (
      <ReminderTemplatesManager templates={reminderTpls} carriers={[...new Set(rows.map((r)=>r.carrier).filter(Boolean))].sort()}
        onChange={loadReminderTpls} onClose={()=>setTplManager(false)} lang={lang}/>
    )}
    {statusMgr && (
      <PaymentStatusManager lang={lang} statuses={paymentStatuses} usage={statusUsage}
        onSaved={(next)=>{ setPaymentStatuses(next); setStatusVer((v)=>v+1); }}
        onClose={()=>setStatusMgr(false)}/>
    )}
    {colMgr && (
      <CustomColumnsManager columns={customColumns} lang={lang} allowPhotoOption
        title={es?'Columnas de Pagos del Mes':'Monthly Payments columns'}
        onClose={()=>setColMgr(false)}
        onSave={persistColumns}/>
    )}
    {cellLightbox && <ImageLightbox src={cellLightbox.src} onClose={()=>setCellLightbox(null)}/>}
    {importOpen && (
      <PaymentImportModal period={period} lang={lang}
        policyOptions={rows.map((r)=>({ id:r.id, label:`${nameOf(r)} · ${r.carrier||'—'}${r.policyNumber?` · #${r.policyNumber}`:''}` }))
          .sort((a,b)=>a.label.localeCompare(b.label))}
        onClose={()=>setImportOpen(false)} onImported={load}/>
    )}
  </div>;
}

// Las etiquetas y su relleno viven en `plantillaDeRecordatorio.js`, junto a la
// elección de plantilla: es código puro y así se prueba sin montar React.
const REMINDER_TOKENS = ETIQUETAS_DE_RECORDATORIO;
const fillReminder = rellenarRecordatorio;

// La eleccion de plantilla vive en `plantillaDeRecordatorio.js`: ahora tiene un
// eje mas —el atraso— y escrita aqui dentro no se podria probar sin montar
// React. Ver alli el porque del orden entre especificidad y atraso.

// Lightweight reminder composer. Loads the saved (type+carrier) template, lets
// the agent edit it (with {tokens}), previews the filled message, and can save
// the edited template back per scope. Sends via the agent's mailbox / WhatsApp
// / SMS.
function ReminderModal({ row, client, period, templates = [], onTemplatesChange, onClose, lang }) {
  const es = lang === 'es';                 // UI chrome language
  // Message language — toggled in the modal before sending (independent of UI).
  const [sendEs, setSendEs] = useState(es);
  const pt = getPolicyType(row.type);
  const name = client ? `${client.firstName} ${client.lastName}`.trim() : (row.clientName || '');
  const firstName = client?.firstName || name.split(' ')[0] || '';
  const company = row.carrier || '';
  const typeLabel = sendEs ? pt.labelEs : pt.label;
  const memberNo = row.policyNumber || '';
  const monthLabel = fmtMonth(period);
  // La prima de la poliza, con el mismo formato que la tarjeta ($1,234.56).
  // Lissette, 2026-09-12: "en el recordatorio de pago, permitir en personalizar
  // el mensaje poder colocar predeterminado la prima de la poliza".
  const premium = (row.monthlyPremium !== null && row.monthlyPremium !== undefined && row.monthlyPremium !== '')
    ? fmtMoneyZero(row.monthlyPremium)
    : '';
  const vars = { firstName, company, typeLabel, monthLabel, memberNo, premium };

  // Built-in default template (with tokens), used when no saved one matches.
  const defaultTpl = plantillaDeFabrica;

  // The editable TEMPLATE (tokens kept). Seeded from the best saved match for
  // the chosen message language; re-seeds when the language toggle flips.
  // EL ATRASO REAL de este recibo, en meses. Es lo que decide con que plantilla
  // ABRE el modal: el primer aviso y el de quien lleva dos meses no se
  // escriben igual (Lissette 2026-09-01).
  const atrasoReal = mesesDeAtraso(period);
  // La etapa elegida. Arranca en la real y la agente puede cambiarla a mano:
  // a veces quiere mandar el mensaje suave aunque el recibo lleve dos meses.
  const [etapa, setEtapa] = useState(atrasoReal);
  const etapas = etapasDisponibles(templates, { tipo: row.type, compania: company, es: sendEs });
  const [tpl, setTpl] = useState(() =>
    elegirPlantilla(templates, { tipo: row.type, compania: company, atraso: atrasoReal, es }) || defaultTpl(es));
  useEffect(() => {
    setTpl(elegirPlantilla(templates, { tipo: row.type, compania: company, atraso: etapa, es: sendEs })
      || defaultTpl(sendEs));
    // `tpl` fuera a proposito: se REESCRIBE aqui, y meterlo seria un bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendEs, etapa]);
  const [sending, setSending] = useState(false);
  // The actual message sent = template with tokens filled for this client.
  const msg = fillReminder(tpl, vars);
  const email = client?.email || '';
  const phone = (client?.phone || '').replace(/[^0-9+]/g, '');
  const subject = sendEs ? `Recordatorio de pago — ${typeLabel}` : `Payment reminder — ${typeLabel}`;

  // Sends server-side from the agent's connected mailbox (seat → supervisor).
  const sendMail = async () => {
    if (!email || sending) return;
    setSending(true);
    try {
      const res = await sendPaymentReminder({ policyId: row.id, period, subject, message: msg });
      toast.success(es ? `Correo enviado a ${res.sentTo || email}` : `Email sent to ${res.sentTo || email}`);
      onClose();
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo enviar el correo' : 'Could not send the email'));
    } finally {
      setSending(false);
    }
  };
  const openWa = () => {
    const num = phone.replace(/^\+/, '');
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };
  // El SMS sale POR DESKTE, igual que el correo de al lado: por el proveedor
  // conectado del agente (Quo / Twilio), desde el numero de la agencia y
  // quedando en el hilo del cliente. Antes se abria la app de mensajes del
  // ordenador y el recordatorio salia del telefono personal de quien estuviera
  // sentado ahi (Lissette, 2026-09-01). Si no se puede, `enviarSmsAlCliente`
  // vuelve a abrir ese enlace y dice por que.
  const [enviandoSms, setEnviandoSms] = useState(false);
  const openSms = async () => {
    if (enviandoSms) return;
    setEnviandoSms(true);
    try {
      const r = await enviarSmsAlCliente({ clientId: client?.id, telefono: client?.phone, mensaje: msg, lang });
      if (r.enviado) { toast.success(es ? `SMS enviado a ${phone}` : `SMS sent to ${phone}`); onClose(); }
      else if (r.texto) toast.warn(r.texto);
    } finally {
      setEnviandoSms(false);
    }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(msg); toast.success(es?'Mensaje copiado':'Message copied'); }
    catch { toast.error(es?'No se pudo copiar':'Could not copy'); }
  };

  const inpSm = {background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',borderRadius:7,color:'var(--c-text,#eef6ff)',fontSize:'.74rem',padding:'.35rem .5rem',fontFamily:'inherit'};
  return <Modal title={es?'Enviar recordatorio de pago':'Send payment reminder'} onClose={onClose}>
    <div style={{display:'flex',flexDirection:'column',gap:12,minWidth:340}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,flexWrap:'wrap'}}>
        <div style={{fontSize:'.78rem',color:'var(--c-text2,#7da0c4)'}}>
          <strong style={{color:'var(--c-text,#eef6ff)'}}>{name||'—'}</strong> · {typeLabel} · {company||'—'} · {monthLabel}
        </div>
        {/* Message language toggle (ES / EN) — applies before sending. */}
        <div style={{display:'inline-flex',borderRadius:8,overflow:'hidden',border:'1px solid var(--c-border,#1a3550)'}}>
          {[['es','🇪🇸 ES'],['en','🇺🇸 EN']].map(([code,label])=>{
            const on = sendEs === (code==='es');
            return <button key={code} onClick={()=>setSendEs(code==='es')}
              style={{padding:'.3rem .6rem',fontSize:'.72rem',fontWeight:800,border:'none',cursor:'pointer',
                background:on?'var(--c-cyan,#00d4ff)':'var(--c-surface2,#112538)',color:on?'#0a1628':'var(--c-text2,#7da0c4)'}}>{label}</button>;
          })}
        </div>
      </div>

      {/* ETAPA DEL RECORDATORIO (Lissette 2026-09-01, punto 7). Abre en el
          atraso REAL de este recibo y se puede cambiar: a veces se quiere
          mandar el mensaje suave aunque lleve dos meses. Solo sale cuando hay
          mas de una etapa guardada — con una sola, este selector seria un
          control que no elige nada. */}
      {etapas.length > 1 && (
        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <span style={{fontSize:'.7rem',color:'var(--c-text2,#7da0c4)',fontWeight:700}}>
            {es?'Etapa':'Stage'}
          </span>
          <select className="dkt-inp" value={etapa} onChange={(e)=>setEtapa(Number(e.target.value))}
            style={{...inpSm,minWidth:170}}>
            {etapas.map((n)=>(
              <option key={n} value={n}>
                {nombreDeEtapa(n, es?'es':'en')}{n===atrasoReal?(es?' — le toca':' — due'):''}
              </option>
            ))}
          </select>
          {etapa !== atrasoReal && (
            // Sin este aviso, cambiar la etapa y olvidarlo manda el mensaje
            // equivocado sin que nada lo diga.
            <span style={{fontSize:'.68rem',color:'#f59e0b'}}>
              {es
                ? `Este recibo lleva ${nombreDeEtapa(atrasoReal,'es').toLowerCase()}.`
                : `This one is at ${nombreDeEtapa(atrasoReal,'en').toLowerCase()}.`}
            </span>
          )}
        </div>
      )}

      {/* Editable TEMPLATE (with tokens). */}
      <div>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'.5rem',marginBottom:4}}>
          <label style={{fontSize:'.66rem',color:'var(--c-text3,#45688a)',fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em'}}>{es?'Plantilla del mensaje':'Message template'}</label>
          <AiDraftButton lang={sendEs?'es':'en'} compact
            contextHint={'This is a payment-reminder message to an insurance client about an upcoming/due premium. Keep it short, polite and clear. You may use the tokens {nombre} {compania} {tipo} {mes} {poliza} {prima} (single brace) which get replaced automatically. {prima} is the monthly premium of that policy, already formatted as money. Return just the message body, no subject.'}
            onResult={({ body }) => setTpl(body)} />
        </div>
        <textarea value={tpl} onChange={(e)=>setTpl(e.target.value)} rows={5}
          style={{width:'100%',padding:'.6rem',borderRadius:8,background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',color:'var(--c-text,#eef6ff)',fontSize:'.8rem',fontFamily:'inherit',resize:'vertical',boxSizing:'border-box'}}/>
        <div style={{fontSize:'.64rem',color:'var(--c-text3,#45688a)',marginTop:4,lineHeight:1.5}}>
          {es?'Etiquetas que se reemplazan solas:':'Tokens replaced automatically:'} {REMINDER_TOKENS.map((t)=>(
            <code key={t} style={{background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',borderRadius:4,padding:'0 4px',marginRight:4,color:'var(--c-cyan,#00d4ff)'}}>{t}</code>
          ))}
        </div>
      </div>

      {/* Live preview (what the client actually receives). */}
      <div>
        <label style={{fontSize:'.66rem',color:'var(--c-text3,#45688a)',fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',display:'block',marginBottom:4}}>{es?'Vista previa':'Preview'}</label>
        <div style={{padding:'.6rem',borderRadius:8,background:'var(--c-surface,#0d1f33)',border:'1px solid var(--c-border,#1a3550)',color:'var(--c-text2,#7da0c4)',fontSize:'.78rem',whiteSpace:'pre-wrap',lineHeight:1.5}}>{msg}</div>
      </div>

      {/* Send actions. */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        <button onClick={sendMail} disabled={!email||sending} title={email||(es?'Sin correo':'No email')}
          style={btnStyle(!email||sending,'#3b82f6')}><Mail size={14}/>{sending?(es?'Enviando…':'Sending…'):(es?'Enviar correo':'Send email')}</button>
        <button onClick={openWa} disabled={!phone} title={phone||(es?'Sin teléfono':'No phone')}
          style={btnStyle(!phone,'#22c55e')}><MessageSquare size={14}/>WhatsApp</button>
        <button onClick={openSms} disabled={!phone||enviandoSms} title={phone||(es?'Sin teléfono':'No phone')}
          style={btnStyle(!phone||enviandoSms,'#8b5cf6')}><Phone size={14}/>{enviandoSms?(es?'Enviando…':'Sending…'):'SMS'}</button>
        <button onClick={copy} style={btnStyle(false,'#64748b')}>{es?'Copiar':'Copy'}</button>
      </div>
      {(!email && !phone) && (
        <div style={{fontSize:'.7rem',color:'#f59e0b'}}>
          {es?'Este cliente no tiene correo ni teléfono en su ficha.':'This client has no email or phone on file.'}
        </div>
      )}
    </div>
  </Modal>;
}

const btnStyle = (disabled, color) => ({
  display:'flex',alignItems:'center',gap:6,padding:'.45rem .8rem',borderRadius:8,
  background:`${color}22`,border:`1px solid ${color}55`,color,fontSize:'.74rem',fontWeight:700,
  cursor:disabled?'not-allowed':'pointer',opacity:disabled?.45:1,
});

// Human label for a template's (type, carrier) scope.
function reminderScopeLabel(t, es) {
  const typeLabel = t.policyType ? (es ? getPolicyType(t.policyType).labelEs : getPolicyType(t.policyType).label) : '';
  const base = (t.carrier && t.policyType) ? `${t.carrier} · ${typeLabel}`
    : t.carrier ? `${t.carrier} · ${es?'cualquier tipo':'any type'}`
    : t.policyType ? `${es?'Todo':'All'} ${typeLabel}`
    : (es ? 'Todas las pólizas (general)' : 'All policies (general)');
  // LA ETAPA VA EN LA ETIQUETA. Ahora puede haber varias plantillas para la
  // MISMA compañía, y sin esto se leerían idénticas en la lista: no habría
  // forma de saber cuál se está editando ni cuál se borra.
  const n = Number(t.monthsOverdue) || 0;
  return n > 0 ? `${base} · ${nombreDeEtapa(n, es?'es':'en')}` : base;
}

// Standalone reminder-template manager. Lets the agent define personalized
// messages per (policy type + company) PROACTIVELY — she can type any company
// name without waiting for a client that has it. Saves to the server.
function ReminderTemplatesManager({ templates = [], carriers = [], onChange, onClose, lang }) {
  const es = lang === 'es';
  const blank = { policyType: '', carrier: '', monthsOverdue: 0, body: '', bodyEs: '' };
  const [draft, setDraft] = useState(blank);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const editExisting = (t) => setDraft({ policyType: t.policyType || '', carrier: t.carrier || '', monthsOverdue: Number(t.monthsOverdue) || 0, body: t.body || '', bodyEs: t.bodyEs || '' });

  const save = async () => {
    if (saving) return;
    if (!draft.body.trim() && !draft.bodyEs.trim()) { toast.error(es?'Escribe el mensaje en español o inglés.':'Write the message in Spanish or English.'); return; }
    setSaving(true);
    try {
      await saveReminderTemplate({
        policyType: draft.policyType || '',
        carrier: (draft.carrier || '').trim(),
        monthsOverdue: Number(draft.monthsOverdue) || 0,
        body: draft.body || '',
        bodyEs: draft.bodyEs || '',
      });
      toast.success(es ? 'Plantilla guardada' : 'Template saved');
      setDraft(blank);
      if (onChange) onChange();
    } catch (e) { toast.error(e.message || (es?'No se pudo guardar':'Could not save')); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    const ok = await confirmDialog({
      title: es ? 'Eliminar plantilla' : 'Delete template',
      message: es ? `¿Eliminar la plantilla de "${reminderScopeLabel(t, es)}"?` : `Delete the "${reminderScopeLabel(t, es)}" template?`,
      danger: true, confirmLabel: es ? 'Eliminar' : 'Delete',
    });
    if (!ok) return;
    try { await deleteReminderTemplate(t.id); if (onChange) onChange(); }
    catch (e) { toast.error(e.message); }
  };

  const inp = {width:'100%',padding:'.5rem .6rem',borderRadius:8,background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',color:'var(--c-text,#eef6ff)',fontSize:'.8rem',fontFamily:'inherit',boxSizing:'border-box'};
  const lbl = {fontSize:'.66rem',color:'var(--c-text3,#45688a)',fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',display:'block',marginBottom:4};

  return <Modal title={es?'Plantillas de recordatorio':'Reminder templates'} onClose={onClose}>
    <div style={{display:'flex',flexDirection:'column',gap:14,minWidth:360,maxWidth:520}}>
      <div style={{fontSize:'.76rem',color:'var(--c-text2,#7da0c4)',lineHeight:1.5}}>
        {es?'Crea mensajes personalizados por tipo de póliza y compañía. Puedes escribir cualquier compañía sin esperar a tener un cliente con ella.'
           :'Create personalized messages per policy type and company. You can type any company without waiting for a client that has it.'}
      </div>

      {/* Editor */}
      <div style={{display:'flex',flexDirection:'column',gap:10,padding:'.8rem',background:'var(--c-surface,#0d1f33)',border:'1px solid var(--c-border,#1a3550)',borderRadius:10}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <div>
            <label style={lbl}>{es?'Según el tipo de póliza':'By policy type'}</label>
            <select value={draft.policyType} onChange={(e)=>set('policyType',e.target.value)} style={inp}>
              <option value="">{es?'Todas (cualquier tipo)':'All (any type)'}</option>
              {POLICY_TYPES.map((pt)=><option key={pt.id} value={pt.id}>{(pt.icon?pt.icon+' ':'')+(es?pt.labelEs:pt.label)}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>{es?'Según la compañía':'By company'}</label>
            <Combobox value={draft.carrier} onChange={(v)=>set('carrier',v)}
              options={carriers} placeholder={es?'Todas / escribe una…':'All / type one…'}
              className="" inputStyle={inp} lang={lang}/>
          </div>
        </div>
        {/* LA ETAPA. Es lo que permite MAS DE UNA plantilla por compañía
            (Lissette 2026-09-01, punto 7): antes la clave era solo tipo +
            compañía, así que guardar una segunda sobreescribía la primera. */}
        <div>
          <label style={lbl}>{es?'¿Cuándo se usa este mensaje?':'When is this message used?'}</label>
          <select value={draft.monthsOverdue} onChange={(e)=>set('monthsOverdue',Number(e.target.value))} style={inp}>
            {[0,1,2,3,4,6,12].map((n)=><option key={n} value={n}>{nombreDeEtapa(n, es?'es':'en')}</option>)}
          </select>
          <div style={{fontSize:'.66rem',color:'var(--c-text3,#45688a)',marginTop:4,lineHeight:1.5}}>
            {es
              ? 'Se usa la etapa más alta que no pase del atraso real: con mensajes de "aviso normal" y "2 meses", uno de un mes usa el normal y uno de tres usa el de 2 meses. Así nunca queda un pago sin mensaje.'
              : 'The highest stage that does not exceed the real delay is used: with "standard" and "2 months" messages, a one-month-late payment uses the standard one and a three-month one uses the 2-month message. No payment is ever left without a message.'}
          </div>
        </div>
        <div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'.5rem'}}>
            <label style={lbl}>🇪🇸 {es?'Mensaje en español':'Spanish message'}</label>
            <AiDraftButton lang="es" compact
              contextHint={'Payment-reminder message to an insurance client about a due premium. Short and polite. Use tokens {nombre} {compania} {tipo} {mes} {poliza} {prima} (single brace; {prima} is the monthly premium, already formatted as money). Return just the body, no subject.'}
              onResult={({ body }) => set('bodyEs', body)} />
          </div>
          <textarea value={draft.bodyEs} onChange={(e)=>set('bodyEs',e.target.value)} rows={3} style={{...inp,resize:'vertical'}}
            placeholder={es?'Hola {nombre}, le recordamos el pago de su póliza de {tipo} con {compania}…':'Hola {nombre}…'}/>
        </div>
        <div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'.5rem'}}>
            <label style={lbl}>🇺🇸 {es?'Mensaje en inglés':'English message'}</label>
            <AiDraftButton lang="en" compact
              contextHint={'Payment-reminder message to an insurance client about a due premium. Short and polite. Use tokens {nombre} {compania} {tipo} {mes} {poliza} {prima} (single brace; {prima} is the monthly premium, already formatted as money). Return just the body, no subject.'}
              onResult={({ body }) => set('body', body)} />
          </div>
          <textarea value={draft.body} onChange={(e)=>set('body',e.target.value)} rows={3} style={{...inp,resize:'vertical'}}
            placeholder={'Hi {nombre}, a friendly reminder about your {tipo} payment with {compania}…'}/>
        </div>
        <div style={{fontSize:'.64rem',color:'var(--c-text3,#45688a)',lineHeight:1.5}}>
          {es?'Etiquetas:':'Tokens:'} {REMINDER_TOKENS.map((t)=>(
            <code key={t} style={{background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',borderRadius:4,padding:'0 4px',marginRight:4,color:'var(--c-cyan,#00d4ff)'}}>{t}</code>
          ))}
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          {(draft.body||draft.bodyEs||draft.carrier||draft.policyType) && (
            <button onClick={()=>setDraft(blank)} style={{...btnStyle(false,'#64748b'),padding:'.4rem .8rem'}}>{es?'Limpiar':'Clear'}</button>
          )}
          <button onClick={save} disabled={saving} style={{...btnStyle(saving,'#00d4ff'),padding:'.45rem 1rem'}}>
            <Save size={13}/>{saving?(es?'Guardando…':'Saving…'):(es?'Guardar plantilla':'Save template')}
          </button>
        </div>
      </div>

      {/* Existing templates */}
      <div>
        <div style={{fontSize:'.66rem',color:'var(--c-text3,#45688a)',fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>
          {es?`Plantillas guardadas (${templates.length})`:`Saved templates (${templates.length})`}
        </div>
        {templates.length===0 && <div style={{fontSize:'.78rem',color:'var(--c-text3,#45688a)',padding:'.5rem 0'}}>{es?'Aún no hay plantillas.':'No templates yet.'}</div>}
        <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:200,overflowY:'auto'}}>
          {templates.map((t)=>(
            <div key={t.id} style={{display:'flex',alignItems:'center',gap:8,padding:'.5rem .6rem',background:'var(--c-surface2,#112538)',border:'1px solid var(--c-border,#1a3550)',borderRadius:8}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:'.78rem',fontWeight:700,color:'var(--c-white,#eef6ff)'}}>{reminderScopeLabel(t,es)}</div>
                <div style={{fontSize:'.7rem',color:'var(--c-text3,#45688a)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{(es?(t.bodyEs||t.body):(t.body||t.bodyEs))||'—'}</div>
              </div>
              <button onClick={()=>editExisting(t)} title={es?'Editar':'Edit'} style={{...btnStyle(false,'#00d4ff'),padding:'.35rem .5rem'}}><Edit2 size={13}/></button>
              <button onClick={()=>remove(t)} title={es?'Eliminar':'Delete'} style={{...btnStyle(false,'#ef4444'),padding:'.35rem .5rem'}}><Trash2 size={13}/></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Modal>;
}
